import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, Menu, MenuItem, Select, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery,
  CircularProgress,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  addDoc, collection, deleteDoc, doc, getDoc, onSnapshot, query, serverTimestamp, setDoc, writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import RateLibraryMigration from '../components/RateLibraryMigration';
import { useMaterialLibrary } from '../office/useMaterialLibrary';
import { createMaterialItem, deleteMaterialItem, setProjectRates, updateMaterialItem } from '../office/materialLibraryStore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { fmtIN, num } from '../office/estimationUtils';
import { type AllocationDestination } from '../office/materialAllocation';
import MaterialAllocationPanel from '../components/MaterialAllocationPanel';
import { defaultDimensionUnitFor, DIMENSION_LABELS, DIMENSION_UNIT_LABEL, DIMENSION_UNITS, pieceShapeForUnit } from '../office/pieceQuantity';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { cartingAgentsFor, planCartingAgents, removeCartingAgent } from '../office/carting';
import { saveCartingAgents, useVendorCartingAgents } from '../office/vendorStore';
import { trackedMaterials, type StockIssue } from '../office/materialStock';
import { libraryPicks, vendorOptionsForItem } from '../office/materialReceiving';
import { SegmentedAdornment } from '../components/phoneUi';

// ─── tokens (match other office pages) ───────────────────────────────────────
const GOLD = '#9d6f32';
const GOLD_D = '#7a5525';
const GOLD_L = '#b8893f';
const GOLD_WASH = 'rgba(157,111,50,0.08)';
const DARK = '#1a1a1a';
const MUTED = '#6b6b6b';
const FAINT = '#938b7d';
const BORDER = '#e8e8e8';
const PANEL2 = '#f3f0ea';
const WARN = '#c07a12';
const FONT_HEADING = "'Georgia', 'Times New Roman', serif";
const FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const POPUP_PAPER_SX = {
  width: { xs: '100%', sm: 'min(750px, calc(100vw - 32px))' },
  maxWidth: { xs: '100%', sm: 750 },
};

const STEEL_DIAMETERS = ['8mm', '10mm', '12mm', '16mm', '20mm', '25mm', '32mm'];
type BuiltInMaterialGroup = 'steel' | 'cement' | 'rmc' | 'sandAggregate' | 'blocks' | 'flooring' | 'doorMaterials' | 'plumbing' | 'electrical' | 'fire' | 'lift' | 'fabrication' | 'aluminiumSections' | 'paint' | 'labour' | 'other';
type MaterialGroup = BuiltInMaterialGroup | (string & {});
type MaterialGstDisplay = 'incl' | 'excl';
type MaterialReportDialogState = { selectedIds: string[]; label?: string } | null;
type EstimateSourceUsageMarker = { label: string; tone: 'same' | 'other'; details?: string };
type EstimateSourceUsageCandidate = { sourceType: 'material' | 'labour' | 'assembly'; sourceId: string; quoteOptionId?: string; assemblyType?: string; variety?: string };
type ComparisonOrderLine = Omit<MaterialProcurementOrder, 'id'>;
type ComparisonOrderReview = { orders: ComparisonOrderLine[]; freightAmount: number };

const MATERIAL_GROUP_LABEL: Record<BuiltInMaterialGroup, string> = {
  steel: 'Steel / TMT',
  cement: 'Cement',
  rmc: 'Concrete / RMC',
  sandAggregate: 'Sand / Aggregate',
  blocks: 'Blocks / Bricks',
  flooring: 'Flooring / Tiles',
  doorMaterials: 'Door Materials',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  fire: 'Fire',
  lift: 'Lift',
  fabrication: 'Fabrication',
  aluminiumSections: 'Aluminium Sections',
  paint: 'Paint / Finishing',
  labour: 'Labour',
  other: 'Other',
};

export const MATERIAL_GROUPS = Object.keys(MATERIAL_GROUP_LABEL) as BuiltInMaterialGroup[];
const GST_PERCENT_OPTIONS = [0, 5, 12, 18, 28];
const isBuiltInMaterialGroup = (group?: string): group is BuiltInMaterialGroup =>
  !!group && MATERIAL_GROUPS.includes(group as BuiltInMaterialGroup);
export const materialGroupLabel = (group?: string) =>
  isBuiltInMaterialGroup(group) ? MATERIAL_GROUP_LABEL[group] : (group || 'Other');
export const materialGroupValueFromLabel = (value: string): MaterialGroup => {
  const trimmed = value.trim();
  if (!trimmed) return 'other';
  const matched = MATERIAL_GROUPS.find((group) =>
    group.toLowerCase() === trimmed.toLowerCase() ||
    MATERIAL_GROUP_LABEL[group].toLowerCase() === trimmed.toLowerCase()
  );
  return matched || trimmed;
};
export const MATERIAL_UNIT_OPTIONS = ['Kg', 'MT', 'Bag', 'Box', 'Cum', 'Sft', 'Sqm', 'Rft', 'Meter', 'Nos', 'Set', 'Roll', 'Day', 'Lumpsum'];
const ASSEMBLY_TYPE_OPTIONS = ['Door', 'Window', 'Kitchen', 'Toilet', 'Flooring', 'Plumbing', 'Electrical', 'Fire', 'Lift', 'Fabrication', 'Paint', 'Furniture', 'Other'];
const fmtQty = (value: number, maxFractionDigits = 3) =>
  Number.isFinite(value)
    ? value.toLocaleString('en-IN', { maximumFractionDigits: maxFractionDigits })
    : '0';
const withoutUndefined = (value: unknown): any => {
  if (Array.isArray(value)) return value.map(withoutUndefined);
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, nested]) => nested !== undefined)
        .map(([key, nested]) => [key, withoutUndefined(nested)]),
    );
  }
  return value;
};
const fmtRate = (value: number, maxFractionDigits = 2) => {
  if (!Number.isFinite(value)) return '0';
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
    });
};
const escHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
const safeFilePart = (value: unknown) =>
  String(value || 'material_rates')
    .trim()
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase() || 'material_rates';

const comparisonPoNumbers = (existing: string[], count: number) => {
  const numbered = existing.map((value) => /^(.*?)(\d+)$/.exec(String(value || '').trim())).filter(Boolean) as RegExpExecArray[];
  const latest = numbered.reduce<{ prefix: string; number: number; width: number } | null>((best, match) => {
    const candidate = { prefix: match[1], number: Number(match[2]), width: match[2].length };
    return !best || candidate.number > best.number ? candidate : best;
  }, null);
  const prefix = latest?.prefix || 'PO-';
  const width = latest?.width || 4;
  const start = (latest?.number || 0) + 1;
  return Array.from({ length: count }, (_, index) => `${prefix}${String(start + index).padStart(width, '0')}`);
};

const blobFromRenderTemplateResult = (resultData: unknown): Blob => {
  const data = (resultData || {}) as Record<string, any>;
  const mimeType = String(data.mimeType || 'application/pdf');
  const rawBytes = Array.isArray(data.pdfBytes)
    ? data.pdfBytes.map((item: any) => Number(item || 0))
    : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes)
          .filter((key) => /^\d+$/.test(key))
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : null;
  if (rawBytes && rawBytes.length > 0) {
    return new Blob([new Uint8Array(rawBytes)], { type: mimeType });
  }
  const rawBase64 =
    typeof data.pdfBase64 === 'string'
      ? data.pdfBase64
      : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
        ? (() => {
            const source = data.pdfBase64?.data || [];
            let binaryBuffer = '';
            const chunkSize = 0x8000;
            for (let i = 0; i < source.length; i += chunkSize) {
              const chunk = source.slice(i, i + chunkSize);
              binaryBuffer += String.fromCharCode(...chunk);
            }
            return btoa(binaryBuffer);
          })()
        : '';
  if (!rawBase64) throw new Error('PDF response was empty.');
  const normalizedBase64 = String(rawBase64)
    .replace(/^data:application\/pdf;base64,/i, '')
    .replace(/\s+/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const paddedBase64 = normalizedBase64.padEnd(
    normalizedBase64.length + ((4 - (normalizedBase64.length % 4)) % 4),
    '='
  );
  const binary = atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
};
const DEFAULT_MATERIAL_UNIT: Record<BuiltInMaterialGroup, string> = {
  steel: 'Kg',
  cement: 'Bag',
  rmc: 'Cum',
  sandAggregate: 'MT',
  blocks: 'Nos',
  flooring: 'Sft',
  doorMaterials: 'Sft',
  plumbing: 'Nos',
  electrical: 'Meter',
  fire: 'Nos',
  lift: 'Set',
  fabrication: 'Kg',
  aluminiumSections: 'Kg',
  paint: 'Sft',
  labour: 'Day',
  other: 'Nos',
};

const DEFAULT_MATERIAL_QUOTE_OPTIONS: Record<BuiltInMaterialGroup, MaterialQuoteOptionTemplate[]> = {
  steel: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Kg' }],
  cement: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Bag' }],
  rmc: [
    { id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Cum' },
    { id: 'pump_charge', name: 'Pump Charge', type: 'addon', unit: 'Cum' },
  ],
  sandAggregate: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'MT' }],
  blocks: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Nos' }],
  flooring: [
    { id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Sft' },
    { id: 'skirting', name: 'Skirting', type: 'addon', unit: 'Rft' },
  ],
  doorMaterials: [
    { id: 'single_core', name: 'Single Core', type: 'base', unit: 'Sft' },
    { id: 'double_core', name: 'Double Core', type: 'base', unit: 'Sft' },
    { id: 'border_patti', name: 'Border Patti', type: 'addon', unit: 'Rft' },
  ],
  plumbing: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Nos' }],
  electrical: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Nos' }],
  fire: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Nos' }],
  lift: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Set' }],
  fabrication: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Kg' }],
  aluminiumSections: [
    { id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Kg' },
    { id: 'finish_extra', name: 'Finish Extra', type: 'addon', unit: 'Kg' },
  ],
  paint: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Sft' }],
  labour: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Day' }],
  other: [{ id: 'base_rate', name: 'Base Rate', type: 'base', unit: 'Nos' }],
};
const defaultMaterialUnit = (group: MaterialGroup) =>
  DEFAULT_MATERIAL_UNIT[isBuiltInMaterialGroup(group) ? group : 'other'];
const defaultMaterialQuoteOptions = (group: MaterialGroup) =>
  DEFAULT_MATERIAL_QUOTE_OPTIONS[isBuiltInMaterialGroup(group) ? group : 'other'];

export type MaterialQuote = {
  id: string;
  vendor: string;
  brand: string;
  refName?: string;
  refNumber?: string;
  pricingMode?: 'uniform' | 'diameterOverrides';
  rate: number;
  baseRateHistory?: MaterialRateRevision[];
  materialCode?: string;
  coveredDiameters?: string[];
  diameterRates?: Record<string, number | null>;
  quoteOptions?: MaterialQuoteOption[];
  unit: string;
  gst: number;
  gstMode?: 'inclusive' | 'exclusive' | 'none';
  freight: number;
  loading: number;
  quoteDate: string;
  validUntil: string;
  /** Quote-level status. Price history is maintained per base rate or option below. */
  effectiveFrom?: string;
  effectiveUntil?: string;
  status?: 'active' | 'superseded' | 'voided';
  supersedesQuoteId?: string;
  supersededByQuoteId?: string;
  voidReason?: string;
  recordedAt?: string;
  paymentTerms: string;
  deliveryTimeline: string;
  notes: string;
  /** Kept as a saved quote, but ignored when finding the lowest comparison rate. */
  excludedFromComparison?: boolean;
  checklist: {
    testCertificate: boolean;
    weighbridgeSlip: boolean;
    heatNumberTraceability: boolean;
    freeUnloading: boolean;
    creditPeriod: boolean;
  };
};

type MaterialRateRevision = {
  id: string;
  previousRate?: number;
  rate: number;
  effectiveFrom: string;
  reason?: string;
  recordedAt: string;
};

type MaterialQuoteOption = {
  id: string;
  templateId?: string;
  group?: string;
  variety?: string;
  name: string;
  type: 'base' | 'addon';
  rate: number;
  unit: string;
  secondaryUnitRequired?: boolean;
  secondaryMultiplier?: number;
  secondaryUnit?: string;
  brand?: string;
  code?: string;
  rateHistory?: MaterialRateRevision[];
};

type MaterialQuoteOptionTemplate = {
  id: string;
  group?: string;
  name: string;
  type: 'base' | 'addon';
  unit: string;
  secondaryUnitRequired?: boolean;
  secondaryMultiplier?: number;
  secondaryUnit?: string;
};

type MaterialAssemblyVarietyMap = {
  id: string;
  assemblyType: string;
  varieties: string[];
  type: 'base' | 'addon';
  unit: string;
  secondaryUnitRequired?: boolean;
  secondaryMultiplier?: number;
  secondaryUnit?: string;
  /** This assembly type is received as pieces in a box (tiles) rather than one typed quantity. */
  boxReceiving?: boolean;
};

export type MaterialBoxPacking = {
  assemblyType: string;
  variety: string;
  piecesPerBox: number;
  /** The estimate/stock quantity represented by one tile or piece, normally Sft. */
  areaPerPiece: number;
};

export type MaterialRateItem = {
  id: string;
  group: MaterialGroup;
  materialName: string;
  grade: string;
  diameter?: string;
  coveredDiameters?: string[];
  brand: string;
  unit: string;
  secondaryUnitRequired?: boolean;
  secondaryMultiplier?: number;
  secondaryUnit?: string;
  /** Received as a list of pieces (length/width/height) rather than one typed number — granite, glass, cut lengths. */
  pieceEntry?: boolean;
  pieceDimensionUnit?: 'in' | 'ft' | 'cm' | 'm';
  /** Legacy material metadata kept for existing records. */
  trackStock?: boolean;
  /** Exact: counted precisely (cement bags). Tentative: bulk volume ordered/estimated by the truckload (sand, aggregate) — ordering estimates the truck size from delivery history rather than a fixed conversion. */
  stockPrecision?: 'exact' | 'tentative';
  /** Labour only — whether this trade appears as a "who used it" option when recording site stock usage. Defaults to shown. */
  laborStockVisible?: boolean;
  codeRequired?: boolean;
  assemblyVarietyMap?: MaterialAssemblyVarietyMap[];
  /** Optional physical packing for a specific assembly and variety, such as tiles in a box. */
  boxPackings?: MaterialBoxPacking[];
  assemblyTypes?: string[];
  varieties?: string[];
  quoteOptionTemplates?: MaterialQuoteOptionTemplate[];
  selectedQuoteId?: string | null;
  selectedOptionQuoteIds?: Record<string, string | null>;
  // Used only for tentative project costing when an actual selected rate is absent.
  defaultCostingQuoteId?: string | null;
  defaultCostingOptionQuoteIds?: Record<string, string | null>;
  dealNotes?: string;
  quotes?: MaterialQuote[];
  order?: number;
};

const materialSecondaryUnitLabel = (item: Pick<MaterialRateItem, 'unit' | 'secondaryUnitRequired' | 'secondaryMultiplier' | 'secondaryUnit'>) => {
  if (!item.secondaryUnitRequired) return '';
  const multiplier = num(item.secondaryMultiplier);
  const secondaryUnit = (item.secondaryUnit || '').trim();
  if (multiplier <= 0 || !secondaryUnit) return '';
  return `1 ${item.unit || 'unit'} = ${fmtQty(multiplier)} ${secondaryUnit}`;
};

const quoteReferenceLabel = (quote: Pick<MaterialQuote, 'refName' | 'refNumber'>) =>
  [quote.refName, quote.refNumber].map((value) => (value || '').trim()).filter(Boolean).join(' · ');

const quoteTemplateSecondaryUnitLabel = (
  template: Pick<MaterialQuoteOptionTemplate, 'unit' | 'secondaryUnitRequired' | 'secondaryMultiplier' | 'secondaryUnit'>,
  item: Pick<MaterialRateItem, 'unit' | 'secondaryUnitRequired' | 'secondaryMultiplier' | 'secondaryUnit'>
) => {
  const hasTemplateOverride = !!template.secondaryUnitRequired;
  const active = hasTemplateOverride || !!item.secondaryUnitRequired;
  if (!active) return '';
  const multiplier = hasTemplateOverride ? num(template.secondaryMultiplier) : num(item.secondaryMultiplier);
  const secondaryUnit = ((hasTemplateOverride ? template.secondaryUnit : item.secondaryUnit) || '').trim();
  if (multiplier <= 0 || !secondaryUnit) return '';
  return `1 ${template.unit || item.unit || 'unit'} = ${fmtQty(multiplier)} ${secondaryUnit}`;
};

const quoteTemplateSecondaryRate = (
  template: Pick<MaterialQuoteOptionTemplate, 'secondaryUnitRequired' | 'secondaryMultiplier' | 'secondaryUnit'>,
  item: Pick<MaterialRateItem, 'secondaryUnitRequired' | 'secondaryMultiplier' | 'secondaryUnit'>,
  rate: number,
) => {
  const hasTemplateOverride = !!template.secondaryUnitRequired;
  const multiplier = hasTemplateOverride ? num(template.secondaryMultiplier) : num(item.secondaryMultiplier);
  const unit = ((hasTemplateOverride ? template.secondaryUnit : item.secondaryUnit) || '').trim();
  if (!rate || multiplier <= 0 || !unit || !(hasTemplateOverride || item.secondaryUnitRequired)) return null;
  return { rate: rate / multiplier, unit };
};

type AssemblyComponent = {
  id: string;
  type: 'material' | 'manual';
  manualRateSource?: 'pending' | 'manual' | 'material';
  basis?: 'output' | 'reference';
  // Retained only to read existing records; active calculations always use unit.
  orderUnit?: string;
  label: string;
  qty: number;
  unit: string;
  materialName?: string;
  materialGroup?: MaterialGroup;
  quoteOptionId?: string;
  linkedMaterialId?: string;
  linkedMaterialName?: string;
  linkedMaterialGroup?: MaterialGroup;
  linkedQuoteId?: string;
  linkedQuoteOptionId?: string;
  manualRate?: number;
  gst?: number;
  gstMode?: 'inclusive' | 'exclusive';
};

type AssemblyComponentDraft = AssemblyComponent & {
  material?: MaterialRateItem | null;
};

export type QuantityProfileComponent = {
  id: string;
  workItemId?: string;
  sourceType?: 'material' | 'labour' | 'assembly';
  sourceId?: string;
  quoteOptionId?: string;
  label: string;
  group?: string;
  assemblyType?: string;
  variety?: string;
  qtyPerUnit: number;
  unit: string;
  areaPerUnit?: number;
  areaUnit?: string;
  secondaryOverride?: boolean;
  leadEnabled?: boolean;
  leadType?: 'perFloor' | 'fixed';
  leadPercent?: number;
};

export type UnitTypeBuildingAllocation = {
  id: string;
  towerId?: string;
  buildingName: string;
  count: number;
  floorFrom?: number;
  floorTo?: number;
  unitsPerFloor?: number;
  notes?: string;
};

const unitAllocationCount = (allocation: UnitTypeBuildingAllocation) => {
  const unitsPerFloor = num(allocation.unitsPerFloor);
  const floorFrom = allocation.floorFrom;
  const floorTo = allocation.floorTo;
  if (unitsPerFloor > 0 && typeof floorFrom === 'number' && typeof floorTo === 'number' && floorTo >= floorFrom) {
    return (floorTo! - floorFrom! + 1) * unitsPerFloor;
  }
  return num(allocation.count);
};

export type UnitTypeMaster = {
  id: string;
  label: string;
  unit: string;
  components: QuantityProfileComponent[];
  sections?: string[];
  buildingAllocations?: UnitTypeBuildingAllocation[];
  order?: number;
};

export type MaterialOrderReceipt = {
  id: string;
  date: string;
  qty: number;
  /** Physical packing captured at receipt; qty remains the material's estimate unit. */
  boxes?: number;
  piecesPerBox?: number;
  areaPerPiece?: number;
  reference?: string;
  notes?: string;
  documentType?: 'challan' | 'bill';
  /** A bill number was noted at the gate, but the office has not entered its rates and totals yet. */
  billDetailsPending?: boolean;
  /** The bill number originally came from the gate (kept so voiding a bill can restore that state). */
  gateBill?: boolean;
  billId?: string;
  billNumber?: string;
  billDate?: string;
  /** Photo of the challan or bill taken at the gate. */
  photoUrl?: string;
  photoPath?: string;
  /** Which of the vendor's regular transporters actually delivered this load. */
  cartingAgent?: string;
};

export type MaterialOrderBuildingAllocation = {
  buildingId: string;
  buildingLabel: string;
  qty: number;
};

export type MaterialProcurementOrder = {
  id: string;
  materialId: string;
  quoteOptionId?: string;
  /** Material-library context retained for direct receipts without a saved quote yet. */
  materialAssemblyType?: string;
  materialVariety?: string;
  materialLabel: string;
  unit: string;
  vendor?: string;
  brand?: string;
  code?: string;
  quoteId?: string;
  /** Immutable price breakdown captured when this order was placed. */
  orderedBaseRate?: number;
  orderedRateExclGst?: number;
  orderedRateInclGst?: number;
  orderedGstPercent?: number;
  orderedGstMode?: 'inclusive' | 'exclusive' | 'none';
  orderedFreightRate?: number;
  orderedLoadingRate?: number;
  orderedFreightAmount?: number;
  orderedLoadingAmount?: number;
  orderedLineTotal?: number;
  rateSnapshot?: number;
  rateUnit?: string;
  rateEffectiveFrom?: string;
  scopeType: 'project' | 'building' | 'unitType';
  scopeId?: string;
  scopeLabel?: string;
  orderQty: number;
  /** When the vendor is expected to deliver (yyyy-mm-dd). */
  expectedDate?: string;
  orderDate: string;
  poNumber?: string;
  notes?: string;
  /** What the purchaser expects this material to be used for, e.g. building, trimix, plumbing, flooring. */
  intendedUse?: string;
  buildingAllocations?: MaterialOrderBuildingAllocation[];
  receipts: MaterialOrderReceipt[];
  sourceType?: 'purchaseOrder' | 'directReceipt';
  /** Purchasing confirmed a delivery that arrived without a PO. */
  unplannedReviewed?: boolean;
  /** A delivery entered as "Other item": not linked to a library material yet. */
  needsMapping?: boolean;
  /** Confirmed delivery from a vendor with no rate quote for this material yet. */
  vendorQuotePending?: boolean;
  /** The vendor was typed at the gate and had no saved quote or earlier order. */
  newVendor?: boolean;
  unplannedRejected?: boolean;
  unplannedRejectNote?: string;
  /** The undelivered balance is not coming; the line stops showing as open. */
  closedShort?: boolean;
  closedShortNote?: string;
  createdAt?: any;
  updatedAt?: any;
};

export type ItemWorkMaster = {
  id: string;
  name: string;
  category: string;
  unit: string;
  procurementMode?: 'unit' | 'oneTime';
  oneTimeKind?: 'material' | 'labour';
  oneTimeQty?: number;
  secondaryUnitRequired?: boolean;
  secondaryMultiplier?: number;
  secondaryUnit?: string;
  areaUnit?: string;
  trackArea?: boolean;
  notes?: string;
  order?: number;
};

export type WorkRateSetup = {
  workItemId: string;
  sourceType: 'none' | 'direct' | 'assembly' | 'manual';
  sourceId?: string;
  quoteOptionId?: string;
  basis?: 'primary' | 'secondary';
  manualRate?: number;
  notes?: string;
};

export type ProjectEstimateScopeItem = {
  id: string;
  workItemId?: string;
  sourceType?: 'material' | 'labour' | 'assembly';
  sourceId?: string;
  label?: string;
  assemblyType?: string;
  group: string;
  groupCode?: string;
  subgroup?: string;
  subgroupCode?: string;
  subgroupIsCustom?: boolean;
  variety?: string;
  quoteOptionId?: string;
  lineCode?: string;
  scopeType: 'common' | 'building' | 'project' | 'unitType';
  buildingName?: string;
  unitTypeId?: string;
  quantityMethod?: 'manual' | 'areaInch' | 'volumeInch';
  nos?: number;
  length?: number;
  breadth?: number;
  height?: number;
  qty: number;
  unit?: string;
  notes?: string;
  order?: number;
};

export type ProjectEstimateScopeSection = {
  id: string;
  group: string;
  subgroup?: string;
  order?: number;
};

type ProjectEstimateOrderCandidate = {
  line: ProjectEstimateScopeItem;
  material: MaterialRateItem;
  quote: MaterialQuote;
  optionId?: string;
  label: string;
  qty: number;
  unit: string;
  rate: number;
};

const projectScopeCalculatedQty = (item: ProjectEstimateScopeItem, unitTypes: UnitTypeMaster[] = []) => {
  const count = num(item.nos) || 1;
  const method = item.quantityMethod || 'manual';
  const measuredQty = method === 'areaInch'
    ? count * num(item.length) * num(item.breadth) / 144
    : method === 'volumeInch'
      ? count * num(item.length) * num(item.breadth) * num(item.height) / 1728
      : num(item.qty);
  if (item.scopeType !== 'unitType') return measuredQty;
  const unitType = unitTypes.find((unit) => unit.id === item.unitTypeId);
  const allocated = (unitType?.buildingAllocations || []).reduce((sum, allocation) => sum + num(allocation.count), 0);
  return measuredQty * allocated;
};

const projectScopeWithAutoCodes = (items: ProjectEstimateScopeItem[]) => {
  const groupNumbers = new Map<string, number>();
  const subgroupNumbers = new Map<string, number>();
  const lineNumbers = new Map<string, number>();
  let nextGroup = 1;
  return items.slice().sort((a, b) => num(a.order) - num(b.order)).map((item, index) => {
    const group = item.group.trim() || 'Project works';
    const groupKey = group.toLowerCase();
    if (!groupNumbers.has(groupKey)) groupNumbers.set(groupKey, nextGroup++);
    const groupNumber = groupNumbers.get(groupKey)!;
    const subgroup = (item.subgroup || '').trim();
    if (!subgroup) {
      const directKey = `${groupKey}__direct`;
      const lineNumber = (lineNumbers.get(directKey) || 0) + 1;
      lineNumbers.set(directKey, lineNumber);
      return { ...item, group, groupCode: `${groupNumber}.00`, subgroup: '', subgroupCode: '', lineCode: `${groupNumber}.${String(lineNumber).padStart(2, '0')}`, order: index + 1 };
    }
    const subgroupKey = `${groupKey}__${subgroup.toLowerCase()}`;
    if (!subgroupNumbers.has(subgroupKey)) {
      const groupSubgroupCount = Array.from(subgroupNumbers.keys()).filter((key) => key.startsWith(`${groupKey}__`)).length;
      subgroupNumbers.set(subgroupKey, groupSubgroupCount + 1);
    }
    const subgroupNumber = subgroupNumbers.get(subgroupKey)!;
    const lineNumber = (lineNumbers.get(subgroupKey) || 0) + 1;
    lineNumbers.set(subgroupKey, lineNumber);
    const subgroupCode = `${groupNumber}.${String(subgroupNumber).padStart(2, '0')}`;
    return { ...item, group, groupCode: `${groupNumber}.00`, subgroup, subgroupCode, lineCode: `${subgroupCode}.${String(lineNumber).padStart(2, '0')}`, order: index + 1 };
  });
};

type MaterialRateUsageStatus = {
  usedCount: number;
  pendingWholeItem: boolean;
  pendingOptionIds: Set<string>;
  sources: string[];
};

type UnitTypeEstimateSummary = {
  total: number;
  pending: number;
  rules: number;
};

export type MaterialAssembly = {
  id: string;
  workItemId?: string;
  name: string;
  category: string;
  outputUnit: string;
  referenceUnit?: string;
  referenceQty?: number;
  components: AssemblyComponent[];
  order?: number;
};

/** A concrete grade (M20, M25, ...) and how much of each other tracked material typically goes with
 * one unit of cement — entered by hand per grade, used only to suggest a Sand/Aggregate quantity
 * while recording cement stock usage. Never applied automatically. */
export type ConcreteGrade = {
  id: string;
  name: string;
  ratios: Array<{ materialId: string; qtyPerCementUnit: number }>;
};

/** A vendor bill with no order or delivery behind it — the only bills with material to allocate. */
export type DirectMaterialVendorBill = {
  id: string;
  vendor: string;
  invoiceDate: string;
  invoiceNumber: string;
  sourceType?: string;
  lines: Array<{ materialId?: string; quoteOptionId?: string; materialLabel: string; unit: string; qty: number; rate: number }>;
};

/** A saved record of where some received or billed material actually got used. */
export type SavedMaterialAllocation = {
  id: string;
  sourceKey: string;
  sourceType: 'receipt' | 'bill';
  orderId?: string; receiptId?: string; billId?: string; billLineKey?: string;
  materialId?: string; quoteOptionId?: string; materialLabel: string; unit: string;
  qty: number; rate: number; amount: number;
  destination: AllocationDestination;
  /** Multiple work items linked to the same quantity. Quantity is counted once. */
  destinations?: AllocationDestination[];
  allocationMode?: 'single' | 'shared';
  /** Optional link to the site stock-usage entry this allocation consumed. */
  stockIssueId?: string;
  note?: string;
  allocatedDate: string;
};

export default function OfficeEstimationPage() {
  const { currentProjectId, roleId } = useAuth();
  const isAdmin = ['super-admin', 'admin'].includes(String(roleId || ''));
  const cartingAgentsByVendor = useVendorCartingAgents();

  // Materials are shared by every project; quotes, selections and deal notes belong to this project.
  const library = useMaterialLibrary(currentProjectId);
  const materialRateItems = library.items;
  const allMaterialRateItems = library.items;
  const [justMigrated, setJustMigrated] = useState<number | null>(null);
  const [materialAssemblies, setMaterialAssemblies] = useState<MaterialAssembly[]>([]);
  const [concreteGrades, setConcreteGrades] = useState<ConcreteGrade[]>([]);
  const [projectEstimateScopeItems, setProjectEstimateScopeItems] = useState<ProjectEstimateScopeItem[]>([]);
  const [projectEstimateScopeSections, setProjectEstimateScopeSections] = useState<ProjectEstimateScopeSection[]>([]);
  const [workRateSetups, setWorkRateSetups] = useState<WorkRateSetup[]>([]);
  const [unitTypeMasters, setUnitTypeMasters] = useState<UnitTypeMaster[]>([]);
  const [unitTypeBuildingAllocations, setUnitTypeBuildingAllocations] = useState<Record<string, UnitTypeBuildingAllocation[]>>({});
  const [materialProcurementOrders, setMaterialProcurementOrders] = useState<MaterialProcurementOrder[]>([]);
  const [rateComparisonQuantities, setRateComparisonQuantities] = useState<Record<string, Record<string, number>>>({});
  // Only bills with no order or delivery behind them (sourceType 'direct') have material to allocate.
  const [materialVendorBills, setMaterialVendorBills] = useState<DirectMaterialVendorBill[]>([]);
  const [materialAllocations, setMaterialAllocations] = useState<SavedMaterialAllocation[]>([]);
  // Read here only for the Allocate tab's "stock usage for this material" reference — Stock itself is
  // owned and edited on the Material Orders & Receipts page.
  const [stockIssuesForAllocation, setStockIssuesForAllocation] = useState<StockIssue[]>([]);
  const [stockUsageSeen, setStockUsageSeen] = useState<Record<string, string>>({});
  const [projectMeta, setProjectMeta] = useState<any | null>(null);
  const [companyMeta, setCompanyMeta] = useState<any | null>(null);

  // Project estimation is now managed entirely through the rate library workspace.
  const [materialTab, setMaterialTab] = useState<'materials' | 'labour' | 'assemblies' | 'projectScope' | 'orderSummary' | 'projectCost' | 'allocate'>('materials');
  const [materialPdfBusy, setMaterialPdfBusy] = useState<string | null>(null);
  const [materialReportDlg, setMaterialReportDlg] = useState<MaterialReportDialogState>(null);

  // dialogs
  const [materialDlg, setMaterialDlg] = useState<{ edit?: MaterialRateItem; initialGroup?: MaterialGroup } | null>(null);
  const [quoteDlg, setQuoteDlg] = useState<{ item: MaterialRateItem; quote?: MaterialQuote } | null>(null);
  const [rateRevisionDlg, setRateRevisionDlg] = useState<{ item: MaterialRateItem; quote: MaterialQuote; optionId?: string; edit?: MaterialRateRevision } | null>(null);
  const [assemblyDlg, setAssemblyDlg] = useState<{ edit?: MaterialAssembly } | null>(null);
  const [concreteGradesDlg, setConcreteGradesDlg] = useState(false);

  // ── Firestore subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentProjectId) {
        setProjectMeta(null);
        setCompanyMeta(null);
        return;
      }
      try {
        const projectSnap = await getDoc(doc(db, 'projects', currentProjectId));
        if (!projectSnap.exists()) {
          if (!cancelled) {
            setProjectMeta(null);
            setCompanyMeta(null);
          }
          return;
        }
        const projectData = projectSnap.data() as any;
        if (cancelled) return;
        setProjectMeta(projectData);
        const companyId = String(projectData?.companyId || '').trim();
        if (companyId) {
          const companySnap = await getDoc(doc(db, 'companies', companyId));
          if (!cancelled) setCompanyMeta(companySnap.exists() ? companySnap.data() : null);
        } else {
          setCompanyMeta(null);
        }
      } catch {
        if (!cancelled) {
          setProjectMeta(null);
          setCompanyMeta(null);
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setMaterialAssemblies([]); return; }
    return onSnapshot(
      doc(db, 'officeSettings', 'materialAssemblyLibrary'),
      (s) => setMaterialAssemblies(((s.data()?.assemblies || []) as MaterialAssembly[]).sort(byOrder)),
      () => setMaterialAssemblies([])
    );
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setConcreteGrades([]); return; }
    return onSnapshot(
      doc(db, 'officeSettings', 'concreteGrades'),
      (s) => setConcreteGrades((s.data()?.grades || []) as ConcreteGrade[]),
      () => setConcreteGrades([])
    );
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setUnitTypeMasters([]); return; }
    return onSnapshot(
      doc(db, 'officeSettings', 'unitTypeMasterLibrary'),
      (s) => setUnitTypeMasters(((s.data()?.unitTypes || []) as UnitTypeMaster[]).sort(byOrder)),
      () => setUnitTypeMasters([])
    );
  }, [currentProjectId]);


  useEffect(() => {
    if (!currentProjectId) { setUnitTypeBuildingAllocations({}); return; }
    return onSnapshot(
      doc(db, 'officeSettings', `unitTypeBuildingAllocations_${currentProjectId}`),
      (s) => setUnitTypeBuildingAllocations((s.data()?.allocations || {}) as Record<string, UnitTypeBuildingAllocation[]>),
      () => setUnitTypeBuildingAllocations({})
    );
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setMaterialProcurementOrders([]); return; }
    return onSnapshot(
      query(collection(db, 'projects', currentProjectId, 'materialProcurementOrders')),
      (s) => setMaterialProcurementOrders(s.docs.map((d) => ({ ...(d.data() as any), id: d.id } as MaterialProcurementOrder)))
    );
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setRateComparisonQuantities({}); return; }
    return onSnapshot(
      doc(db, 'officeSettings', `rateComparisonQuantities_${currentProjectId}`),
      (snapshot) => setRateComparisonQuantities((snapshot.data()?.quantities || {}) as Record<string, Record<string, number>>),
      () => setRateComparisonQuantities({})
    );
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId || !isAdmin) { setMaterialVendorBills([]); return; }
    return onSnapshot(
      query(collection(db, 'projects', currentProjectId, 'materialVendorBills')),
      (s) => setMaterialVendorBills(s.docs.map((d) => ({ ...(d.data() as any), id: d.id } as DirectMaterialVendorBill)))
    );
  }, [currentProjectId, isAdmin]);

  useEffect(() => {
    if (!currentProjectId || !isAdmin) { setMaterialAllocations([]); return; }
    return onSnapshot(
      query(collection(db, 'projects', currentProjectId, 'materialAllocations')),
      (s) => setMaterialAllocations(s.docs.map((d) => ({ ...(d.data() as any), id: d.id } as SavedMaterialAllocation)))
    );
  }, [currentProjectId, isAdmin]);

  useEffect(() => {
    if (!currentProjectId || !isAdmin) { setStockIssuesForAllocation([]); return; }
    return onSnapshot(
      query(collection(db, 'projects', currentProjectId, 'materialStockIssues')),
      (s) => setStockIssuesForAllocation(s.docs.map((d) => ({ ...(d.data() as any), id: d.id } as StockIssue)))
    );
  }, [currentProjectId, isAdmin]);

  useEffect(() => {
    if (!currentProjectId) { setStockUsageSeen({}); return; }
    return onSnapshot(
      doc(db, 'officeSettings', `stockUsageSeen_${currentProjectId}`),
      (s) => setStockUsageSeen((s.data()?.seen || {}) as Record<string, string>),
      () => setStockUsageSeen({})
    );
  }, [currentProjectId]);


  useEffect(() => {
    if (!currentProjectId) { setProjectEstimateScopeItems([]); setProjectEstimateScopeSections([]); return; }
    return onSnapshot(
      doc(db, 'officeSettings', `projectEstimateScope_${currentProjectId}`),
      (s) => {
        setProjectEstimateScopeItems(((s.data()?.items || []) as ProjectEstimateScopeItem[]).sort(byOrder));
        setProjectEstimateScopeSections(((s.data()?.sections || []) as ProjectEstimateScopeSection[]).sort(byOrder));
      },
      () => { setProjectEstimateScopeItems([]); setProjectEstimateScopeSections([]); }
    );
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) { setWorkRateSetups([]); return; }
    return onSnapshot(
      doc(db, 'officeSettings', `workRateSetup_${currentProjectId}`),
      (s) => setWorkRateSetups(((s.data()?.rates || []) as WorkRateSetup[])),
      () => setWorkRateSetups([])
    );
  }, [currentProjectId]);

  const materialQuoteSuggestions = useMemo(() => {
    const vendorSet = new Set<string>();
    const brandSet = new Set<string>();
    for (const item of allMaterialRateItems) {
      if (item.brand?.trim()) brandSet.add(item.brand.trim());
      for (const quote of item.quotes || []) {
        if (quote.vendor?.trim()) vendorSet.add(quote.vendor.trim());
        if (quote.brand?.trim()) brandSet.add(quote.brand.trim());
      }
    }
    const sortText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    return {
      vendors: Array.from(vendorSet).sort(sortText),
      brands: Array.from(brandSet).sort(sortText),
    };
  }, [allMaterialRateItems]);
  const materialGroupOptions = useMemo(() => {
    const builtIns = MATERIAL_GROUPS.map((group) => materialGroupLabel(group));
    const custom = new Set<string>();
    for (const item of allMaterialRateItems) {
      const label = materialGroupLabel(item.group).trim();
      if (label && !builtIns.some((option) => option.toLowerCase() === label.toLowerCase())) {
        custom.add(label);
      }
    }
    return [...builtIns, ...Array.from(custom).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))];
  }, [allMaterialRateItems]);
  const projectUnitTypeMasters = useMemo(() => unitTypeMasters.map((unit) => ({
    ...unit,
    buildingAllocations: unitTypeBuildingAllocations[unit.id] || unit.buildingAllocations || [],
  })), [unitTypeMasters, unitTypeBuildingAllocations]);

  // ── writes ──────────────────────────────────────────────────────────────────
  const col = (name: string) => collection(db, 'projects', currentProjectId as string, name);
  const ref = (name: string, id: string) => doc(db, 'projects', currentProjectId as string, name, id);
  // Until an admin moves the library into the shared one it is read-only, so nothing is written to the old copy.
  const canWriteLibrary = () => {
    if (library.mode === 'shared') return true;
    window.alert(library.mode === 'legacy' ? 'The rate library is read-only until it is moved to the shared library. Use the yellow panel above.' : 'The rate library is still loading. Try again in a moment.');
    return false;
  };
  const nextOrder = (arr: { order?: number }[]) =>
    (arr.reduce((m, x) => Math.max(m, num(x.order)), 0) || 0) + 1;

  const saveMaterialRateItem = async (v: {
    group: MaterialGroup;
    materialName: string;
    grade: string;
    brand: string;
    unit: string;
    secondaryUnitRequired?: boolean;
    secondaryMultiplier?: number;
    secondaryUnit?: string;
    pieceEntry?: boolean;
    pieceDimensionUnit?: 'in' | 'ft' | 'cm' | 'm';
    trackStock?: boolean;
    stockPrecision?: 'exact' | 'tentative';
    laborStockVisible?: boolean;
    codeRequired?: boolean;
    assemblyVarietyMap?: MaterialAssemblyVarietyMap[];
    boxPackings?: MaterialBoxPacking[];
    assemblyTypes?: string[];
    varieties?: string[];
    quoteOptionTemplates?: MaterialQuoteOptionTemplate[];
    coveredDiameters?: string[];
  }) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    const payload: any = {
      group: v.group,
      materialName: v.materialName,
      grade: v.grade,
      brand: v.brand,
      unit: v.unit,
      secondaryUnitRequired: !!v.secondaryUnitRequired,
      secondaryMultiplier: num(v.secondaryMultiplier),
      secondaryUnit: (v.secondaryUnit || '').trim(),
      pieceEntry: !!v.pieceEntry,
      pieceDimensionUnit: v.pieceDimensionUnit || defaultDimensionUnitFor(v.unit),
      trackStock: !!v.trackStock,
      stockPrecision: v.trackStock ? (v.stockPrecision || 'exact') : null,
      laborStockVisible: v.group === 'labour' ? v.laborStockVisible !== false : null,
      codeRequired: !!v.codeRequired,
      assemblyVarietyMap: v.assemblyVarietyMap || [],
      boxPackings: v.boxPackings || [],
      assemblyTypes: v.assemblyTypes || [],
      varieties: v.varieties || [],
      quoteOptionTemplates: v.quoteOptionTemplates || [],
      diameter: v.group === 'steel' ? 'All diameters' : '',
      updatedAt: serverTimestamp(),
    };
    if (v.coveredDiameters?.length) payload.coveredDiameters = v.coveredDiameters;
    else payload.coveredDiameters = [];
    if (materialDlg?.edit) {
      const nextItem = { ...materialDlg.edit, ...payload } as MaterialRateItem;
      const migration = migrateMaterialQuoteOptions(materialDlg.edit, nextItem);
      if (migration.needsConfirmation && !window.confirm('Existing vendor quote rates will be updated for renamed varieties and removed for deleted varieties. Continue?')) {
        return;
      }
      payload.quotes = migration.quotes;
      await updateMaterialItem(currentProjectId, materialDlg.edit.id, payload);
    } else {
      await createMaterialItem(currentProjectId, {
        ...payload,
        selectedQuoteId: null,
        selectedOptionQuoteIds: {},
        defaultCostingQuoteId: null,
        defaultCostingOptionQuoteIds: {},
        quotes: [],
        order: nextOrder(materialRateItems),
        createdAt: serverTimestamp(),
      });
    }
    setMaterialDlg(null);
  };

  const saveMaterialQuote = async (item: MaterialRateItem, quote: MaterialQuote) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    const quotes = [...(item.quotes || [])];
    const index = quotes.findIndex((q) => q.id === quote.id);
    if (index >= 0) quotes[index] = { ...quotes[index], ...quote, status: quotes[index].status || 'active', recordedAt: quotes[index].recordedAt || new Date().toISOString() };
    else quotes.push({ ...quote, status: 'active', recordedAt: new Date().toISOString() });
    // The material's unit and brand are shared by every project, so a quote never changes them.
    await setProjectRates(currentProjectId, item.id, { quotes });
    setQuoteDlg(null);
  };

  const saveMaterialRateRevision = async (item: MaterialRateItem, quoteId: string, optionId: string | undefined, revision: MaterialRateRevision) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    const quotes = (item.quotes || []).map((quote) => {
      if (quote.id !== quoteId) return quote;
      if (!optionId) {
        const history = quote.baseRateHistory || [];
        const nextHistory = history.some((entry) => entry.id === revision.id)
          ? history.map((entry) => entry.id === revision.id ? revision : entry)
          : [...history, { ...revision, previousRate: rateAtDate(history, quote.rate, revision.effectiveFrom) }];
        return { ...quote, baseRateHistory: nextHistory };
      }
      return {
        ...quote,
        quoteOptions: (quote.quoteOptions || []).map((option) => option.id === optionId
          ? { ...option, rateHistory: (option.rateHistory || []).some((entry) => entry.id === revision.id) ? (option.rateHistory || []).map((entry) => entry.id === revision.id ? revision : entry) : [...(option.rateHistory || []), { ...revision, previousRate: rateAtDate(option.rateHistory, num(option.rate) || (option.type === 'base' ? quoteBaseRate(quote, revision.effectiveFrom) : 0), revision.effectiveFrom) }] }
          : option),
      };
    });
    await setProjectRates(currentProjectId, item.id, { quotes });
    setRateRevisionDlg(null);
  };

  const deleteMaterialRateRevision = async (item: MaterialRateItem, quoteId: string, optionId: string | undefined, revisionId: string) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    if (!window.confirm('Delete this revised rate? This cannot be undone.')) return;
    const quotes = (item.quotes || []).map((quote) => {
      if (quote.id !== quoteId) return quote;
      if (!optionId) return { ...quote, baseRateHistory: (quote.baseRateHistory || []).filter((entry) => entry.id !== revisionId) };
      return { ...quote, quoteOptions: (quote.quoteOptions || []).map((option) => option.id === optionId ? { ...option, rateHistory: (option.rateHistory || []).filter((entry) => entry.id !== revisionId) } : option) };
    });
    await setProjectRates(currentProjectId, item.id, { quotes });
  };

  const selectMaterialQuote = async (item: MaterialRateItem, quoteId: string) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    const quote = (item.quotes || []).find((candidate) => candidate.id === quoteId);
    if (!quote || !quoteIsCurrent(quote)) return;
    const nextQuoteId = item.selectedQuoteId === quoteId ? null : quoteId;
    await setProjectRates(currentProjectId, item.id, { selectedQuoteId: nextQuoteId, defaultCostingQuoteId: nextQuoteId });
  };

  const selectMaterialOptionQuote = async (item: MaterialRateItem, optionId: string, quoteId: string) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    const quote = (item.quotes || []).find((candidate) => candidate.id === quoteId);
    if (!quote || !quoteIsCurrent(quote)) return;
    const current = item.selectedOptionQuoteIds || {};
    const next = { ...current };
    if (next[optionId] === quoteId) delete next[optionId];
    else next[optionId] = quoteId;
    const nextDefaults = { ...(item.defaultCostingOptionQuoteIds || {}) };
    if (next[optionId]) nextDefaults[optionId] = next[optionId] as string;
    else delete nextDefaults[optionId];
    await setProjectRates(currentProjectId, item.id, { selectedOptionQuoteIds: next, defaultCostingOptionQuoteIds: nextDefaults });
  };

  const toggleMaterialQuoteComparison = async (item: MaterialRateItem, quoteId: string) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    const quote = (item.quotes || []).find((candidate) => candidate.id === quoteId);
    if (!quote) return;
    await setProjectRates(currentProjectId, item.id, {
      quotes: (item.quotes || []).map((candidate) => candidate.id === quoteId
        ? { ...candidate, excludedFromComparison: !candidate.excludedFromComparison }
        : candidate),
    });
  };

  const deleteMaterialQuote = async (item: MaterialRateItem, quoteId: string) => {
    if (!canWriteLibrary() || !currentProjectId) return false;
    const quote = (item.quotes || []).find((q) => q.id === quoteId);
    if (!quote || !window.confirm(`Void this quote from "${quote.vendor}"? Vendor quotes are shared, so it is voided for ALL projects. It stays in rate history and cannot be used for new costing.`)) return false;
    const voidReason = window.prompt('Why is this rate being voided?', '')?.trim();
    if (voidReason === undefined) return false;
    const selectedOptionQuoteIds = Object.fromEntries(
      Object.entries(item.selectedOptionQuoteIds || {}).filter(([, selectedQuoteId]) => selectedQuoteId !== quoteId)
    );
    const defaultCostingOptionQuoteIds = Object.fromEntries(
      Object.entries(item.defaultCostingOptionQuoteIds || {}).filter(([, defaultQuoteId]) => defaultQuoteId !== quoteId)
    );
    await setProjectRates(currentProjectId, item.id, {
      quotes: (item.quotes || []).map((candidate) => candidate.id === quoteId
        ? { ...candidate, status: 'voided', voidReason: voidReason || 'Voided' }
        : candidate),
      selectedQuoteId: item.selectedQuoteId === quoteId ? null : (item.selectedQuoteId || null),
      selectedOptionQuoteIds,
      defaultCostingQuoteId: item.defaultCostingQuoteId === quoteId ? null : (item.defaultCostingQuoteId || null),
      defaultCostingOptionQuoteIds,
    });
    return true;
  };

  const deleteMaterialRateItem = async (item: MaterialRateItem) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    const quoteCount = item.quotes?.length || 0;
    const message = `Delete "${item.materialName}" from the shared rate library? It disappears for ALL projects${quoteCount ? `, and its ${quoteCount} vendor quote(s) are deleted` : ''}. This cannot be undone.`;
    if (!window.confirm(message)) return;
    await deleteMaterialItem(currentProjectId, item.id);
  };

  const saveMaterialDealNotes = async (item: MaterialRateItem, dealNotes: string) => {
    if (!canWriteLibrary() || !currentProjectId) return;
    await setProjectRates(currentProjectId, item.id, { dealNotes });
  };

  const saveAssembly = async (assembly: MaterialAssembly) => {
    const { drivers: _legacyDrivers, ...assemblyWithoutLegacyDrivers } = assembly as MaterialAssembly & { drivers?: unknown };
    const cleanedAssembly: MaterialAssembly = {
      ...assemblyWithoutLegacyDrivers,
      components: (assembly.components || []).map((component) => {
        const cleaned: AssemblyComponent = {
          id: component.id,
          type: component.type,
          basis: component.basis || 'output',
          label: component.label,
          qty: num(component.qty),
          unit: component.unit,
        };
        if (component.type === 'material') {
          if (component.materialName) cleaned.materialName = component.materialName;
          if (component.materialGroup) cleaned.materialGroup = component.materialGroup;
          if (component.quoteOptionId) cleaned.quoteOptionId = component.quoteOptionId;
        } else {
          cleaned.manualRateSource = component.manualRateSource || (num(component.manualRate) > 0 ? 'manual' : 'pending');
          if (cleaned.manualRateSource === 'material') {
            if (component.linkedMaterialId) cleaned.linkedMaterialId = component.linkedMaterialId;
            if (component.linkedMaterialName) cleaned.linkedMaterialName = component.linkedMaterialName;
            if (component.linkedMaterialGroup) cleaned.linkedMaterialGroup = component.linkedMaterialGroup;
            if (component.linkedQuoteId) cleaned.linkedQuoteId = component.linkedQuoteId;
            if (component.linkedQuoteOptionId) cleaned.linkedQuoteOptionId = component.linkedQuoteOptionId;
          } else if (cleaned.manualRateSource === 'manual') {
            if (num(component.manualRate) > 0) cleaned.manualRate = num(component.manualRate);
            cleaned.gst = num(component.gst);
            cleaned.gstMode = component.gstMode || 'exclusive';
          }
        }
        return cleaned;
      }),
    };
    const next = [...materialAssemblies];
    const index = next.findIndex((x) => x.id === cleanedAssembly.id);
    if (index >= 0) next[index] = cleanedAssembly;
    else next.push({ ...cleanedAssembly, order: nextOrder(next) });
    await setDoc(doc(db, 'officeSettings', 'materialAssemblyLibrary'), {
      assemblies: next.sort(byOrder),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    setAssemblyDlg(null);
  };

  const saveConcreteGrades = async (grades: ConcreteGrade[]) => {
    await setDoc(doc(db, 'officeSettings', 'concreteGrades'), { grades, updatedAt: serverTimestamp() }, { merge: true });
  };

  /** Clears a material's "new stock usage" badge in Allocate — called when its usage list is opened. */
  const markStockUsageSeen = async (materialKey: string) => {
    if (!currentProjectId) return;
    await setDoc(doc(db, 'officeSettings', `stockUsageSeen_${currentProjectId}`), { seen: { [materialKey]: new Date().toISOString() } }, { merge: true });
  };

  const saveUnitTypeMasters = async (unitTypes: UnitTypeMaster[]) => {
    const allocationMap = unitTypes.reduce<Record<string, UnitTypeBuildingAllocation[]>>((acc, unit) => {
      acc[unit.id] = (unit.buildingAllocations || []).map((allocation) => {
        const buildingName = (allocation.buildingName || '').trim();
        const normalized: UnitTypeBuildingAllocation = {
          id: allocation.id,
          towerId: (allocation.towerId || '').trim(),
          buildingName,
          count: num(allocation.count),
          notes: (allocation.notes || '').trim(),
        };
        if (typeof allocation.floorFrom === 'number') normalized.floorFrom = num(allocation.floorFrom);
        if (typeof allocation.floorTo === 'number') normalized.floorTo = num(allocation.floorTo);
        if (num(allocation.unitsPerFloor) > 0) normalized.unitsPerFloor = num(allocation.unitsPerFloor);
        return normalized;
      }).filter((allocation) => allocation.buildingName && allocation.count > 0);
      return acc;
    }, {});
    const savedUnitTypes = unitTypes.map((unit, index) => ({
      id: unit.id,
      label: unit.label.trim(),
      unit: unit.unit.trim() || 'Flat',
      sections: (unit.sections || []).map((section) => section.trim()).filter(Boolean),
      components: (unit.components || []).map((component) => {
        const normalized: QuantityProfileComponent = {
          id: component.id,
          label: component.label.trim(),
          qtyPerUnit: num(component.qtyPerUnit),
          unit: component.unit.trim() || 'Nos',
          areaPerUnit: num(component.areaPerUnit),
          areaUnit: (component.areaUnit || 'Sft').trim() || 'Sft',
          secondaryOverride: !!component.secondaryOverride,
          leadEnabled: !!component.leadEnabled,
        };
        if (component.workItemId) normalized.workItemId = component.workItemId;
        if (component.sourceType) normalized.sourceType = component.sourceType;
        if (component.sourceId) normalized.sourceId = component.sourceId;
        if (component.quoteOptionId) normalized.quoteOptionId = component.quoteOptionId;
        if (component.group) normalized.group = component.group.trim();
        if (component.assemblyType) normalized.assemblyType = component.assemblyType.trim();
        if (component.variety) normalized.variety = component.variety.trim();
        if (component.leadEnabled && component.leadType) normalized.leadType = component.leadType;
        if (component.leadEnabled) normalized.leadPercent = num(component.leadPercent);
        return normalized;
      }).filter((component) => component.label),
      buildingAllocations: [],
      order: unit.order || index + 1,
    })).filter((unit) => unit.label);
    await Promise.all([
      setDoc(doc(db, 'officeSettings', `unitTypeBuildingAllocations_${currentProjectId}`), {
        allocations: withoutUndefined(allocationMap),
        updatedAt: serverTimestamp(),
      }, { merge: true }),
      setDoc(doc(db, 'officeSettings', 'unitTypeMasterLibrary'), {
      unitTypes: JSON.parse(JSON.stringify(withoutUndefined(savedUnitTypes))),
      updatedAt: serverTimestamp(),
      }, { merge: true }),
    ]);
  };

  const saveMaterialProcurementOrder = async (order: MaterialProcurementOrder) => {
    if (!currentProjectId) return;
    const payload = {
      ...order,
      materialLabel: order.materialLabel.trim(),
      unit: order.unit.trim(),
      vendor: (order.vendor || '').trim(),
      brand: (order.brand || '').trim(),
      code: (order.code || '').trim(),
      scopeLabel: (order.scopeLabel || '').trim(),
      poNumber: (order.poNumber || '').trim(),
      notes: (order.notes || '').trim(),
      orderQty: num(order.orderQty),
      buildingAllocations: (order.buildingAllocations || []).map((allocation) => ({
        buildingId: (allocation.buildingId || '').trim(),
        buildingLabel: (allocation.buildingLabel || '').trim(),
        qty: num(allocation.qty),
      })).filter((allocation) => allocation.buildingId && allocation.qty > 0),
      receipts: (order.receipts || []).map((receipt) => ({ ...receipt, date: receipt.date || '', qty: num(receipt.qty), reference: (receipt.reference || '').trim(), notes: (receipt.notes || '').trim() })).filter((receipt) => receipt.date && receipt.qty > 0),
      updatedAt: serverTimestamp(),
    };
    const { id: _orderId, ...orderPayload } = payload;
    if (order.id) await setDoc(ref('materialProcurementOrders', order.id), orderPayload, { merge: true });
    else await addDoc(col('materialProcurementOrders'), { ...orderPayload, createdAt: serverTimestamp() });
  };

  const saveRateComparisonQuantities = async (materialId: string, quantities: Record<string, number>) => {
    if (!currentProjectId) return;
    const cleaned = Object.fromEntries(
      Object.entries(quantities)
        .map(([optionId, quantity]) => [optionId, num(quantity)] as const)
        .filter(([, quantity]) => quantity > 0)
    );
    await setDoc(doc(db, 'officeSettings', `rateComparisonQuantities_${currentProjectId}`), {
      quantities: { ...rateComparisonQuantities, [materialId]: cleaned },
      updatedAt: serverTimestamp(),
    });
  };

  const deleteMaterialProcurementOrder = async (order: MaterialProcurementOrder) => {
    const label = order.poNumber ? `order "${order.poNumber}"` : `the ${order.materialLabel} order dated ${order.orderDate || 'this date'}`;
    if (!window.confirm(`Delete ${label}? Its receipt records will also be removed.`)) return;
    await deleteDoc(ref('materialProcurementOrders', order.id));
  };

  // Saves a split of one receipt or bill line across one or more destinations, in a single batch.
  const saveMaterialAllocations = async (lines: Omit<SavedMaterialAllocation, 'id'>[]) => {
    if (!currentProjectId || !lines.length) return;
    const batch = writeBatch(db);
    lines.forEach((line) => batch.set(doc(col('materialAllocations')), { ...line, createdAt: serverTimestamp() }));
    await batch.commit();
  };
  const deleteMaterialAllocation = async (allocation: SavedMaterialAllocation) => {
    if (!currentProjectId) return;
    if (!window.confirm(`Remove this ${fmtQty(allocation.qty)} ${allocation.unit} allocation to "${allocation.destination.label}"? It goes back to unallocated.`)) return;
    await deleteDoc(ref('materialAllocations', allocation.id));
  };

  const mergeMaterialProcurementOrders = async (orders: MaterialProcurementOrder[]) => {
    if (!currentProjectId || orders.length < 2) return;
    if (!window.confirm(`Merge these ${orders.length} building orders into one material order? Their quantities will remain allocated to the same buildings.`)) return;
    const first = orders[0];
    const buildingAllocations: MaterialOrderBuildingAllocation[] = orders.flatMap((order): MaterialOrderBuildingAllocation[] => (order.buildingAllocations || []).length
      ? order.buildingAllocations || []
      : order.scopeType === 'building' && order.scopeId
        ? [{ buildingId: order.scopeId, buildingLabel: order.scopeLabel || 'Building', qty: num(order.orderQty) }]
        : []);
    const { id: _firstOrderId, ...firstPayload } = first;
    const mergedRef = doc(col('materialProcurementOrders'));
    const batch = writeBatch(db);
    batch.set(mergedRef, {
      ...firstPayload,
      scopeType: 'project',
      scopeId: '',
      scopeLabel: '',
      orderQty: buildingAllocations.reduce<number>((sum, allocation) => sum + num(allocation.qty), 0),
      buildingAllocations,
      receipts: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    orders.forEach((order) => batch.delete(ref('materialProcurementOrders', order.id)));
    await batch.commit();
  };

  const saveProjectEstimateScopeItems = async (items: ProjectEstimateScopeItem[]) => {
    if (!currentProjectId) return;
    const codedItems = projectScopeWithAutoCodes(items);
    await setDoc(doc(db, 'officeSettings', `projectEstimateScope_${currentProjectId}`), {
      items: codedItems.map((item, index) => ({
        ...item,
        workItemId: item.workItemId || '',
        sourceType: item.sourceType || '',
        sourceId: item.sourceId || '',
        label: item.label || '',
        assemblyType: item.assemblyType || '',
        subgroupIsCustom: !!item.subgroupIsCustom,
        groupCode: (item.groupCode || '').trim(),
        group: item.group.trim() || 'Project scope',
        subgroupCode: (item.subgroupCode || '').trim(),
        subgroup: (item.subgroup || '').trim(),
        variety: (item.variety || '').trim(),
        quoteOptionId: (item.quoteOptionId || '').trim(),
        lineCode: (item.lineCode || '').trim(),
        buildingName: (item.buildingName || '').trim(),
        qty: num(item.qty),
        nos: num(item.nos),
        length: num(item.length),
        breadth: num(item.breadth),
        height: num(item.height),
        unit: (item.unit || '').trim(),
        notes: (item.notes || '').trim(),
        order: item.order || index + 1,
      })).filter((item) => item.workItemId || item.sourceId),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const saveProjectEstimateScopeSections = async (sections: ProjectEstimateScopeSection[]) => {
    if (!currentProjectId) return;
    await setDoc(doc(db, 'officeSettings', `projectEstimateScope_${currentProjectId}`), {
      sections: sections.map((section, index) => ({
        ...section,
        group: section.group.trim(),
        subgroup: (section.subgroup || '').trim(),
        order: section.order || index + 1,
      })).filter((section) => section.group),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const deleteAssembly = async (assembly: MaterialAssembly) => {
    if (!window.confirm(`Delete assembly "${assembly.name}"?`)) return;
    await setDoc(doc(db, 'officeSettings', 'materialAssemblyLibrary'), {
      assemblies: materialAssemblies.filter((x) => x.id !== assembly.id),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const openMaterialReportDialog = (itemsToExport: MaterialRateItem[], label?: string) => {
    if (!itemsToExport.length) return;
    setMaterialReportDlg({ selectedIds: itemsToExport.map((item) => item.id), label });
  };

  const downloadMaterialRatesPdf = async (itemsToExport: MaterialRateItem[], gstMode: MaterialGstDisplay, label?: string) => {
    if (!itemsToExport.length || materialPdfBusy) return;
    const busyKey = itemsToExport.length === 1 ? itemsToExport[0].id : 'all';
    setMaterialPdfBusy(busyKey);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const generatedAtLabel = new Date().toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      const title = itemsToExport.length === 1
        ? `Material Rate - ${itemsToExport[0].materialName}`
        : 'All Material Rates';
      const fileBase = `${safeFilePart(label || title)}_${stamp}`;
      const html = buildMaterialRatesPdfHtml(itemsToExport, title, gstMode);
      const payload = buildPdfPayloadFromTemplate({
        html,
        tokenValues: {},
        visualModel: {
          showTitle: false,
          showRefNo: false,
          showDate: false,
          showPageNumbers: true,
          marginTopCm: 0.5,
          marginRightCm: 0.7,
          marginBottomCm: 0.7,
          marginLeftCm: 0.7,
          footerHeightCm: 1.1,
          footerOverlayEnabled: false,
          backgroundImageUrl: String(projectMeta?.backgroundImageUrl || ''),
          backgroundOpacity: Math.min(0.055, Math.max(0.025, Number(projectMeta?.backgroundOpacity || 0.08) * 0.45)),
          backgroundSize: 'contain',
          backgroundScale: 58,
          backgroundPosX: 50,
          backgroundPosY: 50,
          companyLogoHeightCm: 0,
          projectLogoHeightCm: 1.2,
        },
        companyLogoUrl: '',
        projectLogoUrl: String(projectMeta?.logoUrl || ''),
        companyAddress: String(companyMeta?.address || ''),
        companyMobile: String(companyMeta?.mobile || ''),
        companyEmail: String(companyMeta?.email || ''),
        companyFontFamily: String(companyMeta?.fontFamily || ''),
        companyFontUrl: String(companyMeta?.fontUrl || ''),
        companyFontFormat: String(companyMeta?.fontFormat || ''),
        gujaratiFontFamily: String(companyMeta?.gujaratiFontFamily || ''),
        gujaratiFontUrl: String(companyMeta?.gujaratiFontUrl || ''),
        gujaratiFontFormat: String(companyMeta?.gujaratiFontFormat || ''),
        paperSize: 'A4',
        fileName: fileBase,
      });
      const projectLogoUrl = String(projectMeta?.logoUrl || '').trim();
      const headerHeightMm = 13.53;
      const footerHeightMm = 9;
      const sidePaddingMm = 8;
      const customHeaderTemplate = `
        <div style="position:relative;width:100%;height:${headerHeightMm}mm;box-sizing:border-box;overflow:hidden;">
          ${
            projectLogoUrl
              ? `<img src="${projectLogoUrl}" style="position:absolute;right:${sidePaddingMm}mm;top:0.5mm;height:12mm;object-fit:contain;" />`
              : ''
          }
          <div style="position:absolute;left:${sidePaddingMm}mm;right:${sidePaddingMm}mm;bottom:0;border-bottom:1px solid #bbb;"></div>
        </div>
      `;
      const customFooterTemplate = `
        <div style="position:relative;width:100%;height:${footerHeightMm}mm;box-sizing:border-box;overflow:hidden;">
          <div style="position:absolute;left:${sidePaddingMm}mm;right:${sidePaddingMm}mm;top:0;border-top:1px solid #bbb;"></div>
          <div style="position:absolute;left:${sidePaddingMm}mm;right:${sidePaddingMm}mm;top:0;bottom:0;display:flex;align-items:center;justify-content:center;text-align:center;font-size:9px;line-height:1;color:#555;">
            Generated: ${escHtml(generatedAtLabel)}
          </div>
          <div style="position:absolute;right:${sidePaddingMm}mm;bottom:1.2mm;font-size:10px;line-height:1;color:#444;">Page <span class="pageNumber"></span> of <span class="totalPages"></span></div>
        </div>
      `;
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const result = await renderTemplatePdf({
        ...payload,
        headerTemplate: customHeaderTemplate,
        footerTemplate: customFooterTemplate,
        marginTopMm: Math.max(10, headerHeightMm + 7),
        marginBottomMm: Math.max(10, footerHeightMm + 8),
        landscape: true,
      } as Record<string, any>);
      const blob = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileBase}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('download material rates pdf failed', err);
      window.alert('Could not download material rates PDF. Please try again.');
    } finally {
      setMaterialPdfBusy(null);
    }
  };

  // ── empty states ───────────────────────────────────────────────────────────
  if (!currentProjectId) {
    return <Center>Select a project from the top bar to build its estimate.</Center>;
  }

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, fontFamily: FONT_BODY, maxWidth: 1360, mx: 'auto', width: '100%' }}>
      {/* header */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 22, color: DARK, flexGrow: 1 }}>
          Project Rate Library
        </Typography>
      </Stack>

      {library.mode === 'legacy' && currentProjectId && <RateLibraryMigration projectId={currentProjectId} legacyItems={library.legacyItems} onDone={(count) => setJustMigrated(count)} />}
      {justMigrated !== null && library.mode === 'shared' && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setJustMigrated(null)}>{justMigrated} material{justMigrated === 1 ? '' : 's'} moved to the shared rate library. New projects will start with the same materials. The old per-project copy is kept as a read-only backup.</Alert>}
      <MaterialRatesPanel
        items={materialRateItems}
        assemblies={materialAssemblies}
        projectEstimateScopeItems={projectEstimateScopeItems}
        projectEstimateScopeSections={projectEstimateScopeSections}
        workRateSetups={workRateSetups}
        unitTypeMasters={projectUnitTypeMasters}
        isAdmin={isAdmin}
        materialVendorBills={materialVendorBills}
        materialAllocations={materialAllocations}
        onSaveMaterialAllocations={saveMaterialAllocations}
        onDeleteMaterialAllocation={deleteMaterialAllocation}
        stockIssues={stockIssuesForAllocation}
        stockUsageSeen={stockUsageSeen}
        onViewStockUsage={markStockUsageSeen}
        activeTab={materialTab}
        onTabChange={setMaterialTab}
        onSaveProjectEstimateScopeItems={saveProjectEstimateScopeItems}
        onSaveProjectEstimateScopeSections={saveProjectEstimateScopeSections}
        onSaveUnitTypeMasters={saveUnitTypeMasters}
        materialProcurementOrders={materialProcurementOrders}
        rateComparisonQuantities={rateComparisonQuantities}
        onSaveRateComparisonQuantities={saveRateComparisonQuantities}
        onSaveMaterialProcurementOrder={saveMaterialProcurementOrder}
        onDeleteMaterialProcurementOrder={deleteMaterialProcurementOrder}
        onMergeMaterialProcurementOrders={mergeMaterialProcurementOrders}
        onAddMaterial={(initialGroup) => setMaterialDlg({ initialGroup })}
        onAddAssembly={() => setAssemblyDlg({})}
        onManageConcreteGrades={() => setConcreteGradesDlg(true)}
        onSaveAssembly={saveAssembly}
        onEditAssembly={(assembly) => setAssemblyDlg({ edit: assembly })}
        onDeleteAssembly={deleteAssembly}
        onAddQuote={(item) => setQuoteDlg({ item })}
        onEditQuote={(item, quote) => setQuoteDlg({ item, quote })}
        onAddRateRevision={(item, quote, optionId) => setRateRevisionDlg({ item, quote, optionId })}
        onEditRateRevision={(item, quote, optionId, revision) => setRateRevisionDlg({ item, quote, optionId, edit: revision })}
        onDeleteRateRevision={deleteMaterialRateRevision}
        onSelectQuote={selectMaterialQuote}
        onSelectOptionQuote={selectMaterialOptionQuote}
        onToggleQuoteComparison={toggleMaterialQuoteComparison}
        onEditMaterial={(item) => setMaterialDlg({ edit: item })}
        onDeleteMaterial={deleteMaterialRateItem}
        onSaveMaterialDealNotes={saveMaterialDealNotes}
        onDownloadMaterial={(item) => openMaterialReportDialog([item], item.materialName)}
        onDownloadAllMaterials={(itemsToExport, label) => openMaterialReportDialog(itemsToExport || materialRateItems, label || 'all_material_rates')}
        materialPdfBusy={materialPdfBusy}
      />

      {/* ── dialogs ── */}
      {materialDlg && (
        <MaterialDialog
          edit={materialDlg.edit}
          initialGroup={materialDlg.initialGroup}
          materialGroupOptions={materialGroupOptions}
          onClose={() => setMaterialDlg(null)}
          onSave={saveMaterialRateItem}
        />
      )}
      {quoteDlg && (
        <SteelQuoteDialog
          item={quoteDlg.item}
          edit={quoteDlg.quote}
          vendorOptions={vendorOptionsForItem(quoteDlg.item, materialProcurementOrders, materialQuoteSuggestions.vendors)}
          brandOptions={materialQuoteSuggestions.brands}
          cartingAgentsByVendor={cartingAgentsByVendor}
          onSaveCartingAgents={(vendorName, agents) => { saveCartingAgents(vendorName, agents).catch(() => undefined); }}
          onClose={() => setQuoteDlg(null)}
          onSave={(quote) => saveMaterialQuote(quoteDlg.item, quote)}
          onDelete={async () => {
            if (quoteDlg.quote && await deleteMaterialQuote(quoteDlg.item, quoteDlg.quote.id)) setQuoteDlg(null);
          }}
        />
      )}
      {rateRevisionDlg && (
        <MaterialRateRevisionDialog
          item={rateRevisionDlg.item}
          quote={rateRevisionDlg.quote}
          option={rateRevisionDlg.optionId ? selectedQuoteOption(rateRevisionDlg.quote, rateRevisionDlg.optionId) : null}
          edit={rateRevisionDlg.edit}
          onClose={() => setRateRevisionDlg(null)}
          onSave={(revision) => saveMaterialRateRevision(rateRevisionDlg.item, rateRevisionDlg.quote.id, rateRevisionDlg.optionId, revision)}
        />
      )}
      {assemblyDlg && (
        <AssemblyDialog
          edit={assemblyDlg.edit}
          onClose={() => setAssemblyDlg(null)}
          onSave={saveAssembly}
        />
      )}
      {concreteGradesDlg && (
        <ConcreteGradesDialog
          grades={concreteGrades}
          materialItems={materialRateItems}
          onClose={() => setConcreteGradesDlg(false)}
          onSave={(grades) => { saveConcreteGrades(grades).catch(() => undefined); setConcreteGradesDlg(false); }}
        />
      )}
      {materialReportDlg && (
        <MaterialReportDownloadDialog
          items={materialRateItems}
          initialSelectedIds={materialReportDlg.selectedIds}
          busy={!!materialPdfBusy}
          onClose={() => setMaterialReportDlg(null)}
          onDownload={async (selectedIds, gstMode) => {
            const selected = materialRateItems.filter((item) => selectedIds.includes(item.id));
            const label = selected.length === 1 ? selected[0].materialName : materialReportDlg.label || 'all_material_rates';
            await downloadMaterialRatesPdf(selected, gstMode, label);
            setMaterialReportDlg(null);
          }}
        />
      )}

    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Presentational sub-components
// ─────────────────────────────────────────────────────────────────────────────
const byOrder = (a: any, b: any) => num(a.order) - num(b.order);

function MaterialRatesPanel({
  items,
  assemblies,
  projectEstimateScopeItems,
  projectEstimateScopeSections,
  workRateSetups,
  unitTypeMasters,
  isAdmin,
  materialVendorBills,
  materialAllocations,
  onSaveMaterialAllocations,
  onDeleteMaterialAllocation,
  stockIssues,
  stockUsageSeen,
  onViewStockUsage,
  activeTab,
  onTabChange,
  onSaveProjectEstimateScopeItems,
  onSaveProjectEstimateScopeSections,
  onSaveUnitTypeMasters,
  materialProcurementOrders,
  rateComparisonQuantities,
  onSaveRateComparisonQuantities,
  onSaveMaterialProcurementOrder,
  onDeleteMaterialProcurementOrder,
  onMergeMaterialProcurementOrders,
  onAddMaterial,
  onAddAssembly,
  onManageConcreteGrades,
  onSaveAssembly,
  onEditAssembly,
  onDeleteAssembly,
  onAddQuote,
  onEditQuote,
  onAddRateRevision,
  onEditRateRevision,
  onDeleteRateRevision,
  onSelectQuote,
  onSelectOptionQuote,
  onToggleQuoteComparison,
  onEditMaterial,
  onDeleteMaterial,
  onSaveMaterialDealNotes,
  onDownloadMaterial,
  onDownloadAllMaterials,
  materialPdfBusy,
}: {
  items: MaterialRateItem[];
  assemblies: MaterialAssembly[];
  projectEstimateScopeItems: ProjectEstimateScopeItem[];
  projectEstimateScopeSections: ProjectEstimateScopeSection[];
  workRateSetups: WorkRateSetup[];
  unitTypeMasters: UnitTypeMaster[];
  isAdmin: boolean;
  materialVendorBills: DirectMaterialVendorBill[];
  materialAllocations: SavedMaterialAllocation[];
  onSaveMaterialAllocations: (lines: Omit<SavedMaterialAllocation, 'id'>[]) => Promise<void>;
  onDeleteMaterialAllocation: (allocation: SavedMaterialAllocation) => void;
  stockIssues: StockIssue[];
  stockUsageSeen: Record<string, string>;
  onViewStockUsage: (materialKey: string) => void;
  activeTab: 'materials' | 'labour' | 'assemblies' | 'projectScope' | 'orderSummary' | 'projectCost' | 'allocate';
  onTabChange: (tab: 'materials' | 'labour' | 'assemblies' | 'projectScope' | 'orderSummary' | 'projectCost' | 'allocate') => void;
  onSaveProjectEstimateScopeItems: (items: ProjectEstimateScopeItem[]) => void;
  onSaveProjectEstimateScopeSections: (sections: ProjectEstimateScopeSection[]) => void;
  onSaveUnitTypeMasters: (unitTypes: UnitTypeMaster[]) => void;
  materialProcurementOrders: MaterialProcurementOrder[];
  rateComparisonQuantities: Record<string, Record<string, number>>;
  onSaveRateComparisonQuantities: (materialId: string, quantities: Record<string, number>) => Promise<void>;
  onSaveMaterialProcurementOrder: (order: MaterialProcurementOrder) => Promise<void>;
  onDeleteMaterialProcurementOrder: (order: MaterialProcurementOrder) => Promise<void>;
  onMergeMaterialProcurementOrders: (orders: MaterialProcurementOrder[]) => Promise<void>;
  onAddMaterial: (initialGroup?: MaterialGroup) => void;
  onAddAssembly: () => void;
  onManageConcreteGrades: () => void;
  onSaveAssembly: (assembly: MaterialAssembly) => void;
  onEditAssembly: (assembly: MaterialAssembly) => void;
  onDeleteAssembly: (assembly: MaterialAssembly) => void;
  onAddQuote: (item: MaterialRateItem) => void;
  onEditQuote: (item: MaterialRateItem, quote: MaterialQuote) => void;
  onAddRateRevision: (item: MaterialRateItem, quote: MaterialQuote, optionId?: string) => void;
  onEditRateRevision: (item: MaterialRateItem, quote: MaterialQuote, optionId: string | undefined, revision: MaterialRateRevision) => void;
  onDeleteRateRevision: (item: MaterialRateItem, quoteId: string, optionId: string | undefined, revisionId: string) => Promise<void>;
  onSelectQuote: (item: MaterialRateItem, quoteId: string) => void;
  onSelectOptionQuote: (item: MaterialRateItem, optionId: string, quoteId: string) => void;
  onToggleQuoteComparison: (item: MaterialRateItem, quoteId: string) => void;
  onEditMaterial: (item: MaterialRateItem) => void;
  onDeleteMaterial: (item: MaterialRateItem) => void;
  onSaveMaterialDealNotes: (item: MaterialRateItem, dealNotes: string) => void;
  onDownloadMaterial: (item: MaterialRateItem) => void;
  onDownloadAllMaterials: (itemsToExport?: MaterialRateItem[], label?: string) => void;
  materialPdfBusy: string | null;
}) {
  const [assemblyGstDisplay, setAssemblyGstDisplay] = useState<MaterialGstDisplay>('incl');
  const [rateSearch, setRateSearch] = useState('');
  const [rateKindFilter, setRateKindFilter] = useState<'all' | 'material' | 'labour'>('all');
  const [rateAddAnchor, setRateAddAnchor] = useState<HTMLElement | null>(null);
  const [expandedRateGroupKeys, setExpandedRateGroupKeys] = useState<Set<string>>(new Set());
  const [expandedMaterialId, setExpandedMaterialId] = useState<string | null>(null);
  const [focusedPendingRateIds, setFocusedPendingRateIds] = useState<Set<string>>(new Set());
  const isMobileRateView = useMediaQuery('(max-width:600px)');
  useEffect(() => setRateSearch(''), [activeTab]);
  useEffect(() => {
    if (activeTab === 'labour') onTabChange('materials');
  }, [activeTab, onTabChange]);
  const materialRateCount = items.filter((item) => item.group !== 'labour').length;
  const labourRateCount = items.length - materialRateCount;
  const shownRateItems = rateKindFilter === 'material'
    ? items.filter((item) => item.group !== 'labour')
    : rateKindFilter === 'labour'
      ? items.filter((item) => item.group === 'labour')
      : items;
  const isRateListTab = activeTab === 'materials' || activeTab === 'labour';
  const normalizedRateSearch = rateSearch.trim().toLowerCase();
  const filteredRateItems = useMemo(() => {
    if (!normalizedRateSearch) return shownRateItems;
    return shownRateItems.filter((item) => materialRateSearchText(item).includes(normalizedRateSearch));
  }, [shownRateItems, normalizedRateSearch]);
  const groupedFilteredRateItems = useMemo(() => groupMaterialRateItems(filteredRateItems), [filteredRateItems]);
  const rateUsageStatuses = useMemo(
    () => buildMaterialRateUsageStatuses(items, assemblies, workRateSetups),
    [items, assemblies, workRateSetups]
  );
  const pendingUsedRateCount = filteredRateItems.filter((item) => {
    const status = rateUsageStatuses.get(item.id);
    return !!status && (status.pendingWholeItem || status.pendingOptionIds.size > 0);
  }).length;
  const pendingUsedRateIds = filteredRateItems
    .filter((item) => {
      const status = rateUsageStatuses.get(item.id);
      return !!status && (status.pendingWholeItem || status.pendingOptionIds.size > 0);
    })
    .map((item) => item.id);
  const rateSummary = useMemo(() => {
    const quoteCount = items.reduce((total, item) => total + (item.quotes || []).length, 0);
    const selectedCount = items.filter((item) => {
      const selectedOptions = Object.values(item.selectedOptionQuoteIds || {}).filter(Boolean).length;
      return !!item.selectedQuoteId || selectedOptions > 0;
    }).length;
    return { quoteCount, selectedCount };
  }, [items]);
  const openPendingUsedRates = () => {
    const pendingIds = new Set(pendingUsedRateIds);
    if (!pendingIds.size) return;
    setFocusedPendingRateIds(pendingIds);
    if (activeTab === 'materials') {
      const pendingGroupKeys = filteredRateItems
        .filter((item) => pendingIds.has(item.id))
        .map((item) => item.group || 'other');
      setExpandedRateGroupKeys((prev) => {
        const next = new Set(prev);
        pendingGroupKeys.forEach((key) => next.add(key));
        groupedFilteredRateItems.forEach((group) => {
          if (group.items.some((item) => pendingIds.has(item.id))) next.add(group.key);
        });
        return next;
      });
    }
  };
  const renderRateCard = (item: MaterialRateItem) => (
    <MaterialRateCard
      key={item.id}
      item={item}
      expanded={expandedMaterialId === item.id}
      onExpandedChange={(next) => setExpandedMaterialId(next ? item.id : null)}
      usageStatus={rateUsageStatuses.get(item.id)}
      focusOpen={focusedPendingRateIds.has(item.id)}
      onFocusOpenHandled={() => setFocusedPendingRateIds((prev) => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      })}
      onAddQuote={() => onAddQuote(item)}
      onEditQuote={(quote) => onEditQuote(item, quote)}
      onAddRateRevision={(quote, optionId) => onAddRateRevision(item, quote, optionId)}
      onEditRateRevision={(quote, optionId, revision) => onEditRateRevision(item, quote, optionId, revision)}
      onDeleteRateRevision={(quoteId, optionId, revisionId) => onDeleteRateRevision(item, quoteId, optionId, revisionId)}
      onSelectQuote={(quoteId) => onSelectQuote(item, quoteId)}
      onSelectOptionQuote={(optionId, quoteId) => onSelectOptionQuote(item, optionId, quoteId)}
      onToggleQuoteComparison={(quoteId) => onToggleQuoteComparison(item, quoteId)}
      existingPoNumbers={materialProcurementOrders.map((order) => order.poNumber || '')}
      comparisonQuantities={rateComparisonQuantities[item.id] || {}}
      onSaveComparisonQuantities={(quantities) => onSaveRateComparisonQuantities(item.id, quantities)}
      onCreateComparisonOrders={async (orders) => { await Promise.all(orders.map((order) => onSaveMaterialProcurementOrder({ id: '', ...order }))); }}
      onEditMaterial={() => onEditMaterial(item)}
      onDeleteMaterial={() => onDeleteMaterial(item)}
      onSaveDealNotes={(dealNotes) => onSaveMaterialDealNotes(item, dealNotes)}
      onDownloadMaterial={() => onDownloadMaterial(item)}
      pdfBusy={materialPdfBusy === item.id}
    />
  );
  return (
    <Box>
      {!isRateListTab && <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
        <Box sx={{ flex: 1 }} />
        {activeTab !== 'projectScope' && activeTab !== 'orderSummary' && activeTab !== 'projectCost' && (
          <Stack gap={0.75} alignItems="flex-end">
            <Stack direction="row" gap={0.75} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<AddOutlinedIcon />}
                onClick={onAddAssembly}
                sx={{ borderColor: BORDER, color: MUTED }}
              >
                Add assembly
              </Button>
            </Stack>
            {activeTab === 'assemblies' && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={assemblyGstDisplay}
                onChange={(_, value) => value && setAssemblyGstDisplay(value)}
                sx={{ '& .MuiToggleButton-root': { px: 1.4, py: 0.35, textTransform: 'none', fontWeight: 800, borderColor: BORDER, color: MUTED, '&.Mui-selected': { bgcolor: GOLD, color: '#fff' } } }}
              >
                <ToggleButton value="incl">Incl GST</ToggleButton>
                <ToggleButton value="excl">Excl GST</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
        )}
      </Stack>}

      <ToggleButtonGroup exclusive size="small" value={activeTab} onChange={(_, value) => value && onTabChange(value)} sx={{ mb: 2 }}>
        <ToggleButton value="materials" sx={toggleSx}>Rates</ToggleButton>
        <ToggleButton value="assemblies" sx={toggleSx}>Assemblies</ToggleButton>
        <ToggleButton value="projectScope" sx={toggleSx}>Project Estimate</ToggleButton>
        <ToggleButton value="orderSummary" sx={toggleSx}>Order Summary</ToggleButton>
        <ToggleButton value="projectCost" sx={toggleSx}>Project Cost</ToggleButton>
        {isAdmin && <ToggleButton value="allocate" sx={toggleSx}>Allocate</ToggleButton>}
      </ToggleButtonGroup>

      {isRateListTab && (
        <Box sx={{ mb: 2, border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
          <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ px: 2, py: 1.25, bgcolor: '#faf8f5', borderBottom: `1px solid ${BORDER}` }}>
            <Box sx={{ flex: '1 1 210px' }}><Typography sx={{ fontWeight: 900, color: DARK }}>Rate overview</Typography><Typography sx={{ color: MUTED, fontSize: 12.5 }}>Search materials and labour together, compare vendor quotes, and retain every revision.</Typography></Box>
            <Stack direction="row" gap={.75} alignItems="center">
              {activeTab === 'materials' && <Button size="small" variant="outlined" onClick={onManageConcreteGrades} sx={{ borderColor: BORDER, color: MUTED }}>Manage concrete grades</Button>}
              <ActionIcon title="Download visible rates" onClick={() => onDownloadAllMaterials(shownRateItems, 'project_rates')}>{materialPdfBusy === 'all' ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon fontSize="inherit" />}</ActionIcon><Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={(event) => setRateAddAnchor(event.currentTarget)}>Add rate</Button><Menu anchorEl={rateAddAnchor} open={!!rateAddAnchor} onClose={() => setRateAddAnchor(null)}><MenuItem onClick={() => { setRateAddAnchor(null); onAddMaterial(); }}>Material</MenuItem><MenuItem onClick={() => { setRateAddAnchor(null); onAddMaterial('labour'); }}>Labour</MenuItem></Menu>
            </Stack>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, 1fr))' } }}>
            <Box sx={{ px: 1.5, py: 1.15, borderRight: `1px solid ${BORDER}`, borderBottom: { xs: `1px solid ${BORDER}`, sm: 'none' } }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Material rates</Typography><Typography sx={{ fontWeight: 900, color: GOLD_D, fontSize: 20 }}>{materialRateCount}</Typography></Box>
            <Box sx={{ px: 1.5, py: 1.15, borderBottom: { xs: `1px solid ${BORDER}`, sm: 'none' }, borderRight: { sm: `1px solid ${BORDER}` } }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Labour rates</Typography><Typography sx={{ fontWeight: 900, color: DARK, fontSize: 20 }}>{labourRateCount}</Typography></Box>
            <Box sx={{ px: 1.5, py: 1.15, borderRight: `1px solid ${BORDER}`, borderBottom: { xs: `1px solid ${BORDER}`, sm: 'none' } }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Vendor quotes</Typography><Typography sx={{ fontWeight: 900, color: DARK, fontSize: 20 }}>{rateSummary.quoteCount}</Typography></Box>
            <Box sx={{ px: 1.5, py: 1.15, borderBottom: { xs: `1px solid ${BORDER}`, sm: 'none' }, borderRight: { sm: `1px solid ${BORDER}` } }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Selected</Typography><Typography sx={{ fontWeight: 900, color: rateSummary.selectedCount ? GOLD_D : MUTED, fontSize: 20 }}>{rateSummary.selectedCount}</Typography></Box>
            <Box sx={{ px: 1.5, py: 1.15, gridColumn: { xs: '1 / -1', sm: 'auto' } }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Used, selection pending</Typography><Typography sx={{ fontWeight: 900, color: pendingUsedRateCount ? WARN : MUTED, fontSize: 20 }}>{pendingUsedRateCount}</Typography></Box>
          </Box>
        </Box>
      )}

      {activeTab === 'projectScope' ? (
        <ProjectEstimateScopeEditor
          items={projectEstimateScopeItems}
          sections={projectEstimateScopeSections}
          workItems={[]}
          usageIds={new Set()}
          rates={workRateSetups}
          materialItems={items}
          assemblies={assemblies}
          unitTypes={unitTypeMasters}
          buildings={Array.from(new Set(unitTypeMasters.flatMap((unit) => (unit.buildingAllocations || []).map((allocation) => allocation.buildingName)).filter(Boolean)))}
          onSave={onSaveProjectEstimateScopeItems}
          onSaveSections={onSaveProjectEstimateScopeSections}
          onSaveMaterialProcurementOrder={onSaveMaterialProcurementOrder}
          onOpenProjectCost={() => onTabChange('projectCost')}
          onSaveUnitTypes={onSaveUnitTypeMasters}
        />
      ) : activeTab === 'orderSummary' ? (
        <OrderSummaryPanel
          assemblies={assemblies}
          materialItems={items}
          unitTypeMasters={unitTypeMasters}
          projectScopeItems={projectEstimateScopeItems}
          orders={materialProcurementOrders}
          onSaveOrder={onSaveMaterialProcurementOrder}
          onDeleteOrder={onDeleteMaterialProcurementOrder}
          onMergeOrders={onMergeMaterialProcurementOrders}
          onSelectRate={async (materialId, optionId, quoteId) => {
            const material = items.find((item) => item.id === materialId);
            if (!material) return;
            if (optionId) await onSelectOptionQuote(material, optionId, quoteId);
            else await onSelectQuote(material, quoteId);
          }}
        />
      ) : activeTab === 'projectCost' ? (
        <ProjectCostPanel
          unitTypes={unitTypeMasters}
          workItems={[]}
          rates={workRateSetups}
          materialItems={items}
          assemblies={assemblies}
          projectScopeItems={projectEstimateScopeItems}
        />
      ) : activeTab === 'allocate' ? (
        <MaterialAllocationPanel
          orders={materialProcurementOrders}
          bills={materialVendorBills}
          allocations={materialAllocations}
          unitTypes={unitTypeMasters}
          scopeItems={projectEstimateScopeItems}
          materialItems={items}
          onSave={onSaveMaterialAllocations}
          onDeleteAllocation={onDeleteMaterialAllocation}
          stockIssues={stockIssues}
          stockUsageSeen={stockUsageSeen}
          onViewStockUsage={onViewStockUsage}
        />
      ) : activeTab === 'assemblies' ? (
        <AssembliesPanel
          assemblies={assemblies}
          materialItems={items}
          gstDisplay={assemblyGstDisplay}
          unitTypeMasters={unitTypeMasters}
          onAddAssembly={onAddAssembly}
          onSaveAssembly={onSaveAssembly}
          onEditAssembly={onEditAssembly}
          onDeleteAssembly={onDeleteAssembly}
        />
      ) : (
        <>
          {isRateListTab && shownRateItems.length > 0 && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              gap={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
              sx={{ mb: 1.5 }}
            >
              <TextField
                value={rateSearch}
                onChange={(event) => setRateSearch(event.target.value)}
                placeholder="Search materials, labour, vendors, varieties..."
                size="small"
                sx={{ maxWidth: { xs: '100%', md: 520 }, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon sx={{ fontSize: 18, color: FAINT }} />
                    </InputAdornment>
                  ),
                  endAdornment: rateSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setRateSearch('')} edge="end" aria-label="Clear search">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <ToggleButtonGroup exclusive size="small" value={rateKindFilter} onChange={(_, value) => value && setRateKindFilter(value)} sx={{ '& .MuiToggleButton-root': { ...toggleSx, px: 1.25 } }}>
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="material">Materials</ToggleButton>
                <ToggleButton value="labour">Labour</ToggleButton>
              </ToggleButtonGroup>
              <Typography sx={{ color: MUTED, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                Showing {filteredRateItems.length} of {shownRateItems.length} rate{shownRateItems.length === 1 ? '' : 's'}
              </Typography>
              {pendingUsedRateCount > 0 && (
                <Button
                  size="small"
                  onClick={openPendingUsedRates}
                  variant="outlined"
                  sx={{
                    minHeight: 24,
                    py: 0.1,
                    px: 1,
                    borderRadius: 999,
                    bgcolor: '#fff4d8',
                    color: '#7a5525',
                    borderColor: '#e2c982',
                    fontSize: 12,
                    fontWeight: 800,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#ffedbd', borderColor: '#d5b756' },
                  }}
                >
                  {pendingUsedRateCount} used rate{pendingUsedRateCount === 1 ? '' : 's'} pending selection
                </Button>
              )}
            </Stack>
          )}

          {shownRateItems.length === 0 ? (
        <EmptyCard
          title="No rates yet"
          body="Add a material or labour rate, compare vendor quotes, select the preferred quote, and keep rate history for this project."
          cta="Add material"
          onClick={() => onAddMaterial()}
        />
          ) : filteredRateItems.length === 0 ? (
            <EmptyCard
              title="No matching rates"
              body="Try another material, vendor, brand, variety, grade, or quote option name."
              cta="Clear search"
              onClick={() => setRateSearch('')}
            />
          ) : (
            <Stack gap={1.25}>
              {groupedFilteredRateItems.map((group) => isMobileRateView ? (
                <Box
                  key={group.key}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                    overflow: 'hidden',
                  }}
                >
                  {group.items.length > 1 && (
                    <Box sx={{ px: 1.5, py: 0.9, bgcolor: '#fffaf0', borderBottom: `1px solid ${BORDER}` }}>
                      <Typography sx={{ color: GOLD_D, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>{group.label}</Typography>
                    </Box>
                  )}
                  <Stack divider={<Divider flexItem />}>
                    {group.items.map(renderRateCard)}
                  </Stack>
                </Box>
              ) : (
                <Accordion
                  key={group.key}
                  expanded={expandedRateGroupKeys.has(group.key)}
                  onChange={(_, expanded) => setExpandedRateGroupKeys((prev) => {
                    const next = new Set(prev);
                    expanded ? next.add(group.key) : next.delete(group.key);
                    return next;
                  })}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: '8px !important',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: '#fffaf0',
                      minHeight: 54,
                      borderBottom: expandedRateGroupKeys.has(group.key) ? `1px solid ${BORDER}` : 'none',
                      '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.25, my: 1.1 },
                      '& .MuiAccordionSummary-expandIconWrapper': { color: GOLD_D },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <Typography sx={{ color: GOLD_D, fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>{group.label}</Typography>
                      </Stack>
                    </Box>
                    <Stack direction="row" gap={2.2} sx={{ display: { xs: 'none', sm: 'flex' }, mr: 1.5 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: 10, color: FAINT, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.6px' }}>Quotes</Typography>
                        <Typography sx={{ color: GOLD_D, fontWeight: 900 }}>{group.quoteCount}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: 10, color: FAINT, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.6px' }}>Selected</Typography>
                        <Typography sx={{ color: group.selectedCount ? GOLD_D : MUTED, fontWeight: 900 }}>{group.selectedCount}</Typography>
                      </Box>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, bgcolor: '#fff' }}>
                    <Stack divider={<Divider flexItem />}>
                      {group.items.map(renderRateCard)}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

function materialRateSearchText(item: MaterialRateItem): string {
  const parts: string[] = [
    item.materialName,
    materialGroupLabel(item.group),
    item.grade,
    item.brand,
    item.unit,
    materialSecondaryUnitLabel(item),
    ...(item.assemblyTypes || []),
    ...(item.varieties || []),
  ].filter(Boolean) as string[];

  for (const row of item.assemblyVarietyMap || []) {
    parts.push(row.assemblyType, row.unit, row.secondaryUnit || '', ...(row.varieties || []));
  }
  for (const template of item.quoteOptionTemplates || []) {
    parts.push(template.group || '', template.name, template.unit, template.secondaryUnit || '');
  }
  for (const quote of item.quotes || []) {
    parts.push(
      quote.vendor,
      quote.brand || '',
      quote.refName || '',
      quote.refNumber || '',
      quote.materialCode || '',
      quote.paymentTerms || '',
      quote.deliveryTimeline || '',
      quote.notes || '',
    );
    for (const option of quote.quoteOptions || []) {
      parts.push(option.group || '', option.variety || '', option.name, option.unit, option.brand || '', option.code || '');
    }
  }

  return parts.join(' ').toLowerCase();
}

function materialOptionCodeModelSuggestions(item: MaterialRateItem, option: MaterialQuoteOption): string[] {
  const optionKey = materialQuoteOptionMatchKey(option);
  const seen = new Set<string>();
  const suggestions: string[] = [];
  for (const quote of item.quotes || []) {
    for (const savedOption of quote.quoteOptions || []) {
      if (materialQuoteOptionMatchKey(savedOption) !== optionKey) continue;
      const code = (savedOption.code || '').trim();
      if (!code) continue;
      const normalized = code.toLowerCase();
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      suggestions.push(code);
    }
  }
  return suggestions;
}

function materialQuoteOptionMatchKey(option: Pick<MaterialQuoteOption, 'id' | 'templateId' | 'group' | 'name' | 'unit'>): string {
  return [
    option.templateId || option.id || '',
    (option.group || '').trim(),
    option.name.trim(),
    option.unit || '',
  ].join('\u0001').toLowerCase();
}

function groupMaterialRateItems(items: MaterialRateItem[]): Array<{
  key: string;
  label: string;
  items: MaterialRateItem[];
  quoteCount: number;
  selectedCount: number;
}> {
  const grouped = new Map<string, MaterialRateItem[]>();
  for (const item of items) {
    const key = item.group || 'other';
    grouped.set(key, [...(grouped.get(key) || []), item]);
  }
  return Array.from(grouped.entries())
    .sort(([a], [b]) => {
      const aIndex = isBuiltInMaterialGroup(a) ? MATERIAL_GROUPS.indexOf(a) : Number.MAX_SAFE_INTEGER;
      const bIndex = isBuiltInMaterialGroup(b) ? MATERIAL_GROUPS.indexOf(b) : Number.MAX_SAFE_INTEGER;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return materialGroupLabel(a).localeCompare(materialGroupLabel(b));
    })
    .map(([key, groupItems]) => ({
      key,
      label: materialGroupLabel(key),
      items: groupItems,
      quoteCount: groupItems.reduce((sum, item) => sum + (item.quotes?.length || 0), 0),
      selectedCount: groupItems.filter((item) => item.selectedQuoteId || Object.values(item.selectedOptionQuoteIds || {}).some(Boolean)).length,
    }));
}

function MaterialReportDownloadDialog({
  items,
  initialSelectedIds,
  busy,
  onClose,
  onDownload,
}: {
  items: MaterialRateItem[];
  initialSelectedIds: string[];
  busy: boolean;
  onClose: () => void;
  onDownload: (selectedIds: string[], gstMode: MaterialGstDisplay) => void | Promise<void>;
}) {
  const mobileFullScreen = useMediaQuery('(max-width:600px)');
  const [gstMode, setGstMode] = useState<MaterialGstDisplay>('incl');
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const selectedSet = new Set(selectedIds);
  const allSelected = items.length > 0 && items.every((item) => selectedSet.has(item.id));
  const toggleItem = (itemId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(itemId) : next.delete(itemId);
      return Array.from(next);
    });
  };
  return (
    <Dialog
      open
      fullScreen={mobileFullScreen}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: POPUP_PAPER_SX }}
    >
      <DlgTitle onClose={busy ? () => {} : onClose}>Download Material Rate Report</DlgTitle>
      <DialogContent>
        <Stack gap={2} mt={0.5}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: FAINT, textTransform: 'uppercase', letterSpacing: '.08em', mb: 0.75 }}>
              Rate basis
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={gstMode}
              onChange={(_, value) => value && setGstMode(value)}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1.6,
                  textTransform: 'none',
                  fontWeight: 800,
                  borderColor: BORDER,
                  color: MUTED,
                  '&.Mui-selected': { bgcolor: GOLD, color: '#fff', '&:hover': { bgcolor: GOLD_D } },
                },
              }}
            >
              <ToggleButton value="incl">Incl GST</ToggleButton>
              <ToggleButton value="excl">Excl GST</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ color: MUTED, fontSize: 11.5, mt: 0.75 }}>
              Selected materials will be generated in one PDF, with each material starting on a new page.
            </Typography>
          </Box>

          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.25, py: 1, bgcolor: PANEL2, borderBottom: `1px solid ${BORDER}` }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={selectedIds.length > 0 && !allSelected}
                onChange={(event) => setSelectedIds(event.target.checked ? items.map((item) => item.id) : [])}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, color: DARK }}>Materials</Typography>
                <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{selectedIds.length} selected</Typography>
              </Box>
            </Stack>
            <Stack sx={{ maxHeight: { xs: 'none', sm: 360 }, overflowY: 'auto' }}>
              {items.map((item) => (
                <Stack
                  key={item.id}
                  direction="row"
                  gap={1}
                  alignItems="center"
                  sx={{ px: 1.25, py: 1, borderBottom: `1px solid ${BORDER}`, '&:last-child': { borderBottom: 0 } }}
                >
                  <Checkbox size="small" checked={selectedSet.has(item.id)} onChange={(event) => toggleItem(item.id, event.target.checked)} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, color: DARK, fontSize: 13 }}>{item.materialName}</Typography>
                    <Typography sx={{ color: MUTED, fontSize: 11.5 }}>
                      {[materialGroupLabel(item.group), item.grade, `Unit ${item.unit}`, `${item.quotes?.length || 0} quote(s)`].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || selectedIds.length === 0}
          onClick={() => onDownload(selectedIds, gstMode)}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
        >
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssembliesPanel({
  assemblies,
  materialItems,
  gstDisplay,
  unitTypeMasters,
  onAddAssembly,
  onSaveAssembly,
  onEditAssembly,
  onDeleteAssembly,
}: {
  assemblies: MaterialAssembly[];
  materialItems: MaterialRateItem[];
  gstDisplay: MaterialGstDisplay;
  unitTypeMasters: UnitTypeMaster[];
  onAddAssembly: () => void;
  onSaveAssembly: (assembly: MaterialAssembly) => void;
  onEditAssembly: (assembly: MaterialAssembly) => void;
  onDeleteAssembly: (assembly: MaterialAssembly) => void;
}) {
  const [expandedAssemblyIds, setExpandedAssemblyIds] = useState<Set<string>>(new Set());
  const [quick, setQuick] = useState<{
    assemblyId: string;
    type: 'manual' | 'material';
    label: string;
    qty: number;
    unit: string;
    basis: 'output' | 'reference';
    material?: MaterialRateItem | null;
    quoteOptionId?: string;
    manualRateSource?: 'pending' | 'manual' | 'material';
    linkedMaterialId?: string;
    linkedMaterialName?: string;
    linkedMaterialGroup?: MaterialGroup;
    linkedQuoteId?: string;
    linkedQuoteOptionId?: string;
    manualRate?: number;
    gst: number;
    gstMode: 'inclusive' | 'exclusive';
  } | null>(null);
  const [editingComponent, setEditingComponent] = useState<{
    assemblyId: string;
    component: AssemblyComponentDraft;
  } | null>(null);
  const cleanAssemblyComponent = (component: AssemblyComponentDraft): AssemblyComponent => {
    const cleaned: AssemblyComponent = {
      id: component.id,
      type: component.type,
      basis: component.basis || 'reference',
      label: component.label.trim(),
      qty: num(component.qty),
      unit: component.unit || 'Nos',
    };
    if (component.type === 'material') {
      if (component.materialName) cleaned.materialName = component.materialName;
      if (component.materialGroup) cleaned.materialGroup = component.materialGroup;
      if (component.quoteOptionId) cleaned.quoteOptionId = component.quoteOptionId;
    }
    if (component.type === 'manual') {
      cleaned.manualRateSource = component.manualRateSource || (num(component.manualRate) > 0 ? 'manual' : 'pending');
      if (cleaned.manualRateSource === 'material') {
        if (component.linkedMaterialId) cleaned.linkedMaterialId = component.linkedMaterialId;
        if (component.linkedMaterialName) cleaned.linkedMaterialName = component.linkedMaterialName;
        if (component.linkedMaterialGroup) cleaned.linkedMaterialGroup = component.linkedMaterialGroup;
        if (component.linkedQuoteId) cleaned.linkedQuoteId = component.linkedQuoteId;
        if (component.linkedQuoteOptionId) cleaned.linkedQuoteOptionId = component.linkedQuoteOptionId;
      } else if (cleaned.manualRateSource === 'manual') {
        if (num(component.manualRate) > 0) cleaned.manualRate = num(component.manualRate);
        cleaned.gst = num(component.gst ?? 18);
        cleaned.gstMode = component.gstMode || 'exclusive';
      }
    }
    return cleaned;
  };
  const saveQuickComponent = (assembly: MaterialAssembly) => {
    if (!quick || quick.assemblyId !== assembly.id || !quick.label.trim() || num(quick.qty) <= 0) return;
    const component: AssemblyComponent = {
      id: `component_${Date.now()}`,
      type: quick.type,
      basis: quick.basis,
      label: quick.label.trim(),
      qty: num(quick.qty),
      unit: quick.unit || 'Nos',
    };
    if (quick.type === 'material' && quick.material) {
      component.materialName = quick.material.materialName;
      component.materialGroup = quick.material.group;
      if (quick.quoteOptionId) component.quoteOptionId = quick.quoteOptionId;
    }
    if (quick.type === 'manual') {
      component.manualRateSource = quick.manualRateSource || (num(quick.manualRate) > 0 ? 'manual' : 'pending');
      if (component.manualRateSource === 'manual' && num(quick.manualRate) > 0) {
        component.manualRate = num(quick.manualRate);
        component.gst = num(quick.gst);
        component.gstMode = quick.gstMode;
      }
      if (component.manualRateSource === 'material' && quick.linkedMaterialName) {
        if (quick.linkedMaterialId) component.linkedMaterialId = quick.linkedMaterialId;
        component.linkedMaterialName = quick.linkedMaterialName;
        component.linkedMaterialGroup = quick.linkedMaterialGroup;
        if (quick.linkedQuoteId) component.linkedQuoteId = quick.linkedQuoteId;
        if (quick.linkedQuoteOptionId) component.linkedQuoteOptionId = quick.linkedQuoteOptionId;
      }
    }
    onSaveAssembly({ ...assembly, components: [...(assembly.components || []), cleanAssemblyComponent(component)] });
    setQuick(null);
  };
  const startEditComponent = (assembly: MaterialAssembly, component: AssemblyComponent) => {
    setQuick(null);
    setEditingComponent({
      assemblyId: assembly.id,
      component: {
        basis: 'reference',
        ...component,
        material: component.type === 'material' ? findAssemblyMaterial(component, materialItems) || null : null,
        gst: component.gst ?? 18,
        gstMode: component.gstMode || 'exclusive',
      },
    });
  };
  const saveEditedComponent = (assembly: MaterialAssembly) => {
    if (!editingComponent || editingComponent.assemblyId !== assembly.id) return;
    const nextComponent = cleanAssemblyComponent(editingComponent.component);
    if (!nextComponent.label || nextComponent.qty <= 0) return;
    if (nextComponent.type === 'material' && !nextComponent.materialName) return;
    onSaveAssembly({
      ...assembly,
      components: (assembly.components || []).map((component) => component.id === nextComponent.id ? nextComponent : component),
    });
    setEditingComponent(null);
  };
  const deleteComponent = (assembly: MaterialAssembly, componentId: string) => {
    onSaveAssembly({
      ...assembly,
      components: (assembly.components || []).filter((component) => component.id !== componentId),
    });
    setEditingComponent((previous) => previous?.assemblyId === assembly.id && previous.component.id === componentId ? null : previous);
  };
  const patchEditingComponent = (patch: Partial<AssemblyComponentDraft>) =>
    setEditingComponent((prev) => prev && ({ ...prev, component: { ...prev.component, ...patch } }));
  const copyAssembly = (assembly: MaterialAssembly) => {
    const stamp = Date.now();
    onSaveAssembly({
      ...assembly,
      id: `assembly_${stamp}`,
      name: `${assembly.name} copy`,
      components: (assembly.components || []).map((component, index) => ({ ...component, id: `component_${stamp}_${index}` })),
      order: undefined,
    });
  };
  const toggleAssembly = (assemblyId: string) => {
    setExpandedAssemblyIds((previous) => {
      const next = new Set(previous);
      if (next.has(assemblyId)) next.delete(assemblyId);
      else next.add(assemblyId);
      return next;
    });
  };
  const openAssembly = (assemblyId: string) => {
    setExpandedAssemblyIds((previous) => {
      if (previous.has(assemblyId)) return previous;
      const next = new Set(previous);
      next.add(assemblyId);
      return next;
    });
  };
  return (
    <Stack gap={1.5}>
      {!assemblies.length && (
        <EmptyCard
          title="No assemblies yet"
          body="Create a reusable built-up item like Finished Flush Door, Kitchen Platform, or Window Grill from selected material and labour rates."
          cta="Add assembly"
          onClick={onAddAssembly}
        />
      )}
      {assemblies.map((assembly) => {
        const recipeSummary = assemblySummary(assembly, materialItems);
        const showIncl = gstDisplay === 'incl';
        const selectedTotal = showIncl ? recipeSummary.totalIncl : recipeSummary.totalExcl;
        const secondaryRate = assembly.referenceUnit && num(assembly.referenceQty) > 0
          ? selectedTotal / num(assembly.referenceQty)
          : 0;
        const mainRecipeValue = secondaryRate > 0 ? secondaryRate : selectedTotal;
        const mainRecipeUnit = secondaryRate > 0 ? assembly.referenceUnit : assembly.outputUnit;
        const pendingRateCount = recipeSummary.missing + recipeSummary.pending;
        const isPartialRate = mainRecipeValue > 0 && pendingRateCount > 0;
        return (
          <Accordion
            key={assembly.id}
            expanded={expandedAssemblyIds.has(assembly.id)}
            onChange={() => toggleAssembly(assembly.id)}
            disableGutters
            elevation={0}
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: '16px !important',
              bgcolor: '#fff',
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                p: 0,
                bgcolor: PANEL2,
                '& .MuiAccordionSummary-content': { m: 0, minWidth: 0 },
                '& .MuiAccordionSummary-expandIconWrapper': { mr: 1.2, color: GOLD_D },
              }}
            >
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ p: 1.75, pr: 5, width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label={assembly.category || 'Assembly'} sx={{ bgcolor: GOLD_WASH, color: GOLD_D, fontWeight: 700 }} />
                  <Typography sx={{ fontWeight: 800, color: DARK }}>{assembly.name}</Typography>
                </Stack>
                <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.35 }}>
                  Output unit {assembly.outputUnit}
                  {assembly.referenceUnit && num(assembly.referenceQty) ? ` · ${fmtQty(num(assembly.referenceQty))} ${assembly.referenceUnit}/${assembly.outputUnit}` : ''}
                  {' · '}{assembly.components.length} component(s)
                </Typography>
              </Box>
              <Stack direction="row" gap={{ xs: 2, md: 3 }} alignItems="center" sx={{ minWidth: { md: 335 }, justifyContent: { md: 'flex-end' } }}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700, textTransform: 'uppercase' }}>Components</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, color: DARK }}>{assembly.components.length}</Typography>
                </Box>
              <Box sx={{ minWidth: 210, textAlign: { xs: 'left', md: 'right' } }}>
                <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700, textTransform: 'uppercase' }}>Recipe rate</Typography>
                <Stack gap={0.25} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Stack direction="row" gap={0.6} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
                    <Tooltip title={isPartialRate ? 'This rate includes only available component rates. Some component rates are still pending.' : ''}>
                      <Typography component="span" sx={{ fontWeight: 900, color: recipeSummary.missing ? WARN : GOLD_D, fontSize: 18 }}>
                        ₹{fmtRate(mainRecipeValue)}/{mainRecipeUnit}{isPartialRate ? '*' : ''}
                      </Typography>
                    </Tooltip>
                    {isPartialRate && (
                      <Tooltip title="This is a working rate until all component rates are selected.">
                        <Chip
                          size="small"
                          label="Partial rate"
                          sx={{ height: 20, bgcolor: '#fff4d8', color: GOLD_D, border: '1px solid #efd391', fontWeight: 800, fontSize: 10 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  {secondaryRate > 0 && (
                    <Typography sx={{ color: MUTED, fontSize: 11.5, whiteSpace: 'nowrap' }}>
                      ₹{fmtRate(selectedTotal)}/{assembly.outputUnit}
                    </Typography>
                  )}
                </Stack>
                {isPartialRate && (
                  <Typography sx={{ color: WARN, fontSize: 11, fontWeight: 800 }}>
                    Partial only · {pendingRateCount} rate pending
                  </Typography>
                )}
                {!isPartialRate && recipeSummary.missing > 0 && <Typography sx={{ color: WARN, fontSize: 11 }}>{recipeSummary.missing} missing selected rate</Typography>}
                {!isPartialRate && recipeSummary.pending > 0 && <Typography sx={{ color: MUTED, fontSize: 11 }}>{recipeSummary.pending} rate pending</Typography>}
              </Box>
              </Stack>
              <Stack direction="row" gap={0.25} alignItems="center" onClick={(event) => event.stopPropagation()}>
              <ActionIcon title="Edit assembly" onClick={() => onEditAssembly(assembly)}><EditOutlinedIcon fontSize="inherit" /></ActionIcon>
              <ActionIcon title="Copy assembly" onClick={() => copyAssembly(assembly)}><ContentCopyOutlinedIcon fontSize="inherit" /></ActionIcon>
              <Button size="small" variant="outlined" onClick={() => {
                const item = materialItems[0] || null;
                openAssembly(assembly.id);
                setQuick({
                  assemblyId: assembly.id,
                  type: 'material',
                  label: item?.materialName || '',
                  qty: 1,
                  unit: item?.unit || 'Nos',
                  basis: item?.unit && assembly.referenceUnit && item.unit === assembly.referenceUnit ? 'reference' : 'output',
                  material: item,
                  quoteOptionId: '',
                  gst: 18,
                  gstMode: 'exclusive',
                });
              }} sx={{ borderColor: BORDER, color: MUTED }}>
                Add component
              </Button>
              <ActionIcon title="Delete assembly" onClick={() => onDeleteAssembly(assembly)} danger><DeleteOutlineIcon fontSize="inherit" /></ActionIcon>
              </Stack>
            </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, bgcolor: '#fff' }}>
            {quick?.assemblyId === assembly.id && (
              <Box sx={{ p: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: quick.type === 'material' ? 'minmax(220px,1fr) minmax(180px,0.8fr) 90px 90px 120px 88px' : 'minmax(220px,1fr) 100px 100px 130px 120px 120px 88px' }, gap: 1, alignItems: 'center' }}>
                  {quick.type === 'material' ? (
                    <Autocomplete
                      options={materialItems}
                      value={quick.material || null}
                      getOptionLabel={(item) => `${item.materialName} · ${materialGroupLabel(item.group)}`}
                      onChange={(_, item) => item && setQuick((prev) => prev && ({ ...prev, material: item, label: item.materialName, unit: item.unit, quoteOptionId: '' }))}
                      renderInput={(params) => <TextField {...params} label="Material" size="small" />}
                    />
                  ) : (
                    <TextField label="Component" value={quick.label} onChange={(e) => setQuick((prev) => prev && ({ ...prev, label: e.target.value }))} size="small" fullWidth autoFocus />
                  )}
                  {quick.type === 'material' && (() => {
                    const quoteOptions = materialSelectableQuoteOptions(quick.material || undefined);
                    return (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Variant rate</InputLabel>
                        <Select label="Variant rate" value={quick.quoteOptionId || ''} onChange={(e) => setQuick((prev) => prev && ({ ...prev, quoteOptionId: e.target.value }))}>
                          <MenuItem value="">Selected quote</MenuItem>
                          {quoteOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {quoteOptionLabel(option)}{num(option.rate) > 0 ? ` · ₹${fmtRate(num(option.rate))}/${option.unit}` : ' · rate pending'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })()}
                  <TextField label="Qty" value={quick.qty || ''} onChange={(e) => setQuick((prev) => prev && ({ ...prev, qty: num(e.target.value) }))} type="number" size="small" />
                  <FormControl size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select label="Unit" value={quick.unit} onChange={(e) => setQuick((prev) => prev && ({ ...prev, unit: e.target.value }))}>
                      {MATERIAL_UNIT_OPTIONS.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel>Rate basis</InputLabel>
                    <Select label="Rate basis" value={rateBasisSelectValue(quick.basis, assembly.outputUnit, assembly.referenceUnit)} onChange={(e) => setQuick((prev) => prev && ({ ...prev, basis: e.target.value as 'output' | 'reference' }))}>
                      <MenuItem value="output">Per {assembly.outputUnit}</MenuItem>
                      {showReferenceRateBasis(assembly.outputUnit, assembly.referenceUnit) && (
                        <MenuItem value="reference">Per {assembly.referenceUnit || 'secondary'}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {quick.type === 'manual' ? (
                    <TextField label="Rate" value={quick.manualRate || ''} onChange={(e) => setQuick((prev) => prev && ({ ...prev, manualRate: num(e.target.value) }))} type="number" size="small" InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                  ) : <Box />}
                  {quick.type === 'manual' ? (
                    <TextField label="GST %" value={quick.gst} onChange={(e) => setQuick((prev) => prev && ({ ...prev, gst: num(e.target.value) }))} type="number" size="small" />
                  ) : <Box />}
                  <Stack direction="row" gap={0.5} justifyContent="flex-end">
                    <Button size="small" variant="contained" onClick={() => saveQuickComponent(assembly)} disabled={!quick.label.trim() || num(quick.qty) <= 0}>Save</Button>
                    <Button size="small" onClick={() => setQuick(null)} sx={{ color: MUTED }}>Cancel</Button>
                  </Stack>
                </Box>
              </Box>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', minWidth: 880, borderCollapse: 'collapse' }}>
                <Box component="thead">
                  <Box component="tr" sx={{ '& th': headTh }}>
                    <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Component</Box>
                    <Box component="th" sx={headTh}>Show rate as</Box>
                    <Box component="th" sx={headTh}>Qty</Box>
                    <Box component="th" sx={headTh}>Rate source</Box>
                    <Box component="th" sx={headTh}>Rate</Box>
                    <Box component="th" sx={headTh}>Recipe share</Box>
                    <Box component="th" sx={headTh}>Action</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {assembly.components.map((component) => {
                    const isEditing = editingComponent?.assemblyId === assembly.id && editingComponent.component.id === component.id;
                    if (isEditing) {
                      const draft = editingComponent.component;
                      const selectedMaterial = draft.type === 'material' ? (draft.material || findAssemblyMaterial(draft, materialItems) || null) : null;
                      const quoteOptions = materialSelectableQuoteOptions(selectedMaterial || undefined);
                      const linkedMaterial = draft.manualRateSource === 'material' ? (findLinkedAssemblyMaterial(draft, materialItems) || null) : null;
                      const linkedQuotes = (linkedMaterial?.quotes || []).filter(quoteIsCurrent);
                      const linkedQuote = linkedQuotes.find((quote) => quote.id === draft.linkedQuoteId)
                        || selectedMaterialQuoteForOption(linkedMaterial || undefined, draft.linkedQuoteOptionId)
                        || null;
                      const linkedQuoteOptions = linkedQuote?.quoteOptions || [];
                      const linkedOption = selectedQuoteOption(linkedQuote, draft.linkedQuoteOptionId);
                      const linkedRate = linkedOption
                        ? quoteOptionLandedRate(linkedQuote!, linkedOption)
                        : linkedQuote && linkedMaterial ? quoteLandedRate(linkedMaterial, linkedQuote) : 0;
                      const draftManualRateSource = draft.manualRateSource || (num(draft.manualRate) > 0 ? 'manual' : 'pending');
                      const canSaveDraft = !!draft.label.trim() && num(draft.qty) > 0 && (
                        draft.type === 'material'
                          ? !!draft.materialName
                          : draftManualRateSource !== 'material' || (!!linkedMaterial && !!linkedQuote && linkedRate > 0)
                      );
                      return (
                        <Box component="tr" key={component.id}>
                          <Box component="td" colSpan={7} sx={{ p: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                            <Stack gap={1}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: draft.type === 'manual' ? 'minmax(220px,1fr) 170px 90px 90px 130px 96px' : 'minmax(240px,1fr) minmax(160px,0.7fr) 90px 90px 130px 96px' }, gap: 1, alignItems: 'center' }}>
                              {draft.type === 'material' ? (
                                <Autocomplete
                                  options={materialItems}
                                  value={selectedMaterial}
                                  getOptionLabel={(item) => `${item.materialName} · ${materialGroupLabel(item.group)}`}
                                  onChange={(_, item) => item && patchEditingComponent({
                                    material: item,
                                    label: draft.label.trim() || item.materialName,
                                    materialName: item.materialName,
                                    materialGroup: item.group,
                                    unit: item.unit,
                                    quoteOptionId: '',
                                  })}
                                  renderInput={(params) => <TextField {...params} label="Material" size="small" />}
                                />
                              ) : (
                                <TextField label="Component" value={draft.label} onChange={(e) => patchEditingComponent({ label: e.target.value })} size="small" fullWidth autoFocus />
                              )}
                              {draft.type === 'manual' && (
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Rate source</InputLabel>
                                  <Select
                                    label="Rate source"
                                    value={draftManualRateSource}
                                    onChange={(e) => patchEditingComponent({
                                      manualRateSource: e.target.value as AssemblyComponent['manualRateSource'],
                                      linkedMaterialId: undefined,
                                      linkedMaterialName: undefined,
                                      linkedMaterialGroup: undefined,
                                      linkedQuoteId: undefined,
                                      linkedQuoteOptionId: undefined,
                                    })}
                                  >
                                    <MenuItem value="pending">No rate / pending</MenuItem>
                                    <MenuItem value="manual">Manual rate</MenuItem>
                                    <MenuItem value="material">Use material rate</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                              {draft.type === 'material' && (
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Option</InputLabel>
                                  <Select label="Option" value={draft.quoteOptionId || ''} onChange={(e) => patchEditingComponent({ quoteOptionId: e.target.value })}>
                                    <MenuItem value="">Selected quote</MenuItem>
                                    {quoteOptions.map((option) => (
                                      <MenuItem key={option.id} value={option.id}>
                                        {quoteOptionLabel(option)}{num(option.rate) > 0 ? ` · ₹${fmtRate(num(option.rate))}/${option.unit}` : ' · rate pending'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                              <TextField label="Qty" value={draft.qty || ''} onChange={(e) => patchEditingComponent({ qty: num(e.target.value) })} type="number" size="small" />
                              <FormControl size="small">
                                <InputLabel>Unit</InputLabel>
                                <Select label="Unit" value={draft.unit} onChange={(e) => patchEditingComponent({ unit: e.target.value })}>
                                  {MATERIAL_UNIT_OPTIONS.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
                                </Select>
                              </FormControl>
                              <FormControl size="small">
                                <InputLabel>Rate basis</InputLabel>
                                <Select label="Rate basis" value={rateBasisSelectValue(draft.basis || 'reference', assembly.outputUnit, assembly.referenceUnit)} onChange={(e) => patchEditingComponent({ basis: e.target.value as 'output' | 'reference' })}>
                                  <MenuItem value="output">Per {assembly.outputUnit}</MenuItem>
                                  {showReferenceRateBasis(assembly.outputUnit, assembly.referenceUnit) && (
                                    <MenuItem value="reference">Per {assembly.referenceUnit || 'secondary'}</MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                              <Stack direction="row" gap={0.5} justifyContent="flex-end">
                                <Button size="small" variant="contained" onClick={() => saveEditedComponent(assembly)} disabled={!canSaveDraft}>Save</Button>
                                <Button size="small" onClick={() => setEditingComponent(null)} sx={{ color: MUTED }}>Cancel</Button>
                              </Stack>
                              </Box>
                              {draft.type === 'manual' && draftManualRateSource === 'manual' && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 120px' }, gap: 1 }}>
                                  <TextField
                                    label="Manual rate" value={draft.manualRate || ''} onChange={(e) => patchEditingComponent({ manualRate: num(e.target.value) })} type="number" size="small"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                      endAdornment: <InputAdornment position="end" sx={{ m: 0, height: '100%', maxHeight: 'none' }}>
                                        <SegmentedAdornment
                                          value={draft.gstMode || 'exclusive'}
                                          onChange={(value) => patchEditingComponent({ gstMode: value })}
                                          options={[{ value: 'exclusive', label: 'Excl' }, { value: 'inclusive', label: 'Incl' }]}
                                        />
                                      </InputAdornment>,
                                    }}
                                  />
                                  <TextField label="GST %" value={draft.gst ?? 18} onChange={(e) => patchEditingComponent({ gst: num(e.target.value) })} type="number" size="small" />
                                </Box>
                              )}
                              {draft.type === 'manual' && draftManualRateSource === 'material' && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px,1fr) minmax(200px,0.9fr) minmax(200px,0.8fr)' }, gap: 1 }}>
                                  <Autocomplete
                                    options={materialItems}
                                    value={linkedMaterial}
                                    getOptionLabel={(item) => `${item.materialName} · ${materialGroupLabel(item.group)}`}
                                    onChange={(_, item) => {
                                      if (!item) return;
                                      const quote = selectedMaterialQuote(item) || (item.quotes || []).find(quoteIsCurrent) || null;
                                      const option = quote?.quoteOptions?.find((candidate) => quoteOptionLandedRate(quote, candidate) > 0)
                                        || quote?.quoteOptions?.[0]
                                        || null;
                                      patchEditingComponent({
                                        linkedMaterialId: item.id,
                                        linkedMaterialName: item.materialName,
                                        linkedMaterialGroup: item.group,
                                        linkedQuoteId: quote?.id,
                                        linkedQuoteOptionId: option?.id,
                                        label: option ? quoteOptionLabel(option) : item.materialName,
                                      });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Linked material rate" size="small" />}
                                  />
                                  <FormControl size="small" fullWidth disabled={!linkedMaterial}>
                                    <InputLabel>Vendor quote</InputLabel>
                                    <Select
                                      label="Vendor quote"
                                      value={linkedQuote?.id || ''}
                                      onChange={(e) => {
                                        const quote = linkedQuotes.find((candidate) => candidate.id === e.target.value) || null;
                                        const option = quote?.quoteOptions?.find((candidate) => quoteOptionLandedRate(quote, candidate) > 0)
                                          || quote?.quoteOptions?.[0]
                                          || null;
                                        patchEditingComponent({
                                          linkedQuoteId: quote?.id,
                                          linkedQuoteOptionId: option?.id,
                                          label: option ? quoteOptionLabel(option) : linkedMaterial?.materialName || draft.label,
                                        });
                                      }}
                                    >
                                      {linkedQuotes.map((quote) => (
                                        <MenuItem key={quote.id} value={quote.id}>
                                          {quote.vendor || 'Vendor'}{quote.brand ? ` · ${quote.brand}` : ''}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Linked option</InputLabel>
                                    <Select
                                      label="Linked option"
                                      value={draft.linkedQuoteOptionId || ''}
                                      disabled={!linkedQuote}
                                      onChange={(e) => {
                                        const option = linkedQuoteOptions.find((candidate) => candidate.id === e.target.value) || null;
                                        patchEditingComponent({
                                          linkedQuoteOptionId: option?.id,
                                          label: option ? quoteOptionLabel(option) : linkedMaterial?.materialName || draft.label,
                                        });
                                      }}
                                    >
                                      {linkedQuoteOptions.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                          {quoteOptionLabel(option)}{num(option.rate) > 0 ? ` · ₹${fmtRate(num(option.rate))}/${option.unit}` : ' · rate pending'}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        </Box>
                      );
                    }
                    const row = assemblyComponentAmount(component, materialItems, assembly);
                    const rowRate = showIncl ? row.rateIncl : row.rateExcl;
                    const rowAmount = showIncl ? row.amountIncl : row.amountExcl;
                    const rowSecondaryRate = componentUsesReferenceBasis(component, assembly) && num(assembly.referenceQty) > 0 ? rowAmount / num(assembly.referenceQty) : 0;
                    const rowMainValue = rowSecondaryRate > 0 ? rowSecondaryRate : rowAmount;
                    const rowMainUnit = rowSecondaryRate > 0 ? assembly.referenceUnit : assembly.outputUnit;
                    const td: any = { p: '10px 12px', borderTop: `1px solid ${BORDER}`, fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap' };
                    return (
                      <Box component="tr" key={component.id}>
                        <Box component="td" sx={{ ...td, textAlign: 'left' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{component.label}</Typography>
                          <Typography sx={{ color: MUTED, fontSize: 11.5 }}>
                            {component.type === 'manual'
                              ? component.manualRateSource === 'material' && component.linkedMaterialName
                                ? `Material rate · ${component.linkedMaterialName}`
                                : 'Manual component'
                              : materialGroupLabel(component.materialGroup || 'other')}
                          </Typography>
                        </Box>
                        <Box component="td" sx={td}>{componentUsesReferenceBasis(component, assembly) ? `Per ${assembly.referenceUnit || 'reference'}` : `Per ${assembly.outputUnit}`}</Box>
                        <Box component="td" sx={td}>{fmtQty(num(component.qty))} {component.unit}</Box>
                        <Box component="td" sx={td}>{row.source}</Box>
                        <Box component="td" sx={td}>
                          {row.rate ? (
                            <>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>₹{fmtRate(rowRate)}</Typography>
                            </>
                          ) : '—'}
                        </Box>
                        <Box component="td" sx={{ ...td, fontWeight: 800, color: row.missing ? WARN : (row.ratePending ? MUTED : GOLD_D) }}>
                          {row.missing ? 'Missing' : (row.ratePending ? 'Rate pending' : `₹${fmtRate(rowMainValue)}/${rowMainUnit}`)}
                          {!row.missing && !row.ratePending && rowSecondaryRate > 0 && (
                            <Typography sx={{ color: MUTED, fontSize: 10.5 }}>₹{fmtRate(rowAmount)}/{assembly.outputUnit}</Typography>
                          )}
                          {!row.missing && !row.ratePending && assemblyShareBasisLabel(component, assembly) && (
                            <Typography sx={{ color: FAINT, fontSize: 10.5 }}>{assemblyShareBasisLabel(component, assembly)}</Typography>
                          )}
                        </Box>
                        <Box component="td" sx={td}>
                          <Stack direction="row" gap={0.25} justifyContent="flex-end">
                            <ActionIcon title="Edit component" onClick={() => startEditComponent(assembly, component)}><EditOutlinedIcon fontSize="inherit" /></ActionIcon>
                            <ActionIcon title="Delete component" onClick={() => deleteComponent(assembly, component.id)} danger><DeleteOutlineIcon fontSize="inherit" /></ActionIcon>
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}

export type MaterialRequirement = {
  key: string;
  materialId: string;
  quoteOptionId?: string;
  label: string;
  unit: string;
  rateUnit: string;
  qty: number;
  scopeType: 'project' | 'building' | 'unitType';
  scopeId?: string;
  scopeLabel?: string;
  unitTypeId?: string;
  unitTypeLabel?: string;
  sources: Set<string>;
  rate: number;
  ratePending: boolean;
};

export function materialProcurementRequirementRows(
  unitTypes: UnitTypeMaster[],
  materials: MaterialRateItem[],
  assemblies: MaterialAssembly[],
  projectScopeItems: ProjectEstimateScopeItem[] = [],
): MaterialRequirement[] {
  const leaves: MaterialRequirement[] = [];
  const add = (
    material: MaterialRateItem | undefined,
    quoteOptionId: string | undefined,
    qty: number,
    unit: string,
    allocation: UnitTypeBuildingAllocation,
    unitType: UnitTypeMaster,
    source: string,
    displayLabel?: string,
    scopeOverride?: { type: 'project' | 'building'; id: string; label: string },
  ) => {
    if (!material || material.group === 'labour' || qty <= 0) return;
    const quote = selectedMaterialQuoteForOption(material, quoteOptionId);
    const option = selectedQuoteOption(quote, quoteOptionId);
    const rate = option ? quoteOptionLandedRate(quote!, option) : (quote ? quoteLandedRate(material, quote) : 0);
    const optionLabel = option ? quoteOptionLabel(option) : '';
    const isSharedAccessoryItem = material.materialName.trim().toLowerCase().startsWith('door acc');
    const displayName = isSharedAccessoryItem
      ? (optionLabel || displayLabel || material.materialName)
      : optionLabel ? `${material.materialName} · ${optionLabel}` : material.materialName;
    leaves.push({
      key: `${material.id}__${quoteOptionId || 'base'}__${allocation.id}__${unitType.id}`,
      materialId: material.id,
      quoteOptionId,
      label: displayName,
      unit: unit || option?.unit || material.unit || 'Nos',
      rateUnit: materialQuoteRateUnit(option, material.unit || unit || 'Nos'),
      qty,
      scopeType: scopeOverride?.type || 'building',
      scopeId: scopeOverride?.id || allocation.id,
      scopeLabel: scopeOverride?.label || allocation.buildingName,
      unitTypeId: unitType.id,
      unitTypeLabel: unitType.label,
      sources: new Set([source]),
      rate,
      ratePending: !rate,
    });
  };
  for (const scopeItem of projectScopeItems) {
    // Unit-type cost is already calculated directly from the live unit template.
    if (scopeItem.scopeType === 'unitType') continue;
    const outputQty = projectScopeCalculatedQty(scopeItem, unitTypes);
    if (outputQty <= 0) continue;
    const setup = scopeItem.sourceId && scopeItem.sourceType
      ? { workItemId: scopeItem.workItemId || `scope_${scopeItem.id}`, sourceType: scopeItem.sourceType === 'assembly' ? 'assembly' as const : 'direct' as const, sourceId: scopeItem.sourceId, quoteOptionId: scopeItem.quoteOptionId || '', basis: 'primary' as const }
      : undefined;
    if (!setup) continue;
    const scopeOverride = {
      type: scopeItem.scopeType === 'building' ? 'building' as const : 'project' as const,
      id: scopeItem.id,
      label: scopeItem.scopeType === 'building' ? (scopeItem.buildingName || 'Building') : 'Project-wide',
    };
    const allocation = { id: scopeItem.id, buildingName: scopeOverride.label, count: 1 } as UnitTypeBuildingAllocation;
    const unitType = { id: 'project_scope', label: 'Project scope' } as UnitTypeMaster;
    if (setup.sourceType === 'direct') {
      const material = materials.find((item) => item.id === setup.sourceId);
      const optionId = scopeItem.quoteOptionId || materialOptionIdForVariety(material, scopeItem.variety || scopeItem.subgroup) || setup.quoteOptionId;
      add(material, optionId, outputQty, scopeItem.unit || material?.unit || 'Nos', allocation, unitType, scopeItem.label || material?.materialName || 'Project scope material', undefined, scopeOverride);
      continue;
    }
    const assembly = assemblies.find((item) => item.id === setup.sourceId);
    if (!assembly) continue;
    const referenceOutputQty = outputQty * num(assembly.referenceQty || 1);
    for (const component of assembly.components || []) {
      const material = component.type === 'material'
        ? findAssemblyMaterial(component, materials)
        : component.manualRateSource === 'material' ? findLinkedAssemblyMaterial(component, materials) : undefined;
      const optionId = component.type === 'material' ? component.quoteOptionId : component.linkedQuoteOptionId;
      const componentQty = (component.basis === 'reference' ? referenceOutputQty : outputQty) * num(component.qty);
      add(material, optionId, componentQty, component.unit, allocation, unitType, scopeItem.label || assembly.name, component.label, scopeOverride);
    }
  }
  for (const unitType of unitTypes) {
    for (const allocation of unitType.buildingAllocations || []) {
      const allocationCount = num(allocation.count);
      if (allocationCount <= 0) continue;
      for (const rule of unitType.components || []) {
        if (!rule.sourceId) continue;
        const setup = componentRateSetup(rule, [], []);
        if (!setup || setup.sourceType === 'none' || setup.sourceType === 'manual') continue;
        const outputQty = allocationCount * num(rule.qtyPerUnit);
        if (setup.sourceType === 'direct') {
          const material = materials.find((item) => item.id === setup.sourceId);
          const isSecondary = setup.basis === 'secondary';
          const convertedQty = isSecondary ? outputQty * num(rule.areaPerUnit) : outputQty;
          const convertedUnit = isSecondary
            ? (rule.areaUnit || material?.unit || rule.unit)
            : (rule.unit || material?.unit || 'Nos');
          const optionId = rule.quoteOptionId || setup.quoteOptionId;
          const source = rule.variety || rule.assemblyType || rule.label || material?.materialName || 'Unit material';
          add(material, optionId, convertedQty, convertedUnit, allocation, unitType, source);
          continue;
        }
        const assembly = assemblies.find((item) => item.id === setup.sourceId);
        if (!assembly) continue;
        const referenceOutputQty = outputQty * num(assembly.referenceQty || 1);
        for (const component of assembly.components || []) {
          const material = component.type === 'material'
            ? findAssemblyMaterial(component, materials)
            : component.manualRateSource === 'material' ? findLinkedAssemblyMaterial(component, materials) : undefined;
          const optionId = component.type === 'material' ? component.quoteOptionId : component.linkedQuoteOptionId;
          const componentQty = (component.basis === 'reference' ? referenceOutputQty : outputQty) * num(component.qty);
          add(material, optionId, componentQty, component.unit, allocation, unitType, rule.label || assembly.name, component.label);
        }
      }
    }
  }
  return leaves;
}

export function aggregateMaterialRequirements(leaves: MaterialRequirement[], mode: 'project' | 'building' | 'unitType'): MaterialRequirement[] {
  const grouped = new Map<string, MaterialRequirement>();
  for (const leaf of leaves) {
    const scopeId = mode === 'project' ? '' : mode === 'building' ? leaf.scopeId : leaf.unitTypeId || '';
    const scopeLabel = mode === 'project' ? '' : mode === 'building' ? leaf.scopeLabel : leaf.unitTypeLabel || '';
    const key = `${leaf.materialId}__${leaf.quoteOptionId || 'base'}__${scopeId}`;
    const current = grouped.get(key);
    if (current) {
      current.qty += leaf.qty;
      leaf.sources.forEach((source) => current.sources.add(source));
      current.ratePending = current.ratePending || leaf.ratePending;
      if (!current.rate && leaf.rate) current.rate = leaf.rate;
      continue;
    }
    grouped.set(key, { ...leaf, key, scopeType: mode, scopeId, scopeLabel, sources: new Set(leaf.sources) });
  }
  return Array.from(grouped.values()).sort((a, b) => a.label.localeCompare(b.label) || (a.scopeLabel || '').localeCompare(b.scopeLabel || ''));
}

function OrderSummaryPanel({
  assemblies,
  materialItems,
  unitTypeMasters,
  projectScopeItems,
  orders,
  onSaveOrder,
  onDeleteOrder,
  onMergeOrders,
  onSelectRate,
}: {
  assemblies: MaterialAssembly[];
  materialItems: MaterialRateItem[];
  unitTypeMasters: UnitTypeMaster[];
  projectScopeItems: ProjectEstimateScopeItem[];
  orders: MaterialProcurementOrder[];
  onSaveOrder: (order: MaterialProcurementOrder) => Promise<void>;
  onDeleteOrder: (order: MaterialProcurementOrder) => Promise<void>;
  onMergeOrders: (orders: MaterialProcurementOrder[]) => Promise<void>;
  onSelectRate: (materialId: string, optionId: string | undefined, quoteId: string) => Promise<void>;
}) {
  const [orderDialog, setOrderDialog] = useState<{ requirement?: MaterialRequirement; order?: MaterialProcurementOrder; startReceipt?: boolean } | null>(null);
  const [rateDialogRequirement, setRateDialogRequirement] = useState<MaterialRequirement | null>(null);
  const navigate = useNavigate();
  const [expandedOrderHistoryKeys, setExpandedOrderHistoryKeys] = useState<Set<string>>(new Set());
  const [orderSummaryView, setOrderSummaryView] = useState<'material' | 'vendor'>('material');
  const requirementLeaves = useMemo(() => materialProcurementRequirementRows(unitTypeMasters, materialItems, assemblies, projectScopeItems), [unitTypeMasters, materialItems, assemblies, projectScopeItems]);
  const rows = useMemo(() => aggregateMaterialRequirements(requirementLeaves, 'project'), [requirementLeaves]);
  const buildingRows = useMemo(() => aggregateMaterialRequirements(requirementLeaves, 'building'), [requirementLeaves]);
  const orderRequirements = useMemo(() => [...rows, ...buildingRows], [rows, buildingRows]);
  const orderStats = (row: MaterialRequirement) => {
    const matches = orders.filter((order) => order.materialId === row.materialId && (order.quoteOptionId || '') === (row.quoteOptionId || ''));
    const ordered = matches.reduce((sum, order) => sum + num(order.orderQty), 0);
    const received = matches.reduce((sum, order) => sum + (order.receipts || []).reduce((receiptSum, receipt) => receiptSum + num(receipt.qty), 0), 0);
    return { matches, ordered, received, toOrder: Math.max(0, row.qty - ordered), pendingReceipt: Math.max(0, ordered - received) };
  };
  // Quantities are in mixed units (bags, kg, nos), so the summary counts materials, never sums quantities.
  const fullyOrderedCount = rows.filter((row) => orderStats(row).toOrder <= 0).length;
  const fullyReceivedCount = rows.filter((row) => orderStats(row).received >= row.qty).length;
  const pendingRates = rows.filter((row) => row.ratePending).length;
  const vendorGroups = useMemo(() => Array.from(orders.reduce((groups, order) => {
    const vendor = order.vendor?.trim() || 'Vendor not set';
    const group = groups.get(vendor) || [];
    group.push(order);
    groups.set(vendor, group);
    return groups;
  }, new Map<string, MaterialProcurementOrder[]>()).entries()).sort(([left], [right]) => left.localeCompare(right)), [orders]);
  const td: any = { p: '11px 12px', borderTop: `1px solid ${BORDER}`, textAlign: 'right', fontSize: 12.5, whiteSpace: 'nowrap', verticalAlign: 'middle' };
  const formatOrderDate = (date: string) => {
    if (!date) return 'Date not set';
    const value = new Date(`${date}T00:00:00`);
    return Number.isNaN(value.getTime()) ? date : value.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const toggleOrderHistory = (key: string) => {
    setExpandedOrderHistoryKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  return (
    <Stack gap={1.5}>
      <Stack direction="row" justifyContent="flex-end">
        <ToggleButtonGroup exclusive size="small" value={orderSummaryView} onChange={(_, value) => value && setOrderSummaryView(value)} sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1.25 } }}>
          <ToggleButton value="material">By material</ToggleButton>
          <ToggleButton value="vendor">By vendor</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ display: orderSummaryView === 'material' ? 'block' : 'none', border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.25} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ p: 1.5, bgcolor: PANEL2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 900, color: DARK }}>Material order summary</Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>Required quantities are calculated from project building allocations, unit rules, and linked assemblies.</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => setOrderDialog({})}>Add order</Button>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, borderTop: `1px solid ${BORDER}` }}>
          {[
            ['Materials required', String(rows.length), DARK],
            ['Fully ordered', `${fullyOrderedCount} of ${rows.length}`, rows.length && fullyOrderedCount === rows.length ? '#3c6b4b' : GOLD_D],
            ['Fully received', `${fullyReceivedCount} of ${rows.length}`, rows.length && fullyReceivedCount === rows.length ? '#3c6b4b' : GOLD_D],
            ['Rate selection', pendingRates ? `${pendingRates} pending` : 'Ready', pendingRates ? WARN : '#3c6b4b'],
          ].map(([label, value, color]) => (
            <Box key={label} sx={{ p: 1.2, borderRight: { md: `1px solid ${BORDER}` }, '&:last-child': { borderRight: 0 } }}>
              <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.45px' }}>{label}</Typography>
              <Typography sx={{ color, fontWeight: 900 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" sx={{ p: 1.35, bgcolor: '#faf8f5' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 900, color: DARK }}>Project total</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12 }}>Order and receipt quantities include all buildings. Allocate new orders across buildings in the order popup.</Typography>
          </Box>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>{rows.length} material{rows.length === 1 ? '' : 's'}</Typography>
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', minWidth: 1030, borderCollapse: 'collapse' }}>
            <Box component="thead"><Box component="tr">
              <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Material / selected option</Box>
              <Box component="th" sx={headTh}>Required</Box><Box component="th" sx={headTh}>Ordered</Box><Box component="th" sx={headTh}>Yet to order</Box><Box component="th" sx={headTh}>Received</Box><Box component="th" sx={headTh}>Pending receipt</Box><Box component="th" sx={headTh}>Rate</Box><Box component="th" sx={headTh}>Action</Box>
            </Box></Box>
            <Box component="tbody">
              {!rows.length && <Box component="tr"><Box component="td" colSpan={8} sx={{ p: 3, textAlign: 'center', color: MUTED }}>Add building allocations and unit rules to calculate material requirements.</Box></Box>}
              {rows.map((row) => {
                const stats = orderStats(row);
                const historyOpen = expandedOrderHistoryKeys.has(row.key);
                const sortedOrders = [...stats.matches].sort((a, b) => b.orderDate.localeCompare(a.orderDate));
                const legacyMergeGroups = Array.from(sortedOrders
                  .filter((order) => order.scopeType === 'building' && !(order.buildingAllocations || []).length && !(order.receipts || []).length)
                  .reduce((groups, order) => {
                    const signature = [order.orderDate, order.vendor || '', order.brand || '', order.code || '', order.poNumber || '', order.notes || ''].join('__');
                    const group = groups.get(signature) || [];
                    group.push(order);
                    groups.set(signature, group);
                    return groups;
                  }, new Map<string, MaterialProcurementOrder[]>()).values()).filter((group) => group.length > 1);
                const colSpan = 8;
                return <React.Fragment key={row.key}>
                  <Box
                    component="tr"
                    onClick={() => toggleOrderHistory(row.key)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover > td': { bgcolor: 'rgba(157,111,50,0.035)' },
                    }}
                  >
                  <Box component="td" sx={{ ...td, textAlign: 'left' }}><Typography sx={{ fontWeight: 800, color: DARK }}>{row.label}</Typography><Typography sx={{ color: MUTED, fontSize: 11 }}>{Array.from(row.sources).join(', ')}</Typography></Box>
                  <Box component="td" sx={td}>{fmtQty(row.qty)} {row.unit}</Box>
                  <Box component="td" sx={td}>{fmtQty(stats.ordered)} {row.unit}</Box>
                  <Box component="td" sx={{ ...td, color: stats.toOrder ? WARN : '#3c6b4b', fontWeight: 800 }}>{fmtQty(stats.toOrder)} {row.unit}</Box>
                  <Box component="td" sx={td}>{fmtQty(stats.received)} {row.unit}</Box>
                  <Box component="td" sx={{ ...td, color: stats.pendingReceipt ? WARN : MUTED }}>{fmtQty(stats.pendingReceipt)} {row.unit}</Box>
                  <Box component="td" sx={{ ...td, color: row.rate ? GOLD_D : WARN, fontWeight: 800 }}>{row.rate ? `₹${fmtRate(row.rate)}/${row.rateUnit}` : 'Select rate'}</Box>
                  <Box component="td" sx={td} onClick={(event) => event.stopPropagation()}>
                    <Tooltip title={!stats.matches.length && row.ratePending ? 'Select the material rate, then continue to the order.' : ''}>
                      <Stack direction="row" gap={0.25} justifyContent="flex-end">
                        <Box component="span"><ActionIcon title={row.ratePending && !stats.matches.length ? 'Select rate and create order' : 'Create order'} onClick={() => row.ratePending && !stats.matches.length ? setRateDialogRequirement(row) : setOrderDialog({ requirement: row })}><AddOutlinedIcon fontSize="inherit" /></ActionIcon></Box>
                      </Stack>
                    </Tooltip>
                  </Box>
                  </Box>
                  {historyOpen && <Box component="tr">
                    <Box component="td" colSpan={colSpan} sx={{ p: 0, borderTop: `1px solid ${BORDER}`, bgcolor: '#faf8f5' }}>
                      <Box sx={{ px: 1.5, py: 1.1, borderBottom: `1px solid ${BORDER}` }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 900, color: FAINT, textTransform: 'uppercase', letterSpacing: '.45px' }}>Order and receipt history</Typography>
                      </Box>
                      {legacyMergeGroups.map((group) => <Stack key={group.map((order) => order.id).join('_')} direction={{ xs: 'column', sm: 'row' }} gap={0.75} alignItems={{ sm: 'center' }} sx={{ px: 1.5, py: 0.8, bgcolor: GOLD_WASH, borderBottom: `1px solid ${BORDER}` }}>
                        <Typography sx={{ flex: 1, color: GOLD_D, fontSize: 12, fontWeight: 800 }}>{group.length} related building orders were recorded separately.</Typography>
                        <Button size="small" variant="outlined" onClick={() => onMergeOrders(group)} sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, borderColor: GOLD, color: GOLD_D }}>Merge into one order</Button>
                      </Stack>)}
                      <Stack gap={0}>
                        {!sortedOrders.length && <Typography sx={{ px: 1.5, py: 1.2, color: MUTED, fontSize: 12 }}>No orders recorded for this material yet.</Typography>}
                        {sortedOrders.map((order) => {
                          const receivedQty = (order.receipts || []).reduce((sum, receipt) => sum + num(receipt.qty), 0);
                          const pendingQty = Math.max(0, num(order.orderQty) - receivedQty);
                          const sortedReceipts = [...(order.receipts || [])].sort((a, b) => b.date.localeCompare(a.date));
                          const allocationLabel = (order.buildingAllocations || []).length
                            ? order.buildingAllocations!.map((allocation) => `${allocation.buildingLabel || 'Building'}: ${fmtQty(num(allocation.qty))} ${order.unit}`).join(' · ')
                            : order.scopeType === 'building' ? order.scopeLabel || 'Building not set' : 'Project total';
                          return <Box key={order.id} sx={{ borderBottom: `1px solid ${BORDER}`, '&:last-child': { borderBottom: 0 } }}>
                            <Box sx={{ px: 1.5, py: 1.15, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '150px minmax(180px, 1fr) 140px 140px 140px auto' }, gap: 1.25, alignItems: 'center', bgcolor: '#fff' }}>
                              <Box><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Order date</Typography><Typography sx={{ fontWeight: 800, fontSize: 12.5 }}>{formatOrderDate(order.orderDate)}</Typography></Box>
                              <Box><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Vendor / PO</Typography><Typography sx={{ fontWeight: 800, fontSize: 12.5 }}>{order.vendor || 'Vendor not set'}{order.poNumber ? ` · ${order.poNumber}` : ''}</Typography><Typography sx={{ color: MUTED, fontSize: 11 }}>{allocationLabel}{[order.brand, order.code].filter(Boolean).length > 0 ? ` · ${[order.brand, order.code].filter(Boolean).join(' · ')}` : ''}</Typography></Box>
                              <Box><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Ordered</Typography><Typography sx={{ fontWeight: 800, fontSize: 12.5 }}>{fmtQty(num(order.orderQty))} {order.unit}</Typography></Box>
                              <Box><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Received</Typography><Typography sx={{ fontWeight: 800, fontSize: 12.5, color: '#3c6b4b' }}>{fmtQty(receivedQty)} {order.unit}</Typography></Box>
                              <Box><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Pending</Typography><Typography sx={{ fontWeight: 800, fontSize: 12.5, color: pendingQty ? WARN : MUTED }}>{fmtQty(pendingQty)} {order.unit}</Typography></Box>
                              <Stack direction="row" gap={0.25} justifyContent="flex-end">
                                <ActionIcon title="Manage order" onClick={() => setOrderDialog({ requirement: row, order })}><EditOutlinedIcon fontSize="inherit" /></ActionIcon>
                                <ActionIcon title="Delete order" danger onClick={() => onDeleteOrder(order)}><DeleteOutlineIcon fontSize="inherit" /></ActionIcon>
                              </Stack>
                            </Box>
                            {sortedReceipts.length > 0 ? <Box sx={{ pl: { xs: 1.5, md: 4 }, pr: 1.5, py: 0.75, bgcolor: '#f5faf6', borderLeft: '3px solid #79a785' }}>
                              {sortedReceipts.map((receipt) => <Stack key={receipt.id} direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0.35, sm: 1.5 }} alignItems={{ sm: 'center' }} sx={{ py: 0.55, fontSize: 12 }}>
                                <Typography sx={{ minWidth: 52, color: '#3c6b4b', fontSize: 9.5, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.35px' }}>{receipt.documentType === 'bill' ? 'Bill' : 'Challan'}</Typography>
                                <Typography sx={{ minWidth: 112, fontWeight: 800, color: DARK, fontSize: 11.5 }}>{formatOrderDate(receipt.date)}</Typography>
                                <Typography sx={{ minWidth: 138, fontWeight: 800, color: '#3c6b4b', fontSize: 11.5 }}>{fmtQty(num(receipt.qty))} {order.unit} received</Typography>
                                <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{receipt.documentType === 'bill' ? 'Bill' : 'Challan'}: {receipt.reference || 'Not added'}{receipt.billNumber && receipt.documentType !== 'bill' ? ` · Bill: ${receipt.billNumber}` : ''}</Typography>
                                {receipt.notes && <Typography sx={{ color: FAINT, fontSize: 11.5 }}>{receipt.notes}</Typography>}
                              </Stack>)}
                            </Box> : <Typography sx={{ px: 1.5, py: 0.9, color: MUTED, fontSize: 12 }}>No receipts recorded for this order.</Typography>}
                          </Box>;
                        })}
                      </Stack>
                    </Box>
                  </Box>}
                </React.Fragment>;
              })}
            </Box>
          </Box>
        </Box>
      </Box>
      {orderSummaryView === 'vendor' && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1.2, bgcolor: '#faf8f5', borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontWeight: 900, color: DARK }}>Orders by vendor</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>Receive one delivery or record one bill across multiple open orders from the same vendor.</Typography>
        </Box>
        {!vendorGroups.length && <Typography sx={{ p: 2, color: MUTED, fontSize: 13 }}>No material orders recorded yet.</Typography>}
        {vendorGroups.map(([vendor, vendorOrders], index) => {
          const orderedQty = vendorOrders.reduce((sum, order) => sum + num(order.orderQty), 0);
          const receivedQty = vendorOrders.reduce((sum, order) => sum + (order.receipts || []).reduce((receiptSum, receipt) => receiptSum + num(receipt.qty), 0), 0);
          const pendingOrders = vendorOrders.filter((order) => num(order.orderQty) > (order.receipts || []).reduce((sum, receipt) => sum + num(receipt.qty), 0));
          const poCount = new Set(vendorOrders.map((order) => order.poNumber || 'No PO reference')).size;
          return <Stack key={vendor} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={1.25} sx={{ px: 1.5, py: 1.25, borderTop: index ? `1px solid ${BORDER}` : 0 }}>
            <Box sx={{ flex: 1 }}><Typography sx={{ color: DARK, fontWeight: 900 }}>{vendor}</Typography><Typography sx={{ color: MUTED, fontSize: 12 }}>{vendorOrders.length} order line{vendorOrders.length === 1 ? '' : 's'} · {poCount} PO reference{poCount === 1 ? '' : 's'}</Typography></Box>
            <Stack direction="row" gap={2.25} sx={{ '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>
              <Box><Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Ordered</Typography><Typography sx={{ fontWeight: 900 }}>{fmtQty(orderedQty)}</Typography></Box>
              <Box><Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Received</Typography><Typography sx={{ fontWeight: 900, color: '#3c6b4b' }}>{fmtQty(receivedQty)}</Typography></Box>
              <Box><Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Open lines</Typography><Typography sx={{ fontWeight: 900, color: pendingOrders.length ? WARN : MUTED }}>{pendingOrders.length}</Typography></Box>
            </Stack>
            <Button variant="contained" startIcon={<ReceiptLongOutlinedIcon />} disabled={!vendorOrders.length} onClick={() => navigate('/office/material-orders')}>Open in Orders</Button>
          </Stack>;
        })}
      </Box>}
      {orderDialog && <MaterialOrderDialog requirement={orderDialog.requirement} order={orderDialog.order} startReceipt={orderDialog.startReceipt} requirements={orderRequirements} orders={orders} materialItems={materialItems} onClose={() => setOrderDialog(null)} onSave={async (order) => { await onSaveOrder(order); setOrderDialog(null); }} />}
      {rateDialogRequirement && <MaterialOrderRateDialog material={materialItems.find((item) => item.id === rateDialogRequirement.materialId) || null} optionId={rateDialogRequirement.quoteOptionId} onClose={() => setRateDialogRequirement(null)} onSelect={async (quoteId) => { const requirement = rateDialogRequirement; await onSelectRate(requirement.materialId, requirement.quoteOptionId, quoteId); setRateDialogRequirement(null); setOrderDialog({ requirement }); }} />}
    </Stack>
  );
}

function MaterialOrderRateDialog({ material, optionId, onClose, onSelect }: {
  material: MaterialRateItem | null;
  optionId?: string;
  onClose: () => void;
  onSelect: (quoteId: string) => Promise<void>;
}) {
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [saving, setSaving] = useState(false);
  const quoteRows = (material?.quotes || []).map((quote) => {
    const option = selectedQuoteOption(quote, optionId);
    const rate = option ? quoteOptionLandedRate(quote, option) : quoteLandedRate(material!, quote);
    return { quote, option, rate };
  }).filter((row) => row.rate > 0);
  const selected = quoteRows.find((row) => row.quote.id === selectedQuoteId) || null;
  const optionName = selected?.option ? quoteOptionLabel(selected.option) : material?.materialName || 'Material';
  const confirm = async () => {
    if (!selected) return;
    setSaving(true);
    try { await onSelect(selected.quote.id); } finally { setSaving(false); }
  };
  return <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: POPUP_PAPER_SX }}>
    <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>Select material rate</DialogTitle>
    <DialogContent>
      <Stack gap={1.15} sx={{ mt: 1 }}>
        <Box sx={{ p: 1.15, bgcolor: PANEL2, borderRadius: 1.5 }}>
          <Typography sx={{ fontWeight: 900, color: DARK }}>{material?.materialName || 'Material'}{optionId ? ` · ${optionName}` : ''}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>Choose the vendor quote to use for this order. It will also become the selected rate in Material Rates.</Typography>
        </Box>
        {!quoteRows.length && <Typography sx={{ color: WARN, fontWeight: 700, fontSize: 13 }}>No priced vendor quote is available for this material option. Add a quote in Material Rates first.</Typography>}
        {quoteRows.map(({ quote, option, rate }) => {
          const isSelected = quote.id === selectedQuoteId;
          return <Box key={quote.id} onClick={() => setSelectedQuoteId(quote.id)} sx={{ p: 1.2, border: `1px solid ${isSelected ? GOLD : BORDER}`, borderRadius: 1.5, cursor: 'pointer', bgcolor: isSelected ? GOLD_WASH : '#fff' }}>
            <Stack direction="row" gap={1} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, color: DARK }}>{quote.vendor || 'Unnamed vendor'}</Typography>
                <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{[option?.brand || quote.brand, option?.code || quote.materialCode, quoteReferenceLabel(quote)].filter(Boolean).join(' · ') || 'No brand or reference'}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontWeight: 900, color: GOLD_D }}>₹{fmtRate(rate)}/{materialQuoteRateUnit(option, quote.unit || material?.unit || 'unit')}</Typography>
                <Typography sx={{ color: MUTED, fontSize: 10.5 }}>{quote.gstMode === 'inclusive' ? 'Incl. GST' : quote.gstMode === 'exclusive' ? 'Excl. GST' : 'GST not set'}</Typography>
              </Box>
              {isSelected && <CheckOutlinedIcon sx={{ color: GOLD_D, fontSize: 19 }} />}
            </Stack>
          </Box>;
        })}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" disabled={!selected || saving} onClick={confirm}>{saving ? 'Selecting…' : 'Select and continue'}</Button></DialogActions>
  </Dialog>;
}

const assemblyMappingUnit = (item: MaterialRateItem, assemblyType = '', variety = '') =>
  (item.assemblyVarietyMap || []).find((entry) =>
    (!assemblyType || entry.assemblyType.trim().toLowerCase() === assemblyType.trim().toLowerCase())
    && (!variety || (entry.varieties || []).some((value) => value.trim().toLowerCase() === variety.trim().toLowerCase()))
  )?.unit || '';

/** Every orderable material and variety, with the quantity the estimate requires and its default rate. */
export function materialOrderCatalog(materialItems: MaterialRateItem[], requirements: MaterialRequirement[]): MaterialRequirement[] {
  return materialItems
    .filter((item) => item.group !== 'labour')
    .flatMap((item) => {
      const selectableOptions = materialSelectableQuoteOptions(item);
      const options = selectableOptions.length ? selectableOptions : [null];
      return options.map((option) => {
        const quote = selectedMaterialQuoteForOption(item, option?.id);
        const matchedRequirement = requirements.find((candidate) =>
          candidate.materialId === item.id && (candidate.quoteOptionId || '') === (option?.id || '') && candidate.scopeType === 'project');
        const optionLabel = option ? quoteOptionLabel(option) : '';
        const mappedUnit = assemblyMappingUnit(item, option?.group || '', option?.variety || option?.name || '');
        const sharedAccessory = item.materialName.trim().toLowerCase().startsWith('door acc');
        // A material can list varieties before any vendor quote is selected; then there is simply no rate yet.
        const landedRate = quote ? (option ? quoteOptionLandedRate(quote, option) : quoteLandedRate(item, quote)) : 0;
        return {
          key: `catalog_${item.id}_${option?.id || 'base'}`,
          materialId: item.id,
          quoteOptionId: option?.id,
          label: sharedAccessory ? (optionLabel || item.materialName) : optionLabel ? `${item.materialName} · ${optionLabel}` : item.materialName,
          unit: mappedUnit || option?.unit || item.unit || 'Nos',
          rateUnit: mappedUnit || option?.unit || quote?.unit || item.unit || 'Nos',
          qty: num(matchedRequirement?.qty),
          scopeType: 'project' as const,
          sources: new Set<string>(),
          rate: landedRate,
          ratePending: !landedRate,
        } satisfies MaterialRequirement;
      });
    });
}

export function MaterialOrderDialog({ requirement, order, startReceipt = false, requirements, orders, materialItems, onClose, onSave }: {
  requirement?: MaterialRequirement;
  order?: MaterialProcurementOrder;
  startReceipt?: boolean;
  requirements: MaterialRequirement[];
  orders: MaterialProcurementOrder[];
  materialItems: MaterialRateItem[];
  onClose: () => void;
  onSave: (order: MaterialProcurementOrder) => Promise<void>;
}) {
  const materialOptions: MaterialRequirement[] = materialOrderCatalog(materialItems, requirements);
  const [selected, setSelected] = useState<MaterialRequirement | null>(requirement || materialOptions.find((item) => item.materialId === order?.materialId && (item.quoteOptionId || '') === (order?.quoteOptionId || '')) || materialOptions[0] || null);
  const [orderQty, setOrderQty] = useState<number>(num(order?.orderQty) || requirement?.qty || 0);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderScope, setOrderScope] = useState<'project' | 'building'>(order?.scopeType === 'building' ? 'building' : 'project');
  const [buildingOrderQtys, setBuildingOrderQtys] = useState<Record<string, number>>(() =>
    order?.scopeType === 'building' && order.scopeId ? { [order.scopeId]: num(order.orderQty) } : {}
  );
  const [orderDate, setOrderDate] = useState(order?.orderDate || new Date().toISOString().slice(0, 10));
  const [poNumber, setPoNumber] = useState(order?.poNumber || '');
  const [notes, setNotes] = useState(order?.notes || '');
  const [receipts, setReceipts] = useState<MaterialOrderReceipt[]>(() => {
    const existing = order?.receipts || [];
    return startReceipt ? [...existing, { id: `receipt_${Date.now()}`, date: new Date().toISOString().slice(0, 10), qty: 0, reference: '', notes: '' }] : existing;
  });
  const [saving, setSaving] = useState(false);
  const buildingRequirements = selected
    ? Array.from(requirements
      .filter((item) => item.materialId === selected.materialId && (item.quoteOptionId || '') === (selected.quoteOptionId || '') && item.scopeType === 'building')
      .reduce((grouped, item) => {
        const buildingKey = item.scopeLabel?.trim().toLowerCase() || item.scopeId || item.key;
        const current = grouped.get(buildingKey);
        grouped.set(buildingKey, current ? { ...current, qty: num(current.qty) + num(item.qty) } : { ...item, key: buildingKey });
        return grouped;
      }, new Map<string, MaterialRequirement>())
      .values())
    : [];
  const canOrderByBuilding = buildingRequirements.length > 0;
  const isBuildingWiseOrder = !order && orderScope === 'building' && canOrderByBuilding;
  const buildingOrderedQty = (building: MaterialRequirement) => orders
    .filter((item) => item.materialId === building.materialId && (item.quoteOptionId || '') === (building.quoteOptionId || ''))
    .reduce((sum, item) => {
      const allocationQty = (item.buildingAllocations || [])
        .filter((allocation) => allocation.buildingId === building.scopeId)
        .reduce((allocationSum, allocation) => allocationSum + num(allocation.qty), 0);
      if (allocationQty > 0) return sum + allocationQty;
      return item.scopeType === 'building' && item.scopeId === building.scopeId ? sum + num(item.orderQty) : sum;
    }, 0);
  const selectRequirement = (next: MaterialRequirement | null) => {
    setSelected(next);
    setOrderQty(next?.qty || 0);
    if (!order) {
      setOrderScope('project');
      setBuildingOrderQtys({});
      setOrderTotal(0);
    }
  };
  const selectedMaterial = materialItems.find((item) => item.id === selected?.materialId);
  const selectedQuote = selectedMaterial ? selectedMaterialQuoteForOption(selectedMaterial, selected?.quoteOptionId) : null;
  const selectedOption = selectedQuote ? selectedQuoteOption(selectedQuote, selected?.quoteOptionId) : null;
  const save = async () => {
    if (!selected || !orderDate || (!isBuildingWiseOrder && num(orderQty) <= 0)) return;
    setSaving(true);
    try {
      const createOrder = (target: MaterialRequirement, qty: number, existing?: MaterialProcurementOrder, buildingAllocations?: MaterialOrderBuildingAllocation[]) => onSave({
        id: existing?.id || '', materialId: target.materialId, quoteOptionId: target.quoteOptionId, materialLabel: target.label, unit: target.unit,
        vendor: existing?.vendor || selectedQuote?.vendor || '', brand: existing?.brand || selectedOption?.brand || selectedQuote?.brand || selectedMaterial?.brand || '', code: existing?.code || selectedOption?.code || selectedQuote?.materialCode || '',
        ...(() => {
          const quoteId = existing?.quoteId || selectedQuote?.id;
          const rateSnapshot = existing?.rateSnapshot ?? (selectedQuote ? (selectedOption ? quoteOptionLandedRate(selectedQuote, selectedOption) : quoteLandedRate(selectedMaterial as MaterialRateItem, selectedQuote)) : null);
          return quoteId && rateSnapshot !== null ? {
            quoteId,
            rateSnapshot,
            rateUnit: existing?.rateUnit || selectedOption?.unit || selectedQuote?.unit || target.unit,
            rateEffectiveFrom: existing?.rateEffectiveFrom || quoteEffectiveFrom(selectedQuote as MaterialQuote),
          } : {};
        })(),
        scopeType: existing?.scopeType || (buildingAllocations?.length ? 'building' : 'project'), scopeId: existing?.scopeId || target.scopeId, scopeLabel: existing?.scopeLabel || target.scopeLabel,
        orderQty: num(qty), orderDate, poNumber, notes, buildingAllocations: buildingAllocations || existing?.buildingAllocations || [], receipts: existing ? receipts : [],
      });
      if (isBuildingWiseOrder) {
        const buildingAllocations = buildingRequirements
          .map((building) => ({ buildingId: building.scopeId || '', buildingLabel: building.scopeLabel || 'Building', qty: num(buildingOrderQtys[building.scopeId || '']) }))
          .filter((allocation) => allocation.buildingId && allocation.qty > 0);
        if (!buildingAllocations.length) return;
        await createOrder(selected, buildingAllocations.reduce((sum, allocation) => sum + allocation.qty, 0), undefined, buildingAllocations);
      } else {
        await createOrder(selected, orderQty, order);
      }
    } finally { setSaving(false); }
  };
  const hasBuildingOrderQty = buildingRequirements.some((building) => num(buildingOrderQtys[building.scopeId || '']) > 0);
  const allocatedBuildingQty = buildingRequirements.reduce((sum, building) => sum + num(buildingOrderQtys[building.scopeId || '']), 0);
  const remainingToAllocate = Math.max(0, num(orderTotal) - allocatedBuildingQty);
  const overAllocatedQty = Math.max(0, allocatedBuildingQty - num(orderTotal));
  const applyRemainingToBuilding = (building: MaterialRequirement) => {
    const buildingKey = building.scopeId || '';
    const ordered = buildingOrderedQty(building);
    const buildingBalance = Math.max(0, num(building.qty) - ordered);
    const currentQty = num(buildingOrderQtys[buildingKey]);
    const nextQty = Math.min(buildingBalance, currentQty + remainingToAllocate);
    setBuildingOrderQtys((previous) => ({ ...previous, [buildingKey]: nextQty }));
  };
  return <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: POPUP_PAPER_SX }}>
    <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>{order ? 'Manage material order' : 'Add material order'}</DialogTitle>
    <DialogContent><Stack gap={1.4} sx={{ mt: 1 }}>
      {requirement || order ? <Box sx={{ px: 1.3, py: 1.05, bgcolor: PANEL2, border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
        <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Material / selected option</Typography>
        <Typography sx={{ color: DARK, fontSize: 17, fontWeight: 800 }}>{selected?.label || order?.materialLabel || 'Material'}</Typography>
      </Box> : <Autocomplete options={materialOptions} value={selected} onChange={(_, value) => selectRequirement(value)} getOptionLabel={(item) => item.label} isOptionEqualToValue={(a, b) => a.key === b.key} renderInput={(params) => <TextField {...params} label="Material / variety" helperText="All materials from the Material Rate Library are available here." />} />}
      {!order && selected && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.1 }}>
        <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', mb: 0.7 }}>Order scope</Typography>
        <ToggleButtonGroup exclusive size="small" value={orderScope} onChange={(_, value) => value && setOrderScope(value)} sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1.3 } }}>
          <ToggleButton value="project">Project total</ToggleButton>
          <ToggleButton value="building" disabled={!canOrderByBuilding}>Building-wise</ToggleButton>
        </ToggleButtonGroup>
        {!canOrderByBuilding && <Typography sx={{ color: MUTED, fontSize: 11.5, mt: 0.65 }}>No building-wise estimate quantity exists for this material. You can still place a project-total order.</Typography>}
      </Box>}
      {isBuildingWiseOrder ? <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, sm: 0 }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ px: 1.35, py: 1, bgcolor: '#faf8f5' }}>
          <Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 900, color: DARK }}>Building-wise order quantity</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>Enter a total, then use the remaining amount against the required buildings.</Typography></Box>
          <TextField label={`Order total (${selected?.unit || 'unit'})`} size="small" type="number" value={orderTotal || ''} onChange={(event) => setOrderTotal(selected?.unit?.trim().toLowerCase() === 'nos' ? Math.floor(num(event.target.value)) : num(event.target.value))} inputProps={{ min: 0, step: selected?.unit?.trim().toLowerCase() === 'nos' ? 1 : 'any' }} sx={{ width: { xs: '100%', sm: 185 } }} />
        </Stack>
        {num(orderTotal) > 0 && <Stack direction="row" justifyContent="space-between" sx={{ px: 1.35, py: 0.7, bgcolor: overAllocatedQty > 0 ? '#fff4e5' : '#fffaf0', borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ color: overAllocatedQty > 0 ? WARN : GOLD_D, fontSize: 12, fontWeight: 800 }}>{overAllocatedQty > 0 ? `Over-allocated: ${fmtQty(overAllocatedQty)} ${selected?.unit}` : `Remaining to allocate: ${fmtQty(remainingToAllocate)} ${selected?.unit}`}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>Allocated: {fmtQty(allocatedBuildingQty)} {selected?.unit}</Typography>
        </Stack>}
        <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: '100px minmax(90px,1fr) minmax(90px,1fr) minmax(100px,1fr) minmax(195px,1.45fr)', gap: 1, alignItems: 'center', px: 1.35, py: 0.8, bgcolor: PANEL2, borderTop: `1px solid ${BORDER}` }}>
          {['Building', 'Required', 'Ordered', 'Balance', 'Order now'].map((label, index) => <Typography key={label} sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.35px', textAlign: index === 0 ? 'left' : 'right' }}>{label}</Typography>)}
        </Box>
        {buildingRequirements.map((building) => {
          const ordered = buildingOrderedQty(building);
          const remaining = Math.max(0, num(building.qty) - ordered);
          const buildingKey = building.scopeId || '';
          const qtyToApply = Math.min(Math.max(0, remaining - num(buildingOrderQtys[buildingKey])), remainingToAllocate);
          return <Box key={building.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '100px minmax(90px,1fr) minmax(90px,1fr) minmax(100px,1fr) minmax(195px,1.45fr)' }, gap: { xs: 0.65, sm: 1 }, alignItems: 'center', px: 1.35, py: { xs: 1, sm: 0.75 }, borderTop: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontWeight: 800, color: DARK }}>{building.scopeLabel || 'Building'}</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12, textAlign: { sm: 'right' } }}><Box component="span" sx={{ display: { sm: 'none' }, color: FAINT }}>Required: </Box>{fmtQty(building.qty)} {building.unit}</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12, textAlign: { sm: 'right' } }}><Box component="span" sx={{ display: { sm: 'none' }, color: FAINT }}>Ordered: </Box>{fmtQty(ordered)} {building.unit}</Typography>
            <Typography sx={{ color: remaining ? WARN : '#3c6b4b', fontSize: 12, fontWeight: 800, textAlign: { sm: 'right' } }}><Box component="span" sx={{ display: { sm: 'none' }, color: FAINT }}>Balance: </Box>{fmtQty(remaining)} {building.unit}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={0.6} sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0, width: '100%' }}>
              <TextField placeholder="Enter qty" size="small" type="number" value={buildingOrderQtys[buildingKey] || ''} onChange={(event) => setBuildingOrderQtys((previous) => ({ ...previous, [buildingKey]: Math.min(remaining, building.unit.trim().toLowerCase() === 'nos' ? Math.floor(num(event.target.value)) : num(event.target.value)) }))} inputProps={{ min: 0, step: building.unit.trim().toLowerCase() === 'nos' ? 1 : 'any', 'aria-label': `Order quantity for ${building.scopeLabel || 'building'}` }} sx={{ flex: '1 1 0', minWidth: 0, width: 0 }} />
              <Button size="small" variant="outlined" title={qtyToApply > 0 ? `Apply ${fmtQty(qtyToApply)} ${building.unit}` : 'Applied'} disabled={qtyToApply <= 0} onClick={() => applyRemainingToBuilding(building)} sx={{ minWidth: 64, borderColor: BORDER, color: GOLD_D, whiteSpace: 'nowrap', flexShrink: 0 }}>{qtyToApply > 0 ? 'Apply' : 'Applied'}</Button>
            </Stack>
          </Box>;
        })}
      </Box> : selected && <Box sx={{ p: 1.15, bgcolor: PANEL2, borderRadius: 1.5 }}><Typography sx={{ fontWeight: 800, color: DARK }}>{selected.scopeType === 'building' ? selected.scopeLabel : 'Project total'}</Typography><Typography sx={{ fontSize: 12, color: MUTED }}>Calculated requirement: {fmtQty(selected.qty)} {selected.unit}{selectedQuote?.vendor ? ` · ${selectedQuote.vendor}` : ''}{selectedOption?.brand || selectedQuote?.brand ? ` · ${selectedOption?.brand || selectedQuote?.brand}` : ''}</Typography></Box>}
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>{!isBuildingWiseOrder && <TextField label={`Order qty${selected ? ` (${selected.unit})` : ''}`} value={orderQty || ''} onChange={(event) => setOrderQty(num(event.target.value))} type="number" fullWidth />}<TextField label="Order date" value={orderDate} onChange={(event) => setOrderDate(event.target.value)} type="date" InputLabelProps={{ shrink: true }} fullWidth /><TextField label="PO / reference no." value={poNumber} onChange={(event) => setPoNumber(event.target.value)} fullWidth /></Stack>
      {selected && !selected.rate && <Alert severity="warning" sx={{ py: 0 }}>No rate is selected for this material yet. Select a vendor quote in the Rates tab, then place the order.</Alert>}
      <TextField label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} />
      {!isBuildingWiseOrder && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" sx={{ p: 1, bgcolor: '#faf8f5' }}>
          <Typography sx={{ flex: 1, fontWeight: 800, fontSize: 13 }}>{order ? 'Receipts' : 'Initial receipts'}</Typography>
          <Button size="small" onClick={() => setReceipts((prev) => [...prev, { id: `receipt_${Date.now()}`, date: new Date().toISOString().slice(0, 10), qty: 0, reference: '', notes: '' }])}>Add receipt</Button>
        </Stack>
        {receipts.map((receipt) => (
          <Stack key={receipt.id} direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ p: 1, borderTop: `1px solid ${BORDER}` }}>
            <TextField label="Receipt date" type="date" value={receipt.date} InputLabelProps={{ shrink: true }} onChange={(event) => setReceipts((prev) => prev.map((item) => item.id === receipt.id ? { ...item, date: event.target.value } : item))} />
            <TextField label={`Qty (${selected?.unit || 'unit'})`} type="number" value={receipt.qty || ''} onChange={(event) => setReceipts((prev) => prev.map((item) => item.id === receipt.id ? { ...item, qty: num(event.target.value) } : item))} />
            <TextField label="Challan / bill no." value={receipt.reference || ''} onChange={(event) => setReceipts((prev) => prev.map((item) => item.id === receipt.id ? { ...item, reference: event.target.value } : item))} sx={{ flex: 1 }} />
            <ActionIcon title="Remove receipt" danger onClick={() => setReceipts((prev) => prev.filter((item) => item.id !== receipt.id))}><DeleteOutlineIcon fontSize="inherit" /></ActionIcon>
          </Stack>
        ))}
      </Box>}
    </Stack></DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" onClick={save} disabled={!selected || !selected.rate || !orderDate || (isBuildingWiseOrder ? (!hasBuildingOrderQty || (num(orderTotal) > 0 && (remainingToAllocate > 0 || overAllocatedQty > 0))) : !orderQty) || saving}>{saving ? 'Saving…' : 'Save order'}</Button></DialogActions>
  </Dialog>;
}

function blankUnitTypeMaster(id = `unit_${Date.now()}`, order = 1, label = '', unit = 'Flat'): UnitTypeMaster {
  return {
    id,
    label,
    unit,
    components: [],
    buildingAllocations: [],
    order,
  };
}

function componentFromWorkItem(workItem: ItemWorkMaster, id = `component_${Date.now()}`): QuantityProfileComponent {
  const hasSecondary = !!(workItem.secondaryUnitRequired ?? workItem.trackArea);
  return {
    id,
    workItemId: workItem.id,
    label: workItem.name,
    group: workItem.category || 'Unit works',
    variety: '',
    qtyPerUnit: 1,
    unit: workItem.unit || 'Nos',
    areaPerUnit: hasSecondary ? num(workItem.secondaryMultiplier) : 0,
    areaUnit: hasSecondary ? (workItem.secondaryUnit || workItem.areaUnit || 'Sft') : 'Sft',
    secondaryOverride: false,
  };
}

function componentFromRuleInput(
  workItem: ItemWorkMaster,
  qtyPerUnit: number,
  secondaryOverride: boolean,
  overrideSize: number,
  id = `component_${Date.now()}`,
): QuantityProfileComponent {
  const base = componentFromWorkItem(workItem, id);
  return {
    ...base,
    qtyPerUnit: num(qtyPerUnit),
    secondaryOverride,
    areaPerUnit: secondaryOverride ? num(overrideSize) : base.areaPerUnit,
  };
}

function resolveComponentWorkItem(component: QuantityProfileComponent, workItems: ItemWorkMaster[]) {
  return workItems.find((item) => item.id === component.workItemId)
    || workItems.find((item) => item.name.trim().toLowerCase() === component.label.trim().toLowerCase())
    || null;
}

function componentRateSetup(
  component: QuantityProfileComponent,
  workItems: ItemWorkMaster[],
  rates: WorkRateSetup[],
): WorkRateSetup {
  if (component.sourceId && component.sourceType) {
    return {
      workItemId: component.workItemId || `source_${component.sourceId}`,
      sourceType: component.sourceType === 'assembly' ? 'assembly' : 'direct',
      sourceId: component.sourceId,
      quoteOptionId: component.quoteOptionId || '',
      basis: 'primary',
    };
  }
  const workItem = resolveComponentWorkItem(component, workItems);
  return workItem
    ? rates.find((rate) => rate.workItemId === workItem.id) || { workItemId: workItem.id, sourceType: 'none', basis: 'primary' }
    : { workItemId: component.workItemId || '', sourceType: 'none', basis: 'primary' };
}

function componentSecondaryText(component: QuantityProfileComponent, workItem: ItemWorkMaster | null) {
  const primaryUnit = workItem?.unit || component.unit || 'unit';
  const secondaryActive = workItem ? !!(workItem.secondaryUnitRequired ?? workItem.trackArea) : num(component.areaPerUnit) > 0;
  if (!secondaryActive) return '-';
  const multiplier = num(component.areaPerUnit || workItem?.secondaryMultiplier);
  const unit = component.areaUnit || workItem?.secondaryUnit || workItem?.areaUnit || 'Sft';
  return multiplier > 0 ? `1 ${primaryUnit} = ${fmtIN(multiplier)} ${unit}` : `Secondary: ${unit}`;
}

function unitTypeEstimateSummary(
  unit: UnitTypeMaster,
  workItems: ItemWorkMaster[],
  rates: WorkRateSetup[],
  materialItems: MaterialRateItem[],
  assemblies: MaterialAssembly[],
  costBasis: 'selected' | 'tentative' = 'selected',
): UnitTypeEstimateSummary {
  return (unit.components || []).reduce<UnitTypeEstimateSummary>((acc, component) => {
    const workItem = resolveComponentWorkItem(component, workItems);
    if (!component.sourceId && !workItem) return { ...acc, pending: acc.pending + 1, rules: acc.rules + 1 };
    if (workItem?.procurementMode === 'oneTime') return acc;
    const setup = componentRateSetup(component, workItems, rates);
    const resolved = applyLabourLeadRate(
      resolveUnitComponentCostingRate(setup, component, materialItems, assemblies, costBasis),
      labourLeadMultiplier(component, setup, materialItems, unit.buildingAllocations || []),
    );
    const qty = setup.basis === 'secondary'
      ? num(component.qtyPerUnit) * num(component.areaPerUnit || workItem?.secondaryMultiplier)
      : num(component.qtyPerUnit);
    if (resolved.missing || !resolved.rate) {
      return {
        total: acc.total + (resolved.rate > 0 ? qty * resolved.rate : 0),
        pending: acc.pending + (resolved.pendingCount || 1),
        rules: acc.rules + 1,
      };
    }
    return {
      total: acc.total + (qty * resolved.rate),
      pending: acc.pending,
      rules: acc.rules + 1,
    };
  }, { total: 0, pending: 0, rules: 0 });
}

type ProjectCostLine = {
  id: string;
  workItemId: string;
  label: string;
  category: string;
  kind: 'Material' | 'Labour' | 'Assembly' | 'Manual';
  unit: string;
  qty: number;
  rate: number;
  amount: number;
  pending: number;
  rateStatus: 'selected' | 'tentative' | 'pending';
  source: string;
  unitTypeLabel?: string;
  buildingLabel?: string;
  oneTime?: boolean;
};

function ProjectEstimateScopeEditor({
  items,
  sections,
  workItems,
  usageIds,
  rates,
  materialItems,
  assemblies,
  unitTypes,
  buildings,
  onSave,
  onSaveSections,
  onSaveMaterialProcurementOrder,
  onEnableWorkItem = () => {},
  onOpenProjectCost,
  onCreateWorkItem = async () => {},
  onSaveUnitTypes,
}: {
  items: ProjectEstimateScopeItem[];
  sections: ProjectEstimateScopeSection[];
  workItems: ItemWorkMaster[];
  usageIds: Set<string>;
  rates: WorkRateSetup[];
  materialItems: MaterialRateItem[];
  assemblies: MaterialAssembly[];
  unitTypes: UnitTypeMaster[];
  buildings: string[];
  onSave: (items: ProjectEstimateScopeItem[]) => void;
  onSaveSections: (sections: ProjectEstimateScopeSection[]) => void;
  onSaveMaterialProcurementOrder: (order: MaterialProcurementOrder) => Promise<void>;
  onEnableWorkItem?: (itemId: string, use: boolean) => void;
  onOpenProjectCost: () => void;
  onCreateWorkItem?: (item: ItemWorkMaster, rate: WorkRateSetup) => Promise<void>;
  onSaveUnitTypes: (unitTypes: UnitTypeMaster[]) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<ProjectEstimateScopeItem | null>(null);
  const [addingScopeSubsection, setAddingScopeSubsection] = useState(false);
  const [scopeStructureDialog, setScopeStructureDialog] = useState<{ mode: 'group' | 'subgroup'; group?: string } | null>(null);
  const [scopeStructureName, setScopeStructureName] = useState('');
  const [scopeRuleTarget, setScopeRuleTarget] = useState<{ group: string; subgroup: string; line?: ProjectEstimateScopeItem } | null>(null);
  const [addChoiceDialogOpen, setAddChoiceDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandedUnitAssemblyLines, setExpandedUnitAssemblyLines] = useState<Set<string>>(new Set());
  const [entrySource, setEntrySource] = useState<'material' | 'labour' | 'assembly' | 'unitType' | null>(null);
  const [materialGroupFilter, setMaterialGroupFilter] = useState('');
  const [costBasis, setCostBasis] = useState<'selected' | 'tentative'>('tentative');
  const [quickWorkDialogOpen, setQuickWorkDialogOpen] = useState(false);
  const [quickWorkName, setQuickWorkName] = useState('');
  const [quickWorkUnit, setQuickWorkUnit] = useState('Nos');
  const [newUnitTypeLabel, setNewUnitTypeLabel] = useState('');
  const [newUnitTypeUnit, setNewUnitTypeUnit] = useState('Flat');
  const [editingUnitType, setEditingUnitType] = useState<UnitTypeMaster | null>(null);
  const [sectionDialogUnitId, setSectionDialogUnitId] = useState<string | null>(null);
  const [newUnitSection, setNewUnitSection] = useState('');
  const [ruleDialogTarget, setRuleDialogTarget] = useState<{ unitId: string; section: string; component?: QuantityProfileComponent } | null>(null);
  const [quickSourceId, setQuickSourceId] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);
  const [subsectionOrderDialog, setSubsectionOrderDialog] = useState<{ code: string; label: string; candidates: ProjectEstimateOrderCandidate[] } | null>(null);
  const [sessionWorkItems, setSessionWorkItems] = useState<ItemWorkMaster[]>([]);
  const [sessionRates, setSessionRates] = useState<WorkRateSetup[]>([]);
  const availableWorkItems = useMemo(() => [
    ...workItems,
    ...sessionWorkItems.filter((item) => !workItems.some((existing) => existing.id === item.id)),
  ], [workItems, sessionWorkItems]);
  const availableRates = useMemo(() => [
    ...rates.filter((rate) => !sessionRates.some((sessionRate) => sessionRate.workItemId === rate.workItemId)),
    ...sessionRates,
  ], [rates, sessionRates]);
  const rateByWorkItem = useMemo(() => new Map(availableRates.map((rate) => [rate.workItemId, rate])), [availableRates]);
  const sourceLabel = useCallback((item: ItemWorkMaster) => {
    const setup = rateByWorkItem.get(item.id);
    if (setup?.sourceType === 'direct') {
      const material = materialItems.find((entry) => entry.id === setup.sourceId);
      return material ? `${material.group === 'labour' ? 'Labour' : 'Material'}: ${material.materialName}` : 'Linked material removed';
    }
    if (setup?.sourceType === 'assembly') return `Assembly: ${assemblies.find((entry) => entry.id === setup.sourceId)?.name || 'removed'}`;
    if (setup?.sourceType === 'manual') return 'Manual rate';
    return 'Rate source not linked';
  }, [rateByWorkItem, materialItems, assemblies]);
  const workItemOptions = useMemo(() => {
    const unique = new Map<string, ItemWorkMaster>();
    availableWorkItems.forEach((item) => {
      const key = `${item.name.trim().toLowerCase()}__${item.category.trim().toLowerCase()}__${item.unit.trim().toLowerCase()}__${sourceLabel(item).toLowerCase()}`;
      if (!unique.has(key)) unique.set(key, item);
    });
    return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name) || a.category.localeCompare(b.category));
  }, [availableWorkItems, sourceLabel]);
  const materialGroups = useMemo(() => Array.from(new Set(materialItems.filter((item) => item.group !== 'labour').map((item) => materialGroupLabel(item.group)))).sort(), [materialItems]);
  const quickDirectSources = useMemo(() => materialItems.filter((item) => {
    if (entrySource === 'labour') return item.group === 'labour';
    if (entrySource === 'material') return item.group !== 'labour' && (!materialGroupFilter || materialGroupLabel(item.group) === materialGroupFilter);
    return false;
  }), [materialItems, entrySource, materialGroupFilter]);
  const quickSourceOptions = useMemo(() => entrySource === 'assembly'
    ? assemblies.map((item) => ({ id: item.id, label: item.name }))
    : quickDirectSources.map((item) => ({ id: item.id, label: `${item.materialName} · ${item.unit}` })), [assemblies, entrySource, quickDirectSources]);
  const projectGroupOptions = useMemo(() => {
    const unique = new Map<string, string>();
    items.forEach((item) => {
      const label = item.group.trim();
      if (label && !unique.has(label.toLowerCase())) unique.set(label.toLowerCase(), label);
    });
    return Array.from(unique.values()).sort((a, b) => a.localeCompare(b));
  }, [items]);
  const unitSectionOptions = useMemo(() => Array.from(new Set(unitTypes.flatMap((unit) => [
    ...(unit.sections || []),
    ...(unit.components || []).map((component) => component.group || ''),
  ]).map((section) => section.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [unitTypes]);
  const sourceWorkItemOptions = useMemo(() => workItemOptions.filter((item) => {
    const setup = rateByWorkItem.get(item.id);
    if (entrySource === 'assembly') return setup?.sourceType === 'assembly';
    if (entrySource === 'labour') return setup?.sourceType === 'direct' && materialItems.find((entry) => entry.id === setup.sourceId)?.group === 'labour';
    if (entrySource === 'material') {
      const material = setup?.sourceType === 'direct' ? materialItems.find((entry) => entry.id === setup.sourceId) : undefined;
      return !!material && material.group !== 'labour' && (!materialGroupFilter || materialGroupLabel(material.group) === materialGroupFilter);
    }
    return false;
  }), [workItemOptions, rateByWorkItem, materialItems, materialGroupFilter, entrySource]);
  const linkedMaterialVarieties = useMemo(() => {
    if (entrySource !== 'material' || !draft?.workItemId) return [];
    const setup = rateByWorkItem.get(draft.workItemId);
    if (setup?.sourceType !== 'direct') return [];
    const material = materialItems.find((item) => item.id === setup.sourceId);
    if (!material || material.group === 'labour') return [];
    return Array.from(new Set([
      ...(material.varieties || []),
      ...(material.quotes || []).flatMap((quote) => (quote.quoteOptions || []).map((option) => option.variety || option.name)),
    ].map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [entrySource, draft?.workItemId, rateByWorkItem, materialItems]);
  const normaliseMaterialHierarchy = useCallback((item: ProjectEstimateScopeItem) => {
    if (item.sourceId) return item;
    const setup = rateByWorkItem.get(item.workItemId || '');
    const material = setup?.sourceType === 'direct' ? materialItems.find((entry) => entry.id === setup.sourceId) : undefined;
    const workItem = availableWorkItems.find((entry) => entry.id === item.workItemId);
    if (!material || material.group === 'labour' || !workItem || item.subgroupIsCustom) return item;
    const savedVariety = (item.variety || '').trim();
    if (savedVariety) {
      const quoteOptionId = materialOptionIdForVariety(material, savedVariety);
      return quoteOptionId && quoteOptionId !== item.quoteOptionId ? { ...item, quoteOptionId } : item;
    }
    if (!item.subgroup || item.subgroup.trim().toLowerCase() === workItem.name.trim().toLowerCase()) return item;
    const variety = item.subgroup.trim();
    return { ...item, subgroup: workItem.name, subgroupCode: '', variety, quoteOptionId: materialOptionIdForVariety(material, variety) || item.quoteOptionId || setup?.quoteOptionId || '', lineCode: '' };
  }, [rateByWorkItem, materialItems, availableWorkItems]);
  const normalisedItems = useMemo(() => projectScopeWithAutoCodes(items.map(normaliseMaterialHierarchy)), [items, normaliseMaterialHierarchy]);
  const saveItems = (nextItems: ProjectEstimateScopeItem[]) => onSave(nextItems.map(normaliseMaterialHierarchy));
  const existingWorkItemLines = useMemo(() => !draft?.workItemId ? [] : normalisedItems.filter((item) => item.id !== draft.id && item.workItemId === draft.workItemId), [normalisedItems, draft?.id, draft?.workItemId]);
  const duplicateScopeLine = useMemo(() => existingWorkItemLines.some((item) =>
    item.scopeType === draft?.scopeType &&
    (item.group || '').trim().toLowerCase() === (draft?.group || '').trim().toLowerCase() &&
    (item.subgroup || '').trim().toLowerCase() === (draft?.subgroup || '').trim().toLowerCase() &&
    (item.variety || '').trim().toLowerCase() === (draft?.variety || '').trim().toLowerCase() &&
    (item.buildingName || '').trim().toLowerCase() === (draft?.buildingName || '').trim().toLowerCase()
  ), [existingWorkItemLines, draft?.scopeType, draft?.group, draft?.subgroup, draft?.variety, draft?.buildingName]);
  const calculatedQty = (item: ProjectEstimateScopeItem) => projectScopeCalculatedQty(item, unitTypes);
  const scopeComponent = (item: ProjectEstimateScopeItem): QuantityProfileComponent => ({
    id: item.id,
    sourceType: item.sourceType,
    sourceId: item.sourceId,
    quoteOptionId: item.quoteOptionId,
    label: item.label || '',
    assemblyType: item.assemblyType,
    variety: item.variety,
    qtyPerUnit: num(item.qty),
    unit: item.unit || 'Nos',
  });
  const scopeRateSetup = (item: ProjectEstimateScopeItem) => item.sourceId && item.sourceType
    ? componentRateSetup(scopeComponent(item), availableWorkItems, availableRates)
    : rateByWorkItem.get(item.workItemId || '') || { sourceType: 'none' } as WorkRateSetup;
  const lineRate = (item: ProjectEstimateScopeItem) => {
    const setup = scopeRateSetup(item);
    const withOption = item.quoteOptionId ? { ...setup, quoteOptionId: item.quoteOptionId } : setup;
    return item.sourceId ? resolveUnitComponentCostingRate(withOption, scopeComponent(item), materialItems, assemblies, costBasis) : resolveCostingRate(withOption, materialItems, assemblies, costBasis);
  };
  const scopeLabel = (item: ProjectEstimateScopeItem) => {
    if (item.scopeType === 'building') return item.buildingName || 'Building';
    if (item.scopeType === 'unitType') return `${unitTypes.find((unit) => unit.id === item.unitTypeId)?.label || 'Unit type'} driven`;
    return item.scopeType === 'common' ? 'Common area' : 'Project-wide';
  };
  const compactSource = (item: ProjectEstimateScopeItem, resolved: ReturnType<typeof lineRate>) => {
    const baseSetup = scopeRateSetup(item);
    const setup = item.quoteOptionId ? { ...baseSetup, quoteOptionId: item.quoteOptionId } : baseSetup;
    if (setup?.sourceType === 'direct') {
      const material = materialItems.find((entry) => entry.id === setup.sourceId);
      const quote = resolved.tentative
        ? tentativeCostingQuoteForOption(material, setup.quoteOptionId)
        : selectedMaterialQuoteForOption(material, setup.quoteOptionId);
      return [scopeLabel(item), quote?.vendor].filter(Boolean).join(' · ');
    }
    return [scopeLabel(item), resolved.source.replace(/ \(default costing\)/g, '')].filter(Boolean).join(' · ');
  };
  const lineDisplayTitle = (item: ProjectEstimateScopeItem, workItem?: ItemWorkMaster) => {
    const values = [item.variety, item.assemblyType].map((value) => (value || '').trim()).filter(Boolean);
    const unique = values.filter((value, index) => values.findIndex((other) => other.toLowerCase() === value.toLowerCase()) === index);
    return unique.join(' · ') || item.label || workItem?.name || 'Work item removed';
  };
  const openSubsectionOrder = (subgroup: { code: string; label: string; lines: ProjectEstimateScopeItem[] }) => {
    const candidates = subgroup.lines.flatMap((line): ProjectEstimateOrderCandidate[] => {
      const setup = scopeRateSetup(line);
      if (setup.sourceType !== 'direct') return [];
      const material = materialItems.find((entry) => entry.id === setup.sourceId);
      if (!material || material.group === 'labour') return [];
      const optionId = line.quoteOptionId || setup.quoteOptionId || materialOptionIdForVariety(material, line.variety || line.subgroup);
      const quote = selectedMaterialQuoteForOption(material, optionId);
      if (!quote) return [];
      const option = selectedQuoteOption(quote, optionId);
      const rate = option ? quoteOptionLandedRate(quote, option) : quoteLandedRate(material, quote);
      const qty = calculatedQty(line);
      if (!rate || qty <= 0) return [];
      const workItem = availableWorkItems.find((entry) => entry.id === line.workItemId);
      return [{
        line,
        material,
        quote,
        optionId,
        label: lineDisplayTitle(line, workItem),
        qty,
        unit: line.unit || option?.unit || material.unit || 'Nos',
        rate,
      }];
    });
    if (!candidates.length) {
      window.alert('This subsection has no material lines with a selected vendor rate. Select material rates before placing an order.');
      return;
    }
    setSubsectionOrderDialog({ code: subgroup.code, label: subgroup.label, candidates });
  };
  const measurement = (item: ProjectEstimateScopeItem) => {
    if (item.quantityMethod === 'areaInch') return `${fmtQty(num(item.nos) || 1)} Nos × ${fmtQty(num(item.length))} × ${fmtQty(num(item.breadth))} in`;
    if (item.quantityMethod === 'volumeInch') return `${fmtQty(num(item.nos) || 1)} Nos × ${fmtQty(num(item.length))} × ${fmtQty(num(item.breadth))} × ${fmtQty(num(item.height))} in`;
    return 'Manual quantity';
  };
  const grouped = useMemo(() => {
    const result = new Map<string, { code: string; label: string; subgroups: Map<string, { code: string; label: string; lines: ProjectEstimateScopeItem[] }> }>();
    const groupCodes = new Map<string, string>();
    const subgroupCodes = new Map<string, string>();
    const rememberGroupCode = (label: string, preferredCode?: string) => {
      const key = `group_${label.toLowerCase()}`;
      const nextCode = preferredCode || `${groupCodes.size + 1}.00`;
      if (!groupCodes.has(key)) groupCodes.set(key, nextCode);
      return groupCodes.get(key)!;
    };
    const rememberSubgroupCode = (groupLabel: string, subgroupLabel: string, groupCode: string, preferredCode?: string) => {
      const key = `subgroup_${groupLabel.toLowerCase()}__${subgroupLabel.toLowerCase()}`;
      if (!subgroupCodes.has(key)) {
        const existingForGroup = Array.from(subgroupCodes.keys()).filter((entry) => entry.startsWith(`subgroup_${groupLabel.toLowerCase()}__`)).length;
        const groupPrefix = groupCode.replace(/\.00$/, '') || '0';
        subgroupCodes.set(key, preferredCode || `${groupPrefix}.${String(existingForGroup + 1).padStart(2, '0')}`);
      }
      return subgroupCodes.get(key)!;
    };
    sections.slice().sort((a, b) => num(a.order) - num(b.order)).forEach((section) => {
      const label = section.group.trim();
      if (!label) return;
      const key = `group_${label.toLowerCase()}`;
      const code = rememberGroupCode(label);
      if (!result.has(key)) result.set(key, { code, label, subgroups: new Map() });
      else if (!result.get(key)!.code) result.get(key)!.code = code;
      const subgroupLabel = (section.subgroup || '').trim();
      if (!subgroupLabel) return;
      const group = result.get(key)!;
      const subgroupKey = `subgroup_${subgroupLabel.toLowerCase()}`;
      if (!group.subgroups.has(subgroupKey)) group.subgroups.set(subgroupKey, { code: rememberSubgroupCode(label, subgroupLabel, code), label: subgroupLabel, lines: [] });
    });
    normalisedItems.slice().sort((a, b) => num(a.order) - num(b.order)).forEach((item) => {
      const label = item.group || 'Project works';
      const key = `group_${label.toLowerCase()}`;
      const code = rememberGroupCode(label, item.groupCode || undefined);
      if (!result.has(key)) result.set(key, { code, label, subgroups: new Map() });
      else if (!result.get(key)!.code) result.get(key)!.code = code;
      const subgroupLabel = item.subgroup || 'General';
      const subgroupKey = `subgroup_${subgroupLabel.toLowerCase()}`;
      const group = result.get(key)!;
      const subgroupCode = item.subgroupCode || rememberSubgroupCode(label, subgroupLabel, code);
      if (!group.subgroups.has(subgroupKey)) group.subgroups.set(subgroupKey, { code: subgroupCode, label: subgroupLabel, lines: [] });
      group.subgroups.get(subgroupKey)!.lines.push(item);
    });
    return Array.from(result.values());
  }, [normalisedItems, sections]);
  const projectEstimateSummary = (() => {
    const unitTypesTotal = unitTypes.reduce((sum, unit) => {
      const allocated = (unit.buildingAllocations || []).reduce((count, allocation) => count + num(allocation.count), 0);
      return sum + unitTypeEstimateSummary(unit, workItems, rates, materialItems, assemblies, costBasis).total * allocated;
    }, 0);
    const unitPending = unitTypes.reduce((sum, unit) => {
      const allocated = (unit.buildingAllocations || []).reduce((count, allocation) => count + num(allocation.count), 0);
      return sum + (allocated > 0 ? unitTypeEstimateSummary(unit, workItems, rates, materialItems, assemblies, costBasis).pending : 0);
    }, 0);
    const scopeLines = normalisedItems.filter((item) => item.scopeType !== 'unitType');
    const scopeTotal = scopeLines.reduce((sum, line) => sum + calculatedQty(line) * lineRate(line).rate, 0);
    const scopePending = scopeLines.reduce((sum, line) => {
      const resolved = lineRate(line);
      return sum + (resolved.missing || !resolved.rate ? resolved.pendingCount || 1 : 0);
    }, 0);
    return { unitTypesTotal, scopeTotal, total: unitTypesTotal + scopeTotal, pending: unitPending + scopePending };
  })();
  const openAdd = (group = 'Common works', subgroup = '', seed?: ProjectEstimateScopeItem, addSubsection = false) => {
    const setup = seed ? rateByWorkItem.get(seed.workItemId || '') : undefined;
    const material = setup?.sourceType === 'direct' ? materialItems.find((item) => item.id === setup.sourceId) : undefined;
    const source = setup?.sourceType === 'assembly'
      ? 'assembly'
      : setup?.sourceType === 'direct'
        ? material?.group === 'labour' ? 'labour' : 'material'
        : null;
    const workItem = seed ? availableWorkItems.find((item) => item.id === seed.workItemId) : undefined;
    setAddingScopeSubsection(addSubsection);
    setDraft({
      id: `scope_${Date.now()}`,
      workItemId: seed?.workItemId || '',
      group,
      subgroup: seed?.subgroup || subgroup,
      subgroupIsCustom: addSubsection || seed?.subgroupIsCustom || false,
      scopeType: seed?.scopeType === 'building' || seed?.scopeType === 'common' ? seed.scopeType : 'project',
      buildingName: seed?.scopeType === 'building' ? seed.buildingName || '' : '',
      quantityMethod: seed?.quantityMethod || 'manual',
      qty: 0,
      unit: seed?.unit || workItem?.unit || '',
      quoteOptionId: setup?.quoteOptionId || '',
      notes: '',
      order: items.length + 1,
    });
    setEntrySource(source);
    setMaterialGroupFilter(material && material.group !== 'labour' ? materialGroupLabel(material.group) : '');
    setDialogOpen(true);
  };
  const saveDraft = () => {
    if (!draft?.workItemId || !draft.group.trim()) return;
    const workItem = availableWorkItems.find((item) => item.id === draft.workItemId);
    if (!workItem) return;
    if (!usageIds.has(workItem.id)) onEnableWorkItem(workItem.id, true);
    saveItems([...items.filter((item) => item.id !== draft.id), { ...draft, unit: draft.unit || (draft.quantityMethod === 'areaInch' ? 'Sft' : draft.quantityMethod === 'volumeInch' ? 'Cft' : workItem.unit), order: draft.order || items.length + 1 }]);
    setDialogOpen(false);
    setAddingScopeSubsection(false);
    setDraft(null);
  };
  const saveScopeStructure = () => {
    const label = scopeStructureName.trim();
    if (!scopeStructureDialog || !label) return;
    const group = scopeStructureDialog.mode === 'group' ? label : scopeStructureDialog.group || '';
    const subgroup = scopeStructureDialog.mode === 'subgroup' ? label : '';
    const duplicate = sections.some((section) =>
      section.group.trim().toLowerCase() === group.toLowerCase() &&
      (section.subgroup || '').trim().toLowerCase() === subgroup.toLowerCase()
    );
    if (!duplicate) onSaveSections([...sections, { id: `scope_section_${Date.now()}`, group, subgroup, order: sections.length + 1 }]);
    setScopeStructureDialog(null);
    setScopeStructureName('');
  };
  const openQuickWorkItem = () => {
    setQuickWorkName('');
    setQuickWorkUnit(draft?.unit || 'Nos');
    setQuickSourceId('');
    setQuickWorkDialogOpen(true);
  };
  const saveQuickWorkItem = async () => {
    if (!entrySource || !quickWorkName.trim() || !quickWorkUnit.trim() || !quickSourceId) return;
    const directSource = entrySource === 'assembly' ? undefined : materialItems.find((item) => item.id === quickSourceId);
    const assemblySource = entrySource === 'assembly' ? assemblies.find((item) => item.id === quickSourceId) : undefined;
    if (!directSource && !assemblySource) return;
    const id = `work_${Date.now()}`;
    const workItem: ItemWorkMaster = {
      id,
      name: quickWorkName.trim(),
      category: entrySource === 'material'
        ? materialGroupLabel(directSource!.group)
        : entrySource === 'labour' ? 'Labour' : 'Assembly',
      unit: quickWorkUnit.trim(),
      procurementMode: 'unit',
      order: availableWorkItems.length + 1,
    };
    const rate: WorkRateSetup = {
      workItemId: id,
      sourceType: entrySource === 'assembly' ? 'assembly' : 'direct',
      sourceId: quickSourceId,
      basis: 'primary',
    };
    setQuickSaving(true);
    try {
      await onCreateWorkItem(workItem, rate);
      setSessionWorkItems((previous) => [...previous, workItem]);
      setSessionRates((previous) => [...previous.filter((entry) => entry.workItemId !== id), rate]);
      setDraft((previous) => previous ? {
        ...previous,
        workItemId: id,
        unit: workItem.unit,
        subgroup: entrySource === 'material' ? workItem.name : previous.subgroup,
        variety: '',
        quoteOptionId: '',
      } : previous);
      setQuickWorkDialogOpen(false);
    } finally {
      setQuickSaving(false);
    }
  };
  const saveUnitTypeFromEstimate = () => {
    const label = newUnitTypeLabel.trim();
    if (!label) return;
    const duplicate = unitTypes.some((unit) => unit.label.trim().toLowerCase() === label.toLowerCase());
    if (duplicate) return;
    onSaveUnitTypes([...unitTypes, blankUnitTypeMaster(`unit_${Date.now()}`, (unitTypes.reduce((max, unit) => Math.max(max, num(unit.order)), 0) || 0) + 1, label, newUnitTypeUnit.trim() || 'Flat')]);
    setDialogOpen(false);
    setDraft(null);
  };
  const remove = (id: string) => {
    if (window.confirm('Remove this BOQ line from the project estimate?')) saveItems(items.filter((item) => item.id !== id));
  };
  const removeUnitSection = (unitId: string, section: string) => {
    const unit = unitTypes.find((item) => item.id === unitId);
    const hasEstimateLines = (unit?.components || []).some((component) =>
      (component.group || '').trim().toLowerCase() === section.trim().toLowerCase()
    );
    if (!unit || hasEstimateLines) {
      window.alert('Remove or move this section\'s estimate lines before deleting the section.');
      return;
    }
    if (!window.confirm(`Delete the empty ${section} section from ${unit.label || 'this unit type'}?`)) return;
    onSaveUnitTypes(unitTypes.map((item) => item.id === unitId ? {
      ...item,
      sections: (item.sections || []).filter((entry) => entry.trim().toLowerCase() !== section.trim().toLowerCase()),
    } : item));
  };
  const rateVarietyHierarchyForWorkItem = (workItem: ItemWorkMaster) => {
    const sourceId = rateByWorkItem.get(workItem.id)?.sourceId;
    const material = materialItems.find((item) => item.id === sourceId);
    const maps = material?.assemblyVarietyMap || [];
    const types = Array.from(new Set([
      ...maps.map((item) => item.assemblyType),
      ...(material?.assemblyTypes || []),
    ].map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    return {
      types,
      varietiesForType: (type: string) => {
        const matchingMaps = maps.filter((item) => item.assemblyType.trim().toLowerCase() === type.trim().toLowerCase());
        const mappedVarieties = matchingMaps.flatMap((item) => item.varieties || []);
        const quoteVarieties = (material?.quotes || []).flatMap((quote) => (quote.quoteOptions || [])
          .filter((option) => (option.group || '').trim().toLowerCase() === type.trim().toLowerCase())
          .map((option) => option.variety || option.name));
        return Array.from(new Set([...mappedVarieties, ...quoteVarieties].map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
      },
    };
  };
  return <Stack gap={1.5}>
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={1} sx={{ p: 1.5, bgcolor: PANEL2 }}>
        <Box sx={{ flex: 1 }}><Typography sx={{ color: DARK, fontWeight: 900 }}>Project estimate BOQ</Typography><Typography sx={{ color: MUTED, fontSize: 12 }}>Create the project hierarchy here. Each estimate line uses a reusable Work Item linked to material, labour, an assembly, or a manual rate.</Typography></Box>
        <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => setAddChoiceDialogOpen(true)}>Add to estimate</Button>
      </Stack>
      <Box sx={{ borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={1} sx={{ px: 1.5, py: 1 }}>
          <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Project estimate summary</Typography>
          <Stack direction="row" alignItems="center" gap={.75} flexWrap="wrap"><ToggleButtonGroup exclusive size="small" value={costBasis} onChange={(_, value) => value && setCostBasis(value)} sx={{ '& .MuiToggleButton-root': { px: 1, py: .45, textTransform: 'none', fontSize: 11, fontWeight: 800, borderColor: BORDER, color: MUTED, '&.Mui-selected': { bgcolor: '#edeae4', color: DARK } } }}><ToggleButton value="selected">Selected rates only</ToggleButton><ToggleButton value="tentative">Include default rates</ToggleButton></ToggleButtonGroup><Button size="small" variant="outlined" onClick={onOpenProjectCost} sx={{ borderColor: BORDER, color: MUTED }}>Open Project Cost</Button></Stack>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1.4fr 1fr 1fr 1fr' }, borderTop: `1px solid ${BORDER}` }}>
          <Box sx={{ p: 1.1, borderRight: { md: `1px solid ${BORDER}` }, borderBottom: { xs: `1px solid ${BORDER}`, md: 0 } }}><Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>{costBasis === 'tentative' ? 'Tentative project estimate' : 'Selected-rate estimate'}</Typography><Typography sx={{ color: GOLD_D, fontSize: 21, fontWeight: 900 }}>₹{fmtRate(projectEstimateSummary.total)}</Typography></Box>
          <Box sx={{ p: 1.1, borderRight: { md: `1px solid ${BORDER}` }, borderBottom: { xs: `1px solid ${BORDER}`, md: 0 } }}><Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Unit type estimates</Typography><Typography sx={{ color: DARK, fontSize: 16, fontWeight: 900 }}>₹{fmtRate(projectEstimateSummary.unitTypesTotal)}</Typography></Box>
          <Box sx={{ p: 1.1, borderRight: { md: `1px solid ${BORDER}` } }}><Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Project scope work</Typography><Typography sx={{ color: DARK, fontSize: 16, fontWeight: 900 }}>₹{fmtRate(projectEstimateSummary.scopeTotal)}</Typography></Box>
          <Box sx={{ p: 1.1 }}><Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>Rate readiness</Typography><Typography sx={{ color: projectEstimateSummary.pending ? WARN : '#397452', fontSize: 16, fontWeight: 900 }}>{projectEstimateSummary.pending ? `${projectEstimateSummary.pending} pending` : 'All rates priced'}</Typography></Box>
        </Box>
      </Box>
      {unitTypes.map((unit) => { const allocated = (unit.buildingAllocations || []).reduce((sum, allocation) => sum + num(allocation.count), 0); const estimate = unitTypeEstimateSummary(unit, availableWorkItems, availableRates, materialItems, assemblies, costBasis); const componentGroups = new Map<string, QuantityProfileComponent[]>(); (unit.sections || []).forEach((section) => componentGroups.set(section, [])); (unit.components || []).forEach((component) => { const workItem = resolveComponentWorkItem(component, availableWorkItems); const label = component.group || workItem?.category || 'Unit works'; componentGroups.set(label, [...(componentGroups.get(label) || []), component]); }); return <Accordion key={`unit_${unit.id}`} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderTop: `1px solid ${BORDER}` }}><AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#faf8f5' }}><Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}><Stack direction="row" gap={1} alignItems="center"><Chip size="small" label="Unit" sx={{ fontWeight: 900, bgcolor: '#fff2d6', color: GOLD_D }} /><Box><Typography sx={{ fontWeight: 900 }}>{unit.label || 'Unit type'}</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>{fmtQty(allocated)} {unit.unit || 'unit'} allocated · {unit.components?.length || 0} estimate item(s)</Typography></Box></Stack><Stack direction="row" gap={.5} alignItems="center"><Box sx={{ textAlign: 'right' }}><Typography sx={{ color: GOLD_D, fontSize: 12.5, fontWeight: 900 }}>₹{fmtRate(estimate.total * allocated)}</Typography><Typography sx={{ color: MUTED, fontSize: 11 }}>₹{fmtRate(estimate.total)} / {unit.unit || 'unit'}</Typography></Box><Box onClick={(event) => event.stopPropagation()}><ActionIcon title={`Add section to ${unit.label || 'unit type'}`} onClick={() => { setSectionDialogUnitId(unit.id); setNewUnitSection(''); }}><AddOutlinedIcon fontSize="inherit" /></ActionIcon><ActionIcon title={`Edit ${unit.label || 'unit type'}`} onClick={() => setEditingUnitType({ ...unit, buildingAllocations: [...(unit.buildingAllocations || [])] })}><EditOutlinedIcon fontSize="inherit" /></ActionIcon></Box></Stack></Stack></AccordionSummary><AccordionDetails sx={{ p: 0 }}>{Array.from(componentGroups.entries()).map(([group, components]) => { const sectionPerUnit = components.reduce((sum, component) => { const setup = componentRateSetup(component, availableWorkItems, availableRates); const leadMultiplier = labourLeadMultiplier(component, setup, materialItems, unit.buildingAllocations || []); const resolved = applyLabourLeadRate(resolveUnitComponentCostingRate(setup, component, materialItems, assemblies, costBasis), leadMultiplier); return sum + num(component.qtyPerUnit) * resolved.rate; }, 0); const pendingRates = components.filter((component) => { const setup = componentRateSetup(component, availableWorkItems, availableRates); const leadMultiplier = labourLeadMultiplier(component, setup, materialItems, unit.buildingAllocations || []); return !applyLabourLeadRate(resolveUnitComponentCostingRate(setup, component, materialItems, assemblies, costBasis), leadMultiplier).rate; }).length; return <Accordion key={group} disableGutters elevation={0} sx={{ ml: 2, borderLeft: '2px solid #ead8b5', borderTop: `1px solid ${BORDER}`, '&:before': { display: 'none' } }}><AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 52, bgcolor: '#fffaf0', '& .MuiAccordionSummary-content': { my: 0 } }}><Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', mr: 1 }}><Box><Typography sx={{ color: GOLD_D, fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>{group}</Typography><Typography sx={{ color: MUTED, fontSize: 11 }}>{components.length} estimate {components.length === 1 ? 'line' : 'lines'}{pendingRates ? ` · ${pendingRates} pending` : ''}</Typography></Box><Stack direction="row" gap={2} alignItems="center"><Box sx={{ textAlign: 'right' }}><Typography sx={{ color: MUTED, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Per unit</Typography><Typography sx={{ fontSize: 12, fontWeight: 800, color: GOLD_D }}>₹{fmtRate(sectionPerUnit)}</Typography></Box><Box sx={{ minWidth: 105, textAlign: 'right' }}><Typography sx={{ color: MUTED, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Total</Typography><Typography sx={{ fontSize: 12, fontWeight: 900 }}>₹{fmtRate(sectionPerUnit * allocated)}</Typography></Box><Box onClick={(event) => event.stopPropagation()}><ActionIcon title={`Add estimate line to ${group}`} onClick={() => setRuleDialogTarget({ unitId: unit.id, section: group })}><AddOutlinedIcon fontSize="inherit" /></ActionIcon>{components.length === 0 && <ActionIcon title={`Delete empty ${group} section`} danger onClick={() => removeUnitSection(unit.id, group)}><DeleteOutlineIcon fontSize="inherit" /></ActionIcon>}</Box></Stack></Stack></AccordionSummary><AccordionDetails sx={{ p: 0 }}>{components.length ? <Box sx={{ overflowX: 'auto' }}><Box component="table" sx={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}><Box component="thead"><Box component="tr"><Box component="th" sx={{ ...headTh, width: 44, minWidth: 44, p: 0 }} /><Box component="th" sx={{ ...headTh, textAlign: 'left', pl: 3 }}>Estimate</Box><Box component="th" sx={headTh}>Per unit</Box><Box component="th" sx={headTh}>Project qty</Box><Box component="th" sx={headTh}>Rate</Box><Box component="th" sx={headTh}>Total</Box><Box component="th" sx={headTh} /></Box></Box><Box component="tbody">{components.slice().sort((a, b) => unitEstimateLineTypeOrder(unitEstimateLineType(a, availableWorkItems, availableRates, materialItems)) - unitEstimateLineTypeOrder(unitEstimateLineType(b, availableWorkItems, availableRates, materialItems))).map((component, index, sortedComponents) => { const sourceType = unitEstimateLineType(component, availableWorkItems, availableRates, materialItems); const previousSourceType = index ? unitEstimateLineType(sortedComponents[index - 1], availableWorkItems, availableRates, materialItems) : ''; const sourceAccent = sourceType === 'Labour' ? '#a7742d' : sourceType === 'Assembly' ? '#4f7b69' : '#52799b'; const sourceTint = sourceType === 'Labour' ? '#fffaf2' : sourceType === 'Assembly' ? '#f4faf6' : '#f4f8fc'; const workItem = resolveComponentWorkItem(component, availableWorkItems); const setup = componentRateSetup(component, availableWorkItems, availableRates); const assembly = setup.sourceType === 'assembly' ? assemblies.find((item) => item.id === setup.sourceId) : undefined; const leadMultiplier = labourLeadMultiplier(component, setup, materialItems, unit.buildingAllocations || []); const resolved = applyLabourLeadRate(resolveUnitComponentCostingRate(setup, component, materialItems, assemblies, costBasis), leadMultiplier); const qty = num(component.qtyPerUnit); const componentUnit = workItem?.unit || component.unit || 'unit'; return <React.Fragment key={component.id}><Box component="tr">{sourceType !== previousSourceType && <Box component="td" rowSpan={sortedComponents.filter((item) => unitEstimateLineType(item, availableWorkItems, availableRates, materialItems) === sourceType).length} sx={{ width: 44, minWidth: 44, p: 0, bgcolor: sourceTint, borderTop: `1px solid ${BORDER}`, borderLeft: `4px solid ${sourceAccent}`, borderRight: `1px solid ${BORDER}`, verticalAlign: 'middle' }}><Box sx={{ height: '100%', minHeight: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: sourceAccent, fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: .7, lineHeight: 1 }}>{sourceType}</Typography></Box></Box>}<Box component="td" sx={{ p: 1, pl: 3, borderTop: `1px solid ${BORDER}` }}><Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{component.variety || component.assemblyType || workItem?.name || component.label || 'Work item removed'}</Typography> {assembly ? <><Button variant="text" size="small" onClick={() => setExpandedUnitAssemblyLines((previous) => { const next = new Set(previous); next.has(component.id) ? next.delete(component.id) : next.add(component.id); return next; })} sx={{ minWidth: 0, p: 0, color: MUTED, fontSize: 11, fontWeight: 500, textTransform: 'none' }}>Assembly · {assembly.name}<ExpandMoreIcon sx={{ ml: .25, fontSize: 16, transform: expandedUnitAssemblyLines.has(component.id) ? 'rotate(180deg)' : 'none' }} /></Button>{expandedUnitAssemblyLines.has(component.id) && <Box sx={{ mt: .6, pl: .8, borderLeft: `2px solid ${BORDER}` }}>{assembly.components.map((part) => <Typography key={part.id} sx={{ color: MUTED, fontSize: 10.5 }}>{part.label || part.type} · {fmtQty(part.qty)} {part.orderUnit || part.unit}</Typography>)}</Box>}</> : <Typography sx={{ color: MUTED, fontSize: 11 }}>{[component.variety && component.assemblyType && component.variety.trim().toLowerCase() !== component.assemblyType.trim().toLowerCase() ? component.assemblyType : '', workItem?.name || component.label || '', component.leadEnabled ? component.leadType === 'fixed' ? `Lead: ${fmtRate(num(component.leadPercent))}% fixed` : `Lead: ${fmtRate(num(component.leadPercent))}% / floor · Weighted: ${fmtRate((leadMultiplier - 1) * 100)}%` : ''].filter(Boolean).join(' · ')}</Typography>}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, textAlign: 'right', color: qty > 0 ? DARK : WARN }}>{qty > 0 ? `${fmtQty(qty)} ${componentUnit}` : 'Qty pending'}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, textAlign: 'right', color: qty > 0 ? DARK : WARN }}>{qty > 0 ? `${fmtQty(qty * allocated)} ${componentUnit}` : 'Qty pending'}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, textAlign: 'right', color: resolved.rate ? GOLD_D : WARN }}>{resolved.rate ? `₹${fmtRate(resolved.rate)}` : 'Pending'}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, textAlign: 'right', fontWeight: 900 }}>₹{fmtRate(qty * allocated * resolved.rate)}</Box><Box component="td" sx={{ p: .7, borderTop: `1px solid ${BORDER}`, textAlign: 'right' }}><ActionIcon title="Edit estimate line" onClick={() => setRuleDialogTarget({ unitId: unit.id, section: group, component })}><EditOutlinedIcon fontSize="inherit" /></ActionIcon></Box></Box></React.Fragment>; })}</Box></Box></Box> : <Typography sx={{ px: 1.5, py: 1.25, color: MUTED, fontSize: 12 }}>No estimate lines yet. Use + to add one.</Typography>}</AccordionDetails></Accordion>; })}</AccordionDetails></Accordion>; })}
      {!normalisedItems.length ? <Box sx={{ p: 3, textAlign: 'center' }}><Typography sx={{ color: MUTED, fontSize: 13 }}>Start with a group such as Civil Work, Carpentry, Windows, Electrical, Fire Safety, or Common Area.</Typography></Box> : grouped.map((group) => {
        const groupKey = `${group.code}_${group.label}`;
        const open = expanded.has(groupKey);
        const groupTotal = Array.from(group.subgroups.values()).flatMap((subgroup) => subgroup.lines).reduce((sum, line) => sum + calculatedQty(line) * lineRate(line).rate, 0);
        return <Accordion key={groupKey} expanded={open} onChange={(_, next) => setExpanded((previous) => { const result = new Set(previous); next ? result.add(groupKey) : result.delete(groupKey); return result; })} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderTop: `1px solid ${BORDER}` }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#faf8f5' }}><Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}><Stack direction="row" gap={1} alignItems="center"><Chip size="small" label={group.code} sx={{ fontWeight: 900, bgcolor: '#fff2d6', color: GOLD_D }} /><Typography sx={{ fontWeight: 900 }}>{group.label}</Typography></Stack><Stack direction="row" gap={.5} alignItems="center"><Typography sx={{ color: GOLD_D, fontSize: 12.5, fontWeight: 900 }}>₹{fmtRate(groupTotal)}</Typography><Box onClick={(event) => event.stopPropagation()}><ActionIcon title={`Add subsection to ${group.label}`} onClick={() => { setScopeStructureName(''); setScopeStructureDialog({ mode: 'subgroup', group: group.label }); }}><AddOutlinedIcon fontSize="inherit" /></ActionIcon></Box></Stack></Stack></AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>{Array.from(group.subgroups.values()).map((subgroup) => <Box key={`${subgroup.code}_${subgroup.label}`} sx={{ borderTop: `1px solid ${BORDER}`, ml: 2, borderLeft: `2px solid #ead8b5` }}>
            {(subgroup.code || subgroup.label !== 'General') && <Box sx={{ px: 1.5, py: .7, bgcolor: '#fffaf0' }}><Stack direction="row" alignItems="center" justifyContent="space-between"><Typography sx={{ color: GOLD_D, fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>{[subgroup.code, subgroup.label].filter(Boolean).join(' · ')}</Typography><Stack direction="row" alignItems="center" gap={.4} onClick={(event) => event.stopPropagation()}><Button size="small" startIcon={<ShoppingCartOutlinedIcon />} onClick={() => openSubsectionOrder(subgroup)} sx={{ color: GOLD_D, fontWeight: 800, textTransform: 'none' }}>Place order</Button><ActionIcon title={`Add estimate line to ${subgroup.label}`} onClick={() => { setScopeRuleTarget({ group: group.label, subgroup: subgroup.label }); }}><AddOutlinedIcon fontSize="inherit" /></ActionIcon></Stack></Stack></Box>}
            <Box sx={{ overflowX: 'auto' }}><Box component="table" sx={{ width: '100%', minWidth: 1040, borderCollapse: 'collapse' }}><Box component="thead"><Box component="tr"><Box component="th" sx={{ ...headTh, width: 44, minWidth: 44, p: 0 }} /><Box component="th" sx={{ ...headTh, width: 70, pl: 2, textAlign: 'left', fontSize: 10 }}>Ref.</Box><Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Description</Box><Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Calculation</Box><Box component="th" sx={headTh}>Total qty</Box><Box component="th" sx={headTh}>Rate</Box><Box component="th" sx={headTh}>Amount</Box><Box component="th" sx={headTh} /></Box></Box><Box component="tbody">{subgroup.lines.slice().sort((a, b) => projectEstimateLineType(a, rateByWorkItem.get(a.workItemId || ''), materialItems).localeCompare(projectEstimateLineType(b, rateByWorkItem.get(b.workItemId || ''), materialItems))).map((line, index, sortedLines) => {
              const workItem = availableWorkItems.find((item) => item.id === line.workItemId);
              const setup = rateByWorkItem.get(line.workItemId || '');
              const sourceType = projectEstimateLineType(line, setup, materialItems);
              const previousSourceType = index ? projectEstimateLineType(sortedLines[index - 1], rateByWorkItem.get(sortedLines[index - 1].workItemId || ''), materialItems) : '';
              const sourceAccent = sourceType === 'Labour' ? '#a7742d' : sourceType === 'Assembly' ? '#4f7b69' : '#52799b';
              const sourceTint = sourceType === 'Labour' ? '#fffaf2' : sourceType === 'Assembly' ? '#f4faf6' : '#f4f8fc';
              const resolved = lineRate(line);
              const qty = calculatedQty(line);
              return <React.Fragment key={line.id}><Box component="tr">{sourceType !== previousSourceType && <Box component="td" rowSpan={sortedLines.filter((item) => projectEstimateLineType(item, rateByWorkItem.get(item.workItemId || ''), materialItems) === sourceType).length} sx={{ width: 44, minWidth: 44, p: 0, bgcolor: sourceTint, borderTop: `1px solid ${BORDER}`, borderLeft: `4px solid ${sourceAccent}`, borderRight: `1px solid ${BORDER}`, verticalAlign: 'middle' }}><Box sx={{ height: '100%', minHeight: 82, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: sourceAccent, fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: .7, lineHeight: 1 }}>{sourceType}</Typography></Box></Box>}<Box component="td" sx={{ p: 1, pl: 2, borderTop: `1px solid ${BORDER}`, color: MUTED, fontSize: 11, fontWeight: 800 }}>{line.lineCode || '—'}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}` }}><Typography sx={{ fontWeight: 800, fontSize: 13 }}>{lineDisplayTitle(line, workItem || undefined)}</Typography><Typography sx={{ color: MUTED, fontSize: 11 }}>{compactSource(line, resolved) || 'Rate not linked'}</Typography></Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, color: MUTED, fontSize: 12 }}>{measurement(line).replace('Manual quantity', 'Manual')}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, textAlign: 'right', color: qty > 0 ? DARK : WARN, fontWeight: 800 }}>{qty > 0 ? `${fmtQty(qty)} ${line.unit || workItem?.unit || ''}` : 'Qty pending'}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, textAlign: 'right', color: resolved.rate ? GOLD_D : WARN, fontWeight: 800 }}>{resolved.rate ? `₹${fmtRate(resolved.rate)}` : 'Pending'}</Box><Box component="td" sx={{ p: 1, borderTop: `1px solid ${BORDER}`, textAlign: 'right', color: DARK, fontWeight: 900 }}>₹{fmtRate(qty * resolved.rate)}</Box><Box component="td" sx={{ p: .7, borderTop: `1px solid ${BORDER}`, textAlign: 'right' }}><ActionIcon title="Edit estimate line" onClick={() => { if (line.sourceId) { setScopeRuleTarget({ group: group.label, subgroup: subgroup.label, line }); return; } setEntrySource(setup?.sourceType === 'assembly' ? 'assembly' : materialItems.find((entry) => entry.id === setup?.sourceId)?.group === 'labour' ? 'labour' : 'material'); setMaterialGroupFilter(''); setAddingScopeSubsection(false); setDraft(line); setDialogOpen(true); }}><EditOutlinedIcon fontSize="inherit" /></ActionIcon><ActionIcon title="Remove estimate line" onClick={() => remove(line.id)} danger><DeleteOutlineIcon fontSize="inherit" /></ActionIcon></Box></Box></React.Fragment>;
            })}</Box></Box></Box>
          </Box>)}</AccordionDetails>
        </Accordion>;
      })}
    </Box>
    <Dialog open={addChoiceDialogOpen} onClose={() => setAddChoiceDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: POPUP_PAPER_SX }}>
      <DialogTitle>Add to project estimate</DialogTitle>
      <DialogContent><Stack direction={{ xs: 'column', sm: 'row' }} gap={1.2} sx={{ pt: 1 }}>
        <Button variant="outlined" onClick={() => { setAddChoiceDialogOpen(false); openAdd(); setEntrySource('unitType'); }} sx={{ py: 2, flex: 1, borderColor: BORDER, color: DARK }}>Unit type</Button>
        <Button variant="outlined" onClick={() => { setAddChoiceDialogOpen(false); setScopeStructureName(''); setScopeStructureDialog({ mode: 'group' }); }} sx={{ py: 2, flex: 1, borderColor: BORDER, color: DARK }}>Non-unit work</Button>
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setAddChoiceDialogOpen(false)} sx={{ color: MUTED }}>Cancel</Button></DialogActions>
    </Dialog>
    <Dialog open={!!scopeStructureDialog} onClose={() => setScopeStructureDialog(null)} fullWidth maxWidth="sm" PaperProps={{ sx: POPUP_PAPER_SX }}>
      <DialogTitle>{scopeStructureDialog?.mode === 'group' ? 'Add non-unit work' : `Add subsection to ${scopeStructureDialog?.group || ''}`}</DialogTitle>
      <DialogContent><Stack gap={1} sx={{ pt: 1 }}><TextField label={scopeStructureDialog?.mode === 'group' ? 'Non-unit work name' : 'Subsection name'} value={scopeStructureName} onChange={(event) => setScopeStructureName(event.target.value)} autoFocus fullWidth /></Stack></DialogContent>
      <DialogActions><Button onClick={() => setScopeStructureDialog(null)} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" onClick={saveScopeStructure} disabled={!scopeStructureName.trim()}>{scopeStructureDialog?.mode === 'group' ? 'Add work' : 'Add subsection'}</Button></DialogActions>
    </Dialog>
    {scopeRuleTarget && <UnitRuleDialog
      title={scopeRuleTarget.line ? 'Edit estimate line' : `Add estimate line to ${scopeRuleTarget.subgroup}`}
      unitLabel="unit"
      quantityLabel="Total project quantity"
      showSecondary={false}
      workItems={[]}
      directMaterialItems={materialItems}
      directAssemblies={assemblies}
      initialComponent={scopeRuleTarget.line ? scopeComponent(scopeRuleTarget.line) : undefined}
      sourceUsageMarker={(candidate) => {
        const norm = (value?: string) => String(value || '').trim().toLowerCase();
        const optionMatches = (line: ProjectEstimateScopeItem) => {
          if (line.id === scopeRuleTarget.line?.id) return false;
          if (line.sourceType !== candidate.sourceType || line.sourceId !== candidate.sourceId) return false;
          const candidateOption = norm(candidate.quoteOptionId);
          const lineOption = norm(line.quoteOptionId);
          if (candidateOption || lineOption) return candidateOption === lineOption;
          return norm(line.assemblyType) === norm(candidate.assemblyType) && norm(line.variety) === norm(candidate.variety);
        };
        const matches = items.filter(optionMatches);
        if (!matches.length) return null;
        const sameSection = matches.find((line) => norm(line.group) === norm(scopeRuleTarget.group) && norm(line.subgroup) === norm(scopeRuleTarget.subgroup));
        if (sameSection) return { label: 'Added here', tone: 'same', details: [sameSection.lineCode, sameSection.group, sameSection.subgroup].filter(Boolean).join(' · ') };
        const other = matches[0];
        return { label: 'Added other section', tone: 'other', details: [other.lineCode, other.group, other.subgroup].filter(Boolean).join(' · ') };
      }}
      onClose={() => setScopeRuleTarget(null)}
      onSave={(component) => {
        const previous = scopeRuleTarget.line;
        const next: ProjectEstimateScopeItem = {
          id: previous?.id || `scope_${Date.now()}`,
          sourceType: component.sourceType,
          sourceId: component.sourceId,
          label: component.label,
          group: scopeRuleTarget.group,
          subgroup: scopeRuleTarget.subgroup,
          subgroupIsCustom: true,
          assemblyType: component.assemblyType,
          variety: component.variety,
          quoteOptionId: component.quoteOptionId,
          scopeType: previous?.scopeType || 'project',
          quantityMethod: 'manual',
          qty: component.qtyPerUnit,
          unit: component.unit,
          order: previous?.order || items.length + 1,
        };
        saveItems([...items.filter((item) => item.id !== next.id), next]);
        setScopeRuleTarget(null);
      }}
      onDelete={scopeRuleTarget.line ? () => { remove(scopeRuleTarget.line!.id); setScopeRuleTarget(null); } : undefined}
    />}
    <Dialog open={dialogOpen} onClose={() => { setAddingScopeSubsection(false); setDialogOpen(false); }} fullWidth maxWidth="md" PaperProps={{ sx: POPUP_PAPER_SX }}><DialogTitle>{items.some((item) => item.id === draft?.id) ? 'Edit estimate line' : addingScopeSubsection ? 'Add subsection and estimate line' : 'Add estimate line'}</DialogTitle><DialogContent><Stack gap={1.4} sx={{ pt: 1 }}>
      <Box sx={{ p: 1.2, border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#faf8f5' }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase', mb: .8 }}>What are you estimating?</Typography><ToggleButtonGroup exclusive value={entrySource} onChange={(_, value) => { if (!value) return; setEntrySource(value); setMaterialGroupFilter(''); setDraft((previous) => previous ? { ...previous, workItemId: '' } : previous); if (value === 'unitType') { setNewUnitTypeLabel(''); setNewUnitTypeUnit('Flat'); } }} fullWidth sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, py: .75 } }}><ToggleButton value="material">Material</ToggleButton><ToggleButton value="labour">Labour</ToggleButton><ToggleButton value="assembly">Assembly</ToggleButton><ToggleButton value="unitType">Unit</ToggleButton></ToggleButtonGroup></Box>
      {!entrySource ? <Box sx={{ p: 2, textAlign: 'center', border: `1px dashed ${BORDER}`, borderRadius: 1.5 }}><Typography sx={{ color: MUTED, fontSize: 13 }}>Choose the cost source first. The next selection will show only compatible items.</Typography></Box> : entrySource === 'unitType' ? <Stack gap={1}><Typography sx={{ color: MUTED, fontSize: 12.5 }}>Create a new project unit. Add its building allocation and estimate rules after saving.</Typography><Stack direction={{ xs: 'column', sm: 'row' }} gap={1}><TextField label="Unit type name" value={newUnitTypeLabel} onChange={(event) => setNewUnitTypeLabel(event.target.value)} autoFocus fullWidth /><TextField label="Unit label" value={newUnitTypeUnit} onChange={(event) => setNewUnitTypeUnit(event.target.value)} fullWidth /></Stack>{unitTypes.some((unit) => unit.label.trim().toLowerCase() === newUnitTypeLabel.trim().toLowerCase()) && newUnitTypeLabel.trim() && <Typography sx={{ color: WARN, fontSize: 12, fontWeight: 800 }}>A unit type with this name already exists.</Typography>}</Stack> : <>
        <Autocomplete freeSolo options={projectGroupOptions} value={draft?.group || ''} onInputChange={(_, value) => setDraft((previous) => previous ? { ...previous, group: value } : previous)} onChange={(_, value) => setDraft((previous) => previous ? { ...previous, group: typeof value === 'string' ? value : value || '' } : previous)} renderInput={(params) => <TextField {...params} label="Estimate group" placeholder="Search or add a group" helperText="Select an existing group or enter a new one." />} fullWidth />
        {entrySource === 'material' && <Autocomplete options={materialGroups} value={materialGroupFilter || null} onChange={(_, value) => { setMaterialGroupFilter(value || ''); setDraft((previous) => previous ? { ...previous, workItemId: '' } : previous); }} renderInput={(params) => <TextField {...params} label="Material group" placeholder="Choose a material group" />} />}
        {(entrySource !== 'material' || materialGroupFilter) && <Stack direction="row" gap={.75} alignItems="flex-start"><Autocomplete sx={{ flex: 1 }} options={sourceWorkItemOptions} value={availableWorkItems.find((item) => item.id === draft?.workItemId) || null} getOptionLabel={(item) => item.name} filterOptions={(options, state) => { const query = state.inputValue.trim().toLowerCase(); if (!query) return options.slice(0, 30); return options.filter((item) => `${item.name} ${item.category} ${item.unit} ${sourceLabel(item)}`.toLowerCase().includes(query)); }} noOptionsText="No matching linked work item" onChange={(_, value) => setDraft((previous) => previous ? { ...previous, workItemId: value?.id || '', unit: value?.unit || previous.unit, subgroup: entrySource === 'material' && value && !addingScopeSubsection ? value.name : previous.subgroup, variety: entrySource === 'material' ? '' : previous.variety, quoteOptionId: entrySource === 'material' && value ? (rateByWorkItem.get(value.id)?.quoteOptionId || '') : previous.quoteOptionId } : previous)} renderOption={(props, item) => <Box component="li" {...props} sx={{ display: 'block !important', py: '8px !important' }}><Typography sx={{ fontSize: 14, fontWeight: 800 }}>{item.name}</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>{item.category} · {item.unit} · {sourceLabel(item)}</Typography></Box>} renderInput={(params) => <TextField {...params} label={entrySource === 'assembly' ? 'Assembly-linked work item' : `Select ${entrySource}-linked work item`} placeholder="Search compatible items" helperText="Only work items linked to the selected source type are shown." />} /><Box sx={{ pt: .6 }}><ActionIcon title="Create linked work item" onClick={openQuickWorkItem}><AddOutlinedIcon fontSize="inherit" /></ActionIcon></Box></Stack>}
      </>}
      {entrySource && draft?.workItemId && <><Stack direction={{ xs: 'column', md: 'row' }} gap={1}>{entrySource === 'material' ? <><TextField label="BOQ subsection" value={draft?.subgroup || ''} helperText={addingScopeSubsection ? 'Name this new subsection.' : 'Set automatically from the selected work item.'} InputProps={addingScopeSubsection ? undefined : { readOnly: true }} onChange={addingScopeSubsection ? (event) => setDraft((previous) => previous ? { ...previous, subgroup: event.target.value } : previous) : undefined} fullWidth /><Autocomplete freeSolo options={linkedMaterialVarieties} value={draft?.variety || ''} onInputChange={(_, value) => setDraft((previous) => { if (!previous) return previous; const setup = rateByWorkItem.get(previous.workItemId || ''); const material = setup?.sourceType === 'direct' ? materialItems.find((item) => item.id === setup.sourceId) : undefined; return { ...previous, variety: value, quoteOptionId: materialOptionIdForVariety(material, value) || setup?.quoteOptionId || '' }; })} onChange={(_, value) => setDraft((previous) => { if (!previous) return previous; const variety = typeof value === 'string' ? value : value || ''; const setup = rateByWorkItem.get(previous.workItemId || ''); const material = setup?.sourceType === 'direct' ? materialItems.find((item) => item.id === setup.sourceId) : undefined; return { ...previous, variety, quoteOptionId: materialOptionIdForVariety(material, variety) || setup?.quoteOptionId || '' }; })} renderInput={(params) => <TextField {...params} label="Material variety" placeholder={linkedMaterialVarieties.length ? 'Select a material variety' : 'No varieties available'} helperText={linkedMaterialVarieties.length ? 'The matching vendor rate option is selected automatically.' : 'This linked material has no saved varieties yet.'} />} fullWidth /></> : <TextField label="Subgroup (optional)" value={draft?.subgroup || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, subgroup: event.target.value } : previous)} fullWidth />}</Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1}><FormControl fullWidth><InputLabel>Estimate applies to</InputLabel><Select label="Estimate applies to" value={draft?.scopeType || 'project'} onChange={(event) => setDraft((previous) => previous ? { ...previous, scopeType: event.target.value as ProjectEstimateScopeItem['scopeType'] } : previous)}><MenuItem value="project">Project-wide</MenuItem><MenuItem value="common">Common area</MenuItem><MenuItem value="building">Specific building</MenuItem></Select></FormControl>{draft?.scopeType === 'building' && <Autocomplete freeSolo options={buildings} value={draft?.buildingName || ''} onInputChange={(_, value) => setDraft((previous) => previous ? { ...previous, buildingName: value } : previous)} renderInput={(params) => <TextField {...params} label="Building" />} />}</Stack>
      <FormControl fullWidth><InputLabel>Quantity calculation</InputLabel><Select label="Quantity calculation" value={draft?.quantityMethod || 'manual'} onChange={(event) => setDraft((previous) => previous ? { ...previous, quantityMethod: event.target.value as ProjectEstimateScopeItem['quantityMethod'], unit: event.target.value === 'areaInch' ? 'Sft' : event.target.value === 'volumeInch' ? 'Cft' : previous.unit } : previous)}><MenuItem value="manual">Manual quantity</MenuItem><MenuItem value="areaInch">Nos × length × breadth (inch to Sft)</MenuItem><MenuItem value="volumeInch">Nos × length × breadth × height (inch to Cft)</MenuItem></Select></FormControl>
      {draft?.quantityMethod === 'manual' || !draft?.quantityMethod ? <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}><TextField label={draft?.scopeType === 'unitType' ? 'Quantity per unit' : 'Quantity'} type="number" value={draft?.qty || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, qty: num(event.target.value) } : previous)} fullWidth inputProps={{ min: 0, step: 'any' }} /><TextField label="Unit" value={draft?.unit || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, unit: event.target.value } : previous)} fullWidth /></Stack> : <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}><TextField label="Nos" type="number" value={draft?.nos || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, nos: num(event.target.value) } : previous)} fullWidth inputProps={{ min: 0, step: 'any' }} /><TextField label="Length (inch)" type="number" value={draft?.length || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, length: num(event.target.value) } : previous)} fullWidth inputProps={{ min: 0, step: 'any' }} /><TextField label="Breadth (inch)" type="number" value={draft?.breadth || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, breadth: num(event.target.value) } : previous)} fullWidth inputProps={{ min: 0, step: 'any' }} />{draft?.quantityMethod === 'volumeInch' && <TextField label="Height (inch)" type="number" value={draft?.height || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, height: num(event.target.value) } : previous)} fullWidth inputProps={{ min: 0, step: 'any' }} />}</Stack>}
      {draft && <Box sx={{ px: 1.2, py: .9, bgcolor: '#fff7e8', borderRadius: 1 }}><Typography sx={{ color: GOLD_D, fontSize: 12, fontWeight: 900 }}>Calculated quantity: {fmtQty(calculatedQty(draft))} {draft.unit || (draft.quantityMethod === 'areaInch' ? 'Sft' : draft.quantityMethod === 'volumeInch' ? 'Cft' : '')}{draft.scopeType === 'unitType' ? ' across allocated units' : ''}</Typography></Box>}
      <TextField label="Notes" value={draft?.notes || ''} onChange={(event) => setDraft((previous) => previous ? { ...previous, notes: event.target.value } : previous)} multiline minRows={2} fullWidth /></>}
      {existingWorkItemLines.length > 0 && <Box sx={{ border: `1px solid ${duplicateScopeLine ? '#e6bf72' : BORDER}`, borderRadius: 1.5, overflow: 'hidden', bgcolor: duplicateScopeLine ? '#fff8e8' : '#faf8f5' }}><Box sx={{ px: 1.2, py: .85, borderBottom: `1px solid ${duplicateScopeLine ? '#f0d59e' : BORDER}` }}><Typography sx={{ color: duplicateScopeLine ? GOLD_D : DARK, fontSize: 12.5, fontWeight: 900 }}>{duplicateScopeLine ? 'This estimate line already exists in the same group and scope' : 'This work item is already used elsewhere in the project estimate'}</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>{duplicateScopeLine ? 'Edit the existing line instead of adding a duplicate.' : 'You may add it here only when this is a genuinely separate scope.'}</Typography></Box><Box sx={{ overflowX: 'auto' }}><Box component="table" sx={{ width: '100%', minWidth: 580, borderCollapse: 'collapse' }}><Box component="thead"><Box component="tr"><Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Code</Box><Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Group</Box><Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Scope</Box><Box component="th" sx={headTh}>Quantity</Box></Box></Box><Box component="tbody">{existingWorkItemLines.map((line) => <Box component="tr" key={line.id}><Box component="td" sx={{ p: .85, borderTop: `1px solid ${BORDER}`, color: GOLD_D, fontSize: 12, fontWeight: 900 }}>{line.lineCode || '—'}</Box><Box component="td" sx={{ p: .85, borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>{[line.group, line.subgroup, line.variety].filter(Boolean).join(' · ')}</Box><Box component="td" sx={{ p: .85, borderTop: `1px solid ${BORDER}`, fontSize: 12, color: MUTED }}>{scopeLabel(line)}</Box><Box component="td" sx={{ p: .85, borderTop: `1px solid ${BORDER}`, fontSize: 12, fontWeight: 800, textAlign: 'right' }}>{fmtQty(calculatedQty(line))} {line.unit}</Box></Box>)}</Box></Box></Box></Box>}</Stack></DialogContent><DialogActions><Button onClick={() => setDialogOpen(false)} sx={{ color: MUTED }}>Cancel</Button>{entrySource === 'unitType' ? <Button variant="contained" onClick={saveUnitTypeFromEstimate} disabled={!newUnitTypeLabel.trim() || unitTypes.some((unit) => unit.label.trim().toLowerCase() === newUnitTypeLabel.trim().toLowerCase())}>Save unit type</Button> : <Button variant="contained" onClick={saveDraft} disabled={!draft?.workItemId || !draft.group.trim() || !draft || duplicateScopeLine || (addingScopeSubsection && !(draft?.subgroup || '').trim())}>Save estimate line</Button>}</DialogActions></Dialog>
    <Dialog open={quickWorkDialogOpen} onClose={() => !quickSaving && setQuickWorkDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: POPUP_PAPER_SX }}><DialogTitle>Add linked work item</DialogTitle><DialogContent><Stack gap={1.4} sx={{ pt: 1 }}><Typography sx={{ color: MUTED, fontSize: 12.5 }}>This saves a reusable work item, links it to the selected {entrySource === 'assembly' ? 'assembly' : entrySource === 'labour' ? 'labour' : 'material'}, and enables it for this project.</Typography><TextField label="Work item name" value={quickWorkName} onChange={(event) => setQuickWorkName(event.target.value)} autoFocus fullWidth /><TextField label="Primary unit" value={quickWorkUnit} onChange={(event) => setQuickWorkUnit(event.target.value)} fullWidth /><Autocomplete options={quickSourceOptions} value={quickSourceOptions.find((item) => item.id === quickSourceId) || null} getOptionLabel={(item) => item.label} onChange={(_, value) => setQuickSourceId(value?.id || '')} renderInput={(params) => <TextField {...params} label={entrySource === 'assembly' ? 'Linked assembly' : entrySource === 'labour' ? 'Linked labour' : 'Linked material'} placeholder="Select the source" />} /></Stack></DialogContent><DialogActions><Button onClick={() => setQuickWorkDialogOpen(false)} disabled={quickSaving} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" onClick={saveQuickWorkItem} disabled={quickSaving || !quickWorkName.trim() || !quickWorkUnit.trim() || !quickSourceId}>{quickSaving ? 'Adding...' : 'Add work item'}</Button></DialogActions></Dialog>
    <Dialog open={!!sectionDialogUnitId} onClose={() => setSectionDialogUnitId(null)} fullWidth maxWidth="sm" PaperProps={{ sx: POPUP_PAPER_SX }}>
      <DialogTitle>Add unit section</DialogTitle>
      <DialogContent><Stack gap={1.25} sx={{ pt: 1 }}><Typography sx={{ color: MUTED, fontSize: 12.5 }}>Choose a saved section name or enter a new one. It will be available for estimate lines in this unit.</Typography><Autocomplete freeSolo options={unitSectionOptions} value={newUnitSection} onInputChange={(_, value) => setNewUnitSection(value)} onChange={(_, value) => setNewUnitSection(typeof value === 'string' ? value : value || '')} renderInput={(params) => <TextField {...params} label="Section" placeholder="Search or add Door, Window, Kitchen..." autoFocus />} /></Stack></DialogContent>
      <DialogActions><Button onClick={() => setSectionDialogUnitId(null)} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" disabled={!newUnitSection.trim()} onClick={() => { const label = newUnitSection.trim(); const unit = unitTypes.find((item) => item.id === sectionDialogUnitId); const exists = !!unit && [...(unit.sections || []), ...(unit.components || []).map((component) => component.group || '')].some((section) => section.trim().toLowerCase() === label.toLowerCase()); if (!unit || exists) { setSectionDialogUnitId(null); return; } onSaveUnitTypes(unitTypes.map((item) => item.id === unit.id ? { ...item, sections: [...(item.sections || []), label] } : item)); setSectionDialogUnitId(null); }}>Add section</Button></DialogActions>
    </Dialog>
    {ruleDialogTarget && <UnitRuleDialog
      title={ruleDialogTarget.component ? 'Edit estimate line' : `Add estimate line to ${ruleDialogTarget.section}`}
      unitLabel={unitTypes.find((unit) => unit.id === ruleDialogTarget.unitId)?.unit || 'unit'}
      workItems={availableWorkItems.filter((item) => usageIds.has(item.id))}
      directMaterialItems={materialItems}
      directAssemblies={assemblies}
      sourceForWorkItem={(item) => { const setup = rateByWorkItem.get(item.id); if (setup?.sourceType === 'assembly') return 'assembly'; return materialItems.find((material) => material.id === setup?.sourceId)?.group === 'labour' ? 'labour' : 'material'; }}
      varietyOptionsForWorkItem={(item) => { const sourceId = rateByWorkItem.get(item.id)?.sourceId; const material = materialItems.find((entry) => entry.id === sourceId); return Array.from(new Set([...(material?.varieties || []), ...(material?.quotes || []).flatMap((quote) => (quote.quoteOptions || []).map((option) => option.variety || option.name))].map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)); }}
      varietyHierarchyForWorkItem={rateVarietyHierarchyForWorkItem}
      missingLabourRates={materialItems.filter((material) => material.group === 'labour' && !availableWorkItems.some((item) => rateByWorkItem.get(item.id)?.sourceType === 'direct' && rateByWorkItem.get(item.id)?.sourceId === material.id)).map((material) => ({ id: material.id, label: material.materialName, unit: material.unit || 'Nos' }))}
      onRequestAddWorkItem={(source, sourceId, label, unit) => { setEntrySource(source); setQuickWorkName(label || ''); setQuickWorkUnit(unit || 'Sft'); setQuickSourceId(sourceId || ''); setQuickWorkDialogOpen(true); }}
      initialComponent={ruleDialogTarget.component}
      onClose={() => setRuleDialogTarget(null)}
      onSave={(component) => { onSaveUnitTypes(unitTypes.map((unit) => unit.id === ruleDialogTarget.unitId ? { ...unit, sections: (unit.sections || []).some((section) => section.trim().toLowerCase() === ruleDialogTarget.section.toLowerCase()) ? unit.sections : [...(unit.sections || []), ruleDialogTarget.section], components: ruleDialogTarget.component ? (unit.components || []).map((item) => item.id === ruleDialogTarget.component!.id ? { ...component, group: ruleDialogTarget.section } : item) : [...(unit.components || []), { ...component, group: ruleDialogTarget.section }] } : unit)); setRuleDialogTarget(null); }}
      onDelete={ruleDialogTarget.component ? () => { onSaveUnitTypes(unitTypes.map((unit) => unit.id === ruleDialogTarget.unitId ? { ...unit, components: (unit.components || []).filter((item) => item.id !== ruleDialogTarget.component!.id) } : unit)); setRuleDialogTarget(null); } : undefined}
    />}
    <Dialog open={!!editingUnitType} onClose={() => setEditingUnitType(null)} fullWidth maxWidth="md" PaperProps={{ sx: POPUP_PAPER_SX }}>
      <DialogTitle>Edit unit</DialogTitle>
      <DialogContent dividers>
        {editingUnitType && <Stack gap={1.4} sx={{ pt: .5 }}>
          <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Update this unit's identity and building allocation. Unit estimate lines are managed in the BOQ hierarchy.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <TextField label="Unit type name" value={editingUnitType.label} onChange={(event) => setEditingUnitType((previous) => previous ? { ...previous, label: event.target.value } : previous)} fullWidth autoFocus />
            <TextField label="Unit label" value={editingUnitType.unit} onChange={(event) => setEditingUnitType((previous) => previous ? { ...previous, unit: event.target.value } : previous)} fullWidth />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: .5 }}>
            <Box><Typography sx={{ fontWeight: 900, fontSize: 14 }}>Floor allocations</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>Add a floor band for each regular floor range or exception. Totals are calculated automatically.</Typography></Box>
            <ActionIcon title="Add floor allocation" onClick={() => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: [...(previous.buildingAllocations || []), { id: `allocation_${Date.now()}`, buildingName: '', count: 0, floorFrom: 0, floorTo: 0, unitsPerFloor: 0, notes: '' }] } : previous)}><AddOutlinedIcon fontSize="inherit" /></ActionIcon>
          </Stack>
          {(editingUnitType.buildingAllocations || []).length === 0 ? <Box sx={{ py: 2, textAlign: 'center', border: `1px dashed ${BORDER}`, borderRadius: 1.5 }}><Typography sx={{ color: MUTED, fontSize: 12.5 }}>No floor allocations yet.</Typography></Box> : <Stack gap={.8}>{(editingUnitType.buildingAllocations || []).map((allocation) => <Stack key={allocation.id} direction={{ xs: 'column', sm: 'row' }} gap={.75} alignItems={{ sm: 'center' }}>
            <Autocomplete freeSolo options={buildings} value={allocation.buildingName} onInputChange={(_, value) => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: (previous.buildingAllocations || []).map((item) => item.id === allocation.id ? { ...item, buildingName: value } : item) } : previous)} onChange={(_, value) => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: (previous.buildingAllocations || []).map((item) => item.id === allocation.id ? { ...item, buildingName: typeof value === 'string' ? value : value || '' } : item) } : previous)} renderInput={(params) => <TextField {...params} label="Building" placeholder="Select or enter building" />} sx={{ flex: 1.35 }} />
            <TextField label="From floor" type="number" value={allocation.floorFrom ?? ''} onChange={(event) => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: (previous.buildingAllocations || []).map((item) => item.id === allocation.id ? { ...item, floorFrom: num(event.target.value) } : item) } : previous)} inputProps={{ min: 0, step: 1 }} sx={{ width: { sm: 120 } }} />
            <TextField label="To floor" type="number" value={allocation.floorTo ?? ''} onChange={(event) => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: (previous.buildingAllocations || []).map((item) => item.id === allocation.id ? { ...item, floorTo: num(event.target.value) } : item) } : previous)} inputProps={{ min: 0, step: 1 }} sx={{ width: { sm: 120 } }} />
            <TextField label="Units / floor" type="number" value={(allocation.unitsPerFloor ?? allocation.count) || ''} onChange={(event) => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: (previous.buildingAllocations || []).map((item) => item.id === allocation.id ? { ...item, unitsPerFloor: num(event.target.value) } : item) } : previous)} inputProps={{ min: 0, step: 1 }} sx={{ width: { sm: 130 } }} />
            <Box sx={{ minWidth: 82, color: GOLD_D, fontSize: 12, fontWeight: 900, textAlign: 'right' }}>{fmtQty(unitAllocationCount(allocation))} total</Box>
            <TextField label="Notes" value={allocation.notes || ''} onChange={(event) => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: (previous.buildingAllocations || []).map((item) => item.id === allocation.id ? { ...item, notes: event.target.value } : item) } : previous)} sx={{ flex: 1 }} />
            <ActionIcon title="Remove allocation" danger onClick={() => setEditingUnitType((previous) => previous ? { ...previous, buildingAllocations: (previous.buildingAllocations || []).filter((item) => item.id !== allocation.id) } : previous)}><DeleteOutlineIcon fontSize="inherit" /></ActionIcon>
          </Stack>)}</Stack>}
        </Stack>}
      </DialogContent>
      <DialogActions><Button onClick={() => setEditingUnitType(null)} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" disabled={!editingUnitType?.label.trim()} onClick={() => { if (!editingUnitType?.label.trim()) return; onSaveUnitTypes([...unitTypes.filter((item) => item.id !== editingUnitType.id), { ...editingUnitType, label: editingUnitType.label.trim(), unit: editingUnitType.unit.trim() || 'Flat', buildingAllocations: (editingUnitType.buildingAllocations || []).map((allocation) => ({ ...allocation, buildingName: allocation.buildingName.trim(), count: unitAllocationCount(allocation), floorFrom: num(allocation.floorFrom), floorTo: num(allocation.floorTo), unitsPerFloor: num(allocation.unitsPerFloor), notes: (allocation.notes || '').trim() })).filter((allocation) => allocation.buildingName) }]); setEditingUnitType(null); }}>Save unit</Button></DialogActions>
    </Dialog>
    {subsectionOrderDialog && <ProjectEstimateSubsectionOrderDialog
      code={subsectionOrderDialog.code}
      label={subsectionOrderDialog.label}
      candidates={subsectionOrderDialog.candidates}
      onClose={() => setSubsectionOrderDialog(null)}
      onSave={async (orders) => {
        await Promise.all(orders.map(onSaveMaterialProcurementOrder));
        setSubsectionOrderDialog(null);
      }}
    />}
  </Stack>;
}

function ProjectEstimateSubsectionOrderDialog({
  code,
  label,
  candidates,
  onClose,
  onSave,
}: {
  code: string;
  label: string;
  candidates: ProjectEstimateOrderCandidate[];
  onClose: () => void;
  onSave: (orders: MaterialProcurementOrder[]) => Promise<void>;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(candidates.map((candidate) => candidate.line.id)));
  const [quantities, setQuantities] = useState<Record<string, number>>(() => Object.fromEntries(candidates.map((candidate) => [candidate.line.id, candidate.qty])));
  const [orderDate, setOrderDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [poNumber, setPoNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const selectedCandidates = candidates.filter((candidate) => selectedIds.has(candidate.line.id) && num(quantities[candidate.line.id]) > 0);
  const total = selectedCandidates.reduce((sum, candidate) => sum + num(quantities[candidate.line.id]) * candidate.rate, 0);
  const toggleCandidate = (id: string, checked: boolean) => setSelectedIds((previous) => {
    const next = new Set(previous);
    if (checked) next.add(id); else next.delete(id);
    return next;
  });
  const submit = async () => {
    if (!selectedCandidates.length || saving) return;
    setSaving(true);
    try {
      await onSave(selectedCandidates.map((candidate): MaterialProcurementOrder => {
        const option = selectedQuoteOption(candidate.quote, candidate.optionId);
        const boqRef = candidate.line.lineCode || [candidate.line.groupCode, candidate.line.subgroupCode].filter(Boolean).join('.');
        return {
          id: '',
          materialId: candidate.material.id,
          quoteOptionId: candidate.optionId,
          materialLabel: candidate.label,
          unit: candidate.unit,
          vendor: candidate.quote.vendor || '',
          brand: option?.brand || candidate.quote.brand || '',
          code: option?.code || candidate.quote.materialCode || '',
          quoteId: candidate.quote.id,
          rateSnapshot: candidate.rate,
          rateUnit: candidate.unit,
          rateEffectiveFrom: quoteEffectiveFrom(candidate.quote),
          scopeType: 'project',
          scopeId: candidate.line.id,
          scopeLabel: [boqRef, label].filter(Boolean).join(' · '),
          orderQty: num(quantities[candidate.line.id]),
          orderDate,
          poNumber: poNumber.trim(),
          notes: `Created from BOQ ${boqRef || label}`,
          receipts: [],
        };
      }));
    } finally {
      setSaving(false);
    }
  };
  return <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{ sx: POPUP_PAPER_SX }}>
    <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>Place order for {[code, label].filter(Boolean).join(' · ')}</DialogTitle>
    <DialogContent dividers>
      <Stack gap={1.25} sx={{ pt: .5 }}>
        <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Select the material lines to order and confirm the final quantity for each. Orders retain their BOQ reference and selected vendor rate.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField label="Order date" type="date" value={orderDate} onChange={(event) => setOrderDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="PO / reference number" value={poNumber} onChange={(event) => setPoNumber(event.target.value)} fullWidth />
        </Stack>
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
          {candidates.map((candidate, index) => {
            const selected = selectedIds.has(candidate.line.id);
            return <Stack key={candidate.line.id} direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }} sx={{ px: 1.25, py: 1, borderTop: index ? `1px solid ${BORDER}` : 0, bgcolor: selected ? '#fffaf0' : '#fff' }}>
              <Checkbox checked={selected} onChange={(event) => toggleCandidate(candidate.line.id, event.target.checked)} />
              <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontWeight: 800, fontSize: 13 }}>{candidate.label}</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>{candidate.quote.vendor || 'Vendor'} · ₹{fmtRate(candidate.rate)}/{candidate.unit}</Typography></Box>
              <TextField label={`Final qty (${candidate.unit})`} type="number" size="small" value={quantities[candidate.line.id] ?? ''} disabled={!selected} onChange={(event) => setQuantities((previous) => ({ ...previous, [candidate.line.id]: num(event.target.value) }))} inputProps={{ min: 0, step: 'any' }} sx={{ width: { xs: '100%', sm: 175 } }} />
            </Stack>;
          })}
        </Box>
        <Stack direction="row" justifyContent="space-between"><Typography sx={{ color: MUTED, fontSize: 12.5 }}>{selectedCandidates.length} material line{selectedCandidates.length === 1 ? '' : 's'} selected</Typography><Typography sx={{ color: GOLD_D, fontWeight: 900 }}>₹{fmtRate(total)}</Typography></Stack>
      </Stack>
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" startIcon={<ShoppingCartOutlinedIcon />} disabled={!selectedCandidates.length || saving} onClick={submit}>{saving ? 'Creating orders...' : `Create ${selectedCandidates.length} order${selectedCandidates.length === 1 ? '' : 's'}`}</Button></DialogActions>
  </Dialog>;
}

function ProjectCostPanel({
  unitTypes,
  workItems,
  rates,
  materialItems,
  assemblies,
  projectScopeItems,
}: {
  unitTypes: UnitTypeMaster[];
  workItems: ItemWorkMaster[];
  rates: WorkRateSetup[];
  materialItems: MaterialRateItem[];
  assemblies: MaterialAssembly[];
  projectScopeItems: ProjectEstimateScopeItem[];
}) {
  const [view, setView] = useState<'overview' | 'units' | 'buildings' | 'categories' | 'drivers' | 'readiness'>('overview');
  const [costBasis, setCostBasis] = useState<'selected' | 'tentative'>('tentative');
  const analysis = useMemo(() => {
    const rateByWorkItem = new Map(rates.map((rate) => [rate.workItemId, rate]));
    const lines: ProjectCostLine[] = [];
    const unitRows = unitTypes.map((unit) => {
      const estimate = unitTypeEstimateSummary(unit, workItems, rates, materialItems, assemblies, costBasis);
      const allocated = (unit.buildingAllocations || []).reduce((sum, allocation) => sum + num(allocation.count), 0);
      return { unit, allocated, estimate, total: estimate.total * allocated };
    });
    for (const unitRow of unitRows) {
      for (const allocation of unitRow.unit.buildingAllocations || []) {
        const count = num(allocation.count);
        if (count <= 0) continue;
        for (const component of unitRow.unit.components || []) {
          const workItem = resolveComponentWorkItem(component, workItems);
          if (!component.sourceId && !workItem) continue;
          if (workItem?.procurementMode === 'oneTime') continue;
          const setup = componentRateSetup(component, workItems, rates);
          const resolved = applyLabourLeadRate(
            resolveUnitComponentCostingRate(setup, component, materialItems, assemblies, costBasis),
            labourLeadMultiplier(component, setup, materialItems, [allocation]),
          );
          const qtyPerUnit = setup.basis === 'secondary'
            ? num(component.qtyPerUnit) * num(component.areaPerUnit || workItem?.secondaryMultiplier)
            : num(component.qtyPerUnit);
          const linkedMaterial = setup.sourceType === 'direct' ? materialItems.find((item) => item.id === setup.sourceId) : undefined;
          const kind: ProjectCostLine['kind'] = setup.sourceType === 'direct'
            ? linkedMaterial?.group === 'labour' ? 'Labour' : 'Material'
            : setup.sourceType === 'assembly' ? 'Assembly' : 'Manual';
          const category = component.assemblyType || workItem?.category || (setup.sourceType === 'direct' && linkedMaterial ? materialGroupLabel(linkedMaterial.group) : kind);
          const pending = resolved.missing || !resolved.rate ? (resolved.pendingCount || 1) : (resolved.pendingCount || 0);
          lines.push({
            id: `${unitRow.unit.id}_${allocation.id}_${component.id}`,
            workItemId: workItem?.id || `source_${component.sourceType || 'unknown'}_${component.sourceId || component.id}`,
            label: component.variety || component.assemblyType || workItem?.name || component.label || 'Estimate line',
            category,
            kind,
            unit: setup.basis === 'secondary' ? (component.areaUnit || workItem?.secondaryUnit || 'unit') : (component.unit || workItem?.unit || 'unit'),
            qty: qtyPerUnit * count,
            rate: resolved.rate,
            amount: qtyPerUnit * count * resolved.rate,
            pending,
            rateStatus: resolved.tentative ? 'tentative' : (pending ? 'pending' : 'selected'),
            source: resolved.source,
            unitTypeLabel: unitRow.unit.label,
            buildingLabel: allocation.buildingName,
          });
        }
      }
    }
    for (const scopeItem of projectScopeItems) {
      // A Unit Type is a live BOQ reference, not an additional project-scope line.
      if (scopeItem.scopeType === 'unitType') continue;
      const workItem = workItems.find((item) => item.id === scopeItem.workItemId);
      const scopeQty = projectScopeCalculatedQty(scopeItem, unitTypes);
      if (scopeQty <= 0) continue;
      const baseSetup = scopeItem.sourceId && scopeItem.sourceType
        ? { workItemId: scopeItem.workItemId || `scope_${scopeItem.id}`, sourceType: scopeItem.sourceType === 'assembly' ? 'assembly' as const : 'direct' as const, sourceId: scopeItem.sourceId, quoteOptionId: scopeItem.quoteOptionId || '', basis: 'primary' as const }
        : workItem ? rateByWorkItem.get(workItem.id) || { workItemId: workItem.id, sourceType: 'none', basis: 'primary' } as WorkRateSetup : { workItemId: `scope_${scopeItem.id}`, sourceType: 'none', basis: 'primary' } as WorkRateSetup;
      const linkedMaterial = baseSetup.sourceType === 'direct' ? materialItems.find((item) => item.id === baseSetup.sourceId) : undefined;
      const optionId = scopeItem.quoteOptionId || materialOptionIdForVariety(linkedMaterial, scopeItem.variety || scopeItem.subgroup) || baseSetup.quoteOptionId;
      const setup = optionId ? { ...baseSetup, quoteOptionId: optionId } : baseSetup;
      const resolved = scopeItem.sourceId
        ? resolveUnitComponentCostingRate(setup, { id: scopeItem.id, sourceType: scopeItem.sourceType, sourceId: scopeItem.sourceId, quoteOptionId: scopeItem.quoteOptionId, label: scopeItem.label || '', assemblyType: scopeItem.assemblyType, variety: scopeItem.variety, qtyPerUnit: scopeQty, unit: scopeItem.unit || 'Nos' }, materialItems, assemblies, costBasis)
        : resolveCostingRate(setup, materialItems, assemblies, costBasis);
      const pending = resolved.missing || !resolved.rate ? (resolved.pendingCount || 1) : (resolved.pendingCount || 0);
      lines.push({
        id: `scope_${scopeItem.id}`,
        workItemId: workItem?.id || `scope_${scopeItem.id}`,
        label: scopeItem.label || workItem?.name || linkedMaterial?.materialName || 'Project scope item',
        category: scopeItem.group || workItem?.category || (linkedMaterial ? materialGroupLabel(linkedMaterial.group) : 'Project scope'),
        kind: baseSetup.sourceType === 'assembly' ? 'Assembly' : linkedMaterial?.group === 'labour' ? 'Labour' : 'Material',
        unit: scopeItem.unit || workItem?.unit || linkedMaterial?.unit || 'unit',
        qty: scopeQty,
        rate: resolved.rate,
        amount: scopeQty * resolved.rate,
        pending,
        rateStatus: resolved.tentative ? 'tentative' : (pending ? 'pending' : 'selected'),
        source: resolved.source,
        buildingLabel: scopeItem.scopeType === 'building' ? scopeItem.buildingName : undefined,
        oneTime: true,
      });
    }
    const total = lines.reduce((sum, line) => sum + line.amount, 0);
    const pending = lines.reduce((sum, line) => sum + line.pending, 0);
    const byCategory = Array.from(lines.reduce((groups, line) => {
      const current = groups.get(line.category) || { label: line.category, amount: 0, pending: 0, kind: line.kind };
      current.amount += line.amount;
      current.pending += line.pending;
      groups.set(line.category, current);
      return groups;
    }, new Map<string, { label: string; amount: number; pending: number; kind: ProjectCostLine['kind'] }>()).values()).sort((a, b) => b.amount - a.amount);
    const byBuilding = Array.from(lines.filter((line) => line.buildingLabel).reduce((groups, line) => {
      const key = line.buildingLabel || 'Building';
      const current = groups.get(key) || { label: key, amount: 0, pending: 0, units: 0 };
      current.amount += line.amount;
      current.pending += line.pending;
      groups.set(key, current);
      return groups;
    }, new Map<string, { label: string; amount: number; pending: number; units: number }>()).values()).map((row) => ({
      ...row,
      units: unitTypes.reduce((sum, unit) => sum + (unit.buildingAllocations || []).filter((allocation) => allocation.buildingName === row.label).reduce((allocationSum, allocation) => allocationSum + num(allocation.count), 0), 0),
    })).sort((a, b) => b.amount - a.amount);
    const byWorkItem = Array.from(lines.reduce((groups, line) => {
      const current = groups.get(line.workItemId) || {
        label: line.label,
        category: line.category,
        kind: line.kind,
        source: line.source,
        unit: line.unit,
        qty: 0,
        amount: 0,
        pending: 0,
        tentative: false,
        oneTime: !!line.oneTime,
      };
      current.qty += line.qty;
      current.amount += line.amount;
      current.pending += line.pending;
      current.tentative = current.tentative || line.rateStatus === 'tentative';
      groups.set(line.workItemId, current);
      return groups;
    }, new Map<string, { label: string; category: string; kind: ProjectCostLine['kind']; source: string; unit: string; qty: number; amount: number; pending: number; tentative: boolean; oneTime: boolean }>()).values()).map((row) => ({
      ...row,
      rate: row.qty ? row.amount / row.qty : 0,
    })).sort((a, b) => b.amount - a.amount);
    return { lines, unitRows, total, pending, byCategory, byBuilding, byWorkItem, oneTimeTotal: lines.filter((line) => line.oneTime).reduce((sum, line) => sum + line.amount, 0) };
  }, [unitTypes, workItems, rates, materialItems, assemblies, projectScopeItems, costBasis]);
  const totalAllocated = analysis.unitRows.reduce((sum, row) => sum + row.allocated, 0);
  const readiness = analysis.lines.filter((line) => line.pending > 0).sort((a, b) => b.amount - a.amount);
  const tentativeLines = analysis.lines.filter((line) => line.rateStatus === 'tentative');
  const maxCategoryAmount = analysis.byCategory[0]?.amount || 1;
  return <Stack gap={1.5}>
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={1.2} alignItems={{ lg: 'center' }} sx={{ p: 1.5, bgcolor: PANEL2 }}>
        <Box sx={{ flex: 1 }}><Typography sx={{ color: DARK, fontWeight: 900 }}>Project cost</Typography><Typography sx={{ color: MUTED, fontSize: 12 }}>Unit estimates are multiplied by project allocations. Common and project-wide scope items are added once and remain separate from unit quantities.</Typography></Box>
        <ToggleButtonGroup exclusive size="small" value={costBasis} onChange={(_, value) => value && setCostBasis(value)} sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1 } }}>
          <ToggleButton value="selected">Selected rates only</ToggleButton><ToggleButton value="tentative">Include default rates</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, value) => value && setView(value)} sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1 } }}>
          <ToggleButton value="overview">Overview</ToggleButton><ToggleButton value="units">Unit types</ToggleButton><ToggleButton value="buildings">Buildings</ToggleButton><ToggleButton value="categories">Breakdown</ToggleButton><ToggleButton value="drivers">Work items</ToggleButton><ToggleButton value="readiness">Rate readiness</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, borderTop: `1px solid ${BORDER}` }}>
        <ProjectCostMetric label={costBasis === 'tentative' ? 'Tentative estimate' : 'Selected-rate estimate'} value={`₹${fmtRate(analysis.total)}`} color={analysis.pending ? GOLD_D : '#3c6b4b'} />
        <ProjectCostMetric label="Allocated units" value={fmtQty(totalAllocated)} />
        <ProjectCostMetric label="Project scope cost" value={`₹${fmtRate(analysis.oneTimeTotal)}`} color={GOLD_D} />
        <ProjectCostMetric label="Rate readiness" value={analysis.pending ? `${analysis.pending} pending` : tentativeLines.length ? `${tentativeLines.length} default` : 'Ready'} color={analysis.pending ? WARN : tentativeLines.length ? GOLD_D : '#3c6b4b'} />
      </Box>
    </Box>

    {view === 'overview' && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.15fr .85fr' }, gap: 1.5 }}>
      <CostPanel title="Cost by category" subtitle="Focus negotiation and design review on the largest shares.">
        <Stack gap={1}>{analysis.byCategory.slice(0, 8).map((row) => <Box key={row.label}><Stack direction="row" justifyContent="space-between" gap={1}><Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{row.label}</Typography><Typography sx={{ color: GOLD_D, fontSize: 12.5, fontWeight: 900 }}>₹{fmtRate(row.amount)} · {analysis.total ? fmtRate(row.amount / analysis.total * 100) : 0}%</Typography></Stack><Box sx={{ height: 7, mt: .45, bgcolor: '#eeeae3', borderRadius: 1 }}><Box sx={{ width: `${Math.min(100, row.amount / maxCategoryAmount * 100)}%`, height: '100%', bgcolor: GOLD, borderRadius: 1 }} /></Box></Box>)}</Stack>
      </CostPanel>
      <CostPanel title="Rate status" subtitle="Selected vendor rates always take priority. Default rates are for tentative costing only.">
        <Stack gap={1}><Typography sx={{ color: analysis.pending ? WARN : '#3c6b4b', fontSize: 24, fontWeight: 900 }}>{analysis.pending ? `${analysis.pending} pending` : 'All rates priced'}</Typography><Typography sx={{ color: MUTED, fontSize: 12.5 }}>{tentativeLines.length ? `${tentativeLines.length} line(s) use a fallback quote. An explicit default is used first; otherwise the highest valid quote is used.` : analysis.pending ? 'Open Rate readiness to see the unit type, building, and work item affected.' : 'The current estimate uses selected vendor rates.'}</Typography><Typography sx={{ color: GOLD_D, fontWeight: 900, fontSize: 13 }}>{costBasis === 'tentative' ? 'Tentative' : 'Selected-rate'} estimate: ₹{fmtRate(analysis.total)}</Typography></Stack>
      </CostPanel>
    </Box>}

    {view === 'units' && <CostPanel title="Cost by unit type" subtitle="Rate per unit multiplied by the allocated project quantity."><CostGrid headers={['Unit type', 'Allocated', 'Rate / unit', 'Total', 'Status']} rows={analysis.unitRows.map((row) => [row.unit.label || 'Unit type', `${fmtQty(row.allocated)} ${row.unit.unit || 'unit'}`, row.estimate.total ? `₹${fmtRate(row.estimate.total)}` : '—', `₹${fmtRate(row.total)}`, row.estimate.pending ? `${row.estimate.pending} pending` : 'Ready'])} /></CostPanel>}
    {view === 'buildings' && <CostPanel title="Cost by building" subtitle="Derived from allocated unit types and building-specific project scope items."><CostGrid headers={['Building', 'Allocated units', 'Estimated cost', 'Rate status']} rows={analysis.byBuilding.map((row) => [row.label, fmtQty(row.units), `₹${fmtRate(row.amount)}`, row.pending ? `${row.pending} pending` : 'Ready'])} empty="Assign buildings to unit types or add building-specific project scope items." /></CostPanel>}
    {view === 'categories' && <CostPanel title="Cost breakdown" subtitle="Grouped by the Item / Work Master category. Assemblies remain rate sources, not cost categories."><CostGrid headers={['Work category', 'Rate source', 'Estimated cost', 'Share', 'Status']} rows={analysis.byCategory.map((row) => [row.label, row.kind, `₹${fmtRate(row.amount)}`, analysis.total ? `${fmtRate(row.amount / analysis.total * 100)}%` : '—', row.pending ? `${row.pending} pending` : 'Ready'])} /></CostPanel>}
    {view === 'drivers' && <CostPanel title="Work item costing" subtitle="Each Item / Work Master is priced from its selected or tentative direct-material, labour, manual, or assembly source."><CostGrid headers={['Work item', 'Category', 'Rate source', 'Quantity', 'Rate', 'Total']} rows={analysis.byWorkItem.map((row) => [row.label, row.category, row.source, `${fmtQty(row.qty)} ${row.unit}`, row.rate ? `₹${fmtRate(row.rate)}/${row.unit}${row.tentative ? ' · tentative' : ''}` : 'Rate pending', `₹${fmtRate(row.amount)}`])} /></CostPanel>}
    {view === 'readiness' && <CostPanel title="Rate readiness" subtitle="Default costing rates help planning, but do not replace selected vendor rates for procurement."><CostGrid headers={['Item', 'Scope', 'Costing status', 'Amount']} rows={[...readiness.map((line) => [line.label, line.oneTime ? 'One-time project item' : `${line.unitTypeLabel || 'Unit type'} · ${line.buildingLabel || 'Building'}`, `${line.pending} rate pending`, line.amount ? `₹${fmtRate(line.amount)}` : 'No priced amount']), ...tentativeLines.map((line) => [line.label, line.oneTime ? 'One-time project item' : `${line.unitTypeLabel || 'Unit type'} · ${line.buildingLabel || 'Building'}`, 'Default costing rate', `₹${fmtRate(line.amount)}`])]} empty="All linked rates are selected." /></CostPanel>}
  </Stack>;
}

function ProjectCostMetric({ label, value, color = DARK }: { label: string; value: string; color?: string }) {
  return <Box sx={{ p: 1.2, borderRight: { lg: `1px solid ${BORDER}` }, '&:last-child': { borderRight: 0 } }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.45px' }}>{label}</Typography><Typography sx={{ color, fontWeight: 900, fontSize: 18 }}>{value}</Typography></Box>;
}

function CostPanel({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}><Box sx={{ p: 1.35, bgcolor: '#faf8f5', borderBottom: `1px solid ${BORDER}` }}><Typography sx={{ color: DARK, fontWeight: 900 }}>{title}</Typography><Typography sx={{ color: MUTED, fontSize: 12 }}>{subtitle}</Typography></Box><Box sx={{ p: 1.35 }}>{children}</Box></Box>;
}

function CostGrid({ headers, rows, empty = 'No cost data is available yet.' }: { headers: string[]; rows: string[][]; empty?: string }) {
  if (!rows.length) return <Typography sx={{ color: MUTED, fontSize: 12.5 }}>{empty}</Typography>;
  return <Box sx={{ overflowX: 'auto' }}><Box sx={{ minWidth: 650 }}><Box sx={{ display: 'grid', gridTemplateColumns: `minmax(180px, 1.5fr) repeat(${Math.max(1, headers.length - 1)}, minmax(115px, 1fr))`, gap: 1, px: 1, py: .75, bgcolor: PANEL2 }}>{headers.map((header, index) => <Typography key={header} sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.4px', textAlign: index ? 'right' : 'left' }}>{header}</Typography>)}</Box>{rows.map((row, rowIndex) => <Box key={`${row[0]}_${rowIndex}`} sx={{ display: 'grid', gridTemplateColumns: `minmax(180px, 1.5fr) repeat(${Math.max(1, headers.length - 1)}, minmax(115px, 1fr))`, gap: 1, px: 1, py: 1, borderTop: `1px solid ${BORDER}` }}>{row.map((cell, index) => <Typography key={`${cell}_${index}`} sx={{ color: index ? MUTED : DARK, fontSize: 12.5, fontWeight: index ? 600 : 800, textAlign: index ? 'right' : 'left' }}>{cell}</Typography>)}</Box>)}</Box></Box>;
}

function UnitRuleDialog({
  title,
  unitLabel,
  workItems,
  sourceForWorkItem,
  varietyOptionsForWorkItem,
  varietyHierarchyForWorkItem,
  directMaterialItems,
  directAssemblies,
  quantityLabel,
  showSecondary = true,
  missingLabourRates = [],
  onRequestAddWorkItem,
  sourceUsageMarker,
  initialComponent,
  onClose,
  onSave,
  onDelete,
}: {
  title: string;
  unitLabel: string;
  workItems: ItemWorkMaster[];
  sourceForWorkItem?: (item: ItemWorkMaster) => 'material' | 'labour' | 'assembly';
  varietyOptionsForWorkItem?: (item: ItemWorkMaster) => string[];
  varietyHierarchyForWorkItem?: (item: ItemWorkMaster) => { types: string[]; varietiesForType: (type: string) => string[] };
  directMaterialItems?: MaterialRateItem[];
  directAssemblies?: MaterialAssembly[];
  quantityLabel?: string;
  showSecondary?: boolean;
  missingLabourRates?: { id: string; label: string; unit: string }[];
  onRequestAddWorkItem?: (source: 'material' | 'labour' | 'assembly', sourceId?: string, label?: string, unit?: string) => void;
  sourceUsageMarker?: (candidate: EstimateSourceUsageCandidate) => EstimateSourceUsageMarker | null;
  initialComponent?: QuantityProfileComponent;
  onClose: () => void;
  onSave: (component: QuantityProfileComponent) => void;
  onDelete?: () => void;
}) {
  const [workItem, setWorkItem] = useState<ItemWorkMaster | null>(() => workItems.find((item) => item.id === initialComponent?.workItemId) || null);
  const [qty, setQty] = useState<number>(() => num(initialComponent?.qtyPerUnit));
  const [assemblyType, setAssemblyType] = useState(() => initialComponent?.assemblyType || '');
  const [variety, setVariety] = useState(() => initialComponent?.variety === 'Base Rate' ? '' : initialComponent?.variety || '');
  const [leadEnabled, setLeadEnabled] = useState(() => !!initialComponent?.leadEnabled);
  const [leadType, setLeadType] = useState<'perFloor' | 'fixed'>(() => initialComponent?.leadType || 'perFloor');
  const [leadPercent, setLeadPercent] = useState<number>(() => num(initialComponent?.leadPercent));
  const [override, setOverride] = useState(() => !!initialComponent?.secondaryOverride);
  const [overrideSize, setOverrideSize] = useState<number>(() => num(initialComponent?.areaPerUnit));
  const [lineSource, setLineSource] = useState<'material' | 'labour' | 'assembly'>(() => {
    const existing = workItems.find((item) => item.id === initialComponent?.workItemId);
    return existing && sourceForWorkItem ? sourceForWorkItem(existing) : 'material';
  });
  const compatibleWorkItems = sourceForWorkItem ? workItems.filter((item) => {
    const source = sourceForWorkItem(item);
    return source === lineSource;
  }) : workItems;
  const varietyHierarchy = workItem && varietyHierarchyForWorkItem ? varietyHierarchyForWorkItem(workItem) : { types: [], varietiesForType: () => [] };
  const hasVarietyTypes = varietyHierarchy.types.length > 0;
  const rawVarietyOptions = hasVarietyTypes
    ? (assemblyType ? varietyHierarchy.varietiesForType(assemblyType) : [])
    : (workItem && varietyOptionsForWorkItem ? varietyOptionsForWorkItem(workItem) : []);
  const varietyOptions = rawVarietyOptions.filter((option) => option.trim().toLowerCase() !== 'base rate');
  const hasActualVarieties = varietyOptions.length > 0;
  const canOverride = !!workItem && !!(workItem.secondaryUnitRequired ?? workItem.trackArea);
  const defaultSize = workItem ? num(workItem.secondaryMultiplier) : 0;
  const secondaryUnit = workItem?.secondaryUnit || workItem?.areaUnit || 'Sft';
  const saveDisabled = !workItem || num(qty) <= 0 || (hasVarietyTypes && !assemblyType) || (override && num(overrideSize) <= 0);
  if (directMaterialItems && directAssemblies) {
    return <DirectUnitRuleDialog
      title={title}
      unitLabel={unitLabel}
      materialItems={directMaterialItems}
      assemblies={directAssemblies}
      quantityLabel={quantityLabel}
      showSecondary={showSecondary}
      initialComponent={initialComponent}
      sourceUsageMarker={sourceUsageMarker}
      onClose={onClose}
      onSave={onSave}
      onDelete={onDelete}
    />;
  }
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: POPUP_PAPER_SX }}>
      <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 1 }}>
          {sourceForWorkItem && <Box sx={{ p: 1.2, border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#faf8f5' }}><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase', mb: .8 }}>What does this estimate line include?</Typography><ToggleButtonGroup exclusive value={lineSource} onChange={(_, value) => { if (!value) return; setLineSource(value); setWorkItem(null); setOverride(false); setOverrideSize(0); }} fullWidth sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, py: .7 } }}><ToggleButton value="material">Material</ToggleButton><ToggleButton value="labour">Labour</ToggleButton><ToggleButton value="assembly">Assembly</ToggleButton></ToggleButtonGroup></Box>}
          <Stack direction="row" gap={.75} alignItems="flex-start"><Autocomplete
            options={compatibleWorkItems}
            value={workItem}
            getOptionLabel={(option) => option?.name || ''}
            groupBy={lineSource === 'assembly' && sourceForWorkItem ? () => 'Assemblies' : undefined}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={sourceForWorkItem ? 'No compatible work items in this source' : 'Tick work items for this project first'}
            onChange={(_, value) => {
              setWorkItem(value);
              setAssemblyType('');
              setVariety('');
              setOverride(false);
              setOverrideSize(num(value?.secondaryMultiplier));
            }}
            renderInput={(params) => <TextField {...params} label="Work item" autoFocus />}
            sx={{ flex: 1 }}
          />{onRequestAddWorkItem && <Box sx={{ pt: .55 }}><ActionIcon title={`Add ${lineSource === 'assembly' ? 'assembly' : lineSource} work item`} onClick={() => onRequestAddWorkItem(lineSource)}><AddOutlinedIcon fontSize="inherit" /></ActionIcon></Box>}</Stack>
          {lineSource === 'labour' && <Box sx={{ p: 1.25, border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#faf8f5' }}><FormControlLabel sx={{ m: 0 }} control={<Checkbox checked={leadEnabled} onChange={(event) => setLeadEnabled(event.target.checked)} />} label={<Box><Typography sx={{ fontSize: 13, fontWeight: 800 }}>Apply labour lead</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>Add lead above the selected labour rate for this estimate line.</Typography></Box>} />{leadEnabled && <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mt: 1 }}><ToggleButtonGroup exclusive value={leadType} onChange={(_, value) => value && setLeadType(value)} size="small"><ToggleButton value="perFloor" sx={{ textTransform: 'none' }}>Per floor</ToggleButton><ToggleButton value="fixed" sx={{ textTransform: 'none' }}>Fixed</ToggleButton></ToggleButtonGroup><TextField label={leadType === 'perFloor' ? 'Lead % per floor' : 'Fixed lead %'} value={leadPercent || ''} type="number" onChange={(event) => setLeadPercent(num(event.target.value))} inputProps={{ min: 0, step: .01 }} sx={{ minWidth: 190 }} /></Stack>}</Box>}
          {lineSource === 'labour' && missingLabourRates.length > 0 && <Box sx={{ p: 1, border: `1px dashed ${BORDER}`, borderRadius: 1.25 }}><Typography sx={{ color: MUTED, fontSize: 11.5, mb: .6 }}>Labour rates without a linked work item</Typography><Stack direction="row" gap={.6} flexWrap="wrap">{missingLabourRates.map((rate) => <Button key={rate.id} size="small" variant="outlined" onClick={() => onRequestAddWorkItem?.('labour', rate.id, rate.label, rate.unit)} sx={{ borderColor: BORDER, color: GOLD_D, textTransform: 'none', fontSize: 11 }}>{`Add ${rate.label}`}</Button>)}</Stack></Box>}
          {hasVarietyTypes && <Autocomplete
            options={varietyHierarchy.types}
            value={assemblyType}
            forcePopupIcon
            openOnFocus
            onChange={(_, value) => { setAssemblyType(value || ''); setVariety(''); }}
            disabled={!workItem}
            renderInput={(params) => <TextField {...params} label="Work category" placeholder={workItem ? 'Select category' : 'Choose a work item first'} helperText="Choose the first level before selecting its variety." />}
          />}
          {(!hasVarietyTypes || !assemblyType || hasActualVarieties) && <Autocomplete
            freeSolo
            forcePopupIcon
            openOnFocus
            options={varietyOptions}
            value={variety}
            onInputChange={(_, value) => setVariety(value)}
            onChange={(_, value) => setVariety(typeof value === 'string' ? value : value || '')}
            disabled={!workItem || (hasVarietyTypes && !assemblyType)}
            renderInput={(params) => <TextField {...params} label="Variety (optional)" placeholder={hasVarietyTypes && !assemblyType ? 'Choose a category first' : workItem ? 'Select or add a variety' : 'Choose a work item first'} helperText={hasVarietyTypes ? 'Select a saved variety for the chosen category, or enter a new one.' : workItem ? 'Select a saved variety or enter a new one. Leave blank for Standard.' : 'Choose a work item first.'} />}
          />}
          {hasVarietyTypes && assemblyType && !hasActualVarieties && <Typography sx={{ color: MUTED, fontSize: 12 }}>This category has no varieties. {assemblyType} will be used for this estimate line.</Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <TextField
              label={`Qty per ${unitLabel || 'unit'}`}
              value={qty || ''}
              type="number"
              fullWidth
              onChange={(e) => setQty(num(e.target.value))}
            />
            <Box sx={{ minWidth: 120, p: 1.4, border: `1px solid ${BORDER}`, borderRadius: 1, color: DARK, fontWeight: 700 }}>
              {workItem?.unit || '-'}
            </Box>
          </Stack>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.25 }}>
            <Typography sx={{ fontSize: 12, color: FAINT, textTransform: 'uppercase', fontWeight: 800, letterSpacing: '.5px' }}>
              Secondary unit
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 0.75 }}>
              <Typography sx={{ flex: 1, color: MUTED }}>
                {workItem ? componentSecondaryText(componentFromWorkItem(workItem, 'preview'), workItem) : '-'}
              </Typography>
              {canOverride && (
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Checkbox checked={override} onChange={(e) => {
                    setOverride(e.target.checked);
                    setOverrideSize(e.target.checked ? (overrideSize || defaultSize) : defaultSize);
                  }} />}
                  label="Override size"
                />
              )}
            </Stack>
            {canOverride && override && (
              <TextField
                label={`Size (${secondaryUnit})`}
                value={overrideSize || ''}
                type="number"
                size="small"
                sx={{ mt: 1, maxWidth: 180 }}
                onChange={(e) => setOverrideSize(num(e.target.value))}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onDelete && <Button onClick={onDelete} color="error">Delete line</Button>}
        <Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saveDisabled}
          onClick={() => workItem && onSave({ ...componentFromRuleInput(workItem, qty, override, overrideSize), id: initialComponent?.id || `rule_${Date.now()}`, group: initialComponent?.group, assemblyType: assemblyType.trim(), variety: hasVarietyTypes && !hasActualVarieties ? assemblyType.trim() : variety.trim(), leadEnabled: lineSource === 'labour' && leadEnabled, leadType: lineSource === 'labour' && leadEnabled ? leadType : undefined, leadPercent: lineSource === 'labour' && leadEnabled ? leadPercent : undefined })}
          sx={{ bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D } }}
        >
          {initialComponent ? 'Save estimate line' : 'Add estimate line'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DirectUnitRuleDialog({
  title,
  unitLabel,
  materialItems,
  assemblies,
  quantityLabel,
  showSecondary = true,
  initialComponent,
  sourceUsageMarker,
  onClose,
  onSave,
  onDelete,
}: {
  title: string;
  unitLabel: string;
  materialItems: MaterialRateItem[];
  assemblies: MaterialAssembly[];
  quantityLabel?: string;
  showSecondary?: boolean;
  initialComponent?: QuantityProfileComponent;
  sourceUsageMarker?: (candidate: EstimateSourceUsageCandidate) => EstimateSourceUsageMarker | null;
  onClose: () => void;
  onSave: (component: QuantityProfileComponent) => void;
  onDelete?: () => void;
}) {
  const initialSource = initialComponent?.sourceType || 'material';
  const [lineSource, setLineSource] = useState<'material' | 'labour' | 'assembly'>(initialSource);
  const [sourceId, setSourceId] = useState(initialComponent?.sourceId || '');
  const [quoteOptionId, setQuoteOptionId] = useState(initialComponent?.quoteOptionId || '');
  const [qty, setQty] = useState(num(initialComponent?.qtyPerUnit));
  const [category, setCategory] = useState(initialComponent?.assemblyType || '');
  const [variety, setVariety] = useState(initialComponent?.variety || '');
  const [leadEnabled, setLeadEnabled] = useState(!!initialComponent?.leadEnabled);
  const [leadType, setLeadType] = useState<'perFloor' | 'fixed'>(initialComponent?.leadType || 'perFloor');
  const [leadPercent, setLeadPercent] = useState(num(initialComponent?.leadPercent));
  type EstimateSourcePick =
    | { key: string; sourceType: 'material'; item: MaterialRateItem; sourceId: string; quoteOptionId: string; assemblyType: string; variety: string; label: string; unit: string }
    | { key: string; sourceType: 'labour'; item: MaterialRateItem; sourceId: string; quoteOptionId: string; assemblyType: string; variety: string; label: string; unit: string }
    | { key: string; sourceType: 'assembly'; item: MaterialAssembly; sourceId: string; label: string; unit: string };
  const sourcePicks: EstimateSourcePick[] = useMemo(() => {
    if (lineSource === 'assembly') {
      return assemblies.map((assembly) => ({
        key: `assembly_${assembly.id}`,
        sourceType: 'assembly' as const,
        item: assembly,
        sourceId: assembly.id,
        label: assembly.name,
        unit: assembly.outputUnit || 'Nos',
      }));
    }
    if (lineSource === 'labour') {
      return materialItems.filter((item) => item.group === 'labour').flatMap((item) => {
        const options = materialSelectableQuoteOptions(item);
        if (!options.length) {
          return [{
            key: `labour_${item.id}_base`,
            sourceType: 'labour' as const,
            item,
            sourceId: item.id,
            quoteOptionId: '',
            assemblyType: '',
            variety: '',
            label: ['Labour', item.materialName].filter(Boolean).join(' · '),
            unit: item.unit || 'Nos',
          }];
        }
        return options.map((option) => ({
          key: `labour_${item.id}_${option.id}`,
          sourceType: 'labour' as const,
          item,
          sourceId: item.id,
          quoteOptionId: option.id,
          assemblyType: option.group || '',
          variety: option.variety || option.name || '',
          label: ['Labour', item.materialName, option.group, option.variety || option.name].map((value) => (value || '').trim()).filter((value, index, list) => value && list.findIndex((entry) => entry.toLowerCase() === value.toLowerCase()) === index).join(' · '),
          unit: option.unit || item.unit || 'Nos',
        }));
      });
    }
    return libraryPicks(materialItems.filter((item) => item.group !== 'labour')).map((pick) => ({
      key: `material_${pick.key}`,
      sourceType: 'material' as const,
      item: pick.item,
      sourceId: pick.item.id,
      quoteOptionId: pick.optionId || '',
      assemblyType: pick.assemblyType || '',
      variety: pick.variety || '',
      label: pick.label,
      unit: pick.unit,
    }));
  }, [assemblies, lineSource, materialItems]);
  const selectedMaterial = lineSource === 'assembly' ? null : materialItems.find((item) => item.id === sourceId) || null;
  const selectedAssembly = lineSource === 'assembly' ? assemblies.find((item) => item.id === sourceId) || null : null;
  const selectedSourcePick = sourcePicks.find((pick) => {
    if (pick.sourceId !== sourceId) return false;
    if (pick.sourceType === 'assembly') return true;
    if (quoteOptionId && pick.quoteOptionId !== quoteOptionId) return false;
    if (category && pick.assemblyType && pick.assemblyType.trim().toLowerCase() !== category.trim().toLowerCase()) return false;
    if (variety && pick.variety && pick.variety.trim().toLowerCase() !== variety.trim().toLowerCase()) return false;
    return true;
  }) || null;
  const categoryOptions = selectedMaterial ? Array.from(new Set([
    ...(selectedMaterial.assemblyTypes || []),
    ...(selectedMaterial.assemblyVarietyMap || []).map((entry) => entry.assemblyType),
    ...(selectedMaterial.quotes || []).flatMap((quote) => (quote.quoteOptions || []).map((option) => option.group || '')),
  ].map((value) => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)) : [];
  const allOptions = selectedMaterial ? (selectedMaterial.quotes || []).flatMap((quote) => quote.quoteOptions || []) : [];
  const hasCategories = categoryOptions.length > 0;
  const categoryRateOptions = hasCategories
    ? (category ? allOptions.filter((option) => (option.group || '').trim().toLowerCase() === category.trim().toLowerCase()) : [])
    : allOptions.filter((option) => !(option.group || '').trim());
  const isInternalRateLabel = (value: string) => ['base rate', 'rate based on this'].includes(value.trim().toLowerCase());
  const effectiveVariety = isInternalRateLabel(variety) ? '' : variety;
  const matchingRateOption = effectiveVariety.trim()
    ? categoryRateOptions.find((option) =>
      [option.variety, option.name].some((name) => (name || '').trim().toLowerCase() === effectiveVariety.trim().toLowerCase())
    )
    : categoryRateOptions.find((option) => [option.variety, option.name].some((name) => isInternalRateLabel(name || ''))) || categoryRateOptions[0];
  const selectedCategoryMapping = selectedMaterial?.assemblyVarietyMap?.find((entry) =>
    entry.assemblyType.trim().toLowerCase() === category.trim().toLowerCase()
  );
  const selectedRateOption = matchingRateOption || allOptions.find((option) => option.id === quoteOptionId);
  const sourceUnit = selectedSourcePick?.unit || selectedAssembly?.outputUnit || selectedCategoryMapping?.unit || selectedRateOption?.unit || selectedMaterial?.unit || '';
  const hasSecondary = !!selectedMaterial?.secondaryUnitRequired;
  const secondaryText = selectedMaterial ? materialSecondaryUnitLabel(selectedMaterial) : '';
  const saveDisabled = !sourceId;
  const changeSourceType = (value: 'material' | 'labour' | 'assembly') => {
    setLineSource(value);
    setSourceId('');
    setQuoteOptionId('');
    setCategory('');
    setVariety('');
    setLeadEnabled(false);
  };
  return <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: POPUP_PAPER_SX }}>
    <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>{title}</DialogTitle>
    <DialogContent>
      <Stack gap={2} sx={{ mt: 1 }}>
        <Box sx={{ p: 1.2, border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#faf8f5' }}>
          <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase', mb: .8 }}>What does this estimate line include?</Typography>
          <ToggleButtonGroup exclusive value={lineSource} onChange={(_, value) => value && changeSourceType(value)} fullWidth sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, py: .7 } }}>
            <ToggleButton value="material">Material</ToggleButton><ToggleButton value="labour">Labour</ToggleButton><ToggleButton value="assembly">Assembly</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Autocomplete<EstimateSourcePick, false, false, false>
          options={sourcePicks}
          value={selectedSourcePick}
          getOptionLabel={(item) => item.label}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          filterOptions={(options, state) => {
            const query = state.inputValue.trim().toLowerCase();
            if (!query) return options.slice(0, 80);
            return options.filter((item) => `${item.label} ${item.unit} ${item.sourceType === 'assembly' ? item.item.category : materialGroupLabel(item.item.group)} ${item.sourceType !== 'assembly' ? item.item.materialName : item.item.name}`.toLowerCase().includes(query)).slice(0, 80);
          }}
          onChange={(_, value) => {
            setSourceId(value?.sourceId || '');
            setQuoteOptionId(value && value.sourceType !== 'assembly' ? value.quoteOptionId : '');
            setCategory(value && value.sourceType !== 'assembly' ? value.assemblyType : '');
            setVariety(value && value.sourceType !== 'assembly' ? value.variety : '');
          }}
          renderOption={(props, item) => {
            const quote = item.sourceType === 'assembly' ? null : selectedMaterialQuoteForOption(item.item, item.quoteOptionId);
            const option = item.sourceType === 'assembly' || !quote ? null : selectedQuoteOption(quote, item.quoteOptionId);
            const rate = item.sourceType === 'assembly' || !quote ? 0 : option ? quoteOptionLandedRate(quote, option) : quoteLandedRate(item.item, quote);
            const marker = sourceUsageMarker?.({
              sourceType: item.sourceType,
              sourceId: item.sourceId,
              quoteOptionId: item.sourceType === 'assembly' ? '' : item.quoteOptionId,
              assemblyType: item.sourceType === 'assembly' ? '' : item.assemblyType,
              variety: item.sourceType === 'assembly' ? '' : item.variety,
            });
            const meta = item.sourceType === 'assembly'
              ? [item.item.category, item.unit ? `Output: ${item.unit}` : 'Unit pending']
              : [item.sourceType === 'labour' ? 'Labour' : materialGroupLabel(item.item.group), item.unit, quote?.vendor, rate ? `₹${fmtRate(rate)}` : 'Rate pending'];
            return <Box component="li" {...props} sx={{ display: 'block !important', py: '8px !important' }}>
              <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{item.label}</Typography>
                {marker && <Chip
                  size="small"
                  label={marker.label}
                  title={marker.details || marker.label}
                  sx={{
                    height: 22,
                    borderRadius: '999px',
                    fontWeight: 900,
                    color: marker.tone === 'same' ? '#2f6f49' : GOLD_D,
                    bgcolor: marker.tone === 'same' ? '#e6f4ea' : '#fff2d6',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />}
              </Stack>
              <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{meta.filter(Boolean).join(' · ')}</Typography>
            </Box>;
          }}
          renderInput={(params) => <TextField {...params} label={lineSource === 'assembly' ? 'Assembly' : lineSource === 'labour' ? 'Labour rate' : 'Material rate'} placeholder="Search by group, material, category or variety" autoFocus helperText="Select the full combined item here; category and variety are saved automatically." />}
        />
        {lineSource === 'labour' && <Box sx={{ p: 1.25, border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#faf8f5' }}><FormControlLabel sx={{ m: 0 }} control={<Checkbox checked={leadEnabled} onChange={(event) => setLeadEnabled(event.target.checked)} />} label={<Box><Typography sx={{ fontSize: 13, fontWeight: 800 }}>Apply labour lead</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>Add lead above the selected labour rate for this estimate line.</Typography></Box>} />{leadEnabled && <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mt: 1 }}><ToggleButtonGroup exclusive value={leadType} onChange={(_, value) => value && setLeadType(value)} size="small"><ToggleButton value="perFloor" sx={{ textTransform: 'none' }}>Per floor</ToggleButton><ToggleButton value="fixed" sx={{ textTransform: 'none' }}>Fixed</ToggleButton></ToggleButtonGroup><TextField label={leadType === 'perFloor' ? 'Lead % per floor' : 'Fixed lead %'} value={leadPercent || ''} type="number" onChange={(event) => setLeadPercent(num(event.target.value))} inputProps={{ min: 0, step: .01 }} sx={{ minWidth: 190 }} /></Stack>}</Box>}
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}><TextField label={quantityLabel || `Qty per ${unitLabel || 'unit'}`} value={qty || ''} type="number" fullWidth onChange={(event) => setQty(num(event.target.value))} inputProps={{ min: 0, step: 'any' }} helperText={num(qty) <= 0 ? "Leave blank and save if you don't know this yet — it's marked Qty pending until you fill it in." : ' '} /><Box sx={{ minWidth: 120, p: 1.4, border: `1px solid ${BORDER}`, borderRadius: 1, color: DARK, fontWeight: 700 }}>{sourceUnit || '-'}</Box></Stack>
        {showSecondary && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.25 }}><Typography sx={{ fontSize: 12, color: FAINT, textTransform: 'uppercase', fontWeight: 800, letterSpacing: '.5px' }}>Secondary unit</Typography><Typography sx={{ mt: .75, color: MUTED }}>{secondaryText || '-'}</Typography></Box>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>{onDelete && <Button onClick={onDelete} color="error">Delete line</Button>}<Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" disabled={saveDisabled} onClick={() => {
      const label = selectedSourcePick?.label || selectedAssembly?.name || selectedMaterial?.materialName || '';
      const savedQuoteOptionId = selectedSourcePick?.sourceType === 'material' || selectedSourcePick?.sourceType === 'labour'
        ? selectedSourcePick.quoteOptionId
        : matchingRateOption?.id || quoteOptionId;
      onSave({ id: initialComponent?.id || `rule_${Date.now()}`, sourceType: lineSource, sourceId, quoteOptionId: savedQuoteOptionId, label, group: initialComponent?.group, assemblyType: category.trim(), variety: effectiveVariety.trim(), qtyPerUnit: num(qty), unit: sourceUnit || 'Nos', areaPerUnit: hasSecondary ? num(selectedMaterial?.secondaryMultiplier) : 0, areaUnit: selectedMaterial?.secondaryUnit || 'Sft', secondaryOverride: false, leadEnabled: lineSource === 'labour' && leadEnabled, leadType: lineSource === 'labour' && leadEnabled ? leadType : undefined, leadPercent: lineSource === 'labour' && leadEnabled ? leadPercent : undefined });
    }} sx={{ bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D } }}>{initialComponent ? 'Save estimate line' : 'Add estimate line'}</Button></DialogActions>
  </Dialog>;
}

function MaterialRateCard({
  item,
  expanded,
  onExpandedChange,
  usageStatus,
  focusOpen,
  onFocusOpenHandled,
  onAddQuote,
  onEditQuote,
  onAddRateRevision,
  onEditRateRevision,
  onDeleteRateRevision,
  onSelectQuote,
  onSelectOptionQuote,
  onToggleQuoteComparison,
  existingPoNumbers,
  comparisonQuantities,
  onSaveComparisonQuantities,
  onCreateComparisonOrders,
  onEditMaterial,
  onDeleteMaterial,
  onSaveDealNotes,
  onDownloadMaterial,
  pdfBusy,
}: {
  item: MaterialRateItem;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  usageStatus?: MaterialRateUsageStatus;
  focusOpen?: boolean;
  onFocusOpenHandled?: () => void;
  onAddQuote: () => void;
  onEditQuote: (quote: MaterialQuote) => void;
  onAddRateRevision: (quote: MaterialQuote, optionId?: string) => void;
  onEditRateRevision: (quote: MaterialQuote, optionId: string | undefined, revision: MaterialRateRevision) => void;
  onDeleteRateRevision: (quoteId: string, optionId: string | undefined, revisionId: string) => Promise<void>;
  onSelectQuote: (quoteId: string) => void;
  onSelectOptionQuote: (optionId: string, quoteId: string) => void;
  onToggleQuoteComparison: (quoteId: string) => void;
  existingPoNumbers: string[];
  comparisonQuantities: Record<string, number>;
  onSaveComparisonQuantities: (quantities: Record<string, number>) => Promise<void>;
  onCreateComparisonOrders: (orders: ComparisonOrderLine[]) => Promise<void>;
  onEditMaterial: () => void;
  onDeleteMaterial: () => void;
  onSaveDealNotes: (dealNotes: string) => void;
  onDownloadMaterial: () => void;
  pdfBusy: boolean;
}) {
  const quotes = item.quotes || [];
  const selected = selectedMaterialQuote(item);
  const currentQuotes = quotes.filter((quote) => quoteIsCurrent(quote) && !quote.excludedFromComparison);
  const lowest = currentQuotes.reduce<MaterialQuote | null>((best, q) => (!best || quoteLandedRate(item, q) < quoteLandedRate(item, best) ? q : best), null);
  const diff = selected && lowest ? quoteLandedRate(item, selected) - quoteLandedRate(item, lowest) : 0;
  const showComparisonMatrix = materialQuoteMatrixGroups(item).length > 0;
  const optionSelectedCount = Object.values(item.selectedOptionQuoteIds || {}).filter((quoteId) =>
    !!quoteId && quotes.some((quote) => quote.id === quoteId && quoteIsCurrent(quote))
  ).length;
  const matrixSelectedCount = (selected ? 1 : 0) + optionSelectedCount;
  const selectedHeaderLabel = optionSelectedCount ? 'Mixed' : (selected ? selected.vendor : '—');
  const usedRatePending = !!usageStatus && (usageStatus.pendingWholeItem || usageStatus.pendingOptionIds.size > 0);
  useEffect(() => {
    if (!focusOpen) return;
    onExpandedChange(true);
    onFocusOpenHandled?.();
  }, [focusOpen, onFocusOpenHandled, onExpandedChange]);
  const stopHeaderToggle = (event: React.SyntheticEvent) => event.stopPropagation();
  const renderMaterialActions = () => (
    <Stack direction="row" gap={0.25} alignItems="center">
      <Box onClick={stopHeaderToggle}><ActionIcon title="Download material PDF" onClick={onDownloadMaterial}>{pdfBusy ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon fontSize="inherit" />}</ActionIcon></Box>
      <Box onClick={stopHeaderToggle}><ActionIcon title="Edit material" onClick={onEditMaterial}><EditOutlinedIcon fontSize="inherit" /></ActionIcon></Box>
      <Box onClick={stopHeaderToggle}><ActionIcon title="Delete material" onClick={onDeleteMaterial} danger><DeleteOutlineIcon fontSize="inherit" /></ActionIcon></Box>
      <Box onClick={stopHeaderToggle}>
        <Tooltip title="Add quote">
          <IconButton
            size="small"
            onClick={() => { onExpandedChange(true); onAddQuote(); }}
            sx={{ width: 30, height: 30, bgcolor: GOLD, color: '#fff', fontSize: 18, '&:hover': { bgcolor: GOLD_D } }}
          >
            <AddOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  );
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => onExpandedChange(next)}
      disableGutters
      elevation={0}
      sx={{
        border: 'none',
        borderRadius: 0,
        bgcolor: '#fff',
        overflow: 'hidden',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          p: 0,
          bgcolor: '#f5f7f8',
          '& .MuiAccordionSummary-content': { m: 0, minWidth: 0 },
          '& .MuiAccordionSummary-expandIconWrapper': { mr: 1.2, color: GOLD_D },
        }}
      >
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ p: 1.25, width: '100%', bgcolor: '#f5f7f8', borderLeft: '3px solid #d6b476' }}>
        <Box sx={{ flex: { xs: '1 0 100%', md: 1 }, minWidth: 0 }}>
          <Stack direction="row" gap={.5} alignItems="center" flexWrap="nowrap">
            <Typography sx={{ fontWeight: 900, color: DARK, fontSize: 16, lineHeight: 1.2 }}>{item.materialName}</Typography>
            {usedRatePending && (
              <Tooltip title={usageStatus?.sources.length ? `Used in: ${usageStatus.sources.join(', ')}` : 'Used in rate analysis but no selected rate is available'}>
                <Chip
                  size="small"
                  label="Used, rate not selected"
                  sx={{ bgcolor: '#fff4d8', color: '#7a5525', border: '1px solid #e2c982', fontWeight: 800 }}
                />
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>{renderMaterialActions()}</Box>
          </Stack>
        </Box>
        {showComparisonMatrix ? (
          <>
            <Box sx={{ flex: '0 0 auto', minWidth: { xs: 58, md: 126 }, width: 'fit-content', textAlign: { xs: 'center', md: 'right' } }}>
              <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700, textTransform: 'uppercase' }}>Quotes</Typography>
              <Typography sx={{ fontWeight: 800, color: GOLD_D }}>{quotes.length}</Typography>
            </Box>
            <Box sx={{ flex: '0 0 auto', minWidth: { xs: 142, md: 150 }, width: 'fit-content', textAlign: { xs: 'center', md: 'right' } }}>
              <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700, textTransform: 'uppercase' }}>Selected</Typography>
              <Typography noWrap sx={{ fontWeight: 800, color: selected || optionSelectedCount ? GOLD_D : MUTED }}>{selectedHeaderLabel}</Typography>
            </Box>
            <Box sx={{ flex: '0 0 auto', minWidth: { xs: 54, md: 116 }, width: 'fit-content', textAlign: { xs: 'center', md: 'right' } }}>
              <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700, textTransform: 'uppercase' }}>Marked</Typography>
              <Typography sx={{ fontWeight: 800, color: matrixSelectedCount ? GOLD_D : MUTED }}>{matrixSelectedCount}</Typography>
            </Box>
          </>
        ) : (
          <>
            <RateSummary label="Selected" item={item} quote={selected} />
            <RateSummary label="Lowest" item={item} quote={lowest} />
            <Box sx={{ minWidth: { xs: 58, md: 116 }, textAlign: { xs: 'center', md: 'right' } }}>
              <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700, textTransform: 'uppercase' }}>Difference</Typography>
              <Typography sx={{ fontWeight: 800, color: diff > 0 ? WARN : MUTED }}>
                {selected && lowest ? `₹${fmtIN(diff)}` : '—'}
              </Typography>
            </Box>
          </>
        )}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>{renderMaterialActions()}</Box>
      </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>

      {showComparisonMatrix ? (
        <MaterialQuoteComparisonMatrix
          item={item}
          quotes={quotes}
          selectedQuoteId={item.selectedQuoteId || null}
          selectedOptionQuoteIds={item.selectedOptionQuoteIds || {}}
          pendingUsedOptionIds={usageStatus?.pendingOptionIds}
          onSelectQuote={onSelectQuote}
          onSelectOptionQuote={onSelectOptionQuote}
          onToggleQuoteComparison={onToggleQuoteComparison}
          existingPoNumbers={existingPoNumbers}
          comparisonQuantities={comparisonQuantities}
          onSaveComparisonQuantities={onSaveComparisonQuantities}
          onCreateComparisonOrders={onCreateComparisonOrders}
          onEditQuote={onEditQuote}
          onAddRateRevision={onAddRateRevision}
          onEditRateRevision={onEditRateRevision}
          onDeleteRateRevision={onDeleteRateRevision}
        />
      ) : (
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', minWidth: 980, borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ '& th': headTh }}>
              <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Vendor</Box>
              <Box component="th" sx={headTh}>Rate</Box>
              <Box component="th" sx={headTh}>Coverage</Box>
              <Box component="th" sx={headTh}>GST</Box>
              <Box component="th" sx={headTh}>Freight</Box>
              <Box component="th" sx={headTh}>Landed</Box>
              <Box component="th" sx={headTh}>Effective from</Box>
              <Box component="th" sx={headTh}>Effective until</Box>
              <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Checklist</Box>
              <Box component="th" sx={{ ...headTh, width: 120 }} />
            </Box>
          </Box>
          <Box component="tbody">
            {quotes.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={10} sx={{ p: 2.5, color: MUTED, textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
                  No vendor quotes yet.
                </Box>
              </Box>
            ) : quotes.map((quote) => {
              const isSelected = quote.id === item.selectedQuoteId;
              const isCurrent = quoteIsCurrent(quote);
              const historyStatus = quoteHistoryStatusLabel(quote);
              const td: any = { p: '10px 12px', borderTop: `1px solid ${BORDER}`, fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap', bgcolor: isSelected ? GOLD_WASH : '#fff', opacity: quote.excludedFromComparison ? 0.58 : 1 };
              return (
                <Box component="tr" key={quote.id}>
                  <Box component="td" sx={{ ...td, textAlign: 'left' }}>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Checkbox size="small" checked={isSelected && isCurrent} disabled={!isCurrent} onChange={() => onSelectQuote(quote.id)} />
                      <Box>
                        <Stack direction="row" gap={0.6} alignItems="center">
                          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{quote.vendor || 'Unnamed vendor'}</Typography>
                          <Chip size="small" label={historyStatus} sx={{ height: 18, fontSize: 10, bgcolor: isCurrent ? '#e8f4eb' : '#f2eee7', color: isCurrent ? '#3c6b4b' : MUTED, fontWeight: 800 }} />
                          {quote.excludedFromComparison && <Chip size="small" label="Excluded" sx={{ height: 18, fontSize: 10, bgcolor: '#f2eee7', color: MUTED, fontWeight: 800 }} />}
                        </Stack>
                        <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{quote.brand || item.brand || 'Brand not set'}</Typography>
                        {!!quote.voidReason && <Typography sx={{ color: FAINT, fontSize: 10.5 }}>Void: {quote.voidReason}</Typography>}
                        {!!quoteReferenceLabel(quote) && (
                          <Typography sx={{ color: FAINT, fontSize: 11 }}>
                            Ref: {quoteReferenceLabel(quote)}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                  <Box component="td" sx={td}>
                    <Box sx={{ position: 'relative', pr: 2.5 }}>
                      {quoteRateLabel(item, quote)}
                      <Box sx={{ position: 'absolute', top: -5, right: -7 }}>
                        <RateRevisionControl onClick={() => onAddRateRevision(quote)} />
                      </Box>
                    </Box>
                    {!!item.codeRequired && !!quote.materialCode && (
                      <Typography sx={{ color: FAINT, fontSize: 10.5, mt: 0.25, whiteSpace: 'nowrap' }}>
                        {quote.materialCode}
                      </Typography>
                    )}
                  </Box>
                  <Box component="td" sx={td}>{quoteCoverageLabel(item, quote)}</Box>
                  <Box component="td" sx={td}>{quote.gstMode === 'none' ? 'No GST' : `${quote.gst}% ${quote.gstMode === 'inclusive' ? 'incl' : 'excl'}`}</Box>
                  <Box component="td" sx={td}>₹{fmtIN(num(quote.freight) + num(quote.loading))}</Box>
                  <Box component="td" sx={{ ...td, fontWeight: 800, color: GOLD_D }}>{quoteLandedRateLabel(item, quote)}</Box>
                  <Box component="td" sx={td}>{quoteEffectiveFrom(quote) || '—'}</Box>
                  <Box component="td" sx={td}>{quoteEffectiveUntil(quote) || '—'}</Box>
                  <Box component="td" sx={{ ...td, textAlign: 'left' }}>{checklistSummary(quote)}</Box>
                  <Box component="td" sx={td}>
                    <ActionIcon title={quote.excludedFromComparison ? 'Include in rate comparison' : 'Exclude from rate comparison'} onClick={() => onToggleQuoteComparison(quote.id)}><VisibilityOffOutlinedIcon fontSize="inherit" /></ActionIcon>
                    <ActionIcon title="Edit quote" onClick={() => onEditQuote(quote)}><EditOutlinedIcon fontSize="inherit" /></ActionIcon>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      )}
      <MaterialDealNotesEditor notes={item.dealNotes || ''} onSave={onSaveDealNotes} />
      <Typography noWrap sx={{ px: 1.75, py: 0.9, borderTop: `1px solid ${BORDER}`, fontSize: 11.5, color: FAINT }}>
        Vendor quote only. Final deal rate may vary after negotiation and order terms.
      </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

function quoteRevisionRows(quote: MaterialQuote) {
  const rows: Array<{ id: string; optionId?: string; label: string; previousRate: number; revisedRate: number; effectiveFrom: string; reason?: string }> = [];
  const addHistory = (label: string, fallbackRate: number, history: MaterialRateRevision[] | undefined, optionId?: string) => {
    let previousRate = num(fallbackRate);
    [...(history || [])]
      .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom) || a.recordedAt.localeCompare(b.recordedAt))
      .forEach((revision) => {
        const revisionPreviousRate = previousRate;
        rows.push({ id: revision.id, optionId, label, previousRate: num(revisionPreviousRate), revisedRate: num(revision.rate), effectiveFrom: revision.effectiveFrom, reason: revision.reason });
        previousRate = num(revision.rate);
      });
  };
  addHistory('Base rate', quote.rate, quote.baseRateHistory);
  for (const option of quote.quoteOptions || []) addHistory(quoteOptionLabel(option), num(option.rate) || (option.type === 'base' ? num(quote.rate) : 0), option.rateHistory, option.id);
  return rows.sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
}

function quoteRateTrend(quote: MaterialQuote) {
  const points: Array<{ label: string; date: string; rate: number }> = [];
  const addHistory = (label: string, fallbackRate: number, history: MaterialRateRevision[] | undefined) => {
    const startingDate = quoteEffectiveFrom(quote) || quote.quoteDate;
    if (fallbackRate > 0 && startingDate) points.push({ label, date: startingDate, rate: num(fallbackRate) });
    for (const revision of history || []) if (num(revision.rate) > 0 && revision.effectiveFrom) points.push({ label, date: revision.effectiveFrom, rate: num(revision.rate) });
  };
  addHistory('Base rate', quote.rate, quote.baseRateHistory);
  for (const option of quote.quoteOptions || []) addHistory(quoteOptionLabel(option), num(option.rate) || (option.type === 'base' ? num(quote.rate) : 0), option.rateHistory);
  return points;
}

function shortRateTrendDate(value: string) {
  const [, month = '', day = ''] = value.split('-');
  const monthLabels: Record<string, string> = { '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec' };
  return `${day} ${monthLabels[month] || month}`;
}

function RateHistoryTrend({ quote }: { quote: MaterialQuote }) {
  const points = quoteRateTrend(quote);
  const [hoveredPoint, setHoveredPoint] = useState<{ label: string; date: string; rate: number } | null>(null);
  const chartRef = useRef<SVGSVGElement | null>(null);
  const [chartSize, setChartSize] = useState({ width: 720, height: 176 });
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const updateSize = () => setChartSize({ width: Math.max(1, Math.round(chart.clientWidth)), height: Math.max(1, Math.round(chart.clientHeight)) });
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(chart);
    return () => observer.disconnect();
  }, []);
  if (points.length < 2) return null;
  const { width, height } = chartSize;
  const padding = { top: 22, right: 20, bottom: 30, left: 54 };
  const rates = points.map((point) => point.rate);
  const minRate = Math.min(...rates);
  const maxRate = Math.max(...rates);
  const range = maxRate - minRate || Math.max(maxRate * 0.1, 1);
  const dateValues = points.map((point) => new Date(`${point.date}T00:00:00`).getTime());
  const minDate = Math.min(...dateValues);
  const maxDate = Math.max(...dateValues);
  const dateRange = maxDate - minDate || 1;
  const x = (date: string) => padding.left + ((new Date(`${date}T00:00:00`).getTime() - minDate) / dateRange) * (width - padding.left - padding.right);
  const y = (rate: number) => padding.top + ((maxRate - rate) / range) * (height - padding.top - padding.bottom);
  const colors = ['#a7742d', '#52799b', '#4f7b69', '#805c94'];
  const series = Array.from(new Set(points.map((point) => point.label))).map((label, index) => {
    const seriesPoints = points.filter((point) => point.label === label).sort((a, b) => a.date.localeCompare(b.date));
    const isReduction = seriesPoints[seriesPoints.length - 1].rate < seriesPoints[0].rate;
    return { label, color: isReduction ? '#397452' : colors[index % colors.length], points: seriesPoints };
  });
  const axisDates = Array.from(new Set(points.map((point) => point.date))).sort();
  const dateLabelStep = Math.max(1, Math.ceil((axisDates.length - 1) / 5));
  const primarySeries = series.length === 1 ? series[0] : undefined;
  const primaryDifference = primarySeries ? primarySeries.points[primarySeries.points.length - 1].rate - primarySeries.points[0].rate : 0;
  const primaryPercent = primarySeries?.points[0].rate ? (primaryDifference / primarySeries.points[0].rate) * 100 : 0;
  const tooltipWidth = 112;
  const tooltipX = hoveredPoint ? Math.min(width - padding.right - tooltipWidth, Math.max(padding.left, x(hoveredPoint.date) - tooltipWidth / 2)) : 0;
  const tooltipY = hoveredPoint ? Math.max(2, y(hoveredPoint.rate) - 30) : 0;
  return <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: .6 }}><Box><Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Rate trend by effective date</Typography>{primarySeries && <Typography sx={{ color: primaryDifference < 0 ? '#397452' : GOLD_D, fontSize: 11.5, fontWeight: 800 }}>Current ₹{fmtRate(primarySeries.points[primarySeries.points.length - 1].rate)} · {primaryDifference === 0 ? 'No change' : `${primaryDifference > 0 ? '+' : '−'}₹${fmtRate(Math.abs(primaryDifference))} (${primaryDifference > 0 ? '+' : ''}${fmtRate(primaryPercent)}%) since ${shortRateTrendDate(primarySeries.points[0].date)}`}</Typography>}</Box>{series.length > 1 && <Stack direction="row" gap={1.1} flexWrap="wrap" justifyContent="flex-end">{series.map((item) => <Stack key={item.label} direction="row" gap={.35} alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} /><Typography sx={{ color: MUTED, fontSize: 10.5 }}>{item.label}</Typography></Stack>)}</Stack>}</Stack>
    <Typography sx={{ color: FAINT, fontSize: 10, mb: .4 }}>Zoomed scale</Typography>
    <svg ref={chartRef} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Rate trend by effective date" onMouseMove={(event) => { const bounds = event.currentTarget.getBoundingClientRect(); const pointerX = ((event.clientX - bounds.left) / bounds.width) * width; const nearest = points.reduce((closest, point) => Math.abs(x(point.date) - pointerX) < Math.abs(x(closest.date) - pointerX) ? point : closest); setHoveredPoint(nearest); }} onMouseLeave={() => setHoveredPoint(null)} style={{ display: 'block', width: '100%', height: 'clamp(170px, 18vw, 220px)', overflow: 'visible', cursor: 'crosshair' }}>
      {[0, .5, 1].map((step) => { const rate = maxRate - step * range; const lineY = y(rate); return <g key={step}><line x1={padding.left} x2={width - padding.right} y1={lineY} y2={lineY} stroke="#ebe7df" strokeWidth="1" /><text x={padding.left - 8} y={lineY + 4} textAnchor="end" fill="#938b7d" fontSize="10">₹{fmtRate(rate)}</text></g>; })}
      {axisDates.map((date, index) => (index % dateLabelStep === 0 || index === axisDates.length - 1) && <text key={date} x={x(date)} y={height - 8} textAnchor={index === 0 ? 'start' : index === axisDates.length - 1 ? 'end' : 'middle'} fill="#938b7d" fontSize="10">{shortRateTrendDate(date)}</text>)}
      {series.map((item) => <g key={item.label}><polyline fill="none" stroke={item.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" points={item.points.map((point) => `${x(point.date)},${y(point.rate)}`).join(' ')} />{item.points.map((point, index) => <g key={`${point.date}_${index}`}><circle cx={x(point.date)} cy={y(point.rate)} r="4" fill="#fff" stroke={item.color} strokeWidth="2.5" /><title>{`${item.label}: ₹${fmtRate(point.rate)} from ${point.date}`}</title></g>)}</g>)}
      {hoveredPoint && <g pointerEvents="none"><line x1={x(hoveredPoint.date)} x2={x(hoveredPoint.date)} y1={padding.top} y2={height - padding.bottom} stroke="#9d6f32" strokeWidth="1" strokeDasharray="3 3" /><rect x={tooltipX} y={tooltipY} width={tooltipWidth} height="24" rx="4" fill="#1a1a1a" /><text x={tooltipX + tooltipWidth / 2} y={tooltipY + 15.5} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">{`₹${fmtRate(hoveredPoint.rate)} · ${shortRateTrendDate(hoveredPoint.date)}`}</text></g>}
    </svg>
  </Box>;
}

function MaterialQuoteComparisonMatrix({
  item,
  quotes,
  selectedQuoteId,
  selectedOptionQuoteIds,
  pendingUsedOptionIds,
  onSelectQuote,
  onSelectOptionQuote,
  onToggleQuoteComparison,
  existingPoNumbers,
  comparisonQuantities,
  onSaveComparisonQuantities,
  onCreateComparisonOrders,
  onEditQuote,
  onAddRateRevision,
  onEditRateRevision,
  onDeleteRateRevision,
}: {
  item: MaterialRateItem;
  quotes: MaterialQuote[];
  selectedQuoteId: string | null;
  selectedOptionQuoteIds: Record<string, string | null>;
  pendingUsedOptionIds?: Set<string>;
  onSelectQuote: (quoteId: string) => void;
  onSelectOptionQuote: (optionId: string, quoteId: string) => void;
  onToggleQuoteComparison: (quoteId: string) => void;
  existingPoNumbers: string[];
  comparisonQuantities: Record<string, number>;
  onSaveComparisonQuantities: (quantities: Record<string, number>) => Promise<void>;
  onCreateComparisonOrders: (orders: ComparisonOrderLine[]) => Promise<void>;
  onEditQuote: (quote: MaterialQuote) => void;
  onAddRateRevision: (quote: MaterialQuote, optionId?: string) => void;
  onEditRateRevision: (quote: MaterialQuote, optionId: string | undefined, revision: MaterialRateRevision) => void;
  onDeleteRateRevision: (quoteId: string, optionId: string | undefined, revisionId: string) => Promise<void>;
}) {
  const [rateView, setRateView] = useState<'excl' | 'incl'>('incl');
  const [expandedHistoryQuoteId, setExpandedHistoryQuoteId] = useState<string | null>(null);
  const [quantityEditorOptionId, setQuantityEditorOptionId] = useState<string | null>(null);
  const [vendorTotalSort, setVendorTotalSort] = useState<'none' | 'desc' | 'asc'>('none');
  const [comparisonQty, setComparisonQty] = useState<Record<string, number>>(comparisonQuantities);
  const [placingOrders, setPlacingOrders] = useState(false);
  const [orderReview, setOrderReview] = useState<ComparisonOrderReview | null>(null);
  useEffect(() => setComparisonQty(comparisonQuantities), [comparisonQuantities]);
  const updateComparisonQty = async (next: Record<string, number>) => {
    setComparisonQty(next);
    await onSaveComparisonQuantities(next);
  };
  const groups = materialQuoteMatrixGroups(item);
  const columns = groups.flatMap((group) => group.columns);
  const currentQuoteCount = quotes.filter((quote) => quoteIsCurrent(quote) && !quote.excludedFromComparison).length;
  const isComparison = currentQuoteCount > 1;
  const pendingUsedOptions = pendingUsedOptionIds || new Set<string>();
  const comparisonColumns = columns.filter((column) => num(comparisonQty[column.optionId]) > 0);
  const selectedComparisonLines = comparisonColumns.flatMap((column) => {
    const quoteId = selectedOptionQuoteIds[column.optionId] || selectedQuoteId;
    const quote = quoteId ? quotes.find((candidate) => candidate.id === quoteId && quoteIsCurrent(candidate)) : null;
    const option = quote ? (quote.quoteOptions || []).find((candidate) => candidate.id === column.optionId) : null;
    const qty = num(comparisonQty[column.optionId]);
    if (!quote || !option || qty <= 0) return [];
    return [{ column, quote, option, qty }];
  });
  const comparisonLinesForQuote = (quote: MaterialQuote) => comparisonColumns.flatMap((column) => {
    const option = (quote.quoteOptions || []).find((candidate) => candidate.id === column.optionId);
    const qty = num(comparisonQty[column.optionId]);
    if (!option || qty <= 0 || quoteOptionLandedRate(quote, option) <= 0) return [];
    return [{ column, quote, option, qty }];
  });
  const vendorTotalForQuote = (quote: MaterialQuote) => {
    const entries = comparisonColumns.map((column) => {
      const option = (quote.quoteOptions || []).find((candidate) => candidate.id === column.optionId);
      const rate = option ? (rateView === 'incl' ? quoteOptionRateInclGst(quote, option) : quoteOptionRateExclGst(quote, option)) : 0;
      return { rate, qty: num(comparisonQty[column.optionId]) };
    });
    return entries.reduce((sum, entry) => sum + entry.rate * entry.qty, 0);
  };
  const vendorTotalRankByQuoteId = new Map<string, number>();
  if (comparisonColumns.length) {
    const rankedTotals = quotes
      .filter((quote) => quoteIsCurrent(quote) && !quote.excludedFromComparison)
      .map((quote) => ({ quoteId: quote.id, total: vendorTotalForQuote(quote) }))
      .sort((a, b) => a.total - b.total);
    let rank = 0;
    let previousTotal: number | null = null;
    rankedTotals.forEach((total) => {
      if (previousTotal === null || total.total !== previousTotal) rank += 1;
      previousTotal = total.total;
      if (rank <= 3) vendorTotalRankByQuoteId.set(total.quoteId, rank);
    });
  }
  const displayedQuotes = vendorTotalSort === 'none'
    ? quotes
    : quotes
      .map((quote, index) => ({ quote, index, total: vendorTotalForQuote(quote) }))
      .sort((a, b) => {
        const difference = a.total - b.total;
        return difference === 0 ? a.index - b.index : vendorTotalSort === 'asc' ? difference : -difference;
      })
      .map(({ quote }) => quote);
  const cycleVendorTotalSort = () => setVendorTotalSort((current) => current === 'none' ? 'desc' : current === 'desc' ? 'asc' : 'none');
  const createComparisonOrders = async (linesToOrder: typeof selectedComparisonLines) => {
    if (!linesToOrder.length || placingOrders) return;
    const byVendor = new Map<string, typeof selectedComparisonLines>();
    for (const line of linesToOrder) {
      const key = line.quote.vendor.trim().toLowerCase() || line.quote.id;
      byVendor.set(key, [...(byVendor.get(key) || []), line]);
    }
    const poByVendor = comparisonPoNumbers(existingPoNumbers, byVendor.size);
    const orderDate = new Date().toISOString().slice(0, 10);
    const orders: ComparisonOrderLine[] = [];
    Array.from(byVendor.values()).forEach((lines, vendorIndex) => lines.forEach((line) => {
      const mappedUnit = assemblyMappingUnit(item, line.option.group || '', line.option.variety || line.option.name || '');
      const unit = mappedUnit || line.option.unit || line.quote.unit || item.unit || 'Nos';
      const orderedBaseRate = quoteOptionBaseRate(line.quote, line.option);
      const orderedRateExclGst = quoteOptionRateExclGst(line.quote, line.option);
      const orderedRateInclGst = quoteOptionRateInclGst(line.quote, line.option);
      const orderedFreightRate = num(line.quote.freight);
      const orderedLoadingRate = num(line.quote.loading);
      const orderedLandedRate = quoteOptionLandedRate(line.quote, line.option);
      orders.push({
        materialId: item.id,
        quoteOptionId: line.column.optionId,
        materialLabel: `${item.materialName} · ${quoteOptionLabel(line.option)}`,
        unit,
        vendor: line.quote.vendor || '',
        brand: line.option.brand || line.quote.brand || item.brand || '',
        code: line.option.code || line.quote.materialCode || '',
        quoteId: line.quote.id,
        orderedBaseRate,
        orderedRateExclGst,
        orderedRateInclGst,
        orderedGstPercent: num(line.quote.gst),
        orderedGstMode: line.quote.gstMode || 'exclusive',
        orderedFreightRate,
        orderedLoadingRate,
        orderedLineTotal: line.qty * orderedLandedRate,
        rateSnapshot: orderedLandedRate,
        rateUnit: unit,
        rateEffectiveFrom: quoteEffectiveFrom(line.quote),
        scopeType: 'project',
        scopeLabel: `Rate comparison · ${item.materialName}`,
        orderQty: line.qty,
        orderDate,
        poNumber: poByVendor[vendorIndex],
        notes: 'Created from material rate comparison',
        receipts: [],
      });
    }));
    setOrderReview({
      orders,
      freightAmount: orders.reduce((sum, order) => sum + (num(order.orderedFreightRate) + num(order.orderedLoadingRate)) * num(order.orderQty), 0),
    });
  };
  const confirmComparisonOrders = async () => {
    if (!orderReview || placingOrders) return;
    const reviewOrders = orderReview.orders.filter((order) => num(order.orderQty) > 0);
    if (!reviewOrders.length) return;
    const materialTotal = reviewOrders.reduce((sum, order) => sum + num(order.orderedRateInclGst) * num(order.orderQty), 0);
    const totalQty = reviewOrders.reduce((sum, order) => sum + num(order.orderQty), 0);
    const finalOrders = reviewOrders.map((order) => {
      const materialAmount = num(order.orderedRateInclGst) * num(order.orderQty);
      const share = materialTotal > 0 ? materialAmount / materialTotal : totalQty > 0 ? num(order.orderQty) / totalQty : 0;
      const orderedFreightAmount = num(orderReview.freightAmount) * share;
      const orderedLoadingAmount = 0;
      const quantity = num(order.orderQty);
      const orderedFreightRate = quantity > 0 ? orderedFreightAmount / quantity : 0;
      const orderedLoadingRate = quantity > 0 ? orderedLoadingAmount / quantity : 0;
      const orderedLineTotal = materialAmount + orderedFreightAmount + orderedLoadingAmount;
      return { ...order, orderedFreightAmount, orderedLoadingAmount, orderedFreightRate, orderedLoadingRate, orderedLineTotal, rateSnapshot: num(order.orderedRateInclGst) + orderedFreightRate + orderedLoadingRate };
    });
    setPlacingOrders(true);
    try {
      await onCreateComparisonOrders(finalOrders);
      const remainingQuantities = { ...comparisonQty };
      finalOrders.forEach((order) => delete remainingQuantities[order.quoteOptionId || '']);
      await updateComparisonQty(remainingQuantities);
      setOrderReview(null);
    } finally {
      setPlacingOrders(false);
    }
  };
  const lowestByColumn = new Map<string, number>();
  for (const column of columns) {
    const values = quotes
      .filter((quote) => quoteIsCurrent(quote) && !quote.excludedFromComparison)
      .map((quote) => {
        const option = (quote.quoteOptions || []).find((x) => x.id === column.optionId);
        if (!option) return 0;
        return rateView === 'incl' ? quoteOptionRateInclGst(quote, option) : quoteOptionRateExclGst(quote, option);
      })
      .filter((value) => value > 0);
    if (values.length) lowestByColumn.set(column.optionId, Math.min(...values));
  }
  if (!quotes.length) {
    return (
      <Box sx={{ p: 2.5, color: MUTED, textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
        No vendor quotes yet.
      </Box>
    );
  }
  const agencyColumnWidth = 420;
  const compactAgencyColumnWidth = 340;
  const activeAgencyColumnWidth = columns.length >= 4
    ? compactAgencyColumnWidth
    : agencyColumnWidth;
  const mobileAgencyColumnWidth = 180;
  const rateColumnMinWidth = columns.length >= 6 ? 168 : 178;
  const mobileRateColumnMinWidth = 150;
  const agencyColumnWidths = { xs: mobileAgencyColumnWidth, sm: activeAgencyColumnWidth };
  const rateColumnMinWidths = { xs: mobileRateColumnMinWidth, sm: rateColumnMinWidth };
  const reviewTotals = orderReview && {
    material: orderReview.orders.reduce((sum, order) => sum + num(order.orderedRateInclGst) * num(order.orderQty), 0),
    freight: num(orderReview.freightAmount),
    total: orderReview.orders.reduce((sum, order) => sum + num(order.orderedRateInclGst) * num(order.orderQty), 0) + num(orderReview.freightAmount),
  };
  const reviewLineTotal = (order: ComparisonOrderLine) => {
    if (!orderReview || !reviewTotals) return 0;
    const materialAmount = num(order.orderedRateInclGst) * num(order.orderQty);
    const share = reviewTotals.material > 0 ? materialAmount / reviewTotals.material : 1 / Math.max(1, orderReview.orders.length);
    return materialAmount + reviewTotals.freight * share;
  };
  const stickyCell: any = {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    width: agencyColumnWidths,
    minWidth: agencyColumnWidths,
    maxWidth: agencyColumnWidths,
    bgcolor: '#fff',
    borderRight: `1px solid ${BORDER}`,
  };
  return (
    <Box sx={{ borderTop: `1px solid ${BORDER}` }}>
      {isComparison && <Stack direction="row" gap={1.25} alignItems="center" flexWrap="wrap" sx={{ px: 1.5, py: 0.8, borderBottom: `1px solid ${BORDER}`, bgcolor: '#faf8f5' }}>
        <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Rate comparison</Typography>
        <Stack direction="row" gap={.45} alignItems="center"><Box sx={{ width: 9, height: 9, borderLeft: '3px solid #4677b7', bgcolor: '#fff' }} /><Typography sx={{ color: MUTED, fontSize: 11 }}>Selected vendor</Typography></Stack>
        <Stack direction="row" gap={.45} alignItems="center"><Box sx={{ width: 9, height: 9, bgcolor: 'rgba(76, 130, 80, 0.18)' }} /><Typography sx={{ color: MUTED, fontSize: 11 }}>Lowest current rate</Typography></Stack>
        <Typography sx={{ color: FAINT, fontSize: 11 }}>Hover a rate to revise it.</Typography>
      </Stack>}
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: {
            xs: mobileAgencyColumnWidth + (columns.length * mobileRateColumnMinWidth) + (comparisonColumns.length ? 198 : 0),
            sm: activeAgencyColumnWidth + (columns.length * rateColumnMinWidth) + (comparisonColumns.length ? 226 : 0),
          },
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
        }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box component="th" rowSpan={2} sx={{ ...headTh, ...stickyCell, textAlign: 'left', top: 0, zIndex: 5 }}>
              <Stack gap={0.75} alignItems="flex-start">
                <Box>Agency</Box>
                <ToggleButtonGroup exclusive size="small" value={rateView} onChange={(_, value) => value && setRateView(value)}>
                  <ToggleButton value="excl" sx={{ ...toggleSx, px: 1, fontSize: 11 }}>Excl GST</ToggleButton>
                  <ToggleButton value="incl" sx={{ ...toggleSx, px: 1, fontSize: 11 }}>Incl GST</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>
            {groups.map((group) => (
              <Box key={group.label} component="th" colSpan={group.columns.length} sx={{
                ...headTh,
                position: 'sticky',
                top: 0,
                zIndex: 4,
                textAlign: 'center',
                width: 'auto',
                minWidth: { xs: group.columns.length * mobileRateColumnMinWidth, sm: group.columns.length * rateColumnMinWidth },
                bgcolor: '#ebe6dc',
                color: GOLD_D,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                lineHeight: 1.25,
                px: 1,
              }}>
                {group.label}
              </Box>
            ))}
            {comparisonColumns.length > 0 && <Box component="th" rowSpan={2} sx={{ ...headTh, position: 'sticky', top: 0, zIndex: 4, minWidth: 48, width: 48, px: 0.5, bgcolor: '#ebe6dc' }}>
            </Box>}
            {comparisonColumns.length > 0 && <Box component="th" rowSpan={2} sx={{ ...headTh, position: 'sticky', top: 0, zIndex: 4, minWidth: { xs: 150, sm: 178 }, textAlign: 'right', bgcolor: '#ebe6dc', color: GOLD_D }}>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.25}>
                <Box>Vendor total</Box>
                <Tooltip title={vendorTotalSort === 'none' ? 'Sort highest to lowest' : vendorTotalSort === 'desc' ? 'Sort lowest to highest' : 'Restore current order'}>
                  <IconButton size="small" onClick={cycleVendorTotalSort} aria-label="Sort vendor totals" sx={{ color: GOLD_D, p: 0.25 }}>
                    <SortOutlinedIcon sx={{ fontSize: 16, transform: vendorTotalSort === 'asc' ? 'rotate(180deg)' : undefined }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Box sx={{ mt: 0.3, color: FAINT, fontSize: 9.5, fontWeight: 800, textTransform: 'none' }}>{rateView === 'incl' ? 'Incl GST' : 'Excl GST'}</Box>
            </Box>}
          </Box>
          <Box component="tr">
            {columns.map((column) => (
              <Box key={column.optionId} component="th" sx={{
                  ...headTh,
                  position: 'sticky',
                  top: 0,
                  zIndex: 4,
                  textAlign: 'center',
                  width: 'auto',
                  minWidth: rateColumnMinWidths,
                  borderLeft: column === columns[0] || column.groupLabel !== columns[columns.indexOf(column) - 1]?.groupLabel ? `1px solid ${BORDER}` : undefined,
                  bgcolor: pendingUsedOptions.has(column.optionId) ? '#fff4d8' : headTh.bgcolor,
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  lineHeight: 1.25,
                  px: 1,
                  py: 1.15,
                  verticalAlign: 'middle',
                }}>
                {column.template.name}
                <Box sx={{ color: FAINT, fontSize: 10, mt: 0.2 }}>per {column.template.unit}</Box>
                {!!quoteTemplateSecondaryUnitLabel(column.template, item) && (
                  <Box sx={{ color: MUTED, fontSize: 9.5, mt: 0.2, whiteSpace: 'normal', lineHeight: 1.25 }}>
                    {quoteTemplateSecondaryUnitLabel(column.template, item)}
                  </Box>
                )}
                {quantityEditorOptionId === column.optionId || num(comparisonQty[column.optionId]) > 0 ? (
                  <Stack direction="row" alignItems="center" gap={0.25} sx={{ mt: 0.9 }}>
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      placeholder="Qty"
                      value={comparisonQty[column.optionId] || ''}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => void updateComparisonQty({ ...comparisonQty, [column.optionId]: num(event.target.value) })}
                      inputProps={{ min: 0, step: 'any' }}
                      InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 10.5, color: FAINT, fontWeight: 700 } }}>{column.template.unit}</InputAdornment> }}
                      sx={{ flex: 1, minWidth: 0, '& input': { py: 0.35, fontSize: 13, fontWeight: 800, textAlign: 'center' }, '& .MuiInput-underline:before': { borderColor: BORDER }, '& .MuiInput-underline:after': { borderColor: GOLD } }}
                    />
                    {num(comparisonQty[column.optionId]) <= 0 && <Tooltip title="Hide quantity field">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); setQuantityEditorOptionId(null); }} aria-label={`Hide quantity for ${column.template.name}`}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>}
                  </Stack>
                ) : (
                  <Tooltip title={`Add quantity in ${column.template.unit}`}>
                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); setQuantityEditorOptionId(column.optionId); }} aria-label={`Add quantity for ${column.template.name}`} sx={{ mt: 0.7, border: `1px solid ${BORDER}`, color: GOLD_D }}>
                      <CalculateOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {displayedQuotes.map((quote) => {
            const isCurrent = quoteIsCurrent(quote);
            const isSelected = quote.id === selectedQuoteId && isCurrent;
            const revisionRows = quoteRevisionRows(quote);
            const hasHistory = revisionRows.length > 0;
            const historyExpanded = expandedHistoryQuoteId === quote.id;
            const vendorTotal = vendorTotalForQuote(quote);
            const vendorTotalRank = vendorTotalRankByQuoteId.get(quote.id);
            const vendorTotalHighlight = vendorTotalRank === 1
              ? 'rgba(76, 130, 80, 0.18)'
              : vendorTotalRank === 2
                ? 'rgba(76, 130, 80, 0.12)'
                : vendorTotalRank === 3
                  ? 'rgba(76, 130, 80, 0.07)'
                  : quote.excludedFromComparison ? '#fafafa' : '#fff';
            const vendorOrderLines = comparisonLinesForQuote(quote);
            const canPlaceVendorOrder = !quote.excludedFromComparison && vendorOrderLines.length > 0 && !placingOrders;
            const td: any = {
              p: { xs: '8px 10px', sm: '10px 12px' },
              borderTop: `1px solid ${BORDER}`,
              fontSize: 12.5,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              bgcolor: quote.excludedFromComparison ? '#f5f4f2' : '#fff',
            };
            return (
              <React.Fragment key={quote.id}>
              <Box component="tr">
                <Box component="td" onClick={() => hasHistory && setExpandedHistoryQuoteId(historyExpanded ? null : quote.id)} sx={{ ...td, ...stickyCell, textAlign: 'left', bgcolor: quote.excludedFromComparison ? '#f5f4f2' : '#fff', borderLeft: isSelected ? '4px solid #4677b7' : '4px solid transparent', cursor: hasHistory ? 'pointer' : 'default' }}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Box onClick={(event) => event.stopPropagation()} sx={{ display: { xs: 'none', sm: 'block' } }}><Checkbox size="small" checked={isSelected && isCurrent} disabled={!isCurrent} onChange={() => onSelectQuote(quote.id)} /></Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" alignItems="center" gap={0.75}>
                        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{quote.vendor || 'Unnamed vendor'}</Typography>
                        {isSelected && <Chip size="small" label="Selected" sx={{ display: { xs: 'none', sm: 'inline-flex' }, height: 18, fontSize: 10, bgcolor: '#d7e7ff', color: '#285a9f', fontWeight: 800 }} />}
                        {quote.excludedFromComparison && <Chip size="small" label="Excluded" sx={{ height: 18, fontSize: 10, bgcolor: '#f2eee7', color: MUTED, fontWeight: 800 }} />}
                        {!isCurrent && <Chip size="small" label={quoteHistoryStatusLabel(quote)} sx={{ height: 18, fontSize: 10, bgcolor: '#f2eee7', color: MUTED, fontWeight: 800 }} />}
                      </Stack>
                      <Typography component="div" sx={{ display: { xs: 'none', sm: 'block' }, color: MUTED, fontSize: 11.5 }}>
                        <Box component="span" sx={{ display: 'inline-block', px: 0.45, py: '1px', borderRadius: 0.5, bgcolor: '#f4ead7', color: GOLD_D, fontSize: 11.5, fontWeight: 800, lineHeight: 1.25 }}>
                          {quote.gst}% {quote.gstMode === 'inclusive' ? 'incl' : 'excl'}
                        </Box>
                        {quote.brand && <> · {quote.brand}</>}
                        {quoteEffectiveFrom(quote) && <> · {quoteEffectiveFrom(quote)}</>}
                      </Typography>
                      <Typography sx={{ display: { xs: 'block', sm: 'none' }, color: MUTED, fontSize: 11.5 }}>
                        {quoteEffectiveFrom(quote)}
                      </Typography>
                      {!!quoteReferenceLabel(quote) && (
                        <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: FAINT, fontSize: 11 }}>
                          Ref: {quoteReferenceLabel(quote)}
                        </Typography>
                      )}
                      <Box onClick={(event) => event.stopPropagation()} sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.35 }}>
                        <Stack direction="row" gap={0.25}>
                          <ActionIcon title={quote.excludedFromComparison ? 'Include in rate comparison' : 'Exclude from rate comparison'} onClick={() => onToggleQuoteComparison(quote.id)}><VisibilityOffOutlinedIcon fontSize="inherit" /></ActionIcon>
                          <ActionIcon title="Edit quote" onClick={() => onEditQuote(quote)}><EditOutlinedIcon fontSize="inherit" /></ActionIcon>
                        </Stack>
                      </Box>
                    </Box>
                    <Box onClick={(event) => event.stopPropagation()} sx={{ display: { xs: 'none', sm: 'block' } }}><Stack direction="row" gap={0.25}>
                      <ActionIcon title={quote.excludedFromComparison ? 'Include in rate comparison' : 'Exclude from rate comparison'} onClick={() => onToggleQuoteComparison(quote.id)}><VisibilityOffOutlinedIcon fontSize="inherit" /></ActionIcon>
                      <ActionIcon title="Edit quote" onClick={() => onEditQuote(quote)}><EditOutlinedIcon fontSize="inherit" /></ActionIcon>
                    </Stack></Box>
                    {hasHistory && <ExpandMoreIcon sx={{ fontSize: 18, color: GOLD_D, transform: historyExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .15s' }} />}
                  </Stack>
                </Box>
                {columns.map((column) => {
                  const option = (quote.quoteOptions || []).find((x) => x.id === column.optionId);
                  const rate = option ? (rateView === 'incl' ? quoteOptionRateInclGst(quote, option) : quoteOptionRateExclGst(quote, option)) : 0;
                  const currentRateRevision = effectiveRateRevision(option?.rateHistory);
                  const lowest = lowestByColumn.get(column.optionId);
                  const isLowest = isComparison && rate > 0 && lowest === rate;
                  const optionQuoteId = selectedOptionQuoteIds[column.optionId] || null;
                  const isOptionSelected = optionQuoteId === quote.id && isCurrent;
                  const isEffectiveSelected = isOptionSelected || (!optionQuoteId && isSelected && rate > 0);
                  const optionNeedsSelection = pendingUsedOptions.has(column.optionId);
                  return (
                    <Box
                      component="td"
                      key={column.optionId}
                      onClick={() => {
                        if (rate > 0 && isCurrent) onSelectOptionQuote(column.optionId, quote.id);
                      }}
                      title={rate > 0 && isCurrent ? 'Use this vendor rate for this option' : undefined}
                      sx={{
                      ...td,
                      position: 'relative',
                      '&:hover .rate-revision-control': { opacity: 1 },
                      fontWeight: isLowest ? 900 : 600,
                      color: rate ? DARK : FAINT,
                      bgcolor: isLowest ? 'rgba(76, 130, 80, 0.12)' : (optionNeedsSelection ? '#fffaf0' : td.bgcolor),
                      boxShadow: isOptionSelected ? 'inset 0 0 0 2px rgba(42, 91, 157, 0.32)' : undefined,
                      cursor: rate > 0 && isCurrent ? 'pointer' : 'default',
                      width: 'auto',
                      minWidth: rateColumnMinWidths,
                      borderLeft: column === columns[0] || column.groupLabel !== columns[columns.indexOf(column) - 1]?.groupLabel ? `1px solid ${BORDER}` : undefined,
                    }}>
                      <Stack direction="row" justifyContent="center" alignItems="center" gap={0.5}>
                        <Box>{rate ? fmtRate(rate) : '-'}</Box>
                        {isEffectiveSelected && (
                          <CheckOutlinedIcon sx={{ fontSize: 13, color: '#285a9f' }} />
                        )}
                      </Stack>
                  {num(comparisonQty[column.optionId]) > 0 && rate > 0 && <Typography sx={{ color: GOLD_D, fontSize: 10, mt: 0.25, fontWeight: 800, whiteSpace: 'nowrap' }}>₹{fmtRate(rate * num(comparisonQty[column.optionId]))}</Typography>}
                      {rate > 0 && <Box className="rate-revision-control" onClick={(event) => event.stopPropagation()} sx={{ position: 'absolute', top: 3, right: 3, opacity: isEffectiveSelected ? 1 : 0, transition: 'opacity .15s' }}><RateRevisionControl onClick={() => onAddRateRevision(quote, column.optionId)} title={`Add revised rate for ${column.template.name}`} /></Box>}
                      {currentRateRevision && <Typography sx={{ color: FAINT, fontSize: 9.5, mt: 0.3, fontWeight: 700, whiteSpace: 'nowrap' }}>{currentRateRevision.effectiveFrom}</Typography>}
                      {!!option?.brand && (
                        <Typography sx={{ color: MUTED, fontSize: 10.5, mt: 0.35, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {option.brand}
                        </Typography>
                      )}
                      {!!item.codeRequired && !!option?.code && (
                        <Typography sx={{ color: FAINT, fontSize: 10.5, mt: 0.35, fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {option.code}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                {comparisonColumns.length > 0 && <Box component="td" sx={{ ...td, minWidth: 48, width: 48, p: 0 }}>
                  <Tooltip title={quote.excludedFromComparison ? 'Include this vendor in comparison first' : vendorOrderLines.length ? `Place ${vendorOrderLines.length} order${vendorOrderLines.length === 1 ? '' : 's'} with ${quote.vendor || 'this vendor'}` : 'Select this vendor for at least one quantity first'}>
                    <Box component="span">
                      <IconButton
                        size="small"
                        disabled={!canPlaceVendorOrder}
                        onClick={() => createComparisonOrders(vendorOrderLines)}
                        aria-label={`Place order with ${quote.vendor || 'vendor'}`}
                        sx={{ color: GOLD_D, '&.Mui-disabled': { color: '#b8b3aa' } }}
                      >
                        <ShoppingCartOutlinedIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </Box>}
                {comparisonColumns.length > 0 && <Box component="td" title={vendorTotalRank ? `${vendorTotalRank === 1 ? 'Lowest' : `${vendorTotalRank}${vendorTotalRank === 2 ? 'nd' : 'rd'} lowest`} vendor total` : undefined} sx={{ ...td, minWidth: { xs: 150, sm: 178 }, textAlign: 'right', fontWeight: 900, color: GOLD_D, bgcolor: vendorTotalHighlight }}>
                  ₹{fmtRate(vendorTotal)}
                </Box>}
              </Box>
              {historyExpanded && <Box component="tr">
                <Box component="td" colSpan={columns.length + 1 + (comparisonColumns.length ? 2 : 0)} sx={{ p: 0, borderTop: `1px solid ${BORDER}`, bgcolor: '#faf8f5' }}>
                  <Box sx={{ px: 1.5, py: 1.1, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontWeight: 900, color: DARK, fontSize: 13 }}>Rate history · {quote.vendor || 'Vendor'}</Typography>
                  </Box>
                  <RateHistoryTrend quote={quote} />
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead"><Box component="tr">{['Category / variety', 'Previous rate', 'New rate', 'Change', 'Effective from', 'Reason', ''].map((label) => <Box key={label || 'actions'} component="th" sx={{ ...headTh, textAlign: label === 'Category / variety' || label === 'Reason' ? 'left' : 'right', py: 0.7 }}>{label}</Box>)}</Box></Box>
                    <Box component="tbody">{revisionRows.map((entry, index) => { const difference = entry.revisedRate - entry.previousRate; const reason = entry.reason?.trim(); const revision = entry.optionId ? (quote.quoteOptions || []).find((option) => option.id === entry.optionId)?.rateHistory?.find((item) => item.id === entry.id) : quote.baseRateHistory?.find((item) => item.id === entry.id); return <Box component="tr" key={`${entry.id}_${index}`}><Box component="td" sx={{ p: '8px 12px', borderTop: `1px solid ${BORDER}`, fontSize: 12, fontWeight: 700 }}>{entry.label}</Box><Box component="td" sx={{ p: '8px 12px', borderTop: `1px solid ${BORDER}`, textAlign: 'right', fontSize: 12 }}>₹{fmtRate(entry.previousRate)}</Box><Box component="td" sx={{ p: '8px 12px', borderTop: `1px solid ${BORDER}`, textAlign: 'right', fontSize: 12, fontWeight: 800, color: difference < 0 ? '#397452' : GOLD_D }}>₹{fmtRate(entry.revisedRate)}</Box><Box component="td" sx={{ p: '8px 12px', borderTop: `1px solid ${BORDER}`, textAlign: 'right', fontSize: 12, color: difference > 0 ? GOLD_D : difference < 0 ? '#397452' : MUTED, fontWeight: 700 }}>{difference === 0 ? '—' : `${difference > 0 ? '+' : '−'}₹${fmtRate(Math.abs(difference))}`}</Box><Box component="td" sx={{ p: '8px 12px', borderTop: `1px solid ${BORDER}`, textAlign: 'right', fontSize: 12 }}>{entry.effectiveFrom}</Box><Box component="td" sx={{ p: '8px 12px', borderTop: `1px solid ${BORDER}`, fontSize: 12, color: MUTED }}>{reason && !/^(?:revised(?: rate)?|rate revised)$/i.test(reason) ? reason : '—'}</Box><Box component="td" sx={{ p: '5px 8px', borderTop: `1px solid ${BORDER}`, textAlign: 'right', whiteSpace: 'nowrap' }}>{revision && <><ActionIcon title="Edit revised rate" onClick={() => onEditRateRevision(quote, entry.optionId, revision)}><EditOutlinedIcon fontSize="inherit" /></ActionIcon><ActionIcon title="Delete revised rate" danger onClick={() => onDeleteRateRevision(quote.id, entry.optionId, entry.id)}><DeleteOutlineIcon fontSize="inherit" /></ActionIcon></>}</Box></Box>; })}</Box>
                  </Box>
                </Box>
              </Box>}
              </React.Fragment>
            );
          })}
          {columns.some((column) => pendingUsedOptions.has(column.optionId)) && (
            <Box component="tr">
              <Box component="td" sx={{ ...stickyCell, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff', height: 34 }} />
              {columns.map((column) => {
                const optionNeedsSelection = pendingUsedOptions.has(column.optionId);
                return (
                  <Box
                    component="td"
                    key={`${column.optionId}_pending_footer`}
                    sx={{
                      p: '6px 8px',
                      borderTop: `1px solid ${BORDER}`,
                      textAlign: 'center',
                      bgcolor: optionNeedsSelection ? '#fff4d8' : '#fff',
                      color: optionNeedsSelection ? GOLD_D : 'transparent',
                      fontSize: 10,
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      letterSpacing: '.35px',
                      whiteSpace: 'nowrap',
                      width: 'auto',
                      minWidth: rateColumnMinWidths,
                      borderLeft: column === columns[0] || column.groupLabel !== columns[columns.indexOf(column) - 1]?.groupLabel ? `1px solid ${BORDER}` : undefined,
                    }}
                  >
                    {optionNeedsSelection ? 'Needs selection' : '-'}
                  </Box>
                );
              })}
              {comparisonColumns.length > 0 && <Box component="td" sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: '#fff', minWidth: 48, width: 48 }} />}
              {comparisonColumns.length > 0 && <Box component="td" sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: '#fff', minWidth: { xs: 150, sm: 178 } }} />}
            </Box>
          )}
        </Box>
      </Box>
      <Dialog open={!!orderReview} onClose={placingOrders ? undefined : () => setOrderReview(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontSize: 24, fontWeight: 700 }}>Review purchase order</DialogTitle>
        {orderReview && reviewTotals && <DialogContent dividers>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={0.75} sx={{ mb: 2 }}>
            <Box><Typography sx={{ color: FAINT, fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>Vendor</Typography><Typography sx={{ fontWeight: 800 }}>{orderReview.orders[0]?.vendor || 'Vendor not set'}</Typography></Box>
            <Box sx={{ textAlign: { sm: 'right' } }}><Typography sx={{ color: FAINT, fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>PO / order date</Typography><Typography sx={{ fontWeight: 800 }}>{orderReview.orders[0]?.poNumber || 'Auto-generated PO'} · {orderReview.orders[0]?.orderDate}</Typography></Box>
          </Stack>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(150px, 1fr) 72px 100px', sm: 'minmax(220px, 1fr) 96px 145px 145px' }, gap: 1, px: 1.5, py: 1, bgcolor: PANEL2, color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>
              <Box>Variety</Box><Box sx={{ textAlign: 'right' }}>Qty</Box><Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>Rate incl GST</Box><Box sx={{ textAlign: 'right' }}>Line total</Box>
            </Box>
            {orderReview.orders.map((order, index) => <Box key={`${order.quoteId}_${order.quoteOptionId}`} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(150px, 1fr) 72px 100px', sm: 'minmax(220px, 1fr) 96px 145px 145px' }, gap: 1, alignItems: 'center', px: 1.5, py: 1.1, borderTop: `1px solid ${BORDER}` }}>
              <Box><Typography sx={{ fontWeight: 800, fontSize: 13 }}>{order.materialLabel}</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>{order.orderedGstPercent || 0}% {order.orderedGstMode === 'exclusive' ? 'excl GST' : 'incl GST'}</Typography></Box>
              <TextField size="small" type="number" variant="standard" value={order.orderQty || ''} onChange={(event) => setOrderReview((current) => current ? { ...current, orders: current.orders.map((line, lineIndex) => lineIndex === index ? { ...line, orderQty: num(event.target.value) } : line) } : current)} inputProps={{ min: 0, step: 'any', 'aria-label': `Quantity for ${order.materialLabel}` }} InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 10, color: FAINT } }}>{order.unit}</InputAdornment> }} sx={{ '& input': { textAlign: 'right', fontSize: 13, fontWeight: 800, py: 0.25 } }} />
              <Typography sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', fontWeight: 700, fontSize: 13 }}>₹{fmtRate(num(order.orderedRateInclGst))}/{order.unit}</Typography>
              <Typography sx={{ textAlign: 'right', fontWeight: 900, color: GOLD_D, fontSize: 13 }}>₹{fmtRate(reviewLineTotal(order))}</Typography>
            </Box>)}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 1.5 }}>
            <TextField label="Freight / loading" type="number" variant="standard" value={orderReview.freightAmount || ''} onChange={(event) => setOrderReview((current) => current ? { ...current, freightAmount: num(event.target.value) } : current)} inputProps={{ min: 0, step: 'any', 'aria-label': 'PO freight and loading amount' }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={{ width: { xs: '100%', sm: 220 }, '& input': { textAlign: 'right', fontSize: 14, fontWeight: 800, py: 0.35 }, '& .MuiInput-underline:before': { borderColor: BORDER }, '& .MuiInput-underline:after': { borderColor: GOLD } }} />
          </Box>
          <Stack alignItems="flex-end" sx={{ mt: 1.5 }} gap={0.4}>
            <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Material subtotal: ₹{fmtRate(reviewTotals.material)}</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Freight / loading: ₹{fmtRate(reviewTotals.freight)}</Typography>
            <Typography sx={{ color: DARK, fontWeight: 900, fontSize: 17 }}>Order total: ₹{fmtRate(reviewTotals.total)}</Typography>
          </Stack>
        </DialogContent>}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={placingOrders} onClick={() => setOrderReview(null)} sx={{ color: MUTED }}>Cancel</Button>
          <Button variant="contained" disabled={placingOrders} onClick={confirmComparisonOrders} startIcon={<ShoppingCartOutlinedIcon />} sx={{ bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D } }}>{placingOrders ? 'Creating PO…' : 'Create PO'}</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
}

function MaterialDealNotesEditor({ notes, onSave }: { notes: string; onSave: (notes: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes);
  useEffect(() => {
    if (!editing) setDraft(notes);
  }, [notes, editing]);
  const lines = notes.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const dirty = draft !== notes;
  const save = () => {
    onSave(draft);
    setEditing(false);
  };
  if (editing) {
    return (
      <Box sx={{ px: 1.75, py: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
        <Stack direction="row" alignItems="flex-start" gap={1}>
          <TextField
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            label="Final deal notes"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Enter each condition on a new line"
            size="small"
          />
          <ActionIcon title="Save notes" onClick={save}>
            <CheckOutlinedIcon fontSize="inherit" />
          </ActionIcon>
          <Button size="small" onClick={() => { setDraft(notes); setEditing(false); }} sx={{ color: MUTED, mt: 0.25 }}>
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }
  return (
    <Box
      onClick={() => setEditing(true)}
      sx={{
        px: 1.75,
        py: 1.15,
        borderTop: `1px solid ${BORDER}`,
        bgcolor: lines.length ? '#fff' : '#fafafa',
        cursor: 'text',
        '&:hover': { bgcolor: GOLD_WASH },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, color: FAINT, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.4 }}>
            Final deal notes
          </Typography>
          {lines.length ? (
            <Box component="ul" sx={{ m: 0, pl: 2.2, color: MUTED, fontSize: 12, lineHeight: 1.55, listStyleType: 'disc' }}>
              {lines.map((line, index) => (
                <Box component="li" key={`${line}-${index}`} sx={{ display: 'list-item', mb: 0.25 }}>
                  {line}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: FAINT, fontSize: 12 }}>Add conditions to consider before final deal.</Typography>
          )}
        </Box>
        {dirty && <CheckOutlinedIcon sx={{ color: GOLD, fontSize: 16 }} />}
      </Stack>
    </Box>
  );
}

function RateSummary({ label, item, quote }: { label: string; item: MaterialRateItem; quote: MaterialQuote | null }) {
  return (
    <Box sx={{ minWidth: 126, textAlign: { xs: 'left', md: 'right' } }}>
      <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, color: quote ? GOLD_D : MUTED }}>
        {quote ? quoteLandedRateLabel(item, quote) : '—'}
      </Typography>
      {quote && <Typography sx={{ color: MUTED, fontSize: 11 }}>{quote.vendor}</Typography>}
    </Box>
  );
}

function quoteLandedRate(item: MaterialRateItem, quote: MaterialQuote): number {
  const range = quoteRateRange(item, quote);
  const base = range.min || quoteBaseRate(quote);
  const gst = quote.gstMode === 'inclusive' || quote.gstMode === 'none' ? 0 : base * num(quote.gst) / 100;
  return base + gst + num(quote.freight) + num(quote.loading);
}

function quoteBaseRateExcl(rate: number, gst: number, gstMode?: 'inclusive' | 'exclusive' | 'none'): number {
  if (!rate) return 0;
  if (gstMode === 'inclusive' && gst > 0) return rate / (1 + gst / 100);
  return rate;
}

function effectiveRateRevision(history: MaterialRateRevision[] | undefined, date = new Date().toISOString().slice(0, 10)): MaterialRateRevision | undefined {
  return (history || [])
    .filter((revision) => revision.effectiveFrom <= date && num(revision.rate) > 0)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom) || b.recordedAt.localeCompare(a.recordedAt))[0];
}

function rateAtDate(history: MaterialRateRevision[] | undefined, fallbackRate: number, date = new Date().toISOString().slice(0, 10)): number {
  const effective = effectiveRateRevision(history, date);
  return effective ? num(effective.rate) : num(fallbackRate);
}

export function quoteBaseRate(quote: MaterialQuote, date?: string): number {
  return rateAtDate(quote.baseRateHistory, quote.rate, date);
}

export function quoteOptionBaseRate(quote: MaterialQuote, option: MaterialQuoteOption, date?: string): number {
  const fallback = num(option.rate) || (option.type === 'base' ? quoteBaseRate(quote, date) : 0);
  return rateAtDate(option.rateHistory, fallback, date);
}

function quoteLandedRateExcl(item: MaterialRateItem, quote: MaterialQuote): number {
  const range = quoteRateRange(item, quote);
  const base = range.min || quoteBaseRate(quote);
  return quoteBaseRateExcl(base, num(quote.gst), quote.gstMode) + num(quote.freight) + num(quote.loading);
}

function quoteOptionLandedRate(quote: MaterialQuote, option: MaterialQuoteOption): number {
  const base = quoteOptionBaseRate(quote, option);
  if (!base) return 0;
  const gst = quote.gstMode === 'inclusive' || quote.gstMode === 'none' ? 0 : base * num(quote.gst) / 100;
  return base + gst + num(quote.freight) + num(quote.loading);
}

function quoteOptionLandedRateExcl(quote: MaterialQuote, option: MaterialQuoteOption): number {
  const base = quoteOptionBaseRate(quote, option);
  if (!base) return 0;
  return quoteBaseRateExcl(base, num(quote.gst), quote.gstMode) + num(quote.freight) + num(quote.loading);
}

function quoteOptionRateExclGst(quote: MaterialQuote, option: MaterialQuoteOption): number {
  const base = quoteOptionBaseRate(quote, option);
  if (!base) return 0;
  return quoteBaseRateExcl(base, num(quote.gst), quote.gstMode);
}

function quoteOptionRateInclGst(quote: MaterialQuote, option: MaterialQuoteOption): number {
  const base = quoteOptionBaseRate(quote, option);
  if (!base) return 0;
  if (quote.gstMode === 'inclusive' || quote.gstMode === 'none') return base;
  return base + base * num(quote.gst) / 100;
}

function selectedQuoteOption(quote: MaterialQuote | null, optionId?: string): MaterialQuoteOption | null {
  if (!quote || !optionId) return null;
  return (quote.quoteOptions || []).find((option) => option.id === optionId) || null;
}

function materialSelectableQuoteOptions(item: MaterialRateItem | undefined): MaterialQuoteOption[] {
  if (!item) return [];
  const groups = materialQuoteMatrixGroups(item);
  if (!groups.length) return selectedMaterialQuote(item)?.quoteOptions || [];
  return groups.flatMap((group) =>
    group.columns.map((column) => {
      const quote = selectedMaterialQuoteForOption(item, column.optionId);
      const option = selectedQuoteOption(quote, column.optionId);
      if (option) return option;
      return {
        id: column.optionId,
        templateId: column.template.id,
        group: column.template.group || '',
        variety: column.variety,
        name: column.template.name,
        type: column.template.type,
        rate: 0,
        unit: column.template.unit,
        secondaryUnitRequired: column.template.secondaryUnitRequired,
        secondaryMultiplier: column.template.secondaryMultiplier,
        secondaryUnit: column.template.secondaryUnit,
      };
    })
  );
}

function quoteOptionId(templateId: string, variety: string): string {
  return variety ? `${templateId}__${variety}` : templateId;
}

function quoteOptionLabel(option: MaterialQuoteOption): string {
  return [option.group, option.variety, option.name].filter(Boolean).join(' · ');
}

function materialOptionIdForVariety(item: MaterialRateItem | undefined, variety?: string): string {
  const target = (variety || '').trim().toLowerCase();
  if (!item || !target) return '';
  const option = (item.quotes || []).flatMap((quote) => quote.quoteOptions || []).find((entry) =>
    [entry.variety, entry.name].some((value) => (value || '').trim().toLowerCase() === target)
  );
  return option?.id || '';
}

function materialQuoteRateUnit(option: MaterialQuoteOption | null | undefined, fallbackUnit: string): string {
  const variety = (option?.variety || '').trim();
  // Pack-labelled varieties, such as 50 Kg, are the price denominator rather than the consumption unit.
  if (/^\d+(?:\.\d+)?\s*(kg|kgs|bag|bags|box|boxes|set|sets|roll|rolls|nos?)$/i.test(variety)) return variety;
  return option?.unit || fallbackUnit || 'unit';
}

type MaterialQuoteMatrixColumn = {
  groupLabel: string;
  template: MaterialQuoteOptionTemplate;
  optionId: string;
  variety: string;
};

type MaterialQuoteMatrixGroup = {
  label: string;
  columns: MaterialQuoteMatrixColumn[];
};

function materialQuoteMatrixGroups(item: MaterialRateItem): MaterialQuoteMatrixGroup[] {
  const templates = item.quoteOptionTemplates || [];
  if (!templates.length) return [];
  const hasTemplateGroups = templates.some((template) => !!(template.group || '').trim());
  if (hasTemplateGroups) {
    const groups: MaterialQuoteMatrixGroup[] = [];
    for (const template of templates) {
      const label = (template.group || 'Options').trim() || 'Options';
      let group = groups.find((entry) => entry.label === label);
      if (!group) {
        group = { label, columns: [] };
        groups.push(group);
      }
      group.columns.push({ groupLabel: label, template, optionId: template.id, variety: '' });
    }
    return groups;
  }
  const varieties = item.varieties || [];
  return varieties.map((variety) => ({
    label: variety,
    columns: templates.map((template) => ({
      groupLabel: variety,
      template,
      optionId: quoteOptionId(template.id, variety),
      variety,
    })),
  }));
}

export function expandQuoteOptionsFromTemplate(item: MaterialRateItem, existing: MaterialQuoteOption[] = []): MaterialQuoteOption[] {
  const templates = item.quoteOptionTemplates || [];
  if (!templates.length) return existing;
  const hasTemplateGroups = templates.some((template) => !!(template.group || '').trim());
  const varieties = hasTemplateGroups ? [''] : (item.varieties?.length ? item.varieties : ['']);
  const existingById = new Map(existing.map((option) => [option.id, option]));
  const generatedIds = new Set<string>();
  const rows: MaterialQuoteOption[] = [];
  for (const variety of varieties) {
    for (const template of templates) {
      const id = hasTemplateGroups ? template.id : quoteOptionId(template.id, variety);
      generatedIds.add(id);
      const saved = existingById.get(id);
      rows.push({
        id,
        templateId: template.id,
        group: (template.group || saved?.group || '').trim(),
        variety: hasTemplateGroups ? '' : variety,
        name: template.name,
        type: template.type,
        rate: num(saved?.rate),
        rateHistory: saved?.rateHistory || [],
        unit: saved?.unit || template.unit,
        secondaryUnitRequired: !!(saved?.secondaryUnitRequired ?? template.secondaryUnitRequired),
        secondaryMultiplier: num(saved?.secondaryMultiplier ?? template.secondaryMultiplier),
        secondaryUnit: saved?.secondaryUnit || template.secondaryUnit || '',
        brand: saved?.brand || '',
        code: saved?.code || '',
      });
    }
  }
  for (const option of existing) {
    if (!generatedIds.has(option.id)) rows.push(option);
  }
  return rows;
}

function quoteDiameterRates(quote: MaterialQuote): number[] {
  return STEEL_DIAMETERS
    .map((dia) => {
      const override = num(quote.diameterRates?.[dia]);
      if (override > 0) return override;
      return (quote.coveredDiameters || STEEL_DIAMETERS).includes(dia) ? quoteBaseRate(quote) : 0;
    })
    .filter((rate) => rate > 0);
}

function quoteRateRange(item: MaterialRateItem, quote: MaterialQuote): { min: number; max: number } {
  const rates = item.group === 'steel' ? quoteDiameterRates(quote) : [quoteBaseRate(quote)];
  const filtered = rates.filter((rate) => rate > 0);
  if (!filtered.length) return { min: 0, max: 0 };
  return { min: Math.min(...filtered), max: Math.max(...filtered) };
}

function quoteRateValueByGst(quote: MaterialQuote, rate: number, gstMode: MaterialGstDisplay): number {
  if (!rate) return 0;
  if (gstMode === 'excl') return quoteBaseRateExcl(rate, num(quote.gst), quote.gstMode);
  if (quote.gstMode === 'inclusive' || quote.gstMode === 'none') return rate;
  return rate + rate * num(quote.gst) / 100;
}

function quoteRateRangeByGst(item: MaterialRateItem, quote: MaterialQuote, gstMode: MaterialGstDisplay): { min: number; max: number } {
  const sourceRates = item.group === 'steel' ? quoteDiameterRates(quote) : [quoteBaseRate(quote)];
  const rates = sourceRates.map((rate) => quoteRateValueByGst(quote, rate, gstMode)).filter((rate) => rate > 0);
  if (!rates.length) return { min: 0, max: 0 };
  return { min: Math.min(...rates), max: Math.max(...rates) };
}

function quoteRateLabel(item: MaterialRateItem, quote: MaterialQuote): string {
  const { min, max } = quoteRateRange(item, quote);
  if (!min) return '—';
  return min === max ? `₹${fmtRate(min)} / ${quote.unit}` : `₹${fmtRate(min)}-${fmtRate(max)} / ${quote.unit}`;
}

function quoteRateLabelByGst(item: MaterialRateItem, quote: MaterialQuote, gstMode: MaterialGstDisplay): string {
  const { min, max } = quoteRateRangeByGst(item, quote, gstMode);
  if (!min) return '—';
  return min === max ? `₹${fmtRate(min)} / ${quote.unit}` : `₹${fmtRate(min)}-${fmtRate(max)} / ${quote.unit}`;
}

function quoteLandedRateLabel(item: MaterialRateItem, quote: MaterialQuote): string {
  const { min, max } = quoteRateRange(item, quote);
  if (!min) return '—';
  const landed = (rate: number) => {
    const gst = quote.gstMode === 'inclusive' || quote.gstMode === 'none' ? 0 : rate * num(quote.gst) / 100;
    return rate + gst + num(quote.freight) + num(quote.loading);
  };
  return min === max ? `₹${fmtRate(landed(min))}/${quote.unit}` : `₹${fmtRate(landed(min))}-${fmtRate(landed(max))}/${quote.unit}`;
}

function quoteCoverageLabel(item: MaterialRateItem, quote: MaterialQuote): string {
  if (item.group !== 'steel') return quote.unit || item.unit || '—';
  const count = STEEL_DIAMETERS.filter((dia) =>
    (quote.coveredDiameters || STEEL_DIAMETERS).includes(dia) || num(quote.diameterRates?.[dia]) > 0
  ).length;
  return `${count}/${STEEL_DIAMETERS.length} sizes`;
}

function buildMaterialQuotePayload(args: {
  id: string;
  vendor: string;
  brand: string;
  refName: string;
  refNumber: string;
  rate: number;
  materialCode: string;
  unit: string;
  gst: number;
  gstMode: 'inclusive' | 'exclusive';
  freight: number;
  loading: number;
  quoteDate: string;
  validUntil: string;
  paymentTerms: string;
  deliveryTimeline: string;
  notes: string;
  quoteOptions: MaterialQuoteOption[];
  checklist: MaterialQuote['checklist'];
  isSteel: boolean;
  coveredDiameters: string[];
  diameterRates: Record<string, string>;
}): MaterialQuote {
  const quote: MaterialQuote = {
    id: args.id,
    vendor: args.vendor,
    brand: args.brand,
    refName: args.refName.trim(),
    refNumber: args.refNumber.trim(),
    rate: args.rate,
    materialCode: args.materialCode.trim(),
    unit: args.unit,
    gst: args.gst,
    gstMode: args.gstMode,
    freight: args.freight,
    loading: args.loading,
    quoteDate: args.quoteDate,
    validUntil: args.validUntil,
    effectiveFrom: args.quoteDate,
    effectiveUntil: args.validUntil,
    paymentTerms: args.paymentTerms,
    deliveryTimeline: args.deliveryTimeline,
    notes: args.notes,
    quoteOptions: args.quoteOptions
      .filter((option) => option.name.trim() && (option.type === 'base' || num(option.rate) > 0))
      .map((option) => ({
        id: option.id,
        templateId: option.templateId,
        group: (option.group || '').trim(),
        variety: option.variety,
        name: option.name.trim(),
        type: option.type,
        rate: num(option.rate),
        unit: option.unit,
        secondaryUnitRequired: !!option.secondaryUnitRequired,
        secondaryMultiplier: num(option.secondaryMultiplier),
        secondaryUnit: (option.secondaryUnit || '').trim(),
        brand: (option.brand || '').trim(),
        code: (option.code || '').trim(),
      })),
    checklist: args.checklist,
  };
  if (args.isSteel) {
    quote.coveredDiameters = args.coveredDiameters;
    quote.diameterRates = Object.fromEntries(Object.entries(args.diameterRates).map(([dia, value]) => [dia, value === '' ? null : num(value)]));
  }
  quote.quoteOptions = quote.quoteOptions?.filter((option) => num(option.rate) > 0);
  return quote;
}

function quoteEffectiveFrom(quote: MaterialQuote): string {
  return quote.effectiveFrom || quote.quoteDate || '';
}

function quoteEffectiveUntil(quote: MaterialQuote): string {
  return quote.effectiveUntil || quote.validUntil || '';
}

function quoteIsCurrent(quote: MaterialQuote): boolean {
  // Selection is attached to the stable vendor quote. Individual rate revisions decide
  // which price applies on a date, so an older quote's validity date must not clear it.
  return (quote.status || 'active') === 'active';
}

function quoteHistoryStatusLabel(quote: MaterialQuote): string {
  if (quote.status === 'superseded') return 'Superseded';
  if (quote.status === 'voided') return 'Voided';
  return 'Active';
}

function checklistSummary(quote: MaterialQuote): string {
  const c = quote.checklist || {};
  const done = [
    c.testCertificate,
    c.weighbridgeSlip,
    c.heatNumberTraceability,
    c.freeUnloading,
    c.creditPeriod,
  ].filter(Boolean).length;
  const optionCount = quote.quoteOptions?.length || 0;
  return optionCount ? `${done}/5 checked · ${optionCount} option(s)` : `${done}/5 checked`;
}

function selectedMaterialQuote(item: MaterialRateItem | undefined): MaterialQuote | null {
  if (!item) return null;
  const quote = (item.quotes || []).find((candidate) => candidate.id === item.selectedQuoteId) || null;
  return quote && quoteIsCurrent(quote) ? quote : null;
}

export function selectedMaterialQuoteForOption(item: MaterialRateItem | undefined, optionId?: string): MaterialQuote | null {
  if (!item) return null;
  const optionQuoteId = optionId ? item.selectedOptionQuoteIds?.[optionId] : null;
  const optionQuote = optionQuoteId ? (item.quotes || []).find((quote) => quote.id === optionQuoteId) : null;
  return optionQuote && quoteIsCurrent(optionQuote) ? optionQuote : selectedMaterialQuote(item);
}

function defaultCostingQuoteForOption(item: MaterialRateItem | undefined, optionId?: string): MaterialQuote | null {
  if (!item) return null;
  const quoteId = optionId ? item.defaultCostingOptionQuoteIds?.[optionId] : item.defaultCostingQuoteId;
  const quote = quoteId ? (item.quotes || []).find((candidate) => candidate.id === quoteId) || null : null;
  return quote && quoteIsCurrent(quote) ? quote : null;
}

function tentativeCostingQuoteForOption(item: MaterialRateItem | undefined, optionId?: string): MaterialQuote | null {
  if (!item) return null;
  const explicit = defaultCostingQuoteForOption(item, optionId);
  if (explicit) return explicit;
  const usableQuotes = (item.quotes || []).filter((quote) => {
    if (!quoteIsCurrent(quote)) return false;
    if (!optionId) return quoteLandedRate(item, quote) > 0;
    const option = selectedQuoteOption(quote, optionId);
    return !!option && quoteOptionLandedRate(quote, option) > 0;
  });
  return usableQuotes.reduce<MaterialQuote | null>((highest, quote) => {
    if (!highest) return quote;
    const rate = optionId
      ? quoteOptionLandedRate(quote, selectedQuoteOption(quote, optionId) as MaterialQuoteOption)
      : quoteLandedRate(item, quote);
    const highestRate = optionId
      ? quoteOptionLandedRate(highest, selectedQuoteOption(highest, optionId) as MaterialQuoteOption)
      : quoteLandedRate(item, highest);
    return rate > highestRate ? quote : highest;
  }, null);
}

function costingMaterialItems(materialItems: MaterialRateItem[]): MaterialRateItem[] {
  return materialItems.map((item) => {
    const selectedOptionQuoteIds = { ...(item.defaultCostingOptionQuoteIds || {}), ...(item.selectedOptionQuoteIds || {}) };
    for (const column of materialQuoteMatrixGroups(item).flatMap((group) => group.columns)) {
      if (!selectedOptionQuoteIds[column.optionId]) {
        const quote = tentativeCostingQuoteForOption(item, column.optionId);
        if (quote) selectedOptionQuoteIds[column.optionId] = quote.id;
      }
    }
    return {
      ...item,
      selectedQuoteId: item.selectedQuoteId || tentativeCostingQuoteForOption(item)?.id || null,
      selectedOptionQuoteIds,
    };
  });
}

function materialQuotePdfRows(item: MaterialRateItem, gstMode: MaterialGstDisplay): string {
  const quotes = item.quotes || [];
  const groups = materialQuoteMatrixGroups(item);
  if (!quotes.length) {
    return `<div class="empty">No vendor quotes yet.</div>`;
  }
  if (groups.length) {
    const columns = groups.flatMap((group) => group.columns);
    const lowestByColumn = new Map<string, number>();
    for (const column of columns) {
      const values = quotes
        .filter((quote) => !quote.excludedFromComparison)
        .map((quote) => {
          const option = (quote.quoteOptions || []).find((x) => x.id === column.optionId);
          return option ? (gstMode === 'incl' ? quoteOptionRateInclGst(quote, option) : quoteOptionRateExclGst(quote, option)) : 0;
        })
        .filter((rate) => rate > 0);
      if (values.length) lowestByColumn.set(column.optionId, Math.min(...values));
    }
    const groupHeader = groups.map((group) =>
      `<th colspan="${group.columns.length}" class="center group-head">${escHtml(group.label)}</th>`
    ).join('');
    const optionHeader = columns.map((column, index) => {
      const isGroupStart = index === 0 || column.groupLabel !== columns[index - 1]?.groupLabel;
      const isGroupEnd = index === columns.length - 1 || column.groupLabel !== columns[index + 1]?.groupLabel;
      const secondary = quoteTemplateSecondaryUnitLabel(column.template, item);
      return `<th class="center ${isGroupStart ? 'group-start' : ''} ${isGroupEnd ? 'group-end' : ''}">${escHtml(column.template.name)}<div class="unit">per ${escHtml(column.template.unit)}</div>${secondary ? `<div class="unit">${escHtml(secondary)}</div>` : ''}</th>`;
    }).join('');
    const body = quotes.map((quote) => {
      const isSelected = quote.id === item.selectedQuoteId;
      const cells = columns.map((column, index) => {
        const option = (quote.quoteOptions || []).find((x) => x.id === column.optionId);
        const rate = option ? (gstMode === 'incl' ? quoteOptionRateInclGst(quote, option) : quoteOptionRateExclGst(quote, option)) : 0;
        const alternateRate = quoteTemplateSecondaryRate(column.template, item, rate);
        const lowest = lowestByColumn.get(column.optionId);
        const isLowest = rate > 0 && lowest === rate;
        const isGroupStart = index === 0 || column.groupLabel !== columns[index - 1]?.groupLabel;
        const isGroupEnd = index === columns.length - 1 || column.groupLabel !== columns[index + 1]?.groupLabel;
        return `<td class="num ${isLowest ? 'lowest-price' : ''} ${isGroupStart ? 'group-start' : ''} ${isGroupEnd ? 'group-end' : ''}">${rate ? fmtRate(rate) : '-'}${alternateRate ? `<div class="muted">₹${fmtRate(alternateRate.rate)}/${escHtml(alternateRate.unit)}</div>` : ''}${option?.brand ? `<div class="muted">${escHtml(option.brand)}</div>` : ''}${item.codeRequired && option?.code ? `<div class="muted">${escHtml(option.code)}</div>` : ''}</td>`;
      }).join('');
      return `
        <tr class="${isSelected ? 'selected' : ''}">
          <td>
            <strong>${escHtml(quote.vendor || 'Unnamed vendor')}</strong>
            ${isSelected ? '<span class="pill">Selected</span>' : ''}
            ${quote.excludedFromComparison ? '<span class="pill">Excluded</span>' : ''}
            <div class="muted">${[quote.brand, `${quote.gst}% ${quote.gstMode === 'inclusive' ? 'incl' : 'excl'}`, quote.quoteDate].filter(Boolean).map(escHtml).join(' · ')}</div>
            ${quoteReferenceLabel(quote) ? `<div class="muted">Ref: ${escHtml(quoteReferenceLabel(quote))}</div>` : ''}
          </td>
          ${cells}
        </tr>
      `;
    }).join('');
    return `
      <table>
        <thead>
          <tr><th rowspan="2">Agency</th>${groupHeader}</tr>
          <tr>${optionHeader}</tr>
        </thead>
        <tbody>${body}</tbody>
      </table>
    `;
  }
  const body = quotes.map((quote) => {
    const isSelected = quote.id === item.selectedQuoteId;
    const lowestQuote = quotes.filter((quote) => !quote.excludedFromComparison).reduce<MaterialQuote | null>((best, q) => (
      !best || quoteLandedRate(item, q) < quoteLandedRate(item, best) ? q : best
    ), null);
    const isLowest = quote.id === lowestQuote?.id;
    return `
      <tr class="${isSelected ? 'selected' : ''}">
        <td>
          <strong>${escHtml(quote.vendor || 'Unnamed vendor')}</strong>
          ${isSelected ? '<span class="pill">Selected</span>' : ''}
          ${quote.excludedFromComparison ? '<span class="pill">Excluded</span>' : ''}
          <div class="muted">${escHtml(quote.brand || item.brand || 'Brand not set')}</div>
          ${quoteReferenceLabel(quote) ? `<div class="muted">Ref: ${escHtml(quoteReferenceLabel(quote))}</div>` : ''}
        </td>
        <td class="num">${escHtml(quoteRateLabelByGst(item, quote, gstMode))}${item.codeRequired && quote.materialCode ? `<div class="muted">${escHtml(quote.materialCode)}</div>` : ''}</td>
        <td class="center">${escHtml(quoteCoverageLabel(item, quote))}</td>
        <td class="center">${quote.gstMode === 'none' ? 'No GST' : `${escHtml(quote.gst)}% ${quote.gstMode === 'inclusive' ? 'incl' : 'excl'}`}</td>
        <td class="num">₹${fmtRate(num(quote.freight) + num(quote.loading))}</td>
        <td class="num ${isLowest ? 'lowest-price' : ''}">${escHtml(quoteLandedRateLabel(item, quote))}</td>
        <td class="center">${escHtml(quote.quoteDate || '-')}</td>
        <td class="center">${escHtml(quote.validUntil || '-')}</td>
        <td>${escHtml(checklistSummary(quote))}</td>
      </tr>
    `;
  }).join('');
  return `
    <table>
      <thead>
        <tr>
          <th>Vendor</th>
          <th class="num">Rate (${gstMode === 'incl' ? 'incl GST' : 'excl GST'})</th>
          <th class="center">Coverage</th>
          <th class="center">GST</th>
          <th class="num">Freight</th>
          <th class="num">Landed</th>
          <th class="center">Quote date</th>
          <th class="center">Valid until</th>
          <th>Checklist</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
    </table>
  `;
}

function buildMaterialRatesPdfHtml(items: MaterialRateItem[], title: string, gstMode: MaterialGstDisplay): string {
  const cards = items.map((item, index) => {
    const selected = selectedMaterialQuote(item);
    const dealNoteLines = String(item.dealNotes || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const dealNotesHtml = dealNoteLines.length
      ? `<div class="deal-notes"><div class="deal-title">Final deal notes</div><ul>${dealNoteLines.map((line) => `<li>${escHtml(line)}</li>`).join('')}</ul></div>`
      : '';
    return `
      <section class="card ${items.length > 1 && index > 0 ? 'new-page' : ''}">
        <div class="card-head">
          <div>
            <div class="eyebrow">${escHtml(materialGroupLabel(item.group))}</div>
            <h2>${escHtml(item.materialName)}</h2>
            <div class="muted">${[item.grade, `Unit ${item.unit}`, materialSecondaryUnitLabel(item), item.varieties?.length ? `Varieties: ${item.varieties.join(', ')}` : ''].filter(Boolean).map(escHtml).join(' · ')}</div>
          </div>
          <div class="summary">
            <div><span>Quotes</span><strong>${(item.quotes || []).length}</strong></div>
            <div><span>Selected</span><strong>${escHtml(selected?.vendor || '-')}</strong></div>
          </div>
        </div>
        ${materialQuotePdfRows(item, gstMode)}
        ${dealNotesHtml}
        <div class="note">Vendor quote only. Final deal rate may vary after negotiation and order terms.</div>
      </section>
    `;
  }).join('');
  return `
    <div class="material-rate-pdf">
      <style>
        .material-rate-pdf{font-family:Arial,Helvetica,sans-serif;color:#171717;font-size:11px;}
        .material-rate-pdf h1{font-family:Georgia,'Times New Roman',serif;font-size:22px;margin:0 0 4px;}
        .material-rate-pdf h2{font-size:16px;margin:3px 0 2px;}
        .material-rate-pdf table{width:100%;border-collapse:collapse;table-layout:auto;}
        .material-rate-pdf th{background:#f4f2ee;color:#6f675c;text-transform:uppercase;letter-spacing:.08em;font-size:9px;text-align:left;padding:7px;border:1px solid #dedad2;}
        .material-rate-pdf td{padding:7px;border:1px solid #ececec;vertical-align:top;}
        .material-rate-pdf .top{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:12px;}
        .material-rate-pdf .card{break-inside:avoid;margin:0 0 14px;border:1px solid #e5e1da;border-radius:8px;overflow:hidden;}
        .material-rate-pdf .card.new-page{break-before:page;page-break-before:always;}
        .material-rate-pdf .card-head{display:flex;justify-content:space-between;gap:12px;background:#f2f0eb;padding:12px;}
        .material-rate-pdf .eyebrow,.material-rate-pdf .unit{color:#6e4d24;font-weight:700;text-transform:uppercase;letter-spacing:.08em;font-size:9px;}
        .material-rate-pdf .muted{color:#777;font-size:10px;line-height:1.35;}
        .material-rate-pdf .summary{display:flex;gap:12px;text-align:right;white-space:nowrap;}
        .material-rate-pdf .summary span{display:block;color:#8d8373;text-transform:uppercase;font-size:8px;font-weight:700;letter-spacing:.08em;}
        .material-rate-pdf .summary strong{display:block;color:#5f4726;font-size:13px;margin-top:2px;}
        .material-rate-pdf .num{text-align:right;white-space:nowrap;}
        .material-rate-pdf .center{text-align:center;}
        .material-rate-pdf .group-head{background:#e8e4dc;color:#5f4726;}
        .material-rate-pdf th.group-head{border-left:1px solid #c2baad;border-right:1px solid #c2baad;}
        .material-rate-pdf .group-start{border-left:1px solid #c2baad !important;}
        .material-rate-pdf .group-end{border-right:1px solid #c2baad !important;}
        .material-rate-pdf th.group-start,.material-rate-pdf th.group-end{background:#efebe3;}
        .material-rate-pdf .selected td{background:#eeeeee;border-top:1px solid #cfcfcf;border-bottom:1px solid #cfcfcf;}
        .material-rate-pdf .selected td:first-child{border-left:3px solid #555;}
        .material-rate-pdf td.lowest-price{background:#e9eee8;font-weight:800;box-shadow:inset 0 0 0 1px #c8d2c6;}
        .material-rate-pdf .selected td.lowest-price{background:#dde6dc;}
        .material-rate-pdf .pill{display:inline-block;margin-left:6px;border-radius:999px;background:#fff;color:#333;border:1px solid #777;padding:1px 6px;font-size:8px;font-weight:700;}
        .material-rate-pdf .empty{padding:20px;text-align:center;color:#777;border-top:1px solid #ececec;}
        .material-rate-pdf .deal-notes{padding:8px 10px;border-top:1px solid #ececec;background:#fbfaf8;}
        .material-rate-pdf .deal-title{color:#6f675c;text-transform:uppercase;letter-spacing:.08em;font-size:9px;font-weight:700;margin-bottom:4px;}
        .material-rate-pdf .deal-notes ul{margin:0;padding-left:16px;color:#333;line-height:1.45;}
        .material-rate-pdf .deal-notes li{margin:1px 0;}
        .material-rate-pdf .note{padding:7px 10px;color:#938b7d;font-size:10px;border-top:1px solid #ececec;}
      </style>
      <div class="top">
        <div>
          <h1>${escHtml(title)}</h1>
          <div class="muted">${items.length} material${items.length === 1 ? '' : 's'} · Rates ${gstMode === 'incl' ? 'incl GST' : 'excl GST'}</div>
        </div>
      </div>
      ${cards}
    </div>
  `;
}

function resolveWorkRate(setup: WorkRateSetup, materialItems: MaterialRateItem[], assemblies: MaterialAssembly[]): {
  rate: number;
  source: string;
  missing: boolean;
  pendingCount?: number;
} {
  if (setup.sourceType === 'manual') {
    const rate = num(setup.manualRate);
    return { rate, source: rate ? 'Manual rate' : 'Manual rate pending', missing: !rate };
  }
  if (setup.sourceType === 'direct') {
    const item = materialItems.find((material) => material.id === setup.sourceId);
    const quote = selectedMaterialQuoteForOption(item, setup.quoteOptionId);
    if (!item || !quote) return { rate: 0, source: 'Selected material quote pending', missing: true };
    const option = selectedQuoteOption(quote, setup.quoteOptionId);
    return {
      rate: option ? quoteOptionLandedRate(quote, option) : quoteLandedRate(item, quote),
      source: option ? `${item.materialName} · ${quoteOptionLabel(option)}` : `${item.materialName} · ${quote.vendor || 'selected quote'}`,
      missing: false,
    };
  }
  if (setup.sourceType === 'assembly') {
    const assembly = assemblies.find((item) => item.id === setup.sourceId);
    if (!assembly) return { rate: 0, source: 'Assembly pending', missing: true };
    const summary = assemblySummary(assembly, materialItems);
    const pendingCount = summary.missing + summary.pending;
    return {
      rate: summary.total,
      source: `${assembly.name}${pendingCount ? ` · ${pendingCount} rate pending` : ''}`,
      missing: !!pendingCount,
      pendingCount,
    };
  }
  return { rate: 0, source: 'Not assigned', missing: true };
}

function resolveUnitComponentCostingRate(
  setup: WorkRateSetup,
  component: QuantityProfileComponent,
  materialItems: MaterialRateItem[],
  assemblies: MaterialAssembly[],
  basis: 'selected' | 'tentative',
) {
  if (setup.sourceType !== 'direct') return resolveCostingRate(setup, materialItems, assemblies, basis);
  const material = materialItems.find((item) => item.id === setup.sourceId);
  const category = (component.assemblyType || '').trim().toLowerCase();
  const variety = (component.variety || '').trim().toLowerCase();
  const options = (material?.quotes || []).flatMap((quote) => quote.quoteOptions || []);
  const categoryOptions = category ? options.filter((option) => (option.group || '').trim().toLowerCase() === category) : [];
  const matchedOption = categoryOptions.find((option) => [option.variety, option.name].some((value) => (value || '').trim().toLowerCase() === variety));
  const optionId = matchedOption?.id || categoryOptions[0]?.id || materialOptionIdForVariety(material, component.variety);
  return resolveCostingRate(optionId ? { ...setup, quoteOptionId: optionId } : setup, materialItems, assemblies, basis);
}

function labourLeadMultiplier(
  component: QuantityProfileComponent,
  setup: WorkRateSetup,
  materialItems: MaterialRateItem[],
  allocations: UnitTypeBuildingAllocation[] = [],
) {
  const material = setup.sourceType === 'direct' ? materialItems.find((item) => item.id === setup.sourceId) : undefined;
  if (!component.leadEnabled || material?.group !== 'labour') return 1;
  const leadPercent = num(component.leadPercent) / 100;
  if (component.leadType === 'fixed') return 1 + leadPercent;
  const totalUnits = allocations.reduce((sum, allocation) => sum + unitAllocationCount(allocation), 0);
  if (!totalUnits) return 1;
  const weightedFloors = allocations.reduce((sum, allocation) => {
    const from = typeof allocation.floorFrom === 'number' ? allocation.floorFrom : 0;
    const to = typeof allocation.floorTo === 'number' ? allocation.floorTo : from;
    return sum + unitAllocationCount(allocation) * ((from + to) / 2);
  }, 0);
  return 1 + (weightedFloors / totalUnits) * leadPercent;
}

function applyLabourLeadRate<T extends { rate: number; source: string }>(resolved: T, multiplier: number): T {
  if (multiplier === 1 || !resolved.rate) return resolved;
  return { ...resolved, rate: resolved.rate * multiplier, source: `${resolved.source} · labour lead` };
}

function unitEstimateLineType(component: QuantityProfileComponent, workItems: ItemWorkMaster[], rates: WorkRateSetup[], materialItems: MaterialRateItem[]) {
  const setup = componentRateSetup(component, workItems, rates);
  if (setup?.sourceType === 'assembly') return 'Assembly';
  const material = materialItems.find((item) => item.id === setup?.sourceId);
  return material?.group === 'labour' ? 'Labour' : 'Material';
}

const unitEstimateLineTypeOrder = (type: string) => ['Material', 'Labour', 'Assembly'].indexOf(type);

function projectEstimateLineType(line: ProjectEstimateScopeItem, setup: WorkRateSetup | undefined, materialItems: MaterialRateItem[]) {
  if (line.sourceType === 'assembly' || setup?.sourceType === 'assembly') return 'Assembly';
  if (line.sourceType === 'labour') return 'Labour';
  const material = materialItems.find((item) => item.id === line.sourceId || item.id === setup?.sourceId);
  return material?.group === 'labour' ? 'Labour' : 'Material';
}

function resolveCostingRate(
  setup: WorkRateSetup,
  materialItems: MaterialRateItem[],
  assemblies: MaterialAssembly[],
  basis: 'selected' | 'tentative',
): { rate: number; source: string; missing: boolean; pendingCount?: number; tentative: boolean } {
  const selected = resolveWorkRate(setup, materialItems, assemblies);
  if (basis === 'selected' || setup.sourceType === 'manual') {
    return { ...selected, tentative: false };
  }
  if (setup.sourceType === 'direct') {
    if (selected.rate > 0) return { ...selected, tentative: false };
    const item = materialItems.find((material) => material.id === setup.sourceId);
    const quote = tentativeCostingQuoteForOption(item, setup.quoteOptionId);
    const option = selectedQuoteOption(quote, setup.quoteOptionId);
    const rate = quote ? (option ? quoteOptionLandedRate(quote, option) : quoteLandedRate(item as MaterialRateItem, quote)) : 0;
    if (item && quote && rate > 0) {
      return {
        rate,
        source: option ? `${item.materialName} · ${quote.vendor || 'default'} · ${quoteOptionLabel(option)} (default costing)` : `${item.materialName} · ${quote.vendor || 'default'} (default costing)`,
        missing: false,
        tentative: true,
      };
    }
  }
  if (setup.sourceType === 'assembly') {
    if (!selected.missing) return { ...selected, tentative: false };
    const assembly = assemblies.find((item) => item.id === setup.sourceId);
    if (assembly) {
      const summary = assemblySummary(assembly, costingMaterialItems(materialItems));
      const pendingCount = summary.missing + summary.pending;
      if (summary.total > 0 && summary.total !== selected.rate) {
        return {
          rate: summary.total,
          source: `${assembly.name} · includes default costing rate${pendingCount ? ` · ${pendingCount} rate pending` : ''}`,
          missing: !!pendingCount,
          pendingCount,
          tentative: true,
        };
      }
    }
  }
  return { ...selected, tentative: false };
}

function findAssemblyMaterial(component: AssemblyComponent, materialItems: MaterialRateItem[]): MaterialRateItem | undefined {
  return materialItems.find((item) =>
    item.group === component.materialGroup &&
    item.materialName.trim().toLowerCase() === (component.materialName || component.label).trim().toLowerCase()
  );
}

function findLinkedAssemblyMaterial(component: AssemblyComponent, materialItems: MaterialRateItem[]): MaterialRateItem | undefined {
  const exactItem = component.linkedMaterialId
    ? materialItems.find((item) => item.id === component.linkedMaterialId)
    : undefined;
  if (exactItem) return exactItem;
  return materialItems.find((item) =>
    item.group === component.linkedMaterialGroup &&
    item.materialName.trim().toLowerCase() === (component.linkedMaterialName || '').trim().toLowerCase()
  );
}

function materialRateHasSelectedUsableRate(item: MaterialRateItem, quoteOptionId?: string, linkedQuoteId?: string): boolean {
  const linkedQuote = linkedQuoteId
    ? (item.quotes || []).find((candidate) => candidate.id === linkedQuoteId && quoteIsCurrent(candidate)) || null
    : null;
  const quote = linkedQuote || selectedMaterialQuoteForOption(item, quoteOptionId);
  if (!quote) return false;
  if (!quoteOptionId) return quoteLandedRate(item, quote) > 0;
  const option = selectedQuoteOption(quote, quoteOptionId);
  return !!option && quoteOptionLandedRate(quote, option) > 0;
}

function buildMaterialRateUsageStatuses(
  items: MaterialRateItem[],
  assemblies: MaterialAssembly[],
  workRateSetups: WorkRateSetup[],
): Map<string, MaterialRateUsageStatus> {
  const statuses = new Map<string, MaterialRateUsageStatus>();
  const ensureStatus = (item: MaterialRateItem) => {
    const existing = statuses.get(item.id);
    if (existing) return existing;
    const next: MaterialRateUsageStatus = { usedCount: 0, pendingWholeItem: false, pendingOptionIds: new Set(), sources: [] };
    statuses.set(item.id, next);
    return next;
  };
  const addUsage = (item: MaterialRateItem | undefined, quoteOptionId: string | undefined, source: string, linkedQuoteId?: string) => {
    if (!item) return;
    const status = ensureStatus(item);
    status.usedCount += 1;
    if (source && !status.sources.includes(source)) status.sources.push(source);
    if (!materialRateHasSelectedUsableRate(item, quoteOptionId, linkedQuoteId)) {
      if (quoteOptionId) status.pendingOptionIds.add(quoteOptionId);
      else status.pendingWholeItem = true;
    }
  };

  for (const assembly of assemblies) {
    for (const component of assembly.components || []) {
      if (component.type === 'material') {
        addUsage(findAssemblyMaterial(component, items), component.quoteOptionId, assembly.name);
      } else if (component.manualRateSource === 'material') {
        addUsage(findLinkedAssemblyMaterial(component, items), component.linkedQuoteOptionId, assembly.name, component.linkedQuoteId);
      }
    }
  }

  for (const setup of workRateSetups) {
    if (setup.sourceType !== 'direct' || !setup.sourceId) continue;
    addUsage(items.find((item) => item.id === setup.sourceId), setup.quoteOptionId, 'Item / Work Master');
  }

  return statuses;
}

function assemblyMaterialRateInfo(
  item: MaterialRateItem | undefined,
  quoteOptionId: string | undefined,
  componentUnit?: string,
  linkedQuoteId?: string,
): {
  rateExcl: number;
  rateIncl: number;
  source: string;
  missing: boolean;
  ratePending: boolean;
} {
  const linkedQuote = linkedQuoteId
    ? (item?.quotes || []).find((candidate) => candidate.id === linkedQuoteId && quoteIsCurrent(candidate)) || null
    : null;
  const quote = linkedQuote || selectedMaterialQuoteForOption(item, quoteOptionId);
  if (!item || !quote) return { rateExcl: 0, rateIncl: 0, source: 'Linked material rate pending', missing: false, ratePending: true };
  const option = selectedQuoteOption(quote, quoteOptionId);
  const explicitUnitFactor = linkedRateUnitFactor(option || item, componentUnit);
  const unitFactor = explicitUnitFactor !== 1 ? explicitUnitFactor : inferredPackRateUnitFactor(option || item, componentUnit);
  const rateIncl = (option ? quoteOptionLandedRate(quote, option) : quoteLandedRate(item, quote)) * unitFactor;
  const rateExcl = (option ? quoteOptionLandedRateExcl(quote, option) : quoteLandedRateExcl(item, quote)) * unitFactor;
  if (!rateIncl) return { rateExcl: 0, rateIncl: 0, source: 'Linked material rate pending', missing: false, ratePending: true };
  return {
    rateExcl,
    rateIncl,
    source: option ? `${quote.vendor || 'Selected'} · ${quoteOptionLabel(option)}` : (quote.vendor || 'Selected quote'),
    missing: false,
    ratePending: false,
  };
}

function manualGrossRate(component: AssemblyComponent): number {
  const rate = num(component.manualRate);
  if (component.gstMode === 'inclusive') return rate;
  return rate + (rate * num(component.gst) / 100);
}

function manualRateExcl(component: AssemblyComponent): number {
  const rate = num(component.manualRate);
  if (!rate) return 0;
  if (component.gstMode === 'inclusive' && num(component.gst) > 0) return rate / (1 + num(component.gst) / 100);
  return rate;
}

export function normalizeUnit(value?: string): string {
  const unit = (value || '').trim().toLowerCase().replace(/[.\s_-]+/g, '');
  if (!unit) return '';
  if (['kg', 'kgs', 'kilogram', 'kilograms'].includes(unit)) return 'kg';
  if (['nos', 'no', 'number', 'numbers', 'pc', 'pcs', 'pic', 'piece', 'pieces'].includes(unit)) return 'nos';
  if (['sft', 'sqft', 'sqfeet', 'squarefeet', 'squarefoot'].includes(unit)) return 'sft';
  if (['sqm', 'sqmt', 'squaremeter', 'squaremeters', 'squaremetre', 'squaremetres'].includes(unit)) return 'sqm';
  if (['rft', 'runningfeet', 'runningfoot', 'runningft'].includes(unit)) return 'rft';
  if (['m', 'meter', 'meters', 'metre', 'metres'].includes(unit)) return 'meter';
  if (['box', 'boxes'].includes(unit)) return 'box';
  if (['set', 'sets'].includes(unit)) return 'set';
  if (['bag', 'bags'].includes(unit)) return 'bag';
  if (['roll', 'rolls'].includes(unit)) return 'roll';
  return unit;
}

export function sameUnit(left?: string, right?: string): boolean {
  return !!normalizeUnit(left) && normalizeUnit(left) === normalizeUnit(right);
}

function linkedRateUnitFactor(
  source: Pick<MaterialRateItem | MaterialQuoteOption, 'unit' | 'secondaryUnitRequired' | 'secondaryMultiplier' | 'secondaryUnit'> | undefined,
  componentUnit?: string,
): number {
  if (!source || !componentUnit || !source.secondaryUnitRequired) return 1;
  const multiplier = num(source.secondaryMultiplier);
  if (multiplier <= 0) return 1;
  if (!sameUnit(componentUnit, source.secondaryUnit)) return 1;
  if (sameUnit(source.unit, source.secondaryUnit)) return 1;
  return 1 / multiplier;
}

function inferredPackRateUnitFactor(
  source: { group?: string; variety?: string; name?: string; materialName?: string; unit?: string },
  componentUnit?: string,
): number {
  const targetUnit = normalizeUnit(componentUnit);
  if (!targetUnit) return 1;
  const label = [source.group, source.variety, source.name, source.materialName].filter(Boolean).join(' ');
  if (!label.trim()) return 1;
  const pattern = /(^|[^0-9.])(\d+(?:\.\d+)?)\s*(kg|kgs|kilogram|kilograms|nos|no|pcs|pc|piece|pieces|sft|sqft|rft|meter|meters|metre|metres|box|boxes|set|sets|bag|bags|roll|rolls)\b/gi;
  let match = pattern.exec(label);
  while (match) {
    const qty = num(match[2]);
    const unit = normalizeUnit(match[3]);
    if (qty > 1 && unit === targetUnit) return 1 / qty;
    match = pattern.exec(label);
  }
  return 1;
}

function showReferenceRateBasis(outputUnit?: string, referenceUnit?: string): boolean {
  return !!referenceUnit && !sameUnit(outputUnit, referenceUnit);
}

function rateBasisSelectValue(basis: 'output' | 'reference' | undefined, outputUnit?: string, referenceUnit?: string): 'output' | 'reference' {
  if (basis === 'reference' && showReferenceRateBasis(outputUnit, referenceUnit)) return 'reference';
  return 'output';
}

function componentUsesReferenceBasis(component: AssemblyComponent, assembly: MaterialAssembly): boolean {
  return component.basis === 'reference' && showReferenceRateBasis(assembly.outputUnit, assembly.referenceUnit);
}

function componentBasisMultiplier(component: AssemblyComponent, assembly: MaterialAssembly): number {
  return componentUsesReferenceBasis(component, assembly) ? Math.max(1, num(assembly.referenceQty)) : 1;
}

function assemblyShareBasisLabel(component: AssemblyComponent, assembly: MaterialAssembly): string {
  if (!componentUsesReferenceBasis(component, assembly) || !num(assembly.referenceQty) || !num(component.qty)) return '';
  return `${fmtQty(num(component.qty))} ${component.unit}/${assembly.referenceUnit || 'secondary'} × ${fmtQty(num(assembly.referenceQty))} ${assembly.referenceUnit || 'secondary'}/${assembly.outputUnit || 'unit'}`;
}

function assemblyComponentAmount(component: AssemblyComponent, materialItems: MaterialRateItem[], assembly: MaterialAssembly): {
  rate: number;
  rateExcl: number;
  rateIncl: number;
  amount: number;
  amountExcl: number;
  amountIncl: number;
  grossAmount: number;
  grossAmountExcl: number;
  grossAmountIncl: number;
  source: string;
  missing: boolean;
  ratePending: boolean;
} {
  const multiplier = componentBasisMultiplier(component, assembly);
  if (component.type === 'manual') {
    if (component.manualRateSource === 'material') {
      const rateInfo = assemblyMaterialRateInfo(findLinkedAssemblyMaterial(component, materialItems), component.linkedQuoteOptionId, component.unit, component.linkedQuoteId);
      if (rateInfo.missing || rateInfo.ratePending) {
        return { rate: 0, rateExcl: 0, rateIncl: 0, amount: 0, amountExcl: 0, amountIncl: 0, grossAmount: 0, grossAmountExcl: 0, grossAmountIncl: 0, source: rateInfo.source, missing: rateInfo.missing, ratePending: rateInfo.ratePending };
      }
      const grossAmountIncl = num(component.qty) * rateInfo.rateIncl;
      const grossAmountExcl = num(component.qty) * rateInfo.rateExcl;
      return {
        rate: rateInfo.rateIncl,
        rateExcl: rateInfo.rateExcl,
        rateIncl: rateInfo.rateIncl,
        amount: grossAmountIncl * multiplier,
        amountExcl: grossAmountExcl * multiplier,
        amountIncl: grossAmountIncl * multiplier,
        grossAmount: grossAmountIncl,
        grossAmountExcl,
        grossAmountIncl,
        source: rateInfo.source,
        missing: false,
        ratePending: false,
      };
    }
    const rateIncl = manualGrossRate(component);
    const rateExcl = manualRateExcl(component);
    if (!rateIncl) return { rate: 0, rateExcl: 0, rateIncl: 0, amount: 0, amountExcl: 0, amountIncl: 0, grossAmount: 0, grossAmountExcl: 0, grossAmountIncl: 0, source: 'Manual rate pending', missing: false, ratePending: true };
    const grossAmountIncl = num(component.qty) * rateIncl;
    const grossAmountExcl = num(component.qty) * rateExcl;
    return {
      rate: rateIncl,
      rateExcl,
      rateIncl,
      amount: grossAmountIncl * multiplier,
      amountExcl: grossAmountExcl * multiplier,
      amountIncl: grossAmountIncl * multiplier,
      grossAmount: grossAmountIncl,
      grossAmountExcl,
      grossAmountIncl,
      source: `${num(component.gst)}% ${component.gstMode === 'inclusive' ? 'incl' : 'excl'}`,
      missing: false,
      ratePending: false,
    };
  }
  const item = findAssemblyMaterial(component, materialItems);
  const rateInfo = assemblyMaterialRateInfo(item, component.quoteOptionId, component.unit);
  if (rateInfo.missing || rateInfo.ratePending) return { rate: 0, rateExcl: 0, rateIncl: 0, amount: 0, amountExcl: 0, amountIncl: 0, grossAmount: 0, grossAmountExcl: 0, grossAmountIncl: 0, source: rateInfo.source, missing: rateInfo.missing, ratePending: rateInfo.ratePending };
  const grossAmountIncl = num(component.qty) * rateInfo.rateIncl;
  const grossAmountExcl = num(component.qty) * rateInfo.rateExcl;
  return {
    rate: rateInfo.rateIncl,
    rateExcl: rateInfo.rateExcl,
    rateIncl: rateInfo.rateIncl,
    amount: grossAmountIncl * multiplier,
    amountExcl: grossAmountExcl * multiplier,
    amountIncl: grossAmountIncl * multiplier,
    grossAmount: grossAmountIncl,
    grossAmountExcl,
    grossAmountIncl,
    source: rateInfo.source,
    missing: false,
    ratePending: false,
  };
}

function assemblySummary(assembly: MaterialAssembly, materialItems: MaterialRateItem[]): { total: number; totalExcl: number; totalIncl: number; missing: number; pending: number } {
  return assembly.components.reduce((acc, component) => {
    const row = assemblyComponentAmount(component, materialItems, assembly);
    return {
      total: acc.total + (row.missing || row.ratePending ? 0 : row.amount),
      totalExcl: acc.totalExcl + (row.missing || row.ratePending ? 0 : row.amountExcl),
      totalIncl: acc.totalIncl + (row.missing || row.ratePending ? 0 : row.amountIncl),
      missing: acc.missing + (row.missing ? 1 : 0),
      pending: acc.pending + (row.ratePending ? 1 : 0),
    };
  }, { total: 0, totalExcl: 0, totalIncl: 0, missing: 0, pending: 0 });
}

const headTh: any = {
  position: 'sticky', top: 0, bgcolor: PANEL2, zIndex: 2, fontSize: 10.5, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.6px', color: FAINT, textAlign: 'right',
  p: '9px 12px', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap', fontFamily: FONT_BODY,
};
const toggleSx = {
  textTransform: 'none', fontWeight: 600, fontSize: 12.5, color: MUTED, borderColor: BORDER,
  px: 1.5, '&.Mui-selected': { color: '#fff', bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D } },
};

function ActionIcon({ children, title, onClick, danger, disabled }: { children: React.ReactNode; title: string; onClick: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{ width: 24, height: 24, fontSize: 15, color: danger ? '#c1372c' : MUTED, '&:hover': { color: danger ? '#a52a20' : GOLD } }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function RateRevisionControl({ onClick, title = 'Add revised rate' }: { onClick: () => void; title?: string }) {
  return (
    <Stack direction="row" alignItems="center" gap={0.2}>
      <Tooltip title={title}>
        <IconButton
          size="small"
          onClick={onClick}
          sx={{
            width: 22,
            height: 22,
            border: `1px solid ${BORDER}`,
            bgcolor: '#fff',
            color: FAINT,
            '&:hover': { color: GOLD_D, borderColor: GOLD_L, bgcolor: GOLD_WASH },
          }}
        >
          <AddOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <Box sx={{ p: 6, textAlign: 'center', color: MUTED, fontFamily: FONT_BODY }}>{children}</Box>;
}

function EmptyCard({ title, body, cta, onClick }: { title: string; body: string; cta: string; onClick: () => void }) {
  return (
    <Box sx={{ border: `1px dashed ${BORDER}`, borderRadius: 3, p: 5, textAlign: 'center', bgcolor: '#fff' }}>
      <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 18, fontWeight: 700, color: DARK, mb: 1 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, color: MUTED, maxWidth: 460, mx: 'auto', mb: 2.5 }}>{body}</Typography>
      <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={onClick}>{cta}</Button>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dialogs
// ─────────────────────────────────────────────────────────────────────────────
const slugifyMaterialKey = (value: string) =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'option';

const parseCsvValues = (value: string) =>
  Array.from(new Set(value.split(',').map((part) => part.trim()).filter(Boolean)));

/** Common tile labels such as 24 x 48 are measured in inches unless an area is entered manually. */
const tileAreaFromVariety = (variety: string) => {
  const match = String(variety || '').match(/(\d+(?:\.\d+)?)\s*(?:in|inch|\")?\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:in|inch|\")?/i);
  if (!match) return 0;
  const width = num(match[1]);
  const height = num(match[2]);
  return width > 0 && height > 0 && width <= 96 && height <= 96 ? Math.round((width * height / 144) * 10000) / 10000 : 0;
};

const sanitizeAssemblyVarietyMap = (rows: MaterialAssemblyVarietyMap[]) =>
  rows
    .map((row) => ({
      ...row,
      assemblyType: row.assemblyType.trim(),
      varieties: Array.from(new Set((row.varieties || []).map((v) => v.trim()).filter(Boolean))),
      unit: row.unit || 'Nos',
      type: 'base' as const,
      secondaryUnitRequired: !!row.secondaryUnitRequired,
      secondaryMultiplier: row.secondaryUnitRequired ? num(row.secondaryMultiplier) : 0,
      secondaryUnit: row.secondaryUnitRequired ? (row.secondaryUnit || '').trim() : '',
      // Left unset (rather than forced to false) when never explicitly chosen, so a material that
      // already has box-packing data saved keeps showing it — it hasn't lost the setting, just never had one.
      ...(row.boxReceiving !== undefined ? { boxReceiving: !!row.boxReceiving } : {}),
    }))
    .filter((row) => row.assemblyType);

const quoteTemplatesFromAssemblyVarietyMap = (rows: MaterialAssemblyVarietyMap[]): MaterialQuoteOptionTemplate[] =>
  sanitizeAssemblyVarietyMap(rows).flatMap((row) =>
    (row.varieties.length ? row.varieties : ['Base Rate']).map((variety) => ({
      id: `${row.id}_${slugifyMaterialKey(variety)}`,
      group: row.assemblyType,
      name: variety,
      type: 'base',
      unit: row.unit,
      secondaryUnitRequired: !!row.secondaryUnitRequired,
      secondaryMultiplier: row.secondaryUnitRequired ? num(row.secondaryMultiplier) : 0,
      secondaryUnit: row.secondaryUnitRequired ? (row.secondaryUnit || '').trim() : '',
    }))
  );

const mappingFromGroupedQuoteTemplates = (templates: MaterialQuoteOptionTemplate[]): MaterialAssemblyVarietyMap[] => {
  const grouped = new Map<string, MaterialAssemblyVarietyMap>();
  for (const template of templates) {
    const group = (template.group || '').trim();
    if (template.type === 'addon') continue;
    if (!group || !template.name.trim()) continue;
    const id = `map_${slugifyMaterialKey(group)}`;
    const existing = grouped.get(group);
    if (existing) {
      existing.varieties.push(template.name.trim());
    } else {
      grouped.set(group, {
        id,
        assemblyType: group,
        varieties: [template.name.trim()],
        type: template.type,
        unit: template.unit,
        secondaryUnitRequired: !!template.secondaryUnitRequired,
        secondaryMultiplier: num(template.secondaryMultiplier),
        secondaryUnit: template.secondaryUnit || '',
      });
    }
  }
  return Array.from(grouped.values());
};

const materialTemplatesForMigration = (item: Pick<MaterialRateItem, 'assemblyVarietyMap' | 'quoteOptionTemplates'>) => {
  const mappedTemplates = quoteTemplatesFromAssemblyVarietyMap(item.assemblyVarietyMap || []);
  if (mappedTemplates.length) {
    return [
      ...mappedTemplates,
      ...(item.quoteOptionTemplates || []).filter((template) => template.type === 'addon'),
    ];
  }
  return item.quoteOptionTemplates || [];
};

const materialTemplateKey = (template: MaterialQuoteOptionTemplate) =>
  [
    (template.group || '').trim(),
    template.name.trim(),
    template.type,
    template.unit || '',
  ].join('\u0001').toLowerCase();

function buildMaterialTemplateMigration(oldItem: MaterialRateItem, nextItem: MaterialRateItem) {
  const oldTemplates = materialTemplatesForMigration(oldItem);
  const nextTemplates = materialTemplatesForMigration(nextItem);
  const nextById = new Map(nextTemplates.map((template) => [template.id, template]));
  const nextByKey = new Map(nextTemplates.map((template) => [materialTemplateKey(template), template]));
  const idMap = new Map<string, string>();

  for (const oldTemplate of oldTemplates) {
    if (nextById.has(oldTemplate.id)) {
      idMap.set(oldTemplate.id, oldTemplate.id);
      continue;
    }
    const matchingTemplate = nextByKey.get(materialTemplateKey(oldTemplate));
    if (matchingTemplate) idMap.set(oldTemplate.id, matchingTemplate.id);
  }

  const oldRows = sanitizeAssemblyVarietyMap(oldItem.assemblyVarietyMap || []);
  const nextRowsById = new Map(sanitizeAssemblyVarietyMap(nextItem.assemblyVarietyMap || []).map((row) => [row.id, row]));
  for (const oldRow of oldRows) {
    const nextRow = nextRowsById.get(oldRow.id);
    if (!nextRow) continue;
    const oldRowTemplates = quoteTemplatesFromAssemblyVarietyMap([oldRow]);
    const nextRowTemplates = quoteTemplatesFromAssemblyVarietyMap([nextRow]);
    oldRowTemplates.forEach((oldTemplate, index) => {
      const nextTemplate = nextRowTemplates[index];
      if (nextTemplate) idMap.set(oldTemplate.id, nextTemplate.id);
    });
  }

  const removedIds = new Set(oldTemplates.filter((template) => !idMap.has(template.id)).map((template) => template.id));
  return { nextById, idMap, removedIds };
}

function migrateMaterialQuoteOptions(oldItem: MaterialRateItem, nextItem: MaterialRateItem): { quotes: MaterialQuote[]; needsConfirmation: boolean } {
  const migration = buildMaterialTemplateMigration(oldItem, nextItem);
  let needsConfirmation = false;

  const quotes = (oldItem.quotes || []).map((quote) => {
    const migratedOptions = (quote.quoteOptions || []).flatMap((option) => {
      const oldTemplateId = option.templateId || option.id;
      const nextTemplateId = migration.idMap.get(oldTemplateId);
      if (!nextTemplateId) {
        if (migration.removedIds.has(oldTemplateId) && num(option.rate) > 0) needsConfirmation = true;
        return [];
      }
      if (nextTemplateId !== oldTemplateId) needsConfirmation = true;
      const nextTemplate = migration.nextById.get(nextTemplateId);
      if (!nextTemplate) return [];
      return [{
        ...option,
        id: nextTemplateId,
        templateId: nextTemplate.id,
        group: (nextTemplate.group || '').trim(),
        variety: '',
        name: nextTemplate.name,
        type: nextTemplate.type,
        unit: nextTemplate.unit || option.unit,
        secondaryUnitRequired: !!nextTemplate.secondaryUnitRequired,
        secondaryMultiplier: num(nextTemplate.secondaryMultiplier),
        secondaryUnit: nextTemplate.secondaryUnit || '',
      }];
    });
    return {
      ...quote,
      quoteOptions: expandQuoteOptionsFromTemplate(nextItem, migratedOptions).filter((option) => num(option.rate) > 0),
    };
  });

  return { quotes, needsConfirmation };
}

function MaterialDialog({ edit, initialGroup, materialGroupOptions, onClose, onSave }: {
  edit?: MaterialRateItem;
  initialGroup?: MaterialGroup;
  materialGroupOptions: string[];
  onClose: () => void;
  onSave: (v: {
    group: MaterialGroup;
    materialName: string;
    grade: string;
    brand: string;
    unit: string;
    secondaryUnitRequired?: boolean;
    secondaryMultiplier?: number;
    secondaryUnit?: string;
    pieceEntry?: boolean;
    pieceDimensionUnit?: 'in' | 'ft' | 'cm' | 'm';
    trackStock?: boolean;
    stockPrecision?: 'exact' | 'tentative';
    laborStockVisible?: boolean;
    codeRequired?: boolean;
    assemblyVarietyMap?: MaterialAssemblyVarietyMap[];
    boxPackings?: MaterialBoxPacking[];
    assemblyTypes?: string[];
    varieties?: string[];
    quoteOptionTemplates?: MaterialQuoteOptionTemplate[];
    coveredDiameters?: string[];
  }) => void;
}) {
  const mobileFullScreen = useMediaQuery('(max-width:600px)');
  const seedGroup = edit?.group || initialGroup || 'steel';
  const seedBuiltInGroup = isBuiltInMaterialGroup(seedGroup) ? seedGroup : 'other';
  const initialDefaults: Record<BuiltInMaterialGroup, { name: string; grade: string }> = {
    steel: { name: 'TMT Fe500D', grade: 'Fe500D' },
    cement: { name: 'Cement', grade: 'PPC / OPC' },
    rmc: { name: 'RMC Concrete', grade: 'M25' },
    sandAggregate: { name: 'Sand / Aggregate', grade: 'Standard' },
    blocks: { name: 'Blocks / Bricks', grade: 'Standard' },
    flooring: { name: 'Flooring / Tiles', grade: 'Standard' },
    doorMaterials: { name: 'Flush door / door material', grade: 'Standard' },
    plumbing: { name: 'Plumbing material', grade: 'Standard' },
    electrical: { name: 'Electrical material', grade: 'Standard' },
    fire: { name: 'Fire safety material', grade: 'Standard' },
    lift: { name: 'Lift package', grade: 'Standard' },
    fabrication: { name: 'Fabrication material', grade: 'Standard' },
    aluminiumSections: { name: 'Aluminium section', grade: 'Standard' },
    paint: { name: 'Paint / Finishing material', grade: 'Standard' },
    labour: { name: 'Carpenter labour', grade: 'Skilled' },
    other: { name: 'Other material', grade: 'Standard' },
  };
  const [group, setGroup] = useState(materialGroupLabel(seedGroup));
  const [materialName, setMaterialName] = useState(edit?.materialName || initialDefaults[seedBuiltInGroup].name);
  const [grade, setGrade] = useState(edit?.grade || initialDefaults[seedBuiltInGroup].grade);
  const [brand, setBrand] = useState(edit?.brand || '');
  const [unit, setUnit] = useState(edit?.unit || defaultMaterialUnit(seedGroup));
  const [secondaryUnitRequired, setSecondaryUnitRequired] = useState(!!edit?.secondaryUnitRequired);
  const [secondaryMultiplier, setSecondaryMultiplier] = useState(edit?.secondaryMultiplier ? String(edit.secondaryMultiplier) : '');
  const [secondaryUnit, setSecondaryUnit] = useState(edit?.secondaryUnit || 'Nos');
  const [pieceEntry, setPieceEntry] = useState(!!edit?.pieceEntry);
  // '' means "use the default for whichever unit is chosen"; set once the user picks one explicitly.
  const [pieceDimensionUnit, setPieceDimensionUnit] = useState<'in' | 'ft' | 'cm' | 'm' | ''>(edit?.pieceDimensionUnit || '');
  const [trackStock, setTrackStock] = useState(!!edit?.trackStock);
  const [stockPrecision, setStockPrecision] = useState<'exact' | 'tentative'>(edit?.stockPrecision || 'exact');
  const [laborStockVisible, setLaborStockVisible] = useState(edit?.laborStockVisible !== false);
  const [codeRequired, setCodeRequired] = useState(!!edit?.codeRequired);
  const [assemblyVarietyMap, setAssemblyVarietyMap] = useState<MaterialAssemblyVarietyMap[]>(
    edit?.assemblyVarietyMap?.length
      ? edit.assemblyVarietyMap
      : mappingFromGroupedQuoteTemplates(edit?.quoteOptionTemplates || [])
  );
  const [boxPackings, setBoxPackings] = useState<MaterialBoxPacking[]>(edit?.boxPackings || []);
  const [quoteOptionTemplates, setQuoteOptionTemplates] = useState<MaterialQuoteOptionTemplate[]>(
    edit?.quoteOptionTemplates?.length ? edit.quoteOptionTemplates : defaultMaterialQuoteOptions(seedGroup)
  );
  const [mappingVarietyInput, setMappingVarietyInput] = useState<Record<string, string>>({});
  const mappingRowsWithPendingInput = (rows = assemblyVarietyMap) =>
    rows.map((row) => ({
      ...row,
      varieties: Array.from(new Set([...(row.varieties || []), ...parseCsvValues(mappingVarietyInput[row.id] || '')])),
    }));
  const updateMappingRow = (id: string, patch: Partial<MaterialAssemblyVarietyMap>) =>
    setAssemblyVarietyMap((prev) => prev.map((row) => row.id === id ? { ...row, ...patch } : row));
  const packingFor = (assemblyType: string, variety: string) => boxPackings.find((packing) =>
    packing.assemblyType === assemblyType && packing.variety === variety,
  );
  const updatePacking = (assemblyType: string, variety: string, patch: Partial<MaterialBoxPacking>) =>
    setBoxPackings((previous) => {
      const existing = packingFor(assemblyType, variety);
      const next = { assemblyType, variety, piecesPerBox: existing?.piecesPerBox || 0, areaPerPiece: existing?.areaPerPiece || 0, ...patch };
      return existing
        ? previous.map((packing) => packing === existing ? next : packing)
        : [...previous, next];
    });
  const moveMappingRow = (id: string, direction: -1 | 1) =>
    setAssemblyVarietyMap((prev) => {
      const index = prev.findIndex((row) => row.id === id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  const commitMappingVarietyInput = (id: string) => {
    const values = parseCsvValues(mappingVarietyInput[id] || '');
    if (!values.length) return;
    setAssemblyVarietyMap((prev) => prev.map((row) => row.id === id ? {
      ...row,
      varieties: Array.from(new Set([...(row.varieties || []), ...values])),
    } : row));
    setMappingVarietyInput((prev) => ({ ...prev, [id]: '' }));
  };
  const hasMappingRows = assemblyVarietyMap.length > 0;
  const isLabourDialog = materialGroupValueFromLabel(group) === 'labour';
  const visibleQuoteOptionTemplates = hasMappingRows
    ? quoteOptionTemplates.filter((template) => template.type === 'addon')
    : quoteOptionTemplates;
  const applyGroup = (nextLabel: string) => {
    const next = materialGroupValueFromLabel(nextLabel);
    setGroup(materialGroupLabel(next));
    setUnit(defaultMaterialUnit(next));
    setCodeRequired(['doorMaterials', 'flooring', 'plumbing', 'electrical', 'fire', 'aluminiumSections', 'paint'].includes(next));
    const defaults: Record<BuiltInMaterialGroup, { name: string; grade: string }> = {
      steel: { name: 'TMT Fe500D', grade: 'Fe500D' },
      cement: { name: 'Cement', grade: 'PPC / OPC' },
      rmc: { name: 'RMC Concrete', grade: 'M25' },
      sandAggregate: { name: 'Sand / Aggregate', grade: 'Standard' },
      blocks: { name: 'Blocks / Bricks', grade: 'Standard' },
      flooring: { name: 'Flooring / Tiles', grade: 'Standard' },
      doorMaterials: { name: 'Flush door / door material', grade: 'Standard' },
      plumbing: { name: 'Plumbing material', grade: 'Standard' },
      electrical: { name: 'Electrical material', grade: 'Standard' },
      fire: { name: 'Fire safety material', grade: 'Standard' },
      lift: { name: 'Lift package', grade: 'Standard' },
      fabrication: { name: 'Fabrication material', grade: 'Standard' },
      aluminiumSections: { name: 'Aluminium section', grade: 'Standard' },
      paint: { name: 'Paint / Finishing material', grade: 'Standard' },
      labour: { name: 'Carpenter labour', grade: 'Skilled' },
      other: { name: 'Other material', grade: 'Standard' },
    };
    const builtInGroup = isBuiltInMaterialGroup(next) ? next : 'other';
    setMaterialName(defaults[builtInGroup].name);
    setGrade(defaults[builtInGroup].grade);
    setAssemblyVarietyMap([]);
    setQuoteOptionTemplates(defaultMaterialQuoteOptions(next));
  };
  return (
    <Dialog
      open
      fullScreen={mobileFullScreen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: POPUP_PAPER_SX }}
    >
      <DlgTitle onClose={onClose}>
        {edit
          ? materialGroupValueFromLabel(group) === 'labour' ? 'Edit labour' : 'Edit material'
          : materialGroupValueFromLabel(group) === 'labour' ? 'Add labour' : 'Add material'}
      </DlgTitle>
      <DialogContent>
        <Stack gap={2} mt={0.5}>
          {!isLabourDialog && (
            <Autocomplete
              freeSolo
              options={materialGroupOptions}
              value={group}
              inputValue={group}
              onInputChange={(_, value) => setGroup(value)}
              onChange={(_, value) => applyGroup(String(value || ''))}
              renderInput={(params) => <TextField {...params} label="Material group" size="small" fullWidth />}
            />
          )}
          <TextField label={isLabourDialog ? 'Labour name' : 'Material name'} value={materialName} onChange={(e) => setMaterialName(e.target.value)} size="small" fullWidth autoFocus />
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField label="Grade / specification" value={grade} onChange={(e) => setGrade(e.target.value)} size="small" fullWidth />
            <TextField label="Default brand" value={brand} onChange={(e) => setBrand(e.target.value)} size="small" fullWidth />
          </Stack>
          <Box>
            <FormControlLabel
              control={<Checkbox size="small" checked={secondaryUnitRequired} onChange={(e) => setSecondaryUnitRequired(e.target.checked)} />}
              label={<SmallLabel>Default secondary unit</SmallLabel>}
            />
            <Typography sx={{ color: MUTED, fontSize: 11.5, ml: 4.25, mt: -0.5 }}>
              Used when no assembly/type override is set.
            </Typography>
          </Box>
          {secondaryUnitRequired && (
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                label={`Qty inside 1 ${unit || 'primary unit'}`}
                value={secondaryMultiplier}
                onChange={(e) => setSecondaryMultiplier(e.target.value)}
                type="number"
                size="small"
                fullWidth
                helperText={`Example: 1 ${unit || 'Box'} = 100 Nos`}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Secondary unit</InputLabel>
                <Select label="Secondary unit" value={secondaryUnit} onChange={(e) => setSecondaryUnit(e.target.value)}>
                  {MATERIAL_UNIT_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
          )}
          {pieceShapeForUnit(unit) > 0 && (
            <Box>
              <FormControlLabel
                control={<Checkbox size="small" checked={pieceEntry} onChange={(e) => setPieceEntry(e.target.checked)} />}
                label={<SmallLabel>Quantity is worked out from pieces</SmallLabel>}
              />
              <Typography sx={{ color: MUTED, fontSize: 11.5, ml: 4.25, mt: -0.5 }}>
                Receiving shows {DIMENSION_LABELS[pieceShapeForUnit(unit)].join(' × ')} boxes per piece instead of one typed quantity — for granite, glass, or anything cut to size.
              </Typography>
              {pieceEntry && (
                <FormControl size="small" fullWidth sx={{ mt: 1.25, maxWidth: 260 }}>
                  <InputLabel>Usually measured in</InputLabel>
                  <Select label="Usually measured in" value={pieceDimensionUnit || defaultDimensionUnitFor(unit)} onChange={(e) => setPieceDimensionUnit(e.target.value as typeof pieceDimensionUnit)}>
                    {DIMENSION_UNITS.map((option) => <MenuItem key={option} value={option}>{DIMENSION_UNIT_LABEL[option]}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
          {!isLabourDialog && <Box>
            <FormControlLabel
              control={<Checkbox size="small" checked={trackStock} onChange={(e) => setTrackStock(e.target.checked)} />}
              label={<SmallLabel>Track as site stock (received minus issued)</SmallLabel>}
            />
            <Typography sx={{ color: MUTED, fontSize: 11.5, ml: 4.25, mt: -0.5 }}>
              For bulk material drawn from one common pile across the project — cement, sand, aggregate — rather than bought per activity.
            </Typography>
            {trackStock && <FormControl size="small" fullWidth sx={{ mt: 1.25, maxWidth: 340 }}>
              <InputLabel>Stock precision</InputLabel>
              <Select label="Stock precision" value={stockPrecision} onChange={(e) => setStockPrecision(e.target.value as 'exact' | 'tentative')}>
                <MenuItem value="exact">Exact — counted precisely (cement)</MenuItem>
                <MenuItem value="tentative">Tentative — bulk volume, ordered by the truckload (sand, aggregate)</MenuItem>
              </Select>
            </FormControl>}
          </Box>}
          <FormControlLabel
            control={<Checkbox size="small" checked={codeRequired} onChange={(e) => setCodeRequired(e.target.checked)} />}
            label={<SmallLabel>{isLabourDialog ? 'Code/model required for this labour' : 'Code/model required for this material'}</SmallLabel>}
          />
          {isLabourDialog && <Box>
            <FormControlLabel
              control={<Checkbox size="small" checked={laborStockVisible} onChange={(e) => setLaborStockVisible(e.target.checked)} />}
              label={<SmallLabel>Show in site stock usage dropdown</SmallLabel>}
            />
            <Typography sx={{ color: MUTED, fontSize: 11.5, ml: 4.25, mt: -0.5 }}>
              Lets this trade be picked as "who used it" when recording site stock usage, independent of whether it has a costed line in the estimate yet.
            </Typography>
          </Box>}
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.25, bgcolor: PANEL2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, color: DARK }}>Assembly / variety mapping</Typography>
                <Typography sx={{ color: MUTED, fontSize: 11.5 }}>Use this when options belong under a type, such as Magnet → Light / Heavy.</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setAssemblyVarietyMap((prev) => [...prev, { id: `map_${Date.now()}`, assemblyType: '', varieties: [], type: 'base', unit }])}
                sx={{ borderColor: BORDER, color: MUTED }}
              >
                Add type
              </Button>
            </Stack>
            {assemblyVarietyMap.length ? (
              <Stack gap={1} sx={{ p: 1.25 }}>
                {assemblyVarietyMap.map((row, index) => (
                  <Box key={row.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.25, p: 1, bgcolor: '#fff' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Stack direction="row" gap={0.25} justifyContent={{ xs: 'flex-start', sm: 'center' }} sx={{ minWidth: 52 }}>
                        <ActionIcon title="Move up" onClick={() => moveMappingRow(row.id, -1)} disabled={index === 0}>
                          <KeyboardArrowUpIcon fontSize="inherit" />
                        </ActionIcon>
                        <ActionIcon title="Move down" onClick={() => moveMappingRow(row.id, 1)} disabled={index === assemblyVarietyMap.length - 1}>
                          <KeyboardArrowDownIcon fontSize="inherit" />
                        </ActionIcon>
                      </Stack>
                      <TextField
                        label="Assembly type"
                        value={row.assemblyType}
                        onChange={(e) => updateMappingRow(row.id, { assemblyType: e.target.value })}
                        size="small"
                        placeholder="Magnet / Ingis 5&quot;"
                        sx={{ flex: 1 }}
                      />
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={row.varieties}
                        inputValue={mappingVarietyInput[row.id] || ''}
                        onInputChange={(_, value, reason) => {
                          if (reason !== 'reset') setMappingVarietyInput((prev) => ({ ...prev, [row.id]: value }));
                        }}
                        onChange={(_, value) => updateMappingRow(row.id, { varieties: Array.from(new Set(value.map((v) => String(v).trim()).filter(Boolean))) })}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Varieties"
                            size="small"
                            placeholder="Type and press Enter"
                            helperText="Enter adds chip"
                            onBlur={() => commitMappingVarietyInput(row.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ',') {
                                event.preventDefault();
                                commitMappingVarietyInput(row.id);
                              }
                            }}
                          />
                        )}
                        sx={{ flex: 1.35 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 96 }}>
                        <InputLabel>Unit</InputLabel>
                        <Select
                          label="Unit"
                          value={row.unit}
                          onChange={(e) => updateMappingRow(row.id, { unit: e.target.value })}
                        >
                          {MATERIAL_UNIT_OPTIONS.map((optionUnit) => <MenuItem key={optionUnit} value={optionUnit}>{optionUnit}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <ActionIcon title="Remove type" onClick={() => setAssemblyVarietyMap((prev) => prev.filter((x) => x.id !== row.id))} danger>
                        <DeleteOutlineIcon fontSize="inherit" />
                      </ActionIcon>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1, pl: { xs: 0, sm: '52px' } }}>
                      <Box sx={{ minWidth: { sm: 280 } }}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={!!row.secondaryUnitRequired} onChange={(e) => updateMappingRow(row.id, {
                            secondaryUnitRequired: e.target.checked,
                            secondaryMultiplier: e.target.checked ? num(row.secondaryMultiplier) : 0,
                            secondaryUnit: e.target.checked ? (row.secondaryUnit || 'Nos') : '',
                          })} />}
                          label={<SmallLabel>Override secondary unit for this type</SmallLabel>}
                        />
                        <Typography sx={{ color: MUTED, fontSize: 11.5, ml: 4.25, mt: -0.5 }}>
                          Overrides the material default for this assembly type only.
                        </Typography>
                      </Box>
                      {row.secondaryUnitRequired && (
                        <>
                          <TextField
                            label={`Qty inside 1 ${row.unit || 'unit'}`}
                            value={row.secondaryMultiplier || ''}
                            onChange={(e) => updateMappingRow(row.id, { secondaryMultiplier: num(e.target.value) })}
                            type="number"
                            size="small"
                            sx={{ minWidth: 180, flex: 1 }}
                          />
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Secondary unit</InputLabel>
                            <Select
                              label="Secondary unit"
                              value={row.secondaryUnit || 'Nos'}
                              onChange={(e) => updateMappingRow(row.id, { secondaryUnit: e.target.value })}
                            >
                              {MATERIAL_UNIT_OPTIONS.map((optionUnit) => <MenuItem key={optionUnit} value={optionUnit}>{optionUnit}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </>
                      )}
                    </Stack>
                    {!!row.varieties.length && (() => {
                      const hasExistingPacking = row.varieties.some((variety) => { const p = packingFor(row.assemblyType, variety); return !!p && (p.piecesPerBox > 0 || p.areaPerPiece > 0); });
                      const boxReceivingChecked = row.boxReceiving ?? hasExistingPacking;
                      return <Box sx={{ mt: 1.2, ml: { xs: 0, sm: '52px' }, p: 1, borderRadius: 1, bgcolor: '#faf8f4' }}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={boxReceivingChecked} onChange={(e) => updateMappingRow(row.id, { boxReceiving: e.target.checked })} />}
                          label={<SmallLabel>Received in boxes by variety?</SmallLabel>}
                        />
                        {boxReceivingChecked && <>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: GOLD_D, mb: 0.8, mt: 0.5 }}>BOX RECEIVING BY VARIETY</Typography>
                          <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 0.9 }}>Leave tiles per box blank when this variety is received as Sft only.</Typography>
                          <Stack gap={0.75}>{row.varieties.map((variety) => {
                            const packing = packingFor(row.assemblyType, variety);
                            const calculatedArea = tileAreaFromVariety(variety);
                            return <Stack key={variety} direction={{ xs: 'column', sm: 'row' }} gap={0.75} alignItems={{ sm: 'center' }}>
                              <Typography sx={{ minWidth: { sm: 130 }, fontSize: 12.5, fontWeight: 700 }}>{variety}</Typography>
                              <TextField label="Tiles / box" type="number" size="small" value={packing?.piecesPerBox || ''} onChange={(event) => updatePacking(row.assemblyType, variety, { piecesPerBox: num(event.target.value), areaPerPiece: packing?.areaPerPiece || calculatedArea })} inputProps={{ min: 0, step: 1 }} sx={{ width: { xs: '100%', sm: 145 } }} />
                              <TextField label="Sft / tile" type="number" size="small" value={packing?.areaPerPiece || calculatedArea || ''} onChange={(event) => updatePacking(row.assemblyType, variety, { areaPerPiece: num(event.target.value), piecesPerBox: packing?.piecesPerBox || 0 })} inputProps={{ min: 0, step: 'any' }} sx={{ width: { xs: '100%', sm: 145 } }} />
                              {!!packing?.piecesPerBox && !!(packing.areaPerPiece || calculatedArea) && <Typography sx={{ fontSize: 12, color: MUTED }}>1 box = {fmtQty(packing.piecesPerBox * (packing.areaPerPiece || calculatedArea))} Sft</Typography>}
                            </Stack>;
                          })}</Stack>
                        </>}
                      </Box>;
                    })()}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ p: 1.5, fontSize: 12.5, color: MUTED }}>
                No mapping added. Quote options below will work as the normal rate template.
              </Typography>
            )}
          </Box>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.25, bgcolor: PANEL2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, color: DARK }}>{hasMappingRows ? 'Add-on rate options' : 'Quote option template'}</Typography>
                <Typography sx={{ color: MUTED, fontSize: 11.5 }}>
                  {hasMappingRows ? 'Use only for extra charges beyond the assembly / variety mapping.' : 'These rows appear automatically whenever you add a vendor quote.'}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setQuoteOptionTemplates((prev) => [...prev, { id: `template_${Date.now()}`, group: '', name: '', type: hasMappingRows ? 'addon' : 'base', unit }])}
                sx={{ borderColor: BORDER, color: MUTED }}
              >
                {hasMappingRows ? 'Add add-on' : 'Add option'}
              </Button>
            </Stack>
            {!!visibleQuoteOptionTemplates.length && (
              <Stack gap={1} sx={{ p: 1.25 }}>
                {visibleQuoteOptionTemplates.map((template) => (
                  <Stack key={template.id} direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                      label="Option group"
                      value={template.group || ''}
                      onChange={(e) => setQuoteOptionTemplates((prev) => prev.map((x) => x.id === template.id ? { ...x, group: e.target.value } : x))}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="Magnet / Ingis 5&quot; / Finish"
                    />
                    <TextField
                      label="Option name"
                      value={template.name}
                      onChange={(e) => setQuoteOptionTemplates((prev) => prev.map((x) => x.id === template.id ? { ...x, name: e.target.value } : x))}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    {!hasMappingRows && (
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          label="Type"
                          value={template.type}
                          onChange={(e) => setQuoteOptionTemplates((prev) => prev.map((x) => x.id === template.id ? { ...x, type: e.target.value as 'base' | 'addon' } : x))}
                        >
                          <MenuItem value="base">Base variant</MenuItem>
                          <MenuItem value="addon">Add-on</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        label="Unit"
                        value={template.unit}
                        onChange={(e) => setQuoteOptionTemplates((prev) => prev.map((x) => x.id === template.id ? { ...x, unit: e.target.value } : x))}
                      >
                        {MATERIAL_UNIT_OPTIONS.map((optionUnit) => <MenuItem key={optionUnit} value={optionUnit}>{optionUnit}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <ActionIcon title="Remove option" onClick={() => setQuoteOptionTemplates((prev) => prev.filter((x) => x.id !== template.id))} danger>
                      <DeleteOutlineIcon fontSize="inherit" />
                    </ActionIcon>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!materialName.trim() || !unit.trim()}
          onClick={() => {
            const resolvedGroup = materialGroupValueFromLabel(group);
            const mappedRows = sanitizeAssemblyVarietyMap(mappingRowsWithPendingInput());
            const mappedTemplates = quoteTemplatesFromAssemblyVarietyMap(mappedRows);
            const saveTemplates = mappedTemplates.length
              ? [...mappedTemplates, ...quoteOptionTemplates.filter((template) => template.type === 'addon')]
              : quoteOptionTemplates;
            const preserveLegacyFields = !!edit && resolvedGroup === edit.group && mappedRows.length === 0;
            const resolvedUnit = mappedRows[0]?.unit || unit;
            onSave({
              group: resolvedGroup,
              materialName: materialName.trim(),
              grade: grade.trim(),
              brand: brand.trim(),
              // The type mapping owns the unit. This fallback only preserves older materials
              // that have not been migrated to an assembly/type mapping yet.
              unit: resolvedUnit,
              secondaryUnitRequired,
              secondaryMultiplier: secondaryUnitRequired ? num(secondaryMultiplier) : 0,
              secondaryUnit: secondaryUnitRequired ? secondaryUnit : '',
              pieceEntry: pieceShapeForUnit(unit) > 0 && pieceEntry,
              pieceDimensionUnit: pieceDimensionUnit || defaultDimensionUnitFor(unit),
              trackStock: !isLabourDialog && trackStock,
              stockPrecision: !isLabourDialog && trackStock ? stockPrecision : undefined,
              laborStockVisible: isLabourDialog ? laborStockVisible : undefined,
              codeRequired,
              assemblyVarietyMap: mappedRows,
              boxPackings: boxPackings.filter((packing) => packing.piecesPerBox > 0 && packing.areaPerPiece > 0 && mappedRows.some((row) => row.assemblyType === packing.assemblyType && row.varieties.includes(packing.variety))),
              assemblyTypes: mappedRows.length ? mappedRows.map((row) => row.assemblyType) : (preserveLegacyFields ? (edit?.assemblyTypes || []) : []),
              varieties: mappedRows.length ? Array.from(new Set(mappedRows.flatMap((row) => row.varieties))) : (preserveLegacyFields ? (edit?.varieties || []) : []),
              quoteOptionTemplates: saveTemplates
                .filter((template) => template.name.trim())
                .map((template) => ({ ...template, group: (template.group || '').trim(), name: template.name.trim(), unit: template.unit || resolvedUnit })),
            });
          }}
        >
          {isLabourDialog ? 'Save labour' : 'Save material'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssemblyDialog({ edit, onClose, onSave }: {
  edit?: MaterialAssembly;
  onClose: () => void;
  onSave: (assembly: MaterialAssembly) => void;
}) {
  const [name, setName] = useState(edit?.name || 'Finished Flush Door');
  const [category, setCategory] = useState(edit?.category || 'Door');
  const [outputUnit, setOutputUnit] = useState(edit?.outputUnit || 'Sft');
  const [referenceUnit, setReferenceUnit] = useState(edit?.referenceUnit || 'Door');
  const [referenceQty, setReferenceQty] = useState(String(edit?.referenceQty ?? 21));
  const components = edit?.components || [];
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: POPUP_PAPER_SX }}>
      <DlgTitle onClose={onClose}>{edit ? 'Edit assembly' : 'Add assembly'}</DlgTitle>
      <DialogContent>
        <Stack gap={2} mt={0.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField
              label="Assembly name"
              value={name}
              onChange={(e) => {
                const nextName = e.target.value;
                setName(nextName);
              }}
              size="small"
              fullWidth
              autoFocus
            />
            <Autocomplete
              freeSolo
              options={ASSEMBLY_TYPE_OPTIONS}
              value={category}
              onChange={(_, value) => {
                const nextCategory = value || '';
                setCategory(nextCategory);
              }}
              onInputChange={(_, value) => {
                setCategory(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assembly type"
                  size="small"
                  />
              )}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Output unit</InputLabel>
              <Select
                label="Output unit"
                value={outputUnit}
                onChange={(e) => {
                  const nextOutputUnit = e.target.value;
                  setOutputUnit(nextOutputUnit);
                }}
              >
                {MATERIAL_UNIT_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField
              label={`Secondary qty (${referenceUnit || 'secondary'} per ${outputUnit || 'unit'})`}
              value={referenceQty}
              onChange={(e) => setReferenceQty(e.target.value)}
              type="number"
              size="small"
              fullWidth
              helperText={referenceUnit ? `Use this for components consumed per ${referenceUnit}.` : 'Optional reference unit.'}
            />
            <TextField
              label="Secondary unit"
              value={referenceUnit}
              onChange={(e) => setReferenceUnit(e.target.value)}
              size="small"
              fullWidth
              helperText="Example: Sft, Rft, Kg"
            />
          </Stack>

          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.5, bgcolor: PANEL2 }}>
            <Typography sx={{ fontWeight: 800, color: DARK }}>Components are added after saving</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12, mt: 0.5 }}>
              Save this assembly first, then use Add component on the assembly card. Components must link to an existing material or labour rate item.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || !outputUnit.trim()}
          onClick={() => onSave({
            id: edit?.id || `assembly_${Date.now()}`,
            name: name.trim(),
            category: category.trim(),
            outputUnit,
            referenceUnit: referenceUnit.trim(),
            referenceQty: num(referenceQty),
            components: components
              .filter((component) => component.label.trim() && num(component.qty) > 0 && (component.type === 'manual' || !!component.materialName))
              .map((component) => {
                const next: AssemblyComponent = {
                  id: component.id,
                  type: component.type,
                  basis: component.basis || 'output',
                  label: component.label.trim(),
                  qty: num(component.qty),
                  unit: component.unit,
                };
                if (component.type === 'material') {
                  next.materialName = component.materialName || component.label.trim();
                  next.materialGroup = component.materialGroup || 'other';
                  if (component.quoteOptionId) next.quoteOptionId = component.quoteOptionId;
                } else {
                  next.manualRateSource = component.manualRateSource || (num(component.manualRate) > 0 ? 'manual' : 'pending');
                  if (next.manualRateSource === 'material') {
                    if (component.linkedMaterialId) next.linkedMaterialId = component.linkedMaterialId;
                    if (component.linkedMaterialName) next.linkedMaterialName = component.linkedMaterialName;
                    if (component.linkedMaterialGroup) next.linkedMaterialGroup = component.linkedMaterialGroup;
                    if (component.linkedQuoteId) next.linkedQuoteId = component.linkedQuoteId;
                    if (component.linkedQuoteOptionId) next.linkedQuoteOptionId = component.linkedQuoteOptionId;
                  } else if (next.manualRateSource === 'manual') {
                    if (num(component.manualRate) > 0) next.manualRate = num(component.manualRate);
                    next.gst = num(component.gst);
                    next.gstMode = component.gstMode || 'exclusive';
                  }
                }
                return next;
              }),
            order: edit?.order,
          })}
        >
          Save assembly
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConcreteGradesDialog({ grades, materialItems, onClose, onSave }: {
  grades: ConcreteGrade[];
  materialItems: MaterialRateItem[];
  onClose: () => void;
  onSave: (grades: ConcreteGrade[]) => void;
}) {
  const [rows, setRows] = useState<ConcreteGrade[]>(grades.length ? grades : []);
  const ratioMaterials = trackedMaterials(materialItems).filter((item) => item.group !== 'cement');

  const addGrade = () => setRows((previous) => [...previous, { id: `grade_${Date.now()}`, name: '', ratios: [] }]);
  const removeGrade = (gradeId: string) => setRows((previous) => previous.filter((g) => g.id !== gradeId));
  const renameGrade = (gradeId: string, name: string) => setRows((previous) => previous.map((g) => g.id === gradeId ? { ...g, name } : g));
  const addRatio = (gradeId: string) => setRows((previous) => previous.map((g) => g.id === gradeId
    ? { ...g, ratios: [...g.ratios, { materialId: ratioMaterials[0]?.id || '', qtyPerCementUnit: 0 }] } : g));
  const removeRatio = (gradeId: string, index: number) => setRows((previous) => previous.map((g) => g.id === gradeId
    ? { ...g, ratios: g.ratios.filter((_, i) => i !== index) } : g));
  const updateRatio = (gradeId: string, index: number, patch: Partial<{ materialId: string; qtyPerCementUnit: number }>) =>
    setRows((previous) => previous.map((g) => g.id === gradeId
      ? { ...g, ratios: g.ratios.map((r, i) => i === index ? { ...r, ...patch } : r) } : g));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: POPUP_PAPER_SX }}>
      <DlgTitle onClose={onClose}>Manage concrete grades</DlgTitle>
      <DialogContent>
        <Typography sx={{ color: MUTED, fontSize: 12.5, mb: 1.5 }}>
          Each grade's ratios are per 1 unit of Cement — used only to suggest a quantity while recording cement's site stock usage. Never applied automatically.
        </Typography>
        <Stack gap={2}>
          {rows.map((grade) => (
            <Box key={grade.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.5, bgcolor: PANEL2 }}>
              <Stack direction="row" gap={1} alignItems="center">
                <TextField
                  label="Grade name"
                  placeholder="M20"
                  value={grade.name}
                  onChange={(e) => renameGrade(grade.id, e.target.value)}
                  size="small"
                  sx={{ maxWidth: 200 }}
                />
                <Box flex={1} />
                <IconButton size="small" onClick={() => removeGrade(grade.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Stack>
              <Stack gap={1} mt={1.5}>
                {grade.ratios.map((ratio, index) => (
                  <Stack key={index} direction="row" gap={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel>Material</InputLabel>
                      <Select
                        label="Material"
                        value={ratio.materialId}
                        onChange={(e) => updateRatio(grade.id, index, { materialId: e.target.value })}
                      >
                        {ratioMaterials.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Qty per 1 Cement unit"
                      type="number"
                      value={ratio.qtyPerCementUnit || ''}
                      onChange={(e) => updateRatio(grade.id, index, { qtyPerCementUnit: num(e.target.value) })}
                      size="small"
                      sx={{ maxWidth: 200 }}
                    />
                    <IconButton size="small" onClick={() => removeRatio(grade.id, index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddOutlinedIcon />} onClick={() => addRatio(grade.id)} sx={{ alignSelf: 'flex-start', color: MUTED }} disabled={!ratioMaterials.length}>
                  Add material
                </Button>
              </Stack>
            </Box>
          ))}
          <Button variant="outlined" startIcon={<AddOutlinedIcon />} onClick={addGrade} sx={{ borderColor: BORDER, color: MUTED, alignSelf: 'flex-start' }}>
            Add grade
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave(rows
            .filter((g) => g.name.trim())
            .map((g) => ({ ...g, name: g.name.trim(), ratios: g.ratios.filter((r) => r.materialId && r.qtyPerCementUnit > 0) })))}
        >
          Save grades
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SteelQuoteDialog({ item, edit, vendorOptions, brandOptions, initialVendor, initialBrand, allowNoRate, cartingAgentsByVendor, onSaveCartingAgents, onClose, onSave, onDelete }: {
  item: MaterialRateItem;
  edit?: MaterialQuote;
  vendorOptions: string[];
  brandOptions: string[];
  /** Starts a new quote with this vendor (used when a delivery arrives from a vendor with no quote yet). */
  initialVendor?: string;
  initialBrand?: string;
  /** Lets the vendor be saved without any rate; the rate can be added later. */
  allowNoRate?: boolean;
  /** A vendor's regular transporters — fixed per vendor, unrelated to this material's rate. */
  cartingAgentsByVendor?: Record<string, string[]>;
  onSaveCartingAgents?: (vendorName: string, agents: string[]) => void;
  onClose: () => void;
  onSave: (quote: MaterialQuote) => void;
  onDelete?: () => void | Promise<void>;
}) {
  const mobileFullScreen = useMediaQuery('(max-width:600px)');
  const isSteel = item.group === 'steel';
  const isLabour = item.group === 'labour';
  const today = new Date().toISOString().slice(0, 10);
  const [vendor, setVendor] = useState(edit?.vendor || initialVendor || '');
  const [newCartingAgent, setNewCartingAgent] = useState('');
  const cartingAgents = cartingAgentsFor(cartingAgentsByVendor || {}, vendor);
  const addCartingAgent = () => {
    const next = planCartingAgents(cartingAgents, newCartingAgent);
    if (next !== cartingAgents) onSaveCartingAgents?.(vendor, next);
    setNewCartingAgent('');
  };
  const [brand, setBrand] = useState(edit?.brand || initialBrand || item.brand || '');
  const [refName, setRefName] = useState(edit?.refName || '');
  const [refNumber, setRefNumber] = useState(edit?.refNumber || '');
  const [rate, setRate] = useState(String(edit?.rate ?? ''));
  const [materialCode, setMaterialCode] = useState(edit?.materialCode || '');
  const [coveredDiameters, setCoveredDiameters] = useState<string[]>(edit?.coveredDiameters || item.coveredDiameters || STEEL_DIAMETERS);
  const [diameterRates, setDiameterRates] = useState<Record<string, string>>(() => {
    const rates: Record<string, string> = {};
    for (const dia of STEEL_DIAMETERS) rates[dia] = edit?.diameterRates?.[dia] ? String(edit.diameterRates[dia]) : '';
    return rates;
  });
  const [unit, setUnit] = useState<string>(edit?.unit || item.unit || defaultMaterialUnit(item.group) || 'Nos');
  const [gst, setGst] = useState(edit?.gst === undefined || edit?.gst === null ? '' : String(edit.gst));
  const [gstMode, setGstMode] = useState<'' | 'inclusive' | 'exclusive'>(edit?.gstMode === 'inclusive' ? 'inclusive' : edit?.gstMode === 'exclusive' ? 'exclusive' : '');
  const [freight, setFreight] = useState(String(edit?.freight ?? ''));
  const [loading, setLoading] = useState(String(edit?.loading ?? ''));
  const [quoteDate, setQuoteDate] = useState(edit?.effectiveFrom || edit?.quoteDate || today);
  const [validUntil, setValidUntil] = useState(edit?.effectiveUntil || edit?.validUntil || '');
  const [paymentTerms, setPaymentTerms] = useState(edit?.paymentTerms || '');
  const [deliveryTimeline, setDeliveryTimeline] = useState(edit?.deliveryTimeline || '');
  const [notes, setNotes] = useState(edit?.notes || '');
  const [quoteOptions, setQuoteOptions] = useState<MaterialQuoteOption[]>(() => expandQuoteOptionsFromTemplate(item, edit?.quoteOptions || []));
  const [checklist, setChecklist] = useState<MaterialQuote['checklist']>(edit?.checklist || {
    testCertificate: false,
    weighbridgeSlip: false,
    heatNumberTraceability: false,
    freeUnloading: false,
    creditPeriod: false,
  });
  const setCheck = (key: keyof MaterialQuote['checklist'], checked: boolean) =>
    setChecklist((prev) => ({ ...prev, [key]: checked }));
  const setDiameterCovered = (dia: string, checked: boolean) => {
    setCoveredDiameters((prev) => checked
      ? Array.from(new Set([...prev, dia]))
      : prev.filter((x) => x !== dia));
  };
  const matrixGroups = materialQuoteMatrixGroups(item);
  const matrixColumns = matrixGroups.flatMap((group) => group.columns);
  const showOptionMatrix = matrixGroups.length > 0;
  const hasTemplateOptions = (item.quoteOptionTemplates || []).length > 0;
  const customQuoteOptions = quoteOptions.filter((option) => !option.templateId);
  const hasOptionRate = quoteOptions.some((option) => num(option.rate) > 0);
  const hasPricedRate = hasTemplateOptions ? hasOptionRate : !!num(rate);
  const hasGstSelection = Boolean(gstMode && gst !== '');
  const canSaveQuote = Boolean(vendor.trim() && hasGstSelection && (hasPricedRate || allowNoRate));
  const updateQuoteOption = (id: string, patch: Partial<MaterialQuoteOption>) =>
    setQuoteOptions((prev) => prev.map((option) => option.id === id ? { ...option, ...patch } : option));
  const codeModelOptionsFor = (option?: MaterialQuoteOption) => option ? materialOptionCodeModelSuggestions(item, option) : [];
  const draftQuote = buildMaterialQuotePayload({
    id: edit?.id || 'preview',
    vendor,
    brand,
    refName,
    refNumber,
    rate: hasTemplateOptions ? 0 : num(rate),
    materialCode,
    unit,
    gst: num(gst),
    gstMode: gstMode || 'exclusive',
    freight: num(freight),
    loading: num(loading),
    quoteDate,
    validUntil,
    paymentTerms,
    deliveryTimeline,
    notes,
    quoteOptions,
    checklist,
    isSteel,
    coveredDiameters,
    diameterRates,
  });
  return (
    <Dialog
      open
      fullScreen={mobileFullScreen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: POPUP_PAPER_SX }}
    >
      <DlgTitle onClose={onClose}>
        {edit
          ? isLabour ? 'Edit labour quote' : 'Edit material quote'
          : isLabour ? 'Add labour quote' : 'Add material quote'}
      </DlgTitle>
      <DialogContent>
        <Stack gap={2} mt={0.5}>
          <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: PANEL2, color: MUTED, fontSize: 12.5 }}>
            {[item.materialName, item.grade, isSteel && !showOptionMatrix ? (item.coveredDiameters || STEEL_DIAMETERS).join(', ') : null].filter(Boolean).join(' · ')}
          </Box>
          {allowNoRate && !edit && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: -1 }}>You can save the vendor now without a rate and add the rate later.</Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <Autocomplete
              freeSolo
              options={vendorOptions}
              value={vendor}
              onChange={(_, value) => setVendor(value || '')}
              onInputChange={(_, value) => setVendor(value)}
              renderInput={(params) => <TextField {...params} label="Vendor" size="small" fullWidth autoFocus />}
              fullWidth
            />
            {onSaveCartingAgents && vendor.trim() && <Box sx={{ width: '100%' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: FAINT, textTransform: 'uppercase', mb: 0.5 }}>Carting agents for {vendor.trim()}</Typography>
              <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
                {cartingAgents.map((agent) => <Chip key={agent} size="small" label={agent} onDelete={() => onSaveCartingAgents(vendor, removeCartingAgent(cartingAgents, agent))} />)}
                <TextField size="small" placeholder="Add a transporter" value={newCartingAgent} onChange={(e) => setNewCartingAgent(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCartingAgent(); } }} sx={{ width: 180 }} />
                <Button size="small" onClick={addCartingAgent} disabled={!newCartingAgent.trim()} sx={{ color: GOLD_D, fontWeight: 700 }}>Add</Button>
              </Stack>
              <Typography sx={{ color: MUTED, fontSize: 11.5, mt: 0.5 }}>Rate stays the same regardless of which transporter delivers — this is saved against the vendor, not this quote.</Typography>
            </Box>}
            <Autocomplete
              freeSolo
              options={brandOptions}
              value={brand}
              onChange={(_, value) => setBrand(value || '')}
              onInputChange={(_, value) => setBrand(value)}
              renderInput={(params) => <TextField {...params} label="Brand / mill" size="small" fullWidth />}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label="Ref name"
              value={refName}
              onChange={(e) => setRefName(e.target.value)}
              size="small"
              fullWidth
              placeholder="Contact person"
            />
            <TextField
              label="Ref number"
              value={refNumber}
              onChange={(e) => setRefNumber(e.target.value)}
              size="small"
              fullWidth
              placeholder="Mobile / phone"
            />
          </Stack>
          {!hasTemplateOptions && item.codeRequired && (
            <TextField
              label="Material code / model"
              value={materialCode}
              onChange={(e) => setMaterialCode(e.target.value)}
              size="small"
              fullWidth
              placeholder="Design code, model no., catalogue code"
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            {!hasTemplateOptions && (
              <TextField
                label="Base rate" value={rate} onChange={(e) => setRate(e.target.value)} type="number" size="small" fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
                {MATERIAL_UNIT_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ width: '100%' }}>
              <Typography sx={{ fontSize: 11, color: FAINT, fontWeight: 800, textTransform: 'uppercase', mb: 0.4 }}>Rate GST mode</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={gstMode}
                onChange={(_, value) => value && setGstMode(value)}
                aria-label="Rate GST mode"
                sx={{
                  '& .MuiToggleButton-root': {
                    minHeight: 40,
                    textTransform: 'none',
                    fontWeight: 800,
                    borderColor: BORDER,
                    color: MUTED,
                    '&.Mui-selected': { bgcolor: GOLD_WASH, color: GOLD_D, borderColor: GOLD_D },
                  },
                }}
              >
                <ToggleButton value="exclusive" aria-label="Excl GST">Excl GST</ToggleButton>
                <ToggleButton value="inclusive" aria-label="Incl GST">Incl GST</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField select label="GST %" value={gst} onChange={(e) => setGst(e.target.value)} size="small" fullWidth>
              <MenuItem value="">Select GST</MenuItem>
              {GST_PERCENT_OPTIONS.map((option) => (
                <MenuItem key={option} value={String(option)}>{option === 0 ? 'No GST (0%)' : `${option}%`}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField label="Freight / unit" value={freight} onChange={(e) => setFreight(e.target.value)} type="number" size="small" fullWidth />
            <TextField label="Loading / unit" value={loading} onChange={(e) => setLoading(e.target.value)} type="number" size="small" fullWidth />
            <Box sx={{ minWidth: 130, p: 1, border: `1px solid ${BORDER}`, borderRadius: 1, textAlign: 'right' }}>
              <Typography sx={{ fontSize: 10.5, color: FAINT, fontWeight: 700 }}>LANDED</Typography>
              <Typography sx={{ fontWeight: 800, color: GOLD_D }}>{quoteLandedRateLabel(item, draftQuote)}</Typography>
            </Box>
          </Stack>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.25, bgcolor: PANEL2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, color: DARK }}>Quote options</Typography>
                <Typography sx={{ color: MUTED, fontSize: 11.5 }}>Use for Single Core, Double Core, Border Patti, BWP extra, and similar vendor options.</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setQuoteOptions((prev) => [...prev, { id: `option_${Date.now()}`, name: '', type: 'base', rate: 0, unit }])}
                sx={{ borderColor: BORDER, color: MUTED }}
              >
                Add option
              </Button>
            </Stack>
            {quoteOptions.length > 0 && (
              <Stack gap={1} sx={{ p: 1.25 }}>
                {showOptionMatrix && (
                  <Box sx={{ overflowX: 'auto', border: `1px solid ${BORDER}`, borderRadius: 1.25, bgcolor: '#fff' }}>
                    <Box component="table" sx={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse' }}>
                      <Box component="thead">
                        <Box component="tr">
                          <Box component="th" rowSpan={2} sx={{
                            ...headTh,
                            textAlign: 'left',
                            position: 'sticky',
                            left: 0,
                            bgcolor: '#fff',
                            zIndex: 3,
                            minWidth: 118,
                          }}>Option</Box>
                          {matrixGroups.map((group) => (
                            <Box key={group.label} component="th" colSpan={group.columns.length} sx={{ ...headTh, textAlign: 'center', position: 'static', bgcolor: '#ebe6dc', color: GOLD_D, minWidth: 190 * group.columns.length, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.25 }}>
                              {group.label}
                            </Box>
                          ))}
                        </Box>
                        <Box component="tr">
                          {matrixColumns.map((column, index) => (
                            <Box key={column.optionId} component="th" sx={{ ...headTh, textAlign: 'left', position: 'static', bgcolor: '#fff', minWidth: 190, borderLeft: index === 0 || column.groupLabel !== matrixColumns[index - 1]?.groupLabel ? `1px solid ${BORDER}` : undefined, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.25, verticalAlign: 'middle' }}>
                              {column.template.name}
                              <Box component="span" sx={{ display: 'block', color: FAINT, fontSize: 10, mt: 0.25 }}>
                                per {column.template.unit}
                              </Box>
                              {!!quoteTemplateSecondaryUnitLabel(column.template, item) && (
                                <Box component="span" sx={{ display: 'block', color: MUTED, fontSize: 9.5, mt: 0.25, lineHeight: 1.25 }}>
                                  {quoteTemplateSecondaryUnitLabel(column.template, item)}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        <Box component="tr">
                            <Box component="td" sx={{
                              p: '8px 10px',
                              borderTop: `1px solid ${BORDER}`,
                              fontWeight: 800,
                              whiteSpace: 'nowrap',
                              position: 'sticky',
                              left: 0,
                              bgcolor: '#fff',
                              zIndex: 2,
                            }}>Rate</Box>
                            {matrixColumns.map((column, index) => {
                              const template = column.template;
                              const optionId = column.optionId;
                              const option = quoteOptions.find((x) => x.id === optionId);
                              return (
                                <Box component="td" key={optionId} sx={{ p: '8px 10px', borderTop: `1px solid ${BORDER}`, minWidth: 190, borderLeft: index === 0 || column.groupLabel !== matrixColumns[index - 1]?.groupLabel ? `1px solid ${BORDER}` : undefined }}>
                                  <TextField
                                    value={option?.rate || ''}
                                    onChange={(e) => option && updateQuoteOption(option.id, { rate: num(e.target.value) })}
                                    type="number"
                                    size="small"
                                    placeholder={template.type === 'base' ? 'base' : 'extra'}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                      endAdornment: <InputAdornment position="end">per {template.unit}</InputAdornment>,
                                    }}
                                    sx={{
                                      width: 170,
                                      display: 'block',
                                      '& input': { py: 0.9 },
                                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                      '& input[type=number]': { MozAppearance: 'textfield' },
                                    }}
                                  />
                                  <TextField
                                    value={option?.brand || ''}
                                    onChange={(e) => option && updateQuoteOption(option.id, { brand: e.target.value })}
                                    size="small"
                                    placeholder="brand optional"
                                    sx={{
                                      width: 170,
                                      display: 'block',
                                      mt: 0.75,
                                      '& input': { py: 0.7, fontSize: 12 },
                                    }}
                                  />
                                  {item.codeRequired && (
                                    <Autocomplete
                                      freeSolo
                                      options={codeModelOptionsFor(option)}
                                      value={option?.code || ''}
                                      onChange={(_, value) => option && updateQuoteOption(option.id, { code: value || '' })}
                                      onInputChange={(_, value) => option && updateQuoteOption(option.id, { code: value })}
                                      size="small"
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="code / model"
                                          size="small"
                                        />
                                      )}
                                      sx={{
                                        width: 170,
                                        display: 'block',
                                        mt: 0.75,
                                        '& input': { py: 0.7, fontSize: 12 },
                                      }}
                                    />
                                  )}
                                </Box>
                              );
                            })}
                        </Box>
                      </Box>
                    </Box>
                    <Typography sx={{ color: FAINT, fontSize: 11.5, px: 1.25, py: 1, borderTop: `1px solid ${BORDER}` }}>
                      Blank add-on cells are ignored. Enter rates only for available vendor options.
                    </Typography>
                  </Box>
                )}
                {customQuoteOptions.map((option) => (
                  <Stack key={option.id} direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                      label="Group"
                      value={option.group || ''}
                      onChange={(e) => updateQuoteOption(option.id, { group: e.target.value })}
                      size="small"
                      sx={{ width: 140 }}
                      placeholder="Magnet / Ingis"
                    />
                    <TextField
                      label="Option name"
                      value={option.name}
                      onChange={(e) => updateQuoteOption(option.id, { name: e.target.value })}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="Single Core / Double Core / Border Patti"
                    />
                    {(item.varieties?.length || option.variety) && (
                      <TextField
                        label="Variety"
                        value={option.variety || ''}
                        onChange={(e) => updateQuoteOption(option.id, { variety: e.target.value })}
                        size="small"
                        sx={{ width: 120 }}
                      />
                    )}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={option.type}
                        onChange={(e) => updateQuoteOption(option.id, { type: e.target.value as 'base' | 'addon' })}
                      >
                        <MenuItem value="base">Base variant</MenuItem>
                        <MenuItem value="addon">Add-on</MenuItem>
                      </Select>
                    </FormControl>
                    <Stack gap={0.75} sx={{ width: { xs: '100%', sm: 150 } }}>
                      <TextField
                        label="Rate"
                        value={option.rate || ''}
                        onChange={(e) => updateQuoteOption(option.id, { rate: num(e.target.value) })}
                        type="number"
                        size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                      <TextField
                        label="Brand"
                        value={option.brand || ''}
                        onChange={(e) => updateQuoteOption(option.id, { brand: e.target.value })}
                        size="small"
                        placeholder="Optional"
                      />
                      {item.codeRequired && (
                        <Autocomplete
                          freeSolo
                          options={codeModelOptionsFor(option)}
                          value={option.code || ''}
                          onChange={(_, value) => updateQuoteOption(option.id, { code: value || '' })}
                          onInputChange={(_, value) => updateQuoteOption(option.id, { code: value })}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Code / model"
                              size="small"
                            />
                          )}
                        />
                      )}
                    </Stack>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        label="Unit"
                        value={option.unit || unit}
                        onChange={(e) => updateQuoteOption(option.id, { unit: e.target.value })}
                      >
                        {MATERIAL_UNIT_OPTIONS.map((optionUnit) => <MenuItem key={optionUnit} value={optionUnit}>{optionUnit}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <ActionIcon title="Remove option" onClick={() => setQuoteOptions((prev) => prev.filter((x) => x.id !== option.id))} danger>
                      <DeleteOutlineIcon fontSize="inherit" />
                    </ActionIcon>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
          {isSteel && !showOptionMatrix && (
            <>
              <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.25 }}>
                <Typography sx={{ fontSize: 11, color: FAINT, fontWeight: 800, textTransform: 'uppercase', mb: 0.5 }}>Base-rate diameters</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {STEEL_DIAMETERS.map((dia) => (
                    <FormControlLabel
                      key={dia}
                      control={<Checkbox size="small" checked={coveredDiameters.includes(dia)} onChange={(e) => setDiameterCovered(dia, e.target.checked)} />}
                      label={<SmallLabel>{dia}</SmallLabel>}
                    />
                  ))}
                </Stack>
                <Typography sx={{ color: FAINT, fontSize: 11.5, mt: 0.75 }}>
                  Checked sizes use base rate. Uncheck a size if vendor quotes a special rate for it, then enter that rate below.
                </Typography>
              </Box>
              {coveredDiameters.length < STEEL_DIAMETERS.length && (
                <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.25 }}>
                  <Typography sx={{ fontSize: 11, color: FAINT, fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>Special diameter rates</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1 }}>
                    {STEEL_DIAMETERS.filter((dia) => !coveredDiameters.includes(dia)).map((dia) => (
                      <TextField
                        key={dia}
                        label={dia}
                        value={diameterRates[dia] || ''}
                        onChange={(e) => setDiameterRates((prev) => ({ ...prev, [dia]: e.target.value }))}
                        type="number"
                        size="small"
                        placeholder="special rate"
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                    ))}
                  </Box>
                  <Typography sx={{ color: FAINT, fontSize: 11.5, mt: 1 }}>
                    Unchecked sizes need a special rate to count in coverage. Leave blank if this quote does not apply to that size.
                  </Typography>
                </Box>
              )}
            </>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField label="Rate effective from" type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Rate effective until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField label="Payment terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} size="small" fullWidth placeholder="e.g. 30 days credit" />
            <TextField label="Delivery timeline" value={deliveryTimeline} onChange={(e) => setDeliveryTimeline(e.target.value)} size="small" fullWidth placeholder="e.g. 2 days from PO" />
          </Stack>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 1.25 }}>
            <Typography sx={{ fontSize: 11, color: FAINT, fontWeight: 800, textTransform: 'uppercase', mb: 0.5 }}>Material checklist</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              <FormControlLabel control={<Checkbox size="small" checked={checklist.testCertificate} onChange={(e) => setCheck('testCertificate', e.target.checked)} />} label={<SmallLabel>Test certificate</SmallLabel>} />
              <FormControlLabel control={<Checkbox size="small" checked={checklist.weighbridgeSlip} onChange={(e) => setCheck('weighbridgeSlip', e.target.checked)} />} label={<SmallLabel>Weighbridge slip</SmallLabel>} />
              <FormControlLabel control={<Checkbox size="small" checked={checklist.heatNumberTraceability} onChange={(e) => setCheck('heatNumberTraceability', e.target.checked)} />} label={<SmallLabel>Heat number traceability</SmallLabel>} />
              <FormControlLabel control={<Checkbox size="small" checked={checklist.freeUnloading} onChange={(e) => setCheck('freeUnloading', e.target.checked)} />} label={<SmallLabel>Free unloading</SmallLabel>} />
              <FormControlLabel control={<Checkbox size="small" checked={checklist.creditPeriod} onChange={(e) => setCheck('creditPeriod', e.target.checked)} />} label={<SmallLabel>Credit period</SmallLabel>} />
            </Stack>
          </Box>
          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" fullWidth multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {edit && onDelete && <Button color="error" onClick={() => { void onDelete(); }}>Void quote</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSaveQuote}
          onClick={() => {
            if (!canSaveQuote) return;
            onSave(buildMaterialQuotePayload({
            id: edit?.id || `quote_${Date.now()}`,
            vendor: vendor.trim(),
            brand: brand.trim(),
            refName: refName.trim(),
            refNumber: refNumber.trim(),
            rate: hasTemplateOptions ? 0 : num(rate),
            materialCode: materialCode.trim(),
            unit,
            gst: num(gst),
            gstMode: gstMode || 'exclusive',
            freight: num(freight),
            loading: num(loading),
            quoteDate,
            validUntil,
            paymentTerms: paymentTerms.trim(),
            deliveryTimeline: deliveryTimeline.trim(),
            notes: notes.trim(),
            quoteOptions,
            checklist,
            isSteel,
            coveredDiameters,
            diameterRates,
          }));
          }}
        >
          Save quote
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MaterialRateRevisionDialog({ item, quote, option, edit, onClose, onSave }: {
  item: MaterialRateItem;
  quote: MaterialQuote;
  option: MaterialQuoteOption | null;
  edit?: MaterialRateRevision;
  onClose: () => void;
  onSave: (revision: MaterialRateRevision) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const currentRate = option ? quoteOptionBaseRate(quote, option) : quoteBaseRate(quote);
  const history = option ? (option.rateHistory || []) : (quote.baseRateHistory || []);
  const [rate, setRate] = useState(String(edit?.rate ?? currentRate ?? ''));
  const [effectiveFrom, setEffectiveFrom] = useState(edit?.effectiveFrom || today);
  const [reason, setReason] = useState(edit?.reason || 'Revised rate');
  const [saving, setSaving] = useState(false);
  const label = option ? quoteOptionLabel(option) : 'Base rate';
  const unit = option?.unit || quote.unit || item.unit;
  const save = async () => {
    if (!num(rate) || !effectiveFrom) return;
    setSaving(true);
    try {
      await onSave({ id: edit?.id || `rate_revision_${Date.now()}`, previousRate: edit?.previousRate, rate: num(rate), effectiveFrom, reason: reason.trim(), recordedAt: edit?.recordedAt || new Date().toISOString() });
    } finally {
      setSaving(false);
    }
  };
  return <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: POPUP_PAPER_SX }}>
    <DlgTitle onClose={onClose}>{edit ? 'Edit revised rate' : 'Add revised rate'}</DlgTitle>
    <DialogContent>
      <Stack gap={1.4} sx={{ mt: 0.75 }}>
        <Box sx={{ p: 1.25, bgcolor: PANEL2, borderRadius: 1.5 }}>
          <Typography sx={{ fontWeight: 900, color: DARK }}>{quote.vendor || 'Vendor'} · {label}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12, mt: 0.25 }}>Current rate: ₹{fmtRate(currentRate)} / {unit}. The selected vendor remains selected.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.25}>
          <TextField label={`Revised rate (${unit})`} type="number" value={rate} onChange={(event) => setRate(event.target.value)} inputProps={{ min: 0, step: 'any' }} fullWidth autoFocus />
          <TextField label="Effective from" type="date" value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
        <TextField label="Reason (optional)" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Supplier confirmed September price" fullWidth />
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
          <Typography sx={{ px: 1.2, py: 0.85, bgcolor: PANEL2, color: FAINT, fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase' }}>Rate history</Typography>
          <Stack gap={0}>
            <Stack direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.8, borderTop: `1px solid ${BORDER}` }}>
              <Typography sx={{ color: MUTED, fontSize: 12 }}>Starting rate</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 12 }}>₹{fmtRate(option ? num(option.rate) : num(quote.rate))} / {unit}</Typography>
            </Stack>
            {quoteRevisionRows({ ...quote, quoteOptions: option ? [{ ...option, rateHistory: history }] : [] }).filter((entry) => entry.label === label).map((entry, index) => {
              const difference = entry.revisedRate - entry.previousRate;
              const entryReason = entry.reason?.trim();
              return <Stack key={`${entry.effectiveFrom}_${index}`} direction="row" justifyContent="space-between" gap={1} sx={{ px: 1.2, py: 0.8, borderTop: `1px solid ${BORDER}` }}>
                <Box><Typography sx={{ color: MUTED, fontSize: 12 }}>From {entry.effectiveFrom}{difference ? ` · ${difference > 0 ? '+' : '−'}₹${fmtRate(Math.abs(difference))}` : ''}</Typography>{entryReason && !/^(?:revised(?: rate)?|rate revised)$/i.test(entryReason) && <Typography sx={{ color: FAINT, fontSize: 11 }}>{entryReason}</Typography>}</Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap' }}>₹{fmtRate(entry.previousRate)} → ₹{fmtRate(entry.revisedRate)} / {unit}</Typography>
              </Stack>;
            })}
          </Stack>
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={onClose} sx={{ color: MUTED }}>Cancel</Button><Button variant="contained" disabled={!num(rate) || !effectiveFrom || saving} onClick={save}>{saving ? 'Saving…' : edit ? 'Save revised rate' : 'Add revised rate'}</Button></DialogActions>
  </Dialog>;
}

function SmallLabel({ children }: { children: React.ReactNode }) {
  return <Box component="span" sx={{ fontSize: 12.5, color: MUTED }}>{children}</Box>;
}

function DlgTitle({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 18, pr: 6 }}>
      {children}
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: MUTED }}><CloseIcon /></IconButton>
    </DialogTitle>
  );
}
